/* prettier-ignore-file */
import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  ActivityIndicator,
  TextInput,
  Image,
  Modal,
  LogBox,
  Animated,
} from "react-native";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { auth, db, functions } from "../firebase.config";
import {
  doc,
  getDoc,
  collection,
  getDocs,
  updateDoc,
  arrayUnion,
  deleteDoc,
  setDoc,
  Timestamp,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import AuthHeader from "../components/AuthHeader";
import { useBookings, useProducts, useSettings, useAuthStore, useStripeTestMode } from "../stores";
import { getCollectionPath, getSettingsPath } from "../utils/dbPaths";
import { subscribeToCollection } from "../db_calls";
import { initializeBookingsCache } from "../services/bookingsCache";
import ContactOptionsModal from "../utils/ContactOptionsModal";
import { initializeProductsCache } from "../services/productsCache";
import { getTenantId, getStoreId } from "../services/userStoreCache";
import { calculateTransportationDiscountUtil, calculateDurationDiscountUtil } from '../utils/discountCalculations'
import PinAuthLayer from "../components/PinAuthLayer";
import PreviousOrderSummary from "../components/PreviousOrderSummary";
import CurrentOrderSummary from "../components/CurrentOrderSummary";
import VerbalOrderChanges from "../components/VerbalOrderChanges";
import { isDeviceTrusted } from "../utils/deviceTrust";
import { createChangeTrackingEntry } from "../utils/helperFuns";
import {
  formatPhoneNumber,
  capitalizeWords,
  formatAddress,
  parseFirestoreDate,
  dollarsToCents,
  centsToDollars,
  formatCentsAsUSD,
  formatCentsAsUSD as disp,
  formatDollarsAsUSDWithCents,
  calculatePercentage,
} from "../utils/formatting";

// Helper function to format price without .00
const formatPriceWithoutCents = (cents) => {
  if (cents === null || cents === undefined || cents === 0) return '';
  // Convert cents to dollars as a number (centsToDollars returns a string)
  const dollars = cents / 100;
  const formatted = dollars.toFixed(2);
  // Remove .00 if present
  return formatted.endsWith('.00') ? formatted.slice(0, -3) : formatted;
};

const log = (one, two) => {
  if (!two && two != 0) {
    console.log(one)
  } else {
    if (!(two > 0)) two = two.toString();
    console.log(one + ' : ', two)
  }
}

const logs = (one, two) => console.log(one && two ? one : JSON.stringify(one, null, 2), two ? JSON.stringify(two, null, 2) : '');

const logd = (one, two) => {
  if (!two && two != 0) {
    disp(one)
  } else {
    if (!(two > 0)) two = two.toString();
    console.log(one + " : " + disp(two))
  }
}


const EditBooking = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { bookingId } = useParams();
  
  // Subscribe to Zustand stores for live updates
  const bookingsFromStore = useBookings((state) => state.bookings);
  const products1FromStore = useProducts((state) => state.products1);
  const products2FromStore = useProducts((state) => state.products2);
  const settingsFromStore = useSettings((state) => state.settings);
  const useTestStripe = useStripeTestMode((state) => state.useTestStripe);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isAdmin = useAuthStore((state) => state.isAdmin);
  // Auth state - replaced by store
  // const [isAuthenticated, setIsAuthenticated] = useState(false);
  // const [isAdmin, setIsAdmin] = useState(false);
  // Loading states
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [processingType, setProcessingType] = useState("payment"); // "payment", "refund", "cancellation"
  const [isVerified, setIsVerified] = useState(false);
  const [unauthListenersInitialized, setUnauthListenersInitialized] =
    useState(false);
  const [unauthUnsubscribers, setUnauthUnsubscribers] = useState(null);
  const [bookingNotFound, setBookingNotFound] = useState(false);
  
  // Contact modal state
  const [contactModalVisible, setContactModalVisible] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // Blinking animation for empty order summary
  const emptyOrderSummaryBlinkOpacity = useRef(new Animated.Value(1)).current;

  useEffect(() => {
    const blinkAnimation = Animated.loop(
      Animated.sequence([
        Animated.timing(emptyOrderSummaryBlinkOpacity, {
          toValue: 0.35,
          duration: 800,
          useNativeDriver: true,
        }),
        Animated.timing(emptyOrderSummaryBlinkOpacity, {
          toValue: 1,
          duration: 800,
          useNativeDriver: true,
        }),
      ])
    );
    blinkAnimation.start();

    return () => {
      blinkAnimation.stop();
    };
  }, [emptyOrderSummaryBlinkOpacity]);

  const [paymentMethod, setPaymentMethod] = useState("existing"); // "existing" or "new"
  // Booking data (NEW STRUCTURE: single booking object, no master/sub)
  const [booking, setBooking] = useState(null);
  const [originalBooking, setOriginalBooking] = useState(null); // Track original state for comparison
  // Product catalogs (enriched with availability data)
  const [products1Catalog, setProducts1Catalog] = useState([]);
  const [products2Catalog, setProducts2Catalog] = useState([]);
  // Quantity changes tracking (computed)
  const quantityChanges = useMemo(() => {
    if (!newBooking || !originalBooking) return { products1: {}, products2: {} };
    
    const changes = { products1: {}, products2: {} };
    
    // Compare product1
    if (newBooking.product1) {
       newBooking.product1.forEach(newItem => {
          const originalItem = (originalBooking.product1 || []).find(p => p.id === newItem.id);
          const oldQty = originalItem ? originalItem.quantity : 0;
          if (newItem.quantity !== oldQty) {
             changes.products1[newItem.id] = newItem.quantity;
          }
       });
    }
    // Check for removed product1 (if implicit)
    if (originalBooking.product1) {
       originalBooking.product1.forEach(oldItem => {
          const newItem = (newBooking.product1 || []).find(p => p.id === oldItem.id);
          if (!newItem && oldItem.quantity > 0) {
             changes.products1[oldItem.id] = 0;
          }
       });
    }
    
    // Compare product2
    if (newBooking.product2) {
       newBooking.product2.forEach(newItem => {
          const originalItem = (originalBooking.product2 || []).find(p => p.id === newItem.id);
          const oldQty = originalItem ? originalItem.quantity : 0;
          if (newItem.quantity !== oldQty) {
             changes.products2[newItem.id] = newItem.quantity;
          }
       });
    }
    // Check for removed product2
    if (originalBooking.product2) {
       originalBooking.product2.forEach(oldItem => {
          const newItem = (newBooking.product2 || []).find(p => p.id === oldItem.id);
          if (!newItem && oldItem.quantity > 0) {
             changes.products2[oldItem.id] = 0;
          }
       });
    }
    
    return changes;
  }, [newBooking, originalBooking]);

  // Fallback for Previous Order: compute original booking product cost when newTotals hasn't been populated yet (first load)
  const previousOrderProductCostCents = useMemo(() => {
    if (newTotals?.originalBookingProductCostCents != null && newTotals.originalBookingProductCostCents > 0) {
      return newTotals.originalBookingProductCostCents;
    }
    if (!originalBooking) return 0;
    const p1 = products1Catalog.length > 0 ? products1Catalog : products1FromStore;
    const p2 = products2Catalog.length > 0 ? products2Catalog : products2FromStore;
    if (!p1?.length && !p2?.length) return 0;
    let days = 0;
    if (originalBooking.startDate && originalBooking.endDate) {
      const start = parseFirestoreDate(originalBooking.startDate);
      const end = parseFirestoreDate(originalBooking.endDate);
      if (start && end) {
        start.setHours(0, 0, 0, 0);
        end.setHours(0, 0, 0, 0);
        days = Math.round((end - start) / (1000 * 60 * 60 * 24)) || 0;
      }
    }
    const product1 = originalBooking.product1 || [];
    const product2 = originalBooking.product2 || [];
    const totalBikes = product1.reduce((sum, p) => sum + (p.quantity || 0), 0);
    let total = 0;
    product1.forEach((item) => {
      const cat = p1.find((c) => c.id === item.id);
      if (cat) total += (cat.price || 0) * (item.quantity || 0) * days;
    });
    product2.forEach((item) => {
      const cat = p2.find((c) => c.id === item.id);
      if (!cat) return;
      const opt = cat.option || "per-booking";
      if (opt === "per-booking") total += (cat.price || 0) * (item.quantity || 0);
      else if (opt === "per-bike") total += (cat.price || 0) * (item.quantity || 0) * totalBikes;
      else total += (cat.price || 0) * (item.quantity || 0) * days;
    });
    return total;
  }, [newTotals?.originalBookingProductCostCents, originalBooking, products1Catalog, products2Catalog, products1FromStore, products2FromStore]);

  // Delivery options changes
  // Structure: { delivery: bool, homePickup: bool, inStorePickup: bool }
  const [deliveryChanges, setDeliveryChanges] = useState(null);
  // Settings
  const [shopInfo, setShopInfo] = useState({});
  const [storeLogo, setStoreLogo] = useState("");
  const [product1CategoryName, setProduct1CategoryName] = useState("Product1");
  const [product2CategoryName, setProduct2CategoryName] = useState("Product2");
  const [salesTaxPercent, setSalesTaxPercent] = useState(0);
  const [cancellationPolicy, setCancellationPolicy] = useState("");
  const [refundProcessingFee, setRefundProcessingFee] = useState(3);
  // Date editing
  const [showDateEditor, setShowDateEditor] = useState(false);
  const [showDeliveryEditor, setShowDeliveryEditor] = useState(false);
  const [showDeliveryAddressEditor, setShowDeliveryAddressEditor] = useState(false);
  const [showProductsEditor, setShowProductsEditor] = useState(false); // Default to closed
  const [showPaymentsRefunds, setShowPaymentsRefunds] = useState(true); // Payments & Refunds section, default show
  const [showPreviousOrder, setShowPreviousOrder] = useState(false); // Previous Order summary, default hidden
  const [newStartDate, setNewStartDate] = useState(null);
  const [newEndDate, setNewEndDate] = useState(null);
  const [dateAvailability, setDateAvailability] = useState({}); // { "2025-11-10": true/false }
  const [calculatingAvailability, setCalculatingAvailability] = useState(false);
  const [rentalHasStarted, setRentalHasStarted] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [extensionCost, setExtensionCost] = useState(null);
  // Delivery/Pickup options
  const [deliveryNeeded, setDeliveryNeeded] = useState(false);
  const [homePickupNeeded, setHomePickupNeeded] = useState(false);
  const [inStorePickup, setInStorePickup] = useState(false);
  const [gatedCommunity, setGatedCommunity] = useState(false);
  const [communityName, setCommunityName] = useState("");
  const [addressNotes, setAddressNotes] = useState("");
  // Delivery address editing
  const [showAddressEditor, setShowAddressEditor] = useState(false);
  const [newAddress, setNewAddress] = useState("");
  const [deliveryAddress, setDeliveryAddress] = useState(""); // Different from customer address
  const [addressInput, setAddressInput] = useState("");
  const [addressConfirmed, setAddressConfirmed] = useState(false);
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const selectingAddressRef = useRef(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [newDistance, setNewDistance] = useState(null);
  const [addressLoadedFromBooking, setAddressLoadedFromBooking] =
    useState(false);
  const [addressManuallyChanged, setAddressManuallyChanged] = useState(false);
  const addressDebounceTimer = useRef(null);
  const [mileageRate, setMileageRate] = useState(0);
  const [loadingFee, setLoadingFee] = useState(0);
  const [startingAddress, setStartingAddress] = useState("");
  const [deliveryDiscounts, setDeliveryDiscounts] = useState([]);
  const [useDeliveryDiscounts, setUseDeliveryDiscounts] = useState(false);
  const [durationDiscounts, setDurationDiscounts] = useState([]);
  const [useDurationDiscounts, setUseDurationDiscounts] = useState(false);
  const [mapsLoaded, setMapsLoaded] = useState(false);
  const [showAddressValidationModal, setShowAddressValidationModal] = useState(false);
  const [addressDistanceMiles, setAddressDistanceMiles] = useState(null);
  const [mapLoading, setMapLoading] = useState(false);
  const [mapError, setMapError] = useState(null);
  const [showAddressWrongModal, setShowAddressWrongModal] = useState(false);
  const autocompleteServiceRef = useRef(null);
  const placesServiceRef = useRef(null);
  const mapRef1 = useRef(null);
  const mapInstance1 = useRef(null);
  const directionsService1 = useRef(null);
  const directionsRenderer1 = useRef(null);
  const validateButtonOpacity = useRef(new Animated.Value(1)).current;
  const hasInteractedWithAddressRef = useRef(false);
  const blurTimeoutRef = useRef(null);
  // Delivery/Pickup cost tracking
  const [deliveryCost, setDeliveryCost] = useState(0);
  const [pickupCost, setPickupCost] = useState(0);
  const [deliveryDiscountAmount, setDeliveryDiscountAmount] = useState(0);
  // Info messages
  const [deliveryInfo, setDeliveryInfo] = useState("");
  const [pickupInfo, setPickupInfo] = useState("");
  // Admin refund override
  const [useRefundOverride, setUseRefundOverride] = useState(false);
  const [refundOverridePercent, setRefundOverridePercent] = useState(0);
  const [useCustomAmount, setUseCustomAmount] = useState(false);
  const [customRefundAmount, setCustomRefundAmount] = useState("0.00");
  const [showAdminOverride, setShowAdminOverride] = useState(false);
  const [showLockDialog, setShowLockDialog] = useState(false);
  const [lockDialogMessage, setLockDialogMessage] = useState("");
  // Tooltip and image modal state (for product cards)
  const [visibleTooltip, setVisibleTooltip] = useState(null);
  const [imageModalVisible, setImageModalVisible] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState(null);
  const [showDeliveryInfoFull, setShowDeliveryInfoFull] = useState(false);
  // Discount unlock modal state
  const [discountUnlockModalVisible, setDiscountUnlockModalVisible] = useState(false);
  const [unlockedDiscountType, setUnlockedDiscountType] = useState(null); // "transportation" or "duration"
  const [cancelBookingModalVisible, setCancelBookingModalVisible] = useState(false);
  const [cancelBookingData, setCancelBookingData] = useState(null);
  const [authCancelModalVisible, setAuthCancelModalVisible] = useState(false);
  const [authCancelData, setAuthCancelData] = useState(null);
  const [showRefundConfirmModal, setShowRefundConfirmModal] = useState(false);
  const [showRefundOnlyModal, setShowRefundOnlyModal] = useState(false);
  const [refundOnlyData, setRefundOnlyData] = useState(null);
  const [showPaymentConfirmModal, setShowPaymentConfirmModal] = useState(false);
  const [showCancellationRequestModal, setShowCancellationRequestModal] = useState(false);
  const [cancellationRequestData, setCancellationRequestData] = useState(null);
  const [sliderPosition, setSliderPosition] = useState(0);
  const sliderTrackRef = useRef(null);
  const [unlockedDiscountName, setUnlockedDiscountName] = useState("");
  const [unlockedDiscountPercent, setUnlockedDiscountPercent] = useState(0);
  const [shownTransportationUnlock, setShownTransportationUnlock] = useState(false);
  const [shownDurationUnlock, setShownDurationUnlock] = useState(false);
  // Lower tier modal state
  const [showLowerTransportationTierModal, setShowLowerTransportationTierModal] = useState(false);
  const [showLowerDurationTierModal, setShowLowerDurationTierModal] = useState(false);
  const [lowerTransportationTierData, setLowerTransportationTierData] = useState({ oldName: "", newName: "" });
  const [lowerDurationTierData, setLowerDurationTierData] = useState({ oldName: "", newName: "" });
  const hasInitializedRef = useRef(false);
  const prevDeliveryRef = useRef(null);
  const prevHomePickupRef = useRef(null);
  const previousTransportationDiscountRef = useRef(null);
  const previousDurationDiscountRef = useRef(null);
  // Separate refs for higher tier detection
  const previousTransportationDiscountForHigherTierRef = useRef(null);
  const previousDurationDiscountForHigherTierRef = useRef(null);
  const isCalculatingTotalsRef = useRef(false);
  const prevNumMilesRef = useRef(null);
  const hasLoadedBookingRef = useRef(false);
  const loadedBookingIdRef = useRef(null);
  const [calendarAvailability, setCalendarAvailability] = useState({});
  const [productLeftForBooking, setProductLeftForBooking] = useState({});
  // Cancellation policy configuration
  const [cancellationWindows, setCancellationWindows] = useState([
    { hours: 48, percentPenalty: 0, daysPenalty: 0 },
    { hours: 24, percentPenalty: 25, daysPenalty: 1 },
    { hours: 0, percentPenalty: 50, daysPenalty: 2 },
  ]);
  const [penaltyType, setPenaltyType] = useState("percentage");
  // Admin notes
  const [adminNotes, setAdminNotes] = useState("");
  const [adminNotesSaving, setAdminNotesSaving] = useState(false);
  const [showAdminNotes, setShowAdminNotes] = useState(() => {
    // Default to showing if admin notes exist (will be updated when booking loads)
    return false;
  });
  const adminNotesTimerRef = useRef(null);

  //////////////////////////////////////////////////////////////////////////////////////////////
  /// MY STUFF ////////////////////////////////////////////////////////////////////////////////
  //////////////////////////////////////////////////////////////////////////////////////////////

  const [newTotals, setNewTotals] = useState({
    totals: {
      salesTax: 0,
      subtotalNoDiscount: 0,
      subtotalWithDiscount: 0,
      finalTotal: 0,
    },
    productCost: {
      newCost: 0,
      oldCost: 0,
      costDifference: 0
    },
    transportation: {
      // no discount
      newTotalChargeCents: 0,
      newDeliveryChargeCents: 0,
      newPickupChargeCents: 0,
      originalTotalChargeCents: 0,
      originalPickupChargeCents: 0,
      originalDeliveryChargeCents: 0,
      totalNetChargeCents: 0,
      deliveryNetChargeCents: 0,
      pickupNetChargeCents: 0,

      // discounted
      newTotalDiscountedChargesCents: 0,
      newPickupDiscountedChargeCents: 0,
      newDeliveryDiscountedChargeCents: 0,
      originalTotalDiscountedChargeCents: 0,
      originalPickupDiscountedChargeCents: 0,
      originalDDeliveryDiscountedChargeCents: 0,
      totalNetDiscountedChargeCents: 0,
      totalNetDeliveryChargeCents: 0,
      totalNetPickupChargeCents: 0,
      totalPaidForTransporationAcrossBookings: 0,

      discountName: '',
      discountPercent: 0,
      originalDiscountName: '',
      originalDiscountPercent: 0

    },
    durationDiscount: {
      discountCents: 0,
      discountPercent: 0,
      discountName: '',
      originalDiscountCents: 0,
      originalDiscountPercent: 0,
      originalDiscountName: '',
    },
    changedProducts: [],
    days: {
      new: 0,
      old: 0,
    },

  });

  // product1: [
  //   {
  //     id: string,                    // Product ID from catalog
  //     name: string,                  // Product name
  //     quantity: number,               // Quantity of this product
  //     price?: number,                 // Optional: price in cents (from catalog if not set)
  //     isFromOriginalBooking: boolean, // true if item was in original booking, false if newly added
  //     removedFromOriginalBooking: boolean, // true if item was removed (quantity set to 0), false otherwise
  //     itemPrice: number,              // Price in cents (from catalog)
  //     durationDiscountCents: number   // Duration discount amount in cents for this item
  //   }
  // ]

  const [newBooking, setNewBooking] = useState({
    product1: [],
    product2: [],
    homePickup: false,
    delivery: false,
    inStorePickup: false,
    deliveryAddress: "",
    startDate: null,
    endDate: null,
    bookingId: '',
    id: '',
    gatedCommunity: false,
    communityName: "",
    addressNotes: "",
    numMiles: 0,
  })




  const calculateTotals = async (newBooking, originalBooking, settingsParam = null, products1CatalogParam = null, products2CatalogParam = null) => {
    // log('>>>>> CALCULATING TOTALS <<<<<<<<<<<<')
    log('OriginalBooking:\n')
    logs(originalBooking)
    log('\nNew Booking:\n')
    logs(newBooking)
    // Prevent infinite loops - if already calculating, return early
    if (isCalculatingTotalsRef.current) {
      console.log("⏸️ calculateTotals already running, skipping...");
      return;
    }
    
    isCalculatingTotalsRef.current = true;
    
    try {
      // Use passed params or fall back to state
      const settings = settingsParam || settingsObject;
      const products1CatalogToUse = products1CatalogParam || (products1Catalog.length > 0 ? products1Catalog : products1FromStore);
      const products2CatalogToUse = products2CatalogParam || (products2Catalog.length > 0 ? products2Catalog : products2FromStore);

      // Wait for product catalogs to be available
      if (!products1CatalogToUse || products1CatalogToUse.length === 0 || !products2CatalogToUse || products2CatalogToUse.length === 0) {
        console.log("⏳ Waiting for product catalogs to load...");
        isCalculatingTotalsRef.current = false;
        return;
      }

      if (!settings || Object.keys(settings).length === 0) {
        console.log("⏳ Waiting for settings to load...");
        isCalculatingTotalsRef.current = false;
        return;
      }

      if (!newBooking.startDate || !originalBooking.startDate) {
        isCalculatingTotalsRef.current = false;
        return;
      }

    const calculateDuration = (newBooking, originalBooking) => {
      let newDays = 0;
      if (newBooking.startDate && newBooking.endDate) {
        const newStartDate = parseFirestoreDate(newBooking.startDate);
        const newEndDate = parseFirestoreDate(newBooking.endDate);
        newStartDate.setHours(0, 0, 0, 0);
        newEndDate.setHours(0, 0, 0, 0);
        newDays = Math.round((newEndDate - newStartDate) / (1000 * 60 * 60 * 24));
      }

      let originalDays = 0;
      if (originalBooking.startDate && originalBooking.endDate) {
        const originalStartDate = parseFirestoreDate(originalBooking.startDate);
        const originalEndDate = parseFirestoreDate(originalBooking.endDate);
        originalStartDate.setHours(0, 0, 0, 0);
        originalEndDate.setHours(0, 0, 0, 0);
        originalDays = Math.round((originalEndDate - originalStartDate) / (1000 * 60 * 60 * 24));
      }

      return {
        new: newDays,
        old: originalDays
      };
    };

    // Calculate number of days from newBooking timestamps
    const getBookingDays = (booking) => {
      if (!booking?.startDate || !booking?.endDate) {
        return 0;
      }

      const startDate = parseFirestoreDate(booking.startDate);
      const endDate = parseFirestoreDate(booking.endDate);
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);

      const days = Math.round((endDate - startDate) / (1000 * 60 * 60 * 24));
      return days;
    };

    const calculateTransportationDiscount = (booking, settingsParam = null) => {
      if (!booking) {
        return {
          discountName: '',
          discountPercent: 0
        };
      }

      // Use passed settings or fall back to function scope settings
      const effectiveSettings = settingsParam || settings;

      // Get discount info from API
      const discountResult = calculateTransportationDiscountAPI(booking, effectiveSettings, products1CatalogToUse, products2CatalogToUse);
      if (!discountResult) {
        return {
          discountName: '',
          discountPercent: 0
        };
      }

      const discountPercent = discountResult.discountPercent || 0;
      const discountId = discountResult.discountId || null;

      // Get discount name - use discount.name if available, otherwise generate it
      // Extract discount settings from settings object
      const effectiveSettingsForName = settingsParam || settings;
      const deliveryDiscountsValue = effectiveSettingsForName?.deliveryDiscounts?.discounts || deliveryDiscounts;
      
      let discountName = '';
      if (discountId && deliveryDiscountsValue && deliveryDiscountsValue.length > 0) {
        const discount = deliveryDiscountsValue.find((d) => d.id === discountId);
        if (discount) {
          // Use discount.name if it exists (like RentalBookings.js), otherwise generate it
          discountName = discount.name || `$${discount.from}-${discount.to} order: ${discountPercent}% off delivery`;
        }
      }

      return {
        discountName: discountName,
        discountPercent: discountPercent
      };
    };

    const calculateTransportationCost = async (deliveryAddress, settingsParam = null) => {
      // Use passed settings or fall back to state
      const effectiveSettings = settingsParam || settings;

      // Get starting address from settings param or state (fallback)
      const effectiveStartingAddress = effectiveSettings?.startingAddress?.address ||
        startingAddress ||
        settingsObjectRef.current?.startingAddress?.address ||
        settingsObject?.startingAddress?.address ||
        "";

      // Get rates from settings param or state (fallback)
      // Note: rates from settings are stored in CENTS, not dollars
      const effectiveMileageRate = effectiveSettings?.mileageRate?.rate ||
        mileageRate ||
        settingsObjectRef.current?.mileageRate?.rate ||
        settingsObject?.mileageRate?.rate ||
        0;

      const effectiveLoadingFee = effectiveSettings?.loadingFee?.fee ||
        loadingFee ||
        settingsObjectRef.current?.loadingFee?.fee ||
        settingsObject?.loadingFee?.fee ||
        0;

      if (!deliveryAddress || deliveryAddress.trim() === "") {
        return 0;
      }


      if (!effectiveStartingAddress || effectiveStartingAddress.trim() === "") {
        return 0;
      }

      // Normalize perMileRate and loadingFee (treat null/undefined/NaN as 0)
      // Rates are in CENTS from settings (same as RentalBookings.js)
      const validPerMileRate = (effectiveMileageRate && !isNaN(effectiveMileageRate) && effectiveMileageRate > 0) ? effectiveMileageRate : 0;
      const validLoadingFee = (effectiveLoadingFee && !isNaN(effectiveLoadingFee) && effectiveLoadingFee > 0) ? effectiveLoadingFee : 0;

      // If both perMileRate and loadingFee are 0/invalid, return 0
      if (validPerMileRate === 0 && validLoadingFee === 0) {
        return 0;
      }

      try {
        let distanceMiles = 0;

        // Only calculate distance if perMileRate is valid (needed for distance-based cost)
        if (validPerMileRate > 0) {
          // Calculate distance using cloud function (avoids CORS issues)
          const getDistanceFn = httpsCallable(functions, "getDistance");
          const result = await getDistanceFn({
            fullAddress: deliveryAddress.trim(),
          });

          if (result && result.data) {
            // getDistance returns { origin, destination, miles }
            distanceMiles = result.data.miles || result.data.distanceMiles || 0;

            if (distanceMiles <= 0) {
              // No distance data available - if only perMileRate was needed, return 0
              // If loadingFee is valid, return just the loading fee (one-way) in CENTS
              return { cost: validLoadingFee > 0 ? Math.round(validLoadingFee) : 0, distance: null };
            }
          } else {
            // No distance data available - if only perMileRate was needed, return 0
            // If loadingFee is valid, return just the loading fee (one-way) in CENTS
            return { cost: validLoadingFee > 0 ? Math.round(validLoadingFee) : 0, distance: null };
          }
        }

        // Calculate transportation cost for one-way trip only:
        // Rates are in CENTS, so calculation produces CENTS directly
        // Single distance and single loading fee (no doubling)
        const costInCents = Math.round((distanceMiles * validPerMileRate) + validLoadingFee);

        // Return both cost and distance (distance with 1 decimal place)
        const numMiles = distanceMiles > 0 ? parseFloat(distanceMiles.toFixed(1)) : null;
        return { cost: costInCents, distance: numMiles };
      } catch (error) {
        console.error("Error calculating transportation cost:", error);
        // If error occurs but loadingFee is valid, return just the loading fee (one-way) in CENTS
        return { cost: validLoadingFee > 0 ? Math.round(validLoadingFee) : 0, distance: null };
      }
    };


    const calculateDurationDiscount = ({ newBooking, originalBooking, discountPercentToUse, settingsParam = null }) => {
      // Use passed settings or fall back to function scope settings
      const effectiveSettings = settingsParam || settings;
      const durationDiscountsValue = effectiveSettings?.durationDiscounts?.discounts || durationDiscounts;
      
      // Return empty if both are present
      if (newBooking && originalBooking) {
        return {
          discountName: '',
          discountCents: 0,
          discountPercent: 0
        };
      }

      // Determine which booking to use
      const bookingToUse = newBooking || originalBooking;
      if (!bookingToUse) {
        return {
          discountName: '',
          discountCents: 0,
          discountPercent: 0
        };
      }

      // Use provided discountPercentToUse if available, otherwise get from API
      let discountPercent = 0;
      let discountId = null;
      let discountCents = 0;
      let discountName = '';

      if (discountPercentToUse !== undefined && discountPercentToUse !== null) {
        // Use the provided discount percent
        discountPercent = discountPercentToUse;
        // Find the discount ID that matches this percent
        if (durationDiscountsValue && durationDiscountsValue.length > 0) {
          const matchingDiscount = durationDiscountsValue.find((d) => d.value === discountPercentToUse);
          if (matchingDiscount) {
            discountId = matchingDiscount.id;
            // Use discount.name if available (like RentalBookings), otherwise generate it
            discountName = matchingDiscount.name || `${matchingDiscount.fromDays}-${matchingDiscount.toDays} day rental: ${discountPercent}% off`;
          }
        }

        // Calculate discount amount using the booking's product costs
        // Calculate rental duration in days
        if (bookingToUse.startDate && bookingToUse.endDate) {
          const startDate = parseFirestoreDate(bookingToUse.startDate);
          const endDate = parseFirestoreDate(bookingToUse.endDate);
          startDate.setHours(0, 0, 0, 0);
          endDate.setHours(0, 0, 0, 0);
          const days = Math.round((endDate - startDate) / (1000 * 60 * 60 * 24));

          // Calculate equipment subtotal from booking products (in cents)
          let equipmentSubtotalCents = 0;

          // Calculate product1 costs (in cents)
          if (bookingToUse.product1) {
            bookingToUse.product1.forEach((item) => {
              const catalogProduct = products1CatalogToUse.find((p) => p.id === item.id);
              if (catalogProduct) {
                const priceCents = catalogProduct.price || 0; // Already in cents
                equipmentSubtotalCents += priceCents * (item.quantity || 0) * days;
              }
            });
          }

          // Calculate product2 costs (in cents)
          if (bookingToUse.product2) {
            bookingToUse.product2.forEach((item) => {
              const catalogProduct = products2CatalogToUse.find((p) => p.id === item.id);
              if (catalogProduct) {
                const priceCents = catalogProduct.price || 0; // Already in cents
                if (catalogProduct.option === "per-booking" && item.quantity > 0) {
                  equipmentSubtotalCents += priceCents * (item.quantity || 0);
                } else if (catalogProduct.option === "per-bike" && item.quantity > 0) {
                  const totalBikes = (bookingToUse.product1 || []).reduce((sum, p) => sum + (p.quantity || 0), 0);
                  equipmentSubtotalCents += priceCents * item.quantity * totalBikes;
                } else if (catalogProduct.option === "user-selection") {
                  equipmentSubtotalCents += priceCents * (item.quantity || 0) * days;
                }
              }
            });
          }

          // Calculate discount amount in cents
          discountCents = Math.round(equipmentSubtotalCents * (discountPercent / 100));
        }
      } else {
        // Get discount info from API
        const discountResult = calculateDurationDiscountAPI(bookingToUse, settings, products1CatalogToUse, products2CatalogToUse);
        discountCents = discountResult?.discountAmountCents || 0;
        discountId = discountResult?.discountId || null;
        discountPercent = discountResult?.discountPercent || 0;
        // Use discountName from API result (utility function returns it)
        discountName = discountResult?.discountName || '';

        // Fallback: Generate discount name if not provided by API
        if (!discountName && discountId && durationDiscountsValue && durationDiscountsValue.length > 0) {
          const discount = durationDiscountsValue.find((d) => d.id === discountId);
          if (discount) {
            discountPercent = discount?.value || discountPercent;
            discountName = discount.name || `${discount.fromDays}-${discount.toDays} day rental: ${discountPercent}% off`;
          }
        }
      }

      const result = {
        discountName: discountName,
        discountCents: discountCents,
        discountPercent: discountPercent
      };
      return result;
    };


    // Calculate duration discount totals for original vs new items
    const calculateDurationDiscountTotals = (bookingToProcess) => {
      // Calculate duration discount for the booking to get the percent
      // This ensures we always have the correct discount percent for the current booking
      const durationDiscountResult = calculateDurationDiscount({
        newBooking: bookingToProcess,
        originalBooking: null,
        discountPercentToUse: undefined,
        settingsParam: settings
      });
      const durationDiscountPercent = durationDiscountResult.discountPercent || 0;
      const originalDurationDiscountPercent = originalBooking?.durationDiscount?.discountPercent || 0;

      // Create a copy of the booking to update
      const updatedBooking = JSON.parse(JSON.stringify(bookingToProcess));

      let originalItemsTotalDurationDiscountCents = 0;
      let newItemsTotalDurationDiscountCents = 0;
      let removedItemsTotalDurationDiscountCents = 0;

      // Helper function to process items and calculate quantity differences
      const processItems = (items, originalItems, catalogToUse, isProduct1) => {
        items.forEach((item) => {
          const isFromOriginal = item.isFromOriginalBooking === true;
          const isRemoved = item.removedFromOriginalBooking === true;

          // Get itemPrice if available, otherwise get from catalog
          let itemPrice = item.itemPrice;
          if (!itemPrice || itemPrice === 0) {
            const catalogProduct = catalogToUse.find((p) => p.id === item.id);
            itemPrice = catalogProduct?.price || 0;
            // Update itemPrice in the item
            item.itemPrice = itemPrice;
          }

          // Find original item to compare quantities
          const originalItem = originalItems?.find((orig) => orig.id === item.id);
          const originalQuantity = originalItem?.quantity || 0;
          const newQuantity = item.quantity || 0;
          const quantityDifference = newQuantity - originalQuantity;

          // Calculate discount per unit based on discount percent
          const itemDurationDiscountCentsPerUnit = Math.round(itemPrice * (durationDiscountPercent / 100));
          const originalItemDurationDiscountCentsPerUnit = Math.round(itemPrice * (originalDurationDiscountPercent / 100));

          // Set durationDiscountCents on the item if not already set (for new items or increased quantities)
          if ((!item.durationDiscountCents || item.durationDiscountCents === 0) && newQuantity > 0) {
            item.durationDiscountCents = itemDurationDiscountCentsPerUnit * newQuantity;
          }

          if (isRemoved && isFromOriginal && originalQuantity > 0) {
            // Item was completely removed - use original discount percent
            removedItemsTotalDurationDiscountCents += originalItemDurationDiscountCentsPerUnit * originalQuantity;
          } else if (isFromOriginal && quantityDifference < 0 && originalQuantity > 0) {
            // Original item with decreased quantity - use original discount percent
            removedItemsTotalDurationDiscountCents += originalItemDurationDiscountCentsPerUnit * Math.abs(quantityDifference);
          } else if (isFromOriginal && quantityDifference > 0) {
            // Original item with increased quantity - use new discount percent
            newItemsTotalDurationDiscountCents -= itemDurationDiscountCentsPerUnit * quantityDifference;
          } else if (!isFromOriginal && newQuantity > 0) {
            // New item (not from original booking) - use new discount percent
            newItemsTotalDurationDiscountCents -= itemDurationDiscountCentsPerUnit * newQuantity;
          }
        });
      };

      // Process product1 items only — duration discounts apply to bicycles (product1) only,
      // matching the behavior of calculateDurationDiscountUtil used in computeBookingCosts.
      processItems(
        updatedBooking.product1 || [],
        originalBooking?.product1 || [],
        products1CatalogToUse,
        true
      );

      return {
        updatedBooking,
        originalItemsTotalDurationDiscountCents,
        newItemsTotalDurationDiscountCents,
        removedItemsTotalDurationDiscountCents
      };
    };

    const calculateProductChanges = (newBooking, originalBooking) => {
      // changedProducts: array of all products with quantity differences
      // Each object contains: name, id, category, quantityDifference, and quantityCentsDifference
      // quantityDifference = newQuantity - originalQuantity
      // quantityCentsDifference = cost difference in cents for the quantity change
      // Positive if new > original, negative if new < original, excluded if same
      const changedProducts = [];

      // Calculate rental days for newBooking
      let days = 0;
      if (newBooking.startDate && newBooking.endDate) {
        const startDate = parseFirestoreDate(newBooking.startDate);
        const endDate = parseFirestoreDate(newBooking.endDate);
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(0, 0, 0, 0);
        days = Math.round((endDate - startDate) / (1000 * 60 * 60 * 24));
      }

      // Calculate total product1 quantity for per-bike calculations
      const totalProduct1Quantity = (newBooking.product1 || []).reduce(
        (sum, p) => sum + (p.quantity || 0),
        0
      );

      // Helper function to calculate cost difference in cents
      const calculateQuantityCentsDifference = (productId, category, quantityDifference, option) => {
        // Find product in catalog - use function scope catalogs
        const catalogProduct = category === "product1"
          ? products1CatalogToUse.find((p) => p.id === productId)
          : products2CatalogToUse.find((p) => p.id === productId);

        if (!catalogProduct) {
          return 0; // Product not found in catalog
        }

        // Note: catalogProduct.price is already stored in CENTS
        const priceCents = catalogProduct.price || 0;
        let costCents = 0;

        if (category === "product1") {
          // Product1: price * quantityDifference * days
          costCents = priceCents * quantityDifference * days;
        } else if (category === "product2") {
          // Product2 depends on option type
          const productOption = option || catalogProduct.option;

          if (productOption === "per-booking") {
            // Per-booking: price * quantityDifference
            costCents = priceCents * quantityDifference;
          } else if (productOption === "per-bike") {
            // Per-bike: price * quantityDifference * totalProduct1Quantity
            costCents = priceCents * quantityDifference * totalProduct1Quantity;
          } else if (productOption === "user-selection") {
            // User-selection: price * quantityDifference * days
            costCents = priceCents * quantityDifference * days;
          }
        }

        return costCents;
      };

      // Helper function to process products consistently for both product1 and product2
      const processProducts = (newItems, originalItems, category) => {
        // Check all items in newBooking
        (newItems || []).forEach((newItem) => {
          const originalItem = (originalItems || []).find((p) => p.id === newItem.id);
          const originalQuantity = originalItem ? (originalItem.quantity || 0) : 0;
          const newQuantity = newItem.quantity || 0;
          const quantityDifference = newQuantity - originalQuantity;

          // Only add if quantity actually changed
          if (quantityDifference !== 0) {
            const option = category === "product2" ? (newItem.option || null) : null;
            const quantityCentsDifference = calculateQuantityCentsDifference(
              newItem.id,
              category,
              quantityDifference,
              option
            );

            changedProducts.push({
              id: newItem.id,
              name: newItem.name || "",
              category: category,
              quantityDifference: quantityDifference,
              quantityCentsDifference: quantityCentsDifference
            });
          }
        });

        // Check items in originalBooking that were completely removed (not in newBooking array)
        (originalItems || []).forEach((originalItem) => {
          const newItem = (newItems || []).find((p) => p.id === originalItem.id);
          if (!newItem) {
            // Product was completely removed from newBooking
            const originalQuantity = originalItem.quantity || 0;
            const quantityDifference = 0 - originalQuantity; // Negative value
            const option = category === "product2" ? (originalItem.option || null) : null;
            const quantityCentsDifference = calculateQuantityCentsDifference(
              originalItem.id,
              category,
              quantityDifference,
              option
            );

            changedProducts.push({
              id: originalItem.id,
              name: originalItem.name || "",
              category: category,
              quantityDifference: quantityDifference,
              quantityCentsDifference: quantityCentsDifference
            });
          }
        });
      };

      // Process product1 and product2 using the same logic
      processProducts(newBooking.product1, originalBooking.product1, "product1");
      processProducts(newBooking.product2, originalBooking.product2, "product2");

      return changedProducts;
    };

    const calculateProductCost = (products) => {
      // Sum all quantityCentsDifference values from changedProducts
      const productCostCents = (products || []).reduce(
        (sum, product) => sum + (product.quantityCentsDifference || 0),
        0
      );

      return {
        productCostCents: productCostCents,
      };
    };

    const calculateProductBookingCost = (booking) => {
      if (!booking) {
        return 0;
      }

      // Use products from function scope (passed as params or from state)
      const products1ToUse = products1CatalogToUse;
      const products2ToUse = products2CatalogToUse;

      // Get days from booking using calculateDuration
      const daysResult = calculateDuration(booking, booking);
      const days = daysResult.new || 0;

      // Get product arrays from booking
      const products1 = booking.product1 || [];
      const products2 = booking.product2 || [];

      // Calculate total product1 quantity for per-bike calculations
      const totalProduct1Quantity = products1.reduce(
        (sum, p) => sum + (p.quantity || 0),
        0
      );

      let totalCostCents = 0;

      // Calculate product1 costs (in cents)
      // Note: catalogProduct.price is already stored in CENTS
      products1.forEach((item) => {
        if (item.removedFromOriginalBooking) return;
        const catalogProduct = products1ToUse.find((p) => p.id === item.id);
        if (catalogProduct) {
          const priceCents = catalogProduct.price || 0; // Already in cents
          totalCostCents += priceCents * (item.quantity || 0) * days;
        }
      });

      // Calculate product2 costs (in cents)
      // Note: catalogProduct.price is already stored in CENTS
      products2.forEach((item) => {
        if (item.removedFromOriginalBooking) return;
        const catalogProduct = products2ToUse.find((p) => p.id === item.id);
        if (catalogProduct) {
          const priceCents = catalogProduct.price || 0; // Already in cents

          if (catalogProduct.option === "per-booking" && item.quantity > 0) {
            // Per-booking: multiply price by quantity
            totalCostCents += priceCents * item.quantity;
          } else if (catalogProduct.option === "per-bike" && item.quantity > 0) {
            // Per-bike: multiply by total product1 quantity
            totalCostCents += priceCents * item.quantity * totalProduct1Quantity;
          } else if (catalogProduct.option === "user-selection") {
            // User-selection: multiply by quantity and days
            totalCostCents += priceCents * (item.quantity || 0) * days;
          }
        }
      });

      return totalCostCents;
    }

      log(newBooking)

      ///////////////////////////// --> PRODUCT TOTALS ///////////////////////////////////////

    const totalBookingProductCost = calculateProductBookingCost(newBooking)
    const originalBookingProductCost = calculateProductBookingCost(originalBooking)
      const bookingChangesProductCost = totalBookingProductCost - originalBookingProductCost

      log('\n ---- PRODUCT COST VALUES ----')
      logd('new total product cost', (totalBookingProductCost))
      logd('old total product cost', originalBookingProductCost)
      logd('product cost edit differ', bookingChangesProductCost)


      ////////////////////////////// --> TRANSPORTATION ADJUSTMENTS //////////////////////////////
      let oneWayTranspCostBeforeDiscount = originalBooking.pickupCostCents > 0 ? originalBooking.pickupCostCents : originalBooking.deliveryCostCents
      // change in delivery address
    if (newBooking.deliveryAddress != originalBooking.deliveryAddress && (newBooking.homePickup || newBooking.delivery)) {
      const transportationResult = await calculateTransportationCost(newBooking.deliveryAddress, settings);
      // Handle both old format (number) and new format (object)
      if (typeof transportationResult === 'object' && transportationResult !== null) {
        oneWayTranspCostBeforeDiscount = transportationResult.cost;
        // Update newBooking with the calculated distance (1 decimal place)
        if (transportationResult.distance !== null && transportationResult.distance !== undefined) {
          newBooking = {
            ...newBooking,
            numMiles: transportationResult.distance
          };
          // Update state to persist numMiles (only if it changed)
          // Use ref to track previous value and prevent unnecessary updates
          if (prevNumMilesRef.current !== transportationResult.distance) {
            prevNumMilesRef.current = transportationResult.distance;
            setNewBooking(prev => {
              if (prev.numMiles === transportationResult.distance) {
                return prev; // No change, return same object to prevent re-render
              }
              return {
                ...prev,
                numMiles: transportationResult.distance
              };
            });
          }
        }
      } else {
        // Fallback for old format (shouldn't happen, but just in case)
        oneWayTranspCostBeforeDiscount = transportationResult;
    }
    }


      let newTransportationDiscount = calculateTransportationDiscountUtil({
        settings,
        newBooking: newBooking,
        products1Catalog: products1CatalogToUse,
        products2Catalog: products2CatalogToUse
      })

      let newDeliveryChargeNoDiscount = newBooking.delivery ? oneWayTranspCostBeforeDiscount : 0
      let newPickupChargeNoDiscount = newBooking.homePickup ? oneWayTranspCostBeforeDiscount : 0

      let deliveryDiscountSavings = calculatePercentage(newDeliveryChargeNoDiscount, newTransportationDiscount.discountPercent)
      let pickupDiscountSavings = calculatePercentage(newPickupChargeNoDiscount, newTransportationDiscount.discountPercent)

      let newDeliveryChargeWithDiscount = newDeliveryChargeNoDiscount - deliveryDiscountSavings;
      let newPickupChargeWithDiscount = newPickupChargeNoDiscount - pickupDiscountSavings;

      let deliveryChargeChange = newDeliveryChargeNoDiscount - originalBooking.deliveryCostCents
      let pickupChargeChange = newPickupChargeNoDiscount - originalBooking.pickupCostCents
      // logd('d change', deliveryChargeChange)
      // logd('p change', pickupChargeChange)

      // unused, only for logging
      const origTransPct = originalBooking.transportationDiscount?.discountPercent ?? 0;
      let oldDeliveryChargeWithDiscount = calculatePercentage(originalBooking.deliveryCostCents, (100 - origTransPct));
      let oldPickupChargeWithDiscount = calculatePercentage(originalBooking.pickupCostCents, (100 - origTransPct));



      let totalTranspoSavings = deliveryDiscountSavings + pickupDiscountSavings


      // log('\n---- TRANSPORTATION VALUES ----')
      // log('transportation discount', newTransportationDiscount.discountPercent)
      // logd('one way transportation before discount', oneWayTranspCostBeforeDiscount)
      // logd('new delivery cost', newDeliveryChargeNoDiscount)
      // logd('new pickup cost', newPickupChargeNoDiscount)
      // logd('delivery savings', deliveryDiscountSavings)
      // logd('New delivery cost with discount', newDeliveryChargeWithDiscount)
      // logd('new pickup cost with discount', newPickupChargeWithDiscount)
      // logd('old delivery cost with discount', oldDeliveryChargeWithDiscount)
      // logd('old pickup cost with discount', oldPickupChargeWithDiscount)


      let deliveryDiffWithDiscount = newDeliveryChargeWithDiscount - oldDeliveryChargeWithDiscount
      let pickupDiffWithDiscount = newPickupChargeWithDiscount - oldPickupChargeWithDiscount
      let deliveryDiffNoDiscount = newDeliveryChargeNoDiscount - originalBooking.deliveryCostCents;
      let pickupDiffNoDiscount = newPickupChargeNoDiscount - originalBooking.pickupCostCents;


      ////////////////////////////// --> DURATION ADJUSTMENTS //////////////////////////////////////
      const newDurationDiscount = calculateDurationDiscount({ newBooking, settingsParam: settings });
      const durationEditedValues = calculateDurationDiscountTotals(newBooking)
      // Create a new object instead of mutating to prevent reference issues
      newBooking = { ...durationEditedValues.updatedBooking }
      const addedItemsDurationDiscountTotal = durationEditedValues.newItemsTotalDurationDiscountCents;
      const removedItemsDurationDiscountTotal = durationEditedValues.removedItemsTotalDurationDiscountCents;

      // log('\n---- DURATION ADJUSTMENTS ----')


      ////////////////////////////// --> SUBTOTALS ////////////////////////////////////////////////
      const subtotalNoDiscount = bookingChangesProductCost + deliveryDiffNoDiscount + pickupDiffNoDiscount

      const subtotalWithDiscount = bookingChangesProductCost + deliveryDiffWithDiscount + pickupDiffWithDiscount + addedItemsDurationDiscountTotal + removedItemsDurationDiscountTotal

      // Tax is calculated first (on subtotal), then card fee on (subtotal + tax),
      // matching the formula order used in computeBookingCosts.
      let salesTax = subtotalWithDiscount * ((settings?.salesTax?.percent || 0) / 100);

      // card processing fee
      let cardProcessingFee = 0;
      if (settings.cardProcessingFee.forPurchases && subtotalWithDiscount > 0) {
        cardProcessingFee = (subtotalWithDiscount + salesTax) * (settings.cardProcessingFee.percent / 100)
      } else if (settings.cardProcessingFee.forRefunds && subtotalWithDiscount < 0) {
        cardProcessingFee = (subtotalWithDiscount + salesTax) * (settings.cardProcessingFee.percent / 100)
      }

      // if (!(bookingChangesProductCost > 0)) salesTax = 0;

      // log('\n---- SUBTOTALS ----')
      // logd('subtotal without discount', disp(bookingChangesProductCost))
      // logd('subtotal with discount', disp(subtotalWithDiscount))
      // logd('cc processing fee', cardProcessingFee)
      // logd('sales tax', disp(salesTax))

      ///////////////////////////// --> TOTALS ///////////////////////////////////////////////////
      const finalTotal = salesTax + subtotalWithDiscount + cardProcessingFee

      // log('\n---- TOTALS ----')



    const newTotalsCopy = {
      originalBookingProductCostCents: originalBookingProductCost,
      productCost: {
        newCost: totalBookingProductCost,
        // oldCost: calculateProductBookingCost(originalBooking),
        costDifference: bookingChangesProductCost
      },

      transportation: {
        newDiscountName: newTransportationDiscount.discountName,
        // originalDiscountName: originalBooking.transportationDiscount.discountName,

        /////////// no discount
        // newTotalChargeCents: newPickupCost + newDeliveryCost,
        newDeliveryChargeCents: newDeliveryChargeNoDiscount,
        newPickupChargeCents: newPickupChargeNoDiscount,
        // originalTotalChargeCents: originalBooking.pickupCostCents + originalBooking.deliveryCostCents,
        originalPickupChargeCents: originalBooking.pickupCostCents,
        originalDeliveryChargeCents: originalBooking.deliveryCostCents,
        // totalNetChargeCents: deliveryDiffNoDiscount + pickupDiffNoDiscount,
        // deliveryNetChargeCents: deliveryDiffNoDiscount,
        // pickupNetChargeCents: pickupDiffNoDiscount,

        /////////// discounted
        // newTotalDiscountedChargesCents: newPickupChargesWithDiscount + newDeliveryChargesWithDiscount,
        // newPickupDiscountedChargeCents: newPickupChargesWithDiscount,
        // newDeliveryDiscountedChargeCents: newDeliveryChargesWithDiscount,

        // originalTotalDiscountedChargeCents: oldPickupChargesWithDiscount + oldDeliveryChargesWithDiscount,
        // originalPickupDiscountedChargeCents: oldPickupChargesWithDiscount,
        // originalDDeliveryDiscountedChargeCents: oldDeliveryChargesWithDiscount,

        // problem field
        totalNetDiscountedChargeCents: totalTranspoSavings,
        //

        // totalNetDeliveryChargeCents: newDeliveryChargesWithDiscount,
        // totalNetPickupChargeCents: newPickupChargesWithDiscount,
        // totalPaidForTransporationAcrossBookings: deliveryDiffWithDiscount + pickupDiffWithDiscount + oldPickupChargesWithDiscount + oldDeliveryChargesWithDiscount,

        transportationDiscountToSaveInBooking: {
          discountCents: (originalBooking.transportationDiscount?.discountCents ?? 0) + totalTranspoSavings,
        discountName: newTransportationDiscount.discountName,
          discountPercent: newTransportationDiscount.discountPercent

        }
      },

      durationDiscount: {
        discountPercent: newDurationDiscount.discountPercent,
        discountName: newDurationDiscount.discountName,

        discountCents: addedItemsDurationDiscountTotal,
        removedItemsCents: removedItemsDurationDiscountTotal,
        originalDiscountCents: originalBooking.durationDiscount?.discountCents ?? 0,
        // originalDiscountPercent: originalBooking.durationDiscount.discountPercent,
        originalDiscountName: originalBooking?.durationDiscount?.discountName,

        durationDiscountToSaveInBooking: {
          discountCents: (originalBooking.durationDiscount?.discountCents ?? 0) + addedItemsDurationDiscountTotal + removedItemsDurationDiscountTotal,
          discountName: newDurationDiscount.discountName,
          discountPercent: newDurationDiscount.discountPercent
        }
      },

      changedProducts: calculateProductChanges(newBooking, originalBooking),
      days: {
        new: getBookingDays(newBooking),
        old: getBookingDays(originalBooking),
      },
      salesTax: bookingChangesProductCost * ((settings?.salesTaxPercent || 0) / 100),
      // subtotal: bookingChangesProductCost + newDeliveryChargesWithDiscount - oldDeliveryChargesWithDiscount,
      totals: {
        subtotalNoDiscount,
        subtotalWithDiscount,
        salesTax,
        finalTotal,
        cardProcessingFee,
      },
    }


      // log(">>>>>>> NEW TOTALS <<<<<<<<<<<<<<<")
      // logs(newTotalsCopy)
    // setBooking(bookingToUse)
    setNewTotals(newTotalsCopy)

    // Run calendar availability calculation asynchronously (non-blocking)
    Promise.resolve().then(() => {
      calculateCalendarAvailability(newBooking);
    });
    
    } finally {
      // Always reset the flag when done
      isCalculatingTotalsRef.current = false;
    }
  };








  const calculateProductDurationAvailability = (bookingToUse = null) => {
    // Use the provided booking or fall back to newBooking state
    const targetBooking = (bookingToUse && bookingToUse.startDate) ? bookingToUse : newBooking;
    if (!targetBooking || !targetBooking.startDate) {
      return;
    }
    
    // Use products from state, fallback to store if state is empty
    const products1ToUse = products1Catalog.length > 0 ? products1Catalog : products1FromStore;
    const products2ToUse = products2Catalog.length > 0 ? products2Catalog : products2FromStore;
    
    // Get all bookings from store, excluding targetBooking if it exists in the store
    const allBookings = (bookingsFromStore || []).filter((b) => {
      // Exclude targetBooking if it matches by id or bookingId
      if (targetBooking.id && (b.id === targetBooking.id || b.bookingId === targetBooking.id)) {
        return false;
      }
      if (targetBooking.bookingId && (b.id === targetBooking.bookingId || b.bookingId === targetBooking.bookingId)) {
        return false;
      }
      return true;
    });
    
    // Include targetBooking in calculations
    const bookingsToCheck = [...allBookings, targetBooking];
    
    // Calculate maximum available quantity for ALL products across the rental period
    const productLeftForBooking = {};
    
    try {
      const rentalStart = parseFirestoreDate(targetBooking.startDate);
      const rentalEnd = parseFirestoreDate(targetBooking.endDate);
      rentalStart.setHours(0, 0, 0, 0);
      rentalEnd.setHours(0, 0, 0, 0);
      
      // Get all dates in the rental period
      const rentalDates = [];
      let currentDate = new Date(rentalStart);
      while (currentDate <= rentalEnd) {
        const dateStr = currentDate.toISOString().split("T")[0];
        rentalDates.push(dateStr);
        currentDate.setDate(currentDate.getDate() + 1);
      }
      
      // Calculate maximum available for ALL product1 items in catalog
      // Calculate availability for each date in rental period (not just current month)
      products1ToUse.forEach((catalogProduct) => {
        const productId = catalogProduct.id;
        
        // Check if product has inventory tracking
        const totalInventory = catalogProduct.quantity || 0;
        const hasInventory = totalInventory > 0;
        
        if (hasInventory) {
          // Find minimum available across all days in rental period
          // An item is only available if it's fully available for the ENTIRE duration
          let minAvailable = Infinity;
          rentalDates.forEach((dateStr) => {
            // Parse the date string to a Date object for comparison
            const checkDate = new Date(dateStr + 'T00:00:00');
            checkDate.setHours(0, 0, 0, 0);
            
            // Count booked quantity from all bookings that overlap with this date
            let bookedQty = 0;
            bookingsToCheck.forEach((b) => {
              if (!b.startDate || !b.endDate) return;
              
              const bStart = parseFirestoreDate(b.startDate);
              const bEnd = parseFirestoreDate(b.endDate);
              bStart.setHours(0, 0, 0, 0);
              bEnd.setHours(0, 0, 0, 0);
              
              // Check if this booking overlaps with the check date
              if (checkDate >= bStart && checkDate <= bEnd) {
                const bProducts1 = b.product1 || [];
                const matchingItem = bProducts1.find((p) => p.id === productId);
                if (matchingItem) {
                  bookedQty += matchingItem.quantity || 0;
                }
              }
            });
            
            // Calculate available quantity for this date
            const available = Math.max(0, totalInventory - bookedQty);
            // Track the minimum available across all days (item must be available for entire duration)
            if (available < minAvailable) {
              minAvailable = available;
            }
          });
          
          // ⭐ LOCATION: Setting quantity for product1 with inventory
          // Store the minimum available (should always be >= 0)
          // This represents the quantity available for the ENTIRE rental period
          productLeftForBooking[productId] = minAvailable === Infinity ? 0 : minAvailable;
        } else {
          // ⭐ LOCATION: Setting quantity for product1 without inventory
          // For products without inventory, set to null to indicate unlimited
          productLeftForBooking[productId] = null;
        }
      });
      
      // Calculate maximum available for ALL product2 items in catalog
      // Calculate availability for each date in rental period (not just current month)
      products2ToUse.forEach((catalogProduct) => {
        const productId = catalogProduct.id;
        
        // Check if product has inventory tracking
        const totalInventory = catalogProduct.inventory || 0;
        const hasInventory = totalInventory > 0;
        
        if (hasInventory) {
          // Find minimum available across all days in rental period
          // An item is only available if it's fully available for the ENTIRE duration
          let minAvailable = Infinity;
          rentalDates.forEach((dateStr) => {
            // Parse the date string to a Date object for comparison
            const checkDate = new Date(dateStr + 'T00:00:00');
            checkDate.setHours(0, 0, 0, 0);
            
            // Count booked quantity from all bookings that overlap with this date
            let bookedQty = 0;
            bookingsToCheck.forEach((b) => {
              if (!b.startDate || !b.endDate) return;
              
              const bStart = parseFirestoreDate(b.startDate);
              const bEnd = parseFirestoreDate(b.endDate);
              bStart.setHours(0, 0, 0, 0);
              bEnd.setHours(0, 0, 0, 0);
              
              // Check if this booking overlaps with the check date
              if (checkDate >= bStart && checkDate <= bEnd) {
                const bProducts2 = b.product2 || [];
                const matchingItem = bProducts2.find((p) => p.id === productId);
                if (matchingItem) {
                  bookedQty += matchingItem.quantity || 0;
                }
              }
            });
            
            // Calculate available quantity for this date
            const available = Math.max(0, totalInventory - bookedQty);
            // Track the minimum available across all days (item must be available for entire duration)
            if (available < minAvailable) {
              minAvailable = available;
            }
          });
          
          // ⭐ LOCATION: Setting quantity for product2 with inventory
          // Store the minimum available (should always be >= 0)
          // This represents the quantity available for the ENTIRE rental period
          productLeftForBooking[productId] = minAvailable === Infinity ? 0 : minAvailable;
        } else {
          // ⭐ LOCATION: Setting quantity for product2 without inventory
          // For products without inventory, set to null to indicate unlimited
          productLeftForBooking[productId] = null;
        }
      });
    } catch (error) {
      console.error("Error calculating product availability for booking:", error);
    }
    
    // Set the state with calculated product availability
    setProductLeftForBooking(productLeftForBooking);
    // console.log("📦 Product availability for booking:", productLeftForBooking);
  }

  const calculateCalendarAvailability = (bookingToUse = null) => {
    // Get all days in the current viewing month
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    
    // Result object: { "2025-01-01": { product1: { productId: { available, total, booked } }, product2: { ... } } }
    const availabilityByDate = {};
    
    // Use products from state, fallback to store if state is empty
    const products1ToUse = products1Catalog.length > 0 ? products1Catalog : products1FromStore;
    const products2ToUse = products2Catalog.length > 0 ? products2Catalog : products2FromStore;
    
    // Use the provided booking or fall back to state
    const bookingForCalculation = bookingToUse || newBooking;
    
    // Get all bookings from store (excluding the current booking being edited)
    const allBookings = (bookingsFromStore || []).filter((b) => {
      // Exclude the current booking by ID
      return b.id !== booking?.id && b.bookingId !== booking?.id;
    });
    
    // Include newBooking in calculations if it exists
    const bookingsToCheck = [...allBookings];
    if (bookingForCalculation && bookingForCalculation.startDate && bookingForCalculation.endDate) {
      bookingsToCheck.push(bookingForCalculation);
    }
    
    // Check each day in the current month
    for (let day = 1; day <= daysInMonth; day++) {
      const checkDate = new Date(year, month, day);
      checkDate.setHours(0, 0, 0, 0);
      const dateStr = checkDate.toISOString().split("T")[0];
      
      availabilityByDate[dateStr] = {
        product1: {},
        product2: {}
      };
      
      // Check product1 availability for ALL products in catalog
      products1ToUse.forEach((catalogProduct) => {
        const productId = catalogProduct.id;
        
        // Check if product has inventory (quantity field)
        const totalInventory = catalogProduct.quantity || 0;
        const hasInventory = totalInventory > 0;
        
        // Count booked quantity from all bookings that overlap with this date
        let bookedQty = 0;
        bookingsToCheck.forEach((b) => {
          if (!b.startDate || !b.endDate) return;
          
          const bStart = parseFirestoreDate(b.startDate);
          const bEnd = parseFirestoreDate(b.endDate);
          bStart.setHours(0, 0, 0, 0);
          bEnd.setHours(0, 0, 0, 0);
          
          // Check if this booking overlaps with the check date
          if (checkDate >= bStart && checkDate <= bEnd) {
            const bProducts1 = b.product1 || [];
            const matchingItem = bProducts1.find((p) => p.id === productId);
            if (matchingItem) {
              bookedQty += matchingItem.quantity || 0;
            }
          }
        });
        
        // Calculate available quantity
        // For products without inventory, show booked quantity and mark as unlimited
        const available = hasInventory ? Math.max(0, totalInventory - bookedQty) : null;
        
        availabilityByDate[dateStr].product1[productId] = {
          available,
          total: hasInventory ? totalInventory : null,
          booked: bookedQty,
          hasInventory
        };
      });
      
      // Check product2 availability for ALL products in catalog
      products2ToUse.forEach((catalogProduct) => {
        const productId = catalogProduct.id;
        
        // Check if product has inventory (inventory field for product2)
        const totalInventory = catalogProduct.inventory || 0;
        const hasInventory = totalInventory > 0;
        
        // Count booked quantity from all bookings that overlap with this date
        let bookedQty = 0;
        bookingsToCheck.forEach((b) => {
          if (!b.startDate || !b.endDate) return;
          
          const bStart = parseFirestoreDate(b.startDate);
          const bEnd = parseFirestoreDate(b.endDate);
          bStart.setHours(0, 0, 0, 0);
          bEnd.setHours(0, 0, 0, 0);
          
          // Check if this booking overlaps with the check date
          if (checkDate >= bStart && checkDate <= bEnd) {
            const bProducts2 = b.product2 || [];
            const matchingItem = bProducts2.find((p) => p.id === productId);
            if (matchingItem) {
              bookedQty += matchingItem.quantity || 0;
            }
          }
        });
        
        // Calculate available quantity
        // For products without inventory, show booked quantity and mark as unlimited
        const available = hasInventory ? Math.max(0, totalInventory - bookedQty) : null;
        
        availabilityByDate[dateStr].product2[productId] = {
          available,
          total: hasInventory ? totalInventory : null,
          booked: bookedQty,
          hasInventory
        };
      });
      
      // Check if ALL products in bookingForCalculation are available on this day
      let allAvailable = true;
      
      if (bookingForCalculation && bookingForCalculation.product1 && bookingForCalculation.product1.length > 0) {
        for (const item of bookingForCalculation.product1) {
          const productId = item.id;
          const neededQty = item.quantity || 0;
          
          // Get availability for this product
          const productAvailability = availabilityByDate[dateStr].product1[productId];
          
          // If product not in catalog or has no inventory tracking, skip
          const catalogProduct = products1Catalog.find((p) => p.id === productId);
          if (!catalogProduct || (catalogProduct.quantity || 0) <= 0) {
            continue; // Skip products without inventory tracking
          }
          
          // Check if available quantity is sufficient
          if (!productAvailability || productAvailability.available < neededQty) {
            allAvailable = false;
            break;
          }
        }
      }
      
      // Check product2 items if product1 check passed
      if (allAvailable && bookingForCalculation && bookingForCalculation.product2 && bookingForCalculation.product2.length > 0) {
        for (const item of bookingForCalculation.product2) {
          const productId = item.id;
          const neededQty = item.quantity || 0;
          
          // Get availability for this product
          const productAvailability = availabilityByDate[dateStr].product2[productId];
          
          // If product not in catalog or has no inventory tracking, skip
          const catalogProduct = products2Catalog.find((p) => p.id === productId);
          if (!catalogProduct || (catalogProduct.inventory || 0) <= 0) {
            continue; // Skip products without inventory tracking
          }
          
          // Check if available quantity is sufficient
          if (!productAvailability || productAvailability.available < neededQty) {
            allAvailable = false;
            break;
          }
        }
      }
      
      // Add allAvailable field to this date
      availabilityByDate[dateStr].allAvailable = allAvailable;
    }
    
    // If any day has allAvailable = false, mark that day and all subsequent days as unavailable
    let firstUnavailableDay = null;
    const sortedDates = Object.keys(availabilityByDate).sort();
    
    for (const dateStr of sortedDates) {
      if (availabilityByDate[dateStr].allAvailable === false) {
        firstUnavailableDay = dateStr;
        break;
      }
    }
    
    // Mark all days after first unavailable day as unavailable
    if (firstUnavailableDay) {
      let markAsUnavailable = false;
      for (const dateStr of sortedDates) {
        if (dateStr === firstUnavailableDay) {
          markAsUnavailable = true;
        }
        if (markAsUnavailable) {
          availabilityByDate[dateStr].allAvailable = false;
        }
      }
    }
    
    // Set the state with the calculated availability
    setCalendarAvailability(availabilityByDate);
    // console.log("📅 Calendar availability calculated:", availabilityByDate);
    
    // Calculate product availability for the booking duration
    calculateProductDurationAvailability(bookingForCalculation);
  }

  const onDateChange = async (dateType, newDate) => {
    const updatedBooking = { ...newBooking };
    if (dateType === "startDate") {
      updatedBooking.startDate = newDate;
    } else if (dateType === "endDate") {
      updatedBooking.endDate = newDate;
    }
    await updateNewBooking(updatedBooking);
  };

  const onPickupOrDeliveryChange = async (fieldName, newValue) => {
    const mileageRateCents = Number(settingsObject?.mileageRate?.rate) || 0;
    const loadingFeeCents = Number(settingsObject?.loadingFee?.fee) || 0;
    const numMilesVal = newBooking?.numMiles != null ? parseFloat(newBooking.numMiles) : null;

    let oneWay = 0;
    if (numMilesVal !== null && numMilesVal > 0) {
      oneWay = Math.round(numMilesVal * mileageRateCents) + loadingFeeCents;
    } else if (loadingFeeCents > 0) {
      oneWay = loadingFeeCents;
    }

    let updatedBooking = { ...newBooking, [fieldName]: newValue };

    // Set explicit delivery/pickup costs so computeBookingCosts can preserve them.
    if (fieldName === "delivery") {
      updatedBooking.deliveryCostCents = newValue ? oneWay : 0;
    } else if (fieldName === "homePickup") {
      updatedBooking.pickupCostCents = newValue ? oneWay : 0;
    }

    // Recompute transport discount for the new delivery/pickup state.
    const p1Cat = products1Catalog.length > 0 ? products1Catalog : products1FromStore;
    const p2Cat = products2Catalog.length > 0 ? products2Catalog : products2FromStore;
    const transDiscount = calculateTransportationDiscountUtil({
      settings: settingsObject,
      newBooking: updatedBooking,
      products1Catalog: p1Cat,
      products2Catalog: p2Cat,
    });
    const transDiscountPct = transDiscount?.discountPercent || 0;
    const newDeliveryCents = Number(updatedBooking.deliveryCostCents) || 0;
    const newPickupCents = Number(updatedBooking.pickupCostCents) || 0;
    updatedBooking.transportationDiscount = {
      discountPercent: transDiscountPct,
      discountCents: Math.round((newDeliveryCents + newPickupCents) * (transDiscountPct / 100)),
      discountName: transDiscount?.discountName || "",
    };

    await updateNewBooking(updatedBooking);
  };

    // Handle quantity change button press
  const handleQuantityButtonChange = async (option, currentQuantity, productId) => {
    if (!booking || !newBooking || !originalBooking) return;
    
    // Disable changes if rental has started and user is not admin
    if (rentalHasStarted && !isAdmin) {
      alert("⏰ Your rental has started. Item changes are disabled.");
      return;
    }
    
    // Calculate delta based on option
    const delta = option === 'up' ? 1 : -1;

    // Determine product type by checking both catalogs
    const product1Item = products1Catalog.find((p) => p.id === productId);
    const product2Item = products2Catalog.find((p) => p.id === productId);
    
    if (!product1Item && !product2Item) {
      console.error(`Product with id ${productId} not found in catalogs`);
      return;
    }

    const productType = product1Item ? "product1" : "product2";
    const product = product1Item || product2Item;

    // Call the new handleQuantityChange function
    await handleQuantityChange(productType, productId, delta, product);
  };

  // ---------------------------------------------------------------------------
  // computeBookingCosts
  // Calculates all cost fields for a given booking snapshot and writes them
  // back onto the booking object. Returns the enriched booking. This is the
  // EditBooking equivalent of NewBooking's calculateRentalCost function.
  // For address changes the transportation API call is skipped — the caller
  // must have already resolved numMiles onto the booking before calling this.
  // ---------------------------------------------------------------------------
  const computeBookingCosts = (booking) => {
    const products1CatalogToUse = products1Catalog.length > 0 ? products1Catalog : products1FromStore;
    const products2CatalogToUse = products2Catalog.length > 0 ? products2Catalog : products2FromStore;
    const settings = settingsObject;

    if (!booking || !booking.startDate || !booking.endDate) return booking;

    // Days
    const startD = parseFirestoreDate(booking.startDate);
    const endD = parseFirestoreDate(booking.endDate);
    startD.setHours(0, 0, 0, 0);
    endD.setHours(0, 0, 0, 0);
    // Round, not ceil: midnight-to-midnight, so only the DST hour makes the
    // diff non-whole — ceil billed fall-back ranges an extra day.
    const days = Math.round((endD - startD) / (1000 * 60 * 60 * 24)) || 1;

    // Product1 cost
    const product1 = booking.product1 || [];
    const product2 = booking.product2 || [];
    const totalBikes = product1.reduce((sum, p) => sum + (p.quantity || 0), 0);

    let productCostCents = 0;
    product1.forEach((item) => {
      if ((item.quantity || 0) === 0 || item.removedFromOriginalBooking) return;
      const cat = products1CatalogToUse.find((p) => p.id === item.id);
      if (cat) productCostCents += (cat.price || 0) * item.quantity * days;
    });
    product2.forEach((item) => {
      if ((item.quantity || 0) === 0 || item.removedFromOriginalBooking) return;
      const cat = products2CatalogToUse.find((p) => p.id === item.id);
      if (!cat) return;
      const opt = cat.option || "per-booking";
      if (opt === "per-booking") productCostCents += (cat.price || 0) * item.quantity;
      else if (opt === "per-bike") productCostCents += (cat.price || 0) * item.quantity * totalBikes;
      else productCostCents += (cat.price || 0) * item.quantity * days;
    });

    // Transportation – use numMiles already on the booking (no async API call here).
    // Fresh computation is used only as a fallback when stored cost fields are absent.
    const mileageRateCents = Number(settings?.mileageRate?.rate) || 0;
    const loadingFeeCents = Number(settings?.loadingFee?.fee) || 0;
    const numMiles = booking.numMiles != null ? parseFloat(booking.numMiles) : null;

    let oneWayCostCents = 0;
    if (numMiles !== null && numMiles > 0) {
      oneWayCostCents = Math.round(numMiles * mileageRateCents) + loadingFeeCents;
    } else if (loadingFeeCents > 0) {
      oneWayCostCents = loadingFeeCents;
    }

    // Prefer stored cost values to avoid pricing drift when settings change.
    // onPickupOrDeliveryChange is responsible for setting fresh values when toggling.
    const deliveryCostCents = booking.delivery
      ? (booking.deliveryCostCents != null ? Number(booking.deliveryCostCents) : oneWayCostCents)
      : 0;
    const pickupCostCents = booking.homePickup
      ? (booking.pickupCostCents != null ? Number(booking.pickupCostCents) : oneWayCostCents)
      : 0;

    // Transportation discount – prefer stored cents to avoid drift.
    // Stored cents may exceed raw cost in edge cases; that is intentional (data as-is).
    let transDiscountPct, transDiscountCents, transDiscountName;
    if (booking.transportationDiscount?.discountCents != null) {
      transDiscountCents = Number(booking.transportationDiscount.discountCents);
      transDiscountPct = Number(booking.transportationDiscount.discountPercent) || 0;
      transDiscountName = booking.transportationDiscount.discountName || "";
    } else {
      const transDiscount = calculateTransportationDiscountUtil({
        settings,
        newBooking: booking,
        products1Catalog: products1CatalogToUse,
        products2Catalog: products2CatalogToUse,
      });
      transDiscountPct = transDiscount?.discountPercent || 0;
      transDiscountCents = Math.round((deliveryCostCents + pickupCostCents) * (transDiscountPct / 100));
      transDiscountName = transDiscount?.discountName || "";
    }

    // Duration discount – prefer stored cents to avoid pricing drift when the
    // discount settings change after a booking was originally created without it.
    let durDiscountPct, durDiscountCents, durDiscountName;
    if (booking.durationDiscount?.discountCents != null) {
      durDiscountCents = Number(booking.durationDiscount.discountCents);
      durDiscountPct = Number(booking.durationDiscount.discountPercent) || 0;
      durDiscountName = booking.durationDiscount.discountName || "";
    } else {
      const durDiscountResult = calculateDurationDiscountUtil({
        booking,
        durationDiscounts,
        products1Catalog: products1CatalogToUse,
        products2Catalog: products2CatalogToUse,
        useDurationDiscounts,
      });
      durDiscountPct = durDiscountResult?.discountPercent || 0;
      durDiscountCents = durDiscountResult?.discountAmountCents || 0;
      durDiscountName = durDiscountResult?.discountName || "";
    }

    // Subtotals
    const subtotalWithoutDiscountCents = productCostCents + deliveryCostCents + pickupCostCents;
    const subtotalWithDiscountCents = subtotalWithoutDiscountCents - transDiscountCents - durDiscountCents;

    // Fees
    const platformFeeCents = Number(settings?.bookingPlatform?.perBookingFee) || 0;
    const salesTaxPct = Number(settings?.salesTax?.percent) || 0;
    const taxCents = Math.round((subtotalWithDiscountCents + platformFeeCents) * (salesTaxPct / 100));
    const cardFeePct = Number(settings?.cardProcessingFee?.percent) || 0;
    const cardFeeForPurchases = settings?.cardProcessingFee?.forPurchases;
    const cardFeeCents = cardFeeForPurchases && cardFeePct > 0
      ? Math.round((subtotalWithDiscountCents + platformFeeCents + taxCents) * (cardFeePct / 100))
      : 0;

    const finalTotalCents = subtotalWithDiscountCents + platformFeeCents + taxCents + cardFeeCents;

    return {
      ...booking,
      productPriceAmount: productCostCents,
      deliveryCostCents,
      pickupCostCents,
      transportationDiscount: {
        discountPercent: transDiscountPct,
        discountCents: transDiscountCents,
        discountName: transDiscountName,
      },
      durationDiscount: {
        discountPercent: durDiscountPct,
        discountCents: durDiscountCents,
        discountName: durDiscountName,
      },
      subtotalWithoutDiscountCents,
      subtotalWithDiscountCents,
      salesTaxCents: taxCents,
      cardProcessingFeeCents: cardFeeCents,
      finalTotalCents,
    };
  };

  // ---------------------------------------------------------------------------
  // updateNewBooking
  // Single entry point for all booking changes. Computes costs synchronously,
  // updates newBooking state, then fires calculateTotals for diff math.
  // ---------------------------------------------------------------------------
  const updateNewBooking = async (updatedBooking) => {
    const enriched = computeBookingCosts(updatedBooking);
    setNewBooking(enriched);
    if (originalBooking) {
      calculateTotals(enriched, originalBooking).catch((err) =>
        console.error("updateNewBooking: calculateTotals failed", err)
      );
    }
  };

  const handleQuantityChange = async (productType, productId, delta, product) => {
    // productType: "product1" or "product2"
    // productId: string ID of the product
    // delta: +1 for up, -1 for down
    // product: product object from catalog
    
    // Copy newBooking
    const updatedBooking = { ...newBooking };
    
    // Ensure product arrays exist
    if (!updatedBooking[productType]) {
      updatedBooking[productType] = [];
    }
    
    // Find existing item in the booking
    const targetArray = updatedBooking[productType];
    const existingItemIndex = targetArray.findIndex((item) => item.id === productId);
    
    // Get current quantity
    let currentQty = 0;
    if (existingItemIndex >= 0) {
      currentQty = targetArray[existingItemIndex].quantity || 0;
      } else {
      // Check original booking for initial quantity
      if (originalBooking && originalBooking[productType]) {
        const originalItem = originalBooking[productType].find((item) => item.id === productId);
        if (originalItem) {
          currentQty = originalItem.quantity || 0;
        }
        }
      }
      
      // Calculate new quantity (prevent negative)
      const newQty = Math.max(0, currentQty + delta);
      
      // If quantity is 0 and trying to decrease, don't allow
      if (currentQty === 0 && delta < 0) {
      return;
    }
    
    // Get product catalogs for price and other metadata
    const products1CatalogToUse = products1Catalog.length > 0 ? products1Catalog : products1FromStore;
    const products2CatalogToUse = products2Catalog.length > 0 ? products2Catalog : products2FromStore;
    const catalogToUse = productType === "product1" ? products1CatalogToUse : products2CatalogToUse;
    const catalogProduct = catalogToUse.find((p) => p.id === productId);

    // Check if item is from original booking
    const isFromOriginal = originalBooking && originalBooking[productType]?.some((item) => item.id === productId);

    // Create or update product object
    const productObj = {
      id: productId,
      name: product.name || "",
      quantity: newQty,
    };
    
    // Add option for product2
    if (productType === "product2" && product.option) {
      productObj.option = product.option;
    }
    
    // If from original booking, preserve original fields
    if (isFromOriginal && existingItemIndex >= 0) {
      const existingItem = targetArray[existingItemIndex];
      // Preserve original fields
      productObj.isFromOriginalBooking = existingItem.isFromOriginalBooking !== undefined ? existingItem.isFromOriginalBooking : true;
      productObj.itemPrice = existingItem.itemPrice;
      productObj.durationDiscountCents = existingItem.durationDiscountCents;
    } else if (isFromOriginal) {
      // Item was removed before, now re-adding - preserve original fields from originalBooking
      const originalItem = originalBooking[productType].find((item) => item.id === productId);
      if (originalItem) {
        productObj.isFromOriginalBooking = originalItem.isFromOriginalBooking !== undefined ? originalItem.isFromOriginalBooking : true;
        productObj.itemPrice = originalItem.itemPrice;
        productObj.durationDiscountCents = originalItem.durationDiscountCents;
      } else {
        // Fallback: get from catalog
        productObj.isFromOriginalBooking = true;
        productObj.itemPrice = catalogProduct?.price || 0;
        // Get original duration discount percent
        const originalDurationDiscountPercent = originalBooking.durationDiscount?.discountPercent || 0;
        productObj.durationDiscountCents = Math.round((catalogProduct?.price || 0) * (originalDurationDiscountPercent / 100));
      }
    } else {
      // New item (not from original booking)
      productObj.isFromOriginalBooking = false;
      productObj.itemPrice = catalogProduct?.price || 0;
      // New items don't get original duration discount
      productObj.durationDiscountCents = 0;
    }

    // Set removedFromOriginalBooking flag based on quantity
    if (isFromOriginal) {
      productObj.removedFromOriginalBooking = newQty === 0;
    } else {
      productObj.removedFromOriginalBooking = false;
    }

    // Update or add the item (never remove, just set flag)
    if (existingItemIndex >= 0) {
      // Update existing item
      targetArray[existingItemIndex] = productObj;
    } else {
      // Add new item (always add, even if quantity is 0, if it's from original booking)
      if (newQty > 0 || isFromOriginal) {
        targetArray.push(productObj);
      }
    }
    
    // Recalculate costs and persist state
    await updateNewBooking(updatedBooking);
  };


  ////////////////////////////////////////////////////////////////////////////////////////////
  /// END MY STUFF ////////////////////////////////////////////////////////////////////////////////
  ///////////////////////////////////////////////////////////////////////////////////////////

  
  const calculateProductCosts = (newBooking) => {
    // Calculate rental duration in days
    if (!newBooking.startDate || !newBooking.endDate) {
      return {
        product1: [],
        product2: []
      };
    }

    const startDate = parseFirestoreDate(newBooking.startDate);
    const endDate = parseFirestoreDate(newBooking.endDate);
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);
    const days = Math.round((endDate - startDate) / (1000 * 60 * 60 * 24));

    // Calculate total product1 quantity for per-bike calculations
    const totalProduct1Quantity = (newBooking.product1 || []).reduce(
      (sum, p) => sum + (p.quantity || 0),
      0
    );

    // Calculate product1 costs (in cents)
    const product1Costs = (newBooking.product1 || []).map((item) => {
      const catalogProduct = products1Catalog.find((p) => p.id === item.id);
      if (!catalogProduct) {
        return {
          id: item.id,
          name: item.name || "",
          quantity: item.quantity || 0,
          costCents: 0
        };
      }

      const priceCents = dollarsToCents(catalogProduct.price);
      const costCents = priceCents * (item.quantity || 0) * days;

      return {
        id: item.id,
        name: item.name || catalogProduct.name || "",
        quantity: item.quantity || 0,
        costCents: costCents
      };
    });

    // Calculate product2 costs (in cents)
    const product2Costs = (newBooking.product2 || []).map((item) => {
      const catalogProduct = products2Catalog.find((p) => p.id === item.id);
      if (!catalogProduct) {
        return {
          id: item.id,
          name: item.name || "",
          quantity: item.quantity || 0,
          option: item.option || "",
          costCents: 0
        };
      }

      const priceCents = dollarsToCents(catalogProduct.price);
      let costCents = 0;

      if (catalogProduct.option === "per-booking" && item.quantity > 0) {
        // Per-booking: multiply price by quantity (e.g., 3 units at $5 = $15)
        costCents = priceCents * item.quantity;
      } else if (catalogProduct.option === "per-bike" && item.quantity > 0) {
        // For per-bike option, multiply by total product1 quantity
        costCents = priceCents * item.quantity * totalProduct1Quantity;
      } else if (catalogProduct.option === "user-selection") {
        costCents = priceCents * (item.quantity || 0) * days;
      }

      return {
        id: item.id,
        name: item.name || catalogProduct.name || "",
        quantity: item.quantity || 0,
        option: item.option || catalogProduct.option || "",
        costCents: costCents
      };
    });

    return {
      product1: product1Costs,
      product2: product2Costs
    };
  }

  const calculateTransportationCostAPI = async (address, startingAddress, perMileRate, loadingFee) => {
    // Return 0 if no address provided or if address is empty
    if (!address || address.trim() === "") {
      return 0;
    }

    // Return 0 if no starting address provided
    if (!startingAddress || startingAddress.trim() === "") {
      return 0;
    }

    // Normalize perMileRate and loadingFee (treat null/undefined/NaN as 0)
    const validPerMileRate = (perMileRate && !isNaN(perMileRate) && perMileRate > 0) ? perMileRate : 0;
    const validLoadingFee = (loadingFee && !isNaN(loadingFee) && loadingFee > 0) ? loadingFee : 0;

    // If both perMileRate and loadingFee are 0/invalid, return 0
    if (validPerMileRate === 0 && validLoadingFee === 0) {
      return 0;
    }

    try {
      let distanceMiles = 0;
      
      // Only calculate distance if perMileRate is valid (needed for distance-based cost)
      if (validPerMileRate > 0) {
        // Calculate distance using cloud function (avoids CORS issues)
        const getDistanceFn = httpsCallable(functions, "getDistance");
        const result = await getDistanceFn({
          fullAddress: address.trim(),
        });

        if (result.data && (result.data.distanceMiles !== undefined || result.data.miles !== undefined)) {
          distanceMiles = result.data.distanceMiles || result.data.miles;
        } else {
          // No distance data available - if only perMileRate was needed, return 0
          // If loadingFee is valid, return just the doubled loading fee (round trip)
          return validLoadingFee > 0 ? Math.round(dollarsToCents(validLoadingFee * 2)) : 0;
        }
      }

      // Calculate transportation cost for round trip (delivery + pickup):
      // Double the distance (round trip) and double the loading fee (one for each direction)
      // If perMileRate is 0, only doubled loadingFee applies
      // If loadingFee is 0, only doubled distance-based cost applies
      const costInDollars = (distanceMiles * 2 * validPerMileRate) + (validLoadingFee * 2);

      // Convert to cents and return
      return Math.round(dollarsToCents(costInDollars));
    } catch (error) {
      console.error("Error calculating transportation cost:", error);
      // If error occurs but loadingFee is valid, return just the doubled loading fee (round trip)
      return validLoadingFee > 0 ? Math.round(dollarsToCents(validLoadingFee * 2)) : 0;
    }
  }

  // Calculate transportation discount based on newBooking and settings
  // Returns discount ID and percentage (not the calculated amount)
  const calculateTransportationDiscountAPI = (newBooking, settingsParam = null, products1CatalogParam = null, products2CatalogParam = null) => {
    // Use passed params or fall back to state
    const effectiveSettings = settingsParam || settingsObject;
    const products1ToUse = products1CatalogParam || (products1Catalog.length > 0 ? products1Catalog : products1FromStore);
    const products2ToUse = products2CatalogParam || (products2Catalog.length > 0 ? products2Catalog : products2FromStore);
    
    // Extract discount settings from settings object
    const useDeliveryDiscountsValue = effectiveSettings?.useDeliveryDiscounts?.enabled ?? useDeliveryDiscounts;
    const deliveryDiscountsValue = effectiveSettings?.deliveryDiscounts?.discounts || deliveryDiscounts;
    
    // Return null if delivery address doesn't exist
    if (!newBooking.deliveryAddress || newBooking.deliveryAddress.trim() === "") {
      return null;
    }

    // Return null if delivery discounts are disabled in settings
    if (!useDeliveryDiscountsValue || !deliveryDiscountsValue || deliveryDiscountsValue.length === 0) {
      return null;
    }

    // Calculate equipment subtotal from newBooking products (in cents)
    let equipmentSubtotalCents = 0;

    if (newBooking.startDate && newBooking.endDate) {
      const startDate = parseFirestoreDate(newBooking.startDate);
      const endDate = parseFirestoreDate(newBooking.endDate);
      if (!startDate || !endDate) {
        return { discountId: null, discountPercent: 0 };
      }
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);
      const days = Math.round((endDate - startDate) / (1000 * 60 * 60 * 24));

      // Calculate product1 costs (prices are already in cents)
      newBooking.product1.forEach((item) => {
        const catalogProduct = products1ToUse.find((p) => p.id === item.id);
        if (catalogProduct) {
          const priceCents = catalogProduct.price || 0;
          equipmentSubtotalCents += priceCents * (item.quantity || 0) * days;
        }
      });

      // Calculate product2 costs (prices are already in cents)
      const totalBikes = newBooking.product1.reduce((sum, p) => sum + (p.quantity || 0), 0);
      newBooking.product2.forEach((item) => {
        const catalogProduct = products2ToUse.find((p) => p.id === item.id);
        if (catalogProduct) {
          const priceCents = catalogProduct.price || 0;
          if (catalogProduct.option === "per-booking" && item.quantity > 0) {
            equipmentSubtotalCents += priceCents * item.quantity;
          } else if (catalogProduct.option === "per-bike" && item.quantity > 0) {
            equipmentSubtotalCents += priceCents * item.quantity * totalBikes;
          } else if (catalogProduct.option === "user-selection") {
            equipmentSubtotalCents += priceCents * (item.quantity || 0) * days;
          }
        }
      });
    }

    // Find applicable discounts based on equipment subtotal
    // Convert discount tier values (from/to) from dollars to cents for comparison
    const applicableDiscounts = deliveryDiscountsValue.filter(
      (d) => equipmentSubtotalCents >= dollarsToCents(d.from) && equipmentSubtotalCents <= dollarsToCents(d.to)
    );

    if (applicableDiscounts.length === 0) {
      return { discountId: null, discountPercent: 0 }; // No discount applicable
    }

    // Get the highest discount (highest value percentage)
    const highestDiscount = applicableDiscounts.reduce(
      (max, d) => (d.value > max.value ? d : max),
      applicableDiscounts[0]
    );

    return {
      discountId: highestDiscount.id,
      discountPercent: highestDiscount.value
    };
  };

  const calculateDurationDiscountAPI = (newBooking, settingsParam = null, products1CatalogParam = null, products2CatalogParam = null) => {
    // Use passed params or fall back to state
    const effectiveSettings = settingsParam || settingsObject;
    const products1ToUse = products1CatalogParam || (products1Catalog.length > 0 ? products1Catalog : products1FromStore);
    const products2ToUse = products2CatalogParam || (products2Catalog.length > 0 ? products2Catalog : products2FromStore);
    
    // Extract discount settings from settings object
    const useDurationDiscountsValue = effectiveSettings?.useDurationDiscounts?.enabled ?? useDurationDiscounts;
    const durationDiscountsValue = effectiveSettings?.durationDiscounts?.discounts || durationDiscounts;
    
    // Use the shared utility function
    const result = calculateDurationDiscountUtil({
      booking: newBooking,
      durationDiscounts: durationDiscountsValue,
      products1Catalog: products1ToUse,
      products2Catalog: products2ToUse,
      useDurationDiscounts: useDurationDiscountsValue,
    });

    const apiResult = {
      discountAmountCents: result.discountAmountCents,
      discountId: result.discountId,
      discountPercent: result.discountPercent,
      discountName: result.discountName,
    };
    return apiResult;
  }


  
  //////////////////////////////////////////////////////////////////////////////////////////
  /////////////////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////////////
  
  // Get products from the single booking
  const aggregateProducts = () => {
    if (!booking)
      return {
        products1Map: new Map(),
        products2Map: new Map(),
        totalProducts1Qty: 0,
        totalProducts2Qty: 0,
      };

    let totalProducts1Qty = 0;
    let totalProducts2Qty = 0;
    const products1Map = new Map();
    const products2Map = new Map();

    // Process products1 (bicycles)
    const products1Items = booking.product1;
    products1Items.forEach((item) => {
      const key = item.id;
      const originalQty = item.quantity > 0 ? item.quantity : 1;

      // Get current quantity (considering edits)
      const changes = quantityChanges.products1?.[item.id];
      const currentQty = changes !== undefined ? changes : originalQty;

      products1Map.set(key, {
        ...item,
        quantity: currentQty,
        originalQty,
        currentQty,
      });
      totalProducts1Qty += currentQty;
    });

    // Process products2 (add-ons)
    const products2Items = booking.product2;
    products2Items.forEach((item) => {
      const originalQty = item.quantity;
      const changes = quantityChanges.products2?.[item.id];
      const currentQty = changes !== undefined ? changes : originalQty;

      const product = products2Catalog.find((p) => p.id === item.id);
      products2Map.set(item.id, {
        id: item.id,
        name: product.name,
        price: product.price,
        option: product.option,
        quantity: currentQty,
        originalQty,
        currentQty,
      });
      totalProducts2Qty += currentQty;
    });

    return {
      products1: Array.from(products1Map.values()).sort((a, b) =>
        a.name.localeCompare(b.name)
      ),
      products2: Array.from(products2Map.values()).sort((a, b) =>
        a.name.localeCompare(b.name)
      ),
      totalProducts1Qty,
      totalProducts2Qty,
    };
  };
  
  // Protection: Check authentication/device trust before allowing access
  useEffect(() => {
    const id = bookingId || location.state?.editBookingId;
    if (!id) return;

    // If authenticated, allow access (no check needed)
    if (isAuthenticated) {
      setIsVerified(true);
      return;
    }

    // If unauthenticated, check if device is trusted for this booking
    const trustInfo = isDeviceTrusted(id);
    if (trustInfo) {
      // Device is trusted, allow access
      console.log("✅ Device is trusted for EditBooking access");
      setIsVerified(true);
    } else {
      // Device not trusted - show PIN auth
      console.log("🔒 Device not trusted, showing PIN auth");
      setIsVerified(false);
    }
  }, [bookingId, location.state?.editBookingId, isAuthenticated, navigate]);
  
  // Load booking with fallback to completedBookings
  useEffect(() => {
    const loadBooking = async () => {
      const id = bookingId || location.state?.editBookingId;
      if (!id) return;

      // First check Zustand store (active bookings)
      const foundInStore = bookingsFromStore.find(b => b.id === id || b.bookingId === id);
      
      if (foundInStore) {
        console.log("✅ Booking found in active bookings store, initializing EditBooking state...");
        
        // Deep copy to disconnect from store
        const deepCopy = JSON.parse(JSON.stringify(foundInStore));
        setBooking(deepCopy);
        setOriginalBooking(JSON.parse(JSON.stringify(foundInStore)));
        
        // Load admin notes
        if (deepCopy.adminNotes) {
            setAdminNotes(deepCopy.adminNotes);
            setShowAdminNotes(true);
        }

        // Set catalogs
        if (products1FromStore.length > 0) setProducts1Catalog(products1FromStore);
        if (products2FromStore.length > 0) setProducts2Catalog(products2FromStore);
        
        // Initialize newBooking
        const originalDurationDiscountPercent = deepCopy.durationDiscount?.discountPercent || 0;
        const p1Catalog = products1FromStore;
        const p2Catalog = products2FromStore;

        const product1WithFields = (deepCopy.product1 || []).map((item) => {
            const catalogProduct = p1Catalog.find((p) => p.id === item.id);
            const itemPrice = catalogProduct?.price || 0;
            const durationDiscountCents = Math.round(itemPrice * (originalDurationDiscountPercent / 100));
            return {
              ...item,
              isFromOriginalBooking: true,
              removedFromOriginalBooking: false,
              itemPrice: itemPrice,
              durationDiscountCents: durationDiscountCents,
            };
        });

        const product2WithFields = (deepCopy.product2 || []).map((item) => {
            const catalogProduct = p2Catalog.find((p) => p.id === item.id);
            const itemPrice = catalogProduct?.price || 0;
            const durationDiscountCents = Math.round(itemPrice * (originalDurationDiscountPercent / 100));
            return {
              ...item,
              isFromOriginalBooking: true,
              removedFromOriginalBooking: false,
              itemPrice: itemPrice,
              durationDiscountCents: durationDiscountCents,
            };
        });

        const initialNewBooking = JSON.parse(JSON.stringify({
            id: deepCopy.id,
            bookingId: deepCopy.bookingId,
            product1: product1WithFields,
            product2: product2WithFields,
          homePickup: deepCopy.homePickup || false,
          delivery: deepCopy.delivery || false,
            inStorePickup: deepCopy.inStorePickup || false,
            deliveryAddress: deepCopy.deliveryAddress || "",
            startDate: deepCopy.startDate,
            endDate: deepCopy.endDate,
            gatedCommunity: deepCopy.gatedCommunity || false,
            communityName: deepCopy.communityName || "",
            addressNotes: deepCopy.addressNotes || "",
            numMiles: deepCopy.numMiles !== undefined && deepCopy.numMiles !== null 
              ? parseFloat(Number(deepCopy.numMiles).toFixed(1)) 
              : null,
            // Discount objects
            durationDiscount: deepCopy.durationDiscount || { discountCents: 0, discountPercent: 0, discountName: "" },
            transportationDiscount: deepCopy.transportationDiscount || { discountCents: 0, discountPercent: 0, discountName: "" },
            // Cost/pricing fields
            deliveryCostCents: deepCopy.deliveryCostCents || 0,
            pickupCostCents: deepCopy.pickupCostCents || 0,
            productPriceAmount: deepCopy.productPriceAmount || 0,
        }));

        setNewBooking(initialNewBooking);
        setGatedCommunity(deepCopy.gatedCommunity || false);
        setCommunityName(deepCopy.communityName || "");
        setAddressNotes(deepCopy.addressNotes || "");
        
        // Set delivery/pickup states
        setDeliveryNeeded(initialNewBooking.delivery);
        setHomePickupNeeded(initialNewBooking.homePickup);
        setInStorePickup(initialNewBooking.inStorePickup);
        
        // Initialize newTotals with original booking's discount values
        setNewTotals({
          totals: {
            salesTax: 0,
            subtotalNoDiscount: 0,
            subtotalWithDiscount: 0,
            finalTotal: 0,
          },
          productCost: {
            newCost: 0,
            oldCost: 0,
            costDifference: 0
          },
          transportation: {
            newTotalChargeCents: 0,
            newDeliveryChargeCents: 0,
            newPickupChargeCents: 0,
            originalTotalChargeCents: 0,
            originalPickupChargeCents: 0,
            originalDeliveryChargeCents: 0,
            totalNetChargeCents: 0,
            deliveryNetChargeCents: 0,
            pickupNetChargeCents: 0,
            newTotalDiscountedChargesCents: 0,
            newPickupDiscountedChargeCents: 0,
            newDeliveryDiscountedChargeCents: 0,
            originalTotalDiscountedChargeCents: 0,
            originalPickupDiscountedChargeCents: 0,
            originalDDeliveryDiscountedChargeCents: 0,
            totalNetDiscountedChargeCents: 0,
            totalNetDeliveryChargeCents: 0,
            totalNetPickupChargeCents: 0,
            totalPaidForTransporationAcrossBookings: 0,
            discountName: deepCopy.transportationDiscount?.discountName || '',
            discountPercent: deepCopy.transportationDiscount?.discountPercent || 0,
            originalDiscountName: deepCopy.transportationDiscount?.discountName || '',
            originalDiscountPercent: deepCopy.transportationDiscount?.discountPercent || 0,
          },
          durationDiscount: {
            discountCents: 0,
            discountPercent: deepCopy.durationDiscount?.discountPercent || 0,
            discountName: deepCopy.durationDiscount?.discountName || '',
            originalDiscountCents: 0,
            originalDiscountPercent: deepCopy.durationDiscount?.discountPercent || 0,
            originalDiscountName: deepCopy.durationDiscount?.discountName || '',
          },
          changedProducts: [],
          days: {
            new: 0,
            old: 0,
          },
        });

        // Mark as loaded BEFORE setting state to prevent re-trigger
        hasLoadedBookingRef.current = true;
        loadedBookingIdRef.current = id;

        // Calculate totals
        calculateTotals(initialNewBooking, deepCopy).catch(err => {
          console.error("Error calculating totals on load:", err);
        });

        setLoading(false);
        return;
      }
      
      // Not in store - check if store is still loading
      if (bookingsFromStore.length === 0) {
        console.log("⏳ Bookings store is empty, waiting for data...");
        return; // Still loading
      }
      
      // Store is loaded but booking not found - check completedBookings
      console.log("🔍 Booking not in active bookings, checking completedBookings...");
      try {
        const completedPath = getCollectionPath("completedBookings");
        const bookingDoc = await getDoc(doc(db, completedPath, id));
        
        if (bookingDoc.exists()) {
          console.log("✅ Booking found in completedBookings");
          const completedBookingData = { id: bookingDoc.id, ...bookingDoc.data() };
          
          // Deep copy to disconnect from source
          const deepCopy = JSON.parse(JSON.stringify(completedBookingData));
          setBooking(deepCopy);
          setOriginalBooking(JSON.parse(JSON.stringify(completedBookingData)));
          
          // Load admin notes
          if (deepCopy.adminNotes) {
            setAdminNotes(deepCopy.adminNotes);
            setShowAdminNotes(true);
          }

          // Set catalogs
          if (products1FromStore.length > 0) setProducts1Catalog(products1FromStore);
          if (products2FromStore.length > 0) setProducts2Catalog(products2FromStore);
          
          // Initialize newBooking (same logic as active booking)
          const originalDurationDiscountPercent = deepCopy.durationDiscount?.discountPercent || 0;
          const p1Catalog = products1FromStore;
          const p2Catalog = products2FromStore;

          const product1WithFields = (deepCopy.product1 || []).map((item) => {
            const catalogProduct = p1Catalog.find((p) => p.id === item.id);
            const itemPrice = catalogProduct?.price || 0;
            const durationDiscountCents = Math.round(itemPrice * (originalDurationDiscountPercent / 100));
            return {
              ...item,
              isFromOriginalBooking: true,
              removedFromOriginalBooking: false,
              itemPrice: itemPrice,
              durationDiscountCents: durationDiscountCents,
            };
          });

          const product2WithFields = (deepCopy.product2 || []).map((item) => {
            const catalogProduct = p2Catalog.find((p) => p.id === item.id);
            const itemPrice = catalogProduct?.price || 0;
            const durationDiscountCents = Math.round(itemPrice * (originalDurationDiscountPercent / 100));
            return {
              ...item,
              isFromOriginalBooking: true,
              removedFromOriginalBooking: false,
              itemPrice: itemPrice,
              durationDiscountCents: durationDiscountCents,
            };
          });

          const initialNewBooking = JSON.parse(JSON.stringify({
            id: deepCopy.id,
            bookingId: deepCopy.bookingId,
            product1: product1WithFields,
            product2: product2WithFields,
            homePickup: deepCopy.homePickup || false,
            delivery: deepCopy.delivery || false,
            inStorePickup: deepCopy.inStorePickup || false,
            deliveryAddress: deepCopy.deliveryAddress || "",
            startDate: deepCopy.startDate,
            endDate: deepCopy.endDate,
            gatedCommunity: deepCopy.gatedCommunity || false,
            communityName: deepCopy.communityName || "",
            addressNotes: deepCopy.addressNotes || "",
            numMiles: deepCopy.numMiles !== undefined && deepCopy.numMiles !== null 
              ? parseFloat(Number(deepCopy.numMiles).toFixed(1)) 
              : null,
            // Discount objects
            durationDiscount: deepCopy.durationDiscount || { discountCents: 0, discountPercent: 0, discountName: "" },
            transportationDiscount: deepCopy.transportationDiscount || { discountCents: 0, discountPercent: 0, discountName: "" },
            // Cost/pricing fields
            deliveryCostCents: deepCopy.deliveryCostCents || 0,
            pickupCostCents: deepCopy.pickupCostCents || 0,
            productPriceAmount: deepCopy.productPriceAmount || 0,
          }));

          setNewBooking(initialNewBooking);
          setGatedCommunity(deepCopy.gatedCommunity || false);
          setCommunityName(deepCopy.communityName || "");
          setAddressNotes(deepCopy.addressNotes || "");
          
          // Set delivery/pickup states
          setDeliveryNeeded(initialNewBooking.delivery);
          setHomePickupNeeded(initialNewBooking.homePickup);
          setInStorePickup(initialNewBooking.inStorePickup);
          
          // Initialize newTotals with original booking's discount values
          setNewTotals({
            totals: {
              salesTax: 0,
              subtotalNoDiscount: 0,
              subtotalWithDiscount: 0,
              finalTotal: 0,
            },
            productCost: {
              newCost: 0,
              oldCost: 0,
              costDifference: 0
            },
            transportation: {
              newTotalChargeCents: 0,
              newDeliveryChargeCents: 0,
              newPickupChargeCents: 0,
              originalTotalChargeCents: 0,
              originalPickupChargeCents: 0,
              originalDeliveryChargeCents: 0,
              totalNetChargeCents: 0,
              deliveryNetChargeCents: 0,
              pickupNetChargeCents: 0,
              newTotalDiscountedChargesCents: 0,
              newPickupDiscountedChargeCents: 0,
              newDeliveryDiscountedChargeCents: 0,
              originalTotalDiscountedChargeCents: 0,
              originalPickupDiscountedChargeCents: 0,
              originalDDeliveryDiscountedChargeCents: 0,
              totalNetDiscountedChargeCents: 0,
              totalNetDeliveryChargeCents: 0,
              totalNetPickupChargeCents: 0,
              totalPaidForTransporationAcrossBookings: 0,
              discountName: deepCopy.transportationDiscount?.discountName || '',
              discountPercent: deepCopy.transportationDiscount?.discountPercent || 0,
              originalDiscountName: deepCopy.transportationDiscount?.discountName || '',
              originalDiscountPercent: deepCopy.transportationDiscount?.discountPercent || 0,
            },
            durationDiscount: {
              discountCents: 0,
              discountPercent: deepCopy.durationDiscount?.discountPercent || 0,
              discountName: deepCopy.durationDiscount?.discountName || '',
              originalDiscountCents: 0,
              originalDiscountPercent: deepCopy.durationDiscount?.discountPercent || 0,
              originalDiscountName: deepCopy.durationDiscount?.discountName || '',
            },
            changedProducts: [],
            days: {
              new: 0,
              old: 0,
            },
          });

          // Mark as loaded BEFORE setting state to prevent re-trigger
          hasLoadedBookingRef.current = true;
          loadedBookingIdRef.current = id;

          // Calculate totals
          calculateTotals(initialNewBooking, deepCopy).catch(err => {
            console.error("Error calculating totals on load:", err);
          });

          setLoading(false);
        } else {
          console.log("❌ Booking not found in completedBookings either");
             setBookingNotFound(true);
             setLoading(false);
        }
      } catch (err) {
        console.error("❌ Error checking completedBookings:", err);
        setBookingNotFound(true);
        setLoading(false);
    }
    };
    
    loadBooking();
  }, [bookingsFromStore, bookingId, location.state?.editBookingId, products1FromStore, products2FromStore]);

  // Sync settings from store to local state (Replacing loadSettings)
  useEffect(() => {
    if (settingsFromStore && Object.keys(settingsFromStore).length > 0) {
      const settings = settingsFromStore;
      
      setProduct1CategoryName(settings.product1Title?.title || "Product1");
      setProduct2CategoryName(settings.product2Title?.title || "Product2");
      setSalesTaxPercent(settings.salesTax?.percent || 0);
      setRefundProcessingFee(settings.refundProcessingFee?.percent || 3);
      
      const defaultPolicy = "Our cancellation policy required 48 hours advance notice for a full refund, and a 24 hour advance notice for a 75% refund. All other cancellations only receive 50% refund.";
      setCancellationPolicy(settings.cancellationPolicy?.policy || defaultPolicy);
      
      setShopInfo({
        name: settings.storeName?.name || "",
        email: settings.storeEmail?.email || "",
        phone: settings.storePhone?.phone || "",
      });
      
      setStoreLogo(settings.storeLogo?.url || "");
      setMileageRate(settings.mileageRate?.rate || 0);
      setLoadingFee(settings.loadingFee?.fee || 0);
      setStartingAddress(settings.startingAddress?.address || "");
      setDeliveryInfo(settings.deliveryInfo?.info || "");
      setPickupInfo(settings.pickupInfo?.info || "");
      
      if (settings.deliveryDiscounts?.discounts) {
        setDeliveryDiscounts(settings.deliveryDiscounts.discounts);
      }
      
      setUseDurationDiscounts(settings.useDurationDiscounts?.enabled || false);
      if (settings.durationDiscounts?.discounts) {
        setDurationDiscounts(settings.durationDiscounts.discounts);
      }

      // If setSettingsObject exists (it should based on usage)
      // Only update if settings actually changed to prevent loops
      if (typeof setSettingsObject === 'function') {
        setSettingsObject(prev => {
          // Only update if settings actually changed
          if (JSON.stringify(prev) === JSON.stringify(settings)) {
            return prev; // No change, return same object
          }
          return settings;
        });
      }
      if (settingsObjectRef) {
        settingsObjectRef.current = settings;
      }
      
      if (settings.cancellationPolicyWindows) {
        setCancellationWindows(settings.cancellationPolicyWindows.windows || [
          { hours: 48, percentPenalty: 0, daysPenalty: 0 },
          { hours: 24, percentPenalty: 25, daysPenalty: 1 },
          { hours: 0, percentPenalty: 50, daysPenalty: 2 },
        ]);
        setPenaltyType(settings.cancellationPolicyWindows.penaltyType || "percentage");
      }
    }
  }, [settingsFromStore]);
  
  // Cleanup unauthenticated listeners on unmount
  useEffect(() => {
    return () => {
      if (unauthUnsubscribers) {
        if (unauthUnsubscribers.unsubscribeBookings) {
          unauthUnsubscribers.unsubscribeBookings();
        }
        if (unauthUnsubscribers.unsubscribeProducts1) {
          unauthUnsubscribers.unsubscribeProducts1();
        }
        if (unauthUnsubscribers.unsubscribeProducts2) {
          unauthUnsubscribers.unsubscribeProducts2();
        }
        if (unauthUnsubscribers.unsubscribeSettings) {
          unauthUnsubscribers.unsubscribeSettings();
        }
      }
    };
  }, [unauthUnsubscribers]);
  
  // Watch for booking deletion in Zustand store
  useEffect(() => {
    const currentBookingId = bookingId || location.state?.editBookingId;
    if (!currentBookingId) return;
    
    // Check if booking still exists in store
    const bookingExists = bookingsFromStore.some(
      (b) =>
      b.id === currentBookingId ||
      b.bookingId === currentBookingId
    );
    
    if (!bookingExists && booking) {
      // Booking was deleted
      alert("This booking has been deleted.");
      navigate("/");
    }
  }, [
    bookingsFromStore,
    bookingId,
    location.state?.editBookingId,
    booking,
    navigate,
  ]);

  // Auto-sync inStorePickup state based on delivery/homePickup selections
  useEffect(() => {
    if (!newBooking) return;

    const delivery = newBooking.delivery || false;
    const homePickup = newBooking.homePickup || false;

    if (delivery && homePickup) {
      // Both delivery and home pickup checked - uncheck in-store pickup
      setInStorePickup(false);
    } else {
      // All other cases - in-store pickup MUST be checked
      // (delivery only, home pickup only, or neither)
      setInStorePickup(true);
    }
  }, [newBooking?.delivery, newBooking?.homePickup]);

  // Auto-save admin notes with debounce
  useEffect(() => {
    // Clear any existing timer
    if (adminNotesTimerRef.current) {
      clearTimeout(adminNotesTimerRef.current);
    }

    // Only auto-save if:
    // 1. User is authenticated
    // 2. Booking exists with a valid ID
    // 3. Admin notes have actually changed from original
    const bookingIdToUse = booking?.id || booking?.bookingId;
    if (isAuthenticated && bookingIdToUse && adminNotes !== originalBooking?.adminNotes) {
      adminNotesTimerRef.current = setTimeout(async () => {
        try {
          setAdminNotesSaving(true);

          // Update the booking document's admin notes field
          const bookingsPath = getCollectionPath("bookings");

          // Update the booking document
          const bookingRef = doc(db, bookingsPath, bookingIdToUse);
          const bookingDoc = await getDoc(bookingRef);

          if (bookingDoc.exists()) {
            // Get user metadata for change tracking
            const selectedUser = useAuthStore.getState().selectedUser;
            const userMetadata = selectedUser ? {
              userId: selectedUser.id,
              firstName: selectedUser.firstName || "",
              lastName: selectedUser.lastName || "",
            } : null;

            const updateData = {
              adminNotes: adminNotes,
              updatedAt: Timestamp.now()
            };

            // Add change tracking if user metadata is available
            if (userMetadata) {
              const changeEntry = createChangeTrackingEntry(userMetadata, {
                changeType: "admin_notes",
                fieldChanged: "adminNotes",
                oldValue: originalBooking?.adminNotes || null,
                newValue: adminNotes || null,
                description: "Admin notes updated",
                timestamp: Timestamp.now(),
              });

              if (changeEntry) {
                updateData.editedBy = arrayUnion(changeEntry);
              }
            }

            await updateDoc(bookingRef, updateData);
          }
        } catch (error) {
          console.error("❌ Error auto-saving admin notes:", error);
        } finally {
          setAdminNotesSaving(false);
        }
      }, 2000); // 2 second debounce
    }

    // Cleanup function
    return () => {
      if (adminNotesTimerRef.current) {
        clearTimeout(adminNotesTimerRef.current);
      }
    };
  }, [adminNotes, isAuthenticated, booking, originalBooking]);

  // Detect lower discount tiers and show modals (only after initialization and when there's a change)
  useEffect(() => {
    if (!newTotals || !originalBooking || !newBooking) return;

    // Get current values from newTotals (represents what will be saved to newBooking)
    const newTransportationDiscountPercent = newTotals?.transportation?.transportationDiscountToSaveInBooking?.discountPercent || 0;
    const originalTransportationDiscountPercent = originalBooking?.transportationDiscount?.discountPercent || 0;
    const newTransportationDiscountName = newTotals?.transportation?.transportationDiscountToSaveInBooking?.discountName || "";
    const originalTransportationDiscountName = originalBooking?.transportationDiscount?.discountName || "";

    const newDurationDiscountPercent = newTotals?.durationDiscount?.durationDiscountToSaveInBooking?.discountPercent || 0;
    const originalDurationDiscountPercent = originalBooking?.durationDiscount?.discountPercent || 0;
    const newDurationDiscountName = newTotals?.durationDiscount?.durationDiscountToSaveInBooking?.discountName || "";
    const originalDurationDiscountName = originalBooking?.durationDiscount?.discountName || "";

    // On first load, initialize the refs and mark as initialized
    if (!hasInitializedRef.current) {
      previousTransportationDiscountRef.current = {
        percent: newTransportationDiscountPercent,
        name: newTransportationDiscountName
      };
      previousDurationDiscountRef.current = {
        percent: newDurationDiscountPercent,
        name: newDurationDiscountName
      };
      hasInitializedRef.current = true;
      return; // Don't show modals on initial load
    }

    // Check for lower transportation discount tier (only if it changed from previous)
    const transportationChanged =
      previousTransportationDiscountRef.current?.percent !== newTransportationDiscountPercent ||
      previousTransportationDiscountRef.current?.name !== newTransportationDiscountName;

    if (transportationChanged &&
      newTransportationDiscountPercent < originalTransportationDiscountPercent &&
      originalTransportationDiscountPercent > 0 &&
      !showLowerTransportationTierModal) {
      setLowerTransportationTierData({
        oldName: originalTransportationDiscountName || `Original (${originalTransportationDiscountPercent}% off)`,
        newName: newTransportationDiscountName || `New (${newTransportationDiscountPercent}% off)`
      });
      setShowLowerTransportationTierModal(true);
      // Update ref to current value
      previousTransportationDiscountRef.current = {
        percent: newTransportationDiscountPercent,
        name: newTransportationDiscountName
      };
    } else if (transportationChanged) {
      // Update ref even if modal doesn't show
      previousTransportationDiscountRef.current = {
        percent: newTransportationDiscountPercent,
        name: newTransportationDiscountName
      };
    }

    // Check for lower duration discount tier (only if days shortened and discount changed)
    const daysDiff = (newTotals?.days?.new || 0) - (newTotals?.days?.old || 0);
    const durationChanged =
      previousDurationDiscountRef.current?.percent !== newDurationDiscountPercent ||
      previousDurationDiscountRef.current?.name !== newDurationDiscountName;

    if (durationChanged &&
      daysDiff < 0 &&
      newDurationDiscountPercent < originalDurationDiscountPercent &&
      originalDurationDiscountPercent > 0 &&
      !showLowerDurationTierModal) {
      setLowerDurationTierData({
        oldName: originalDurationDiscountName || `Original (${originalDurationDiscountPercent}% off)`,
        newName: newDurationDiscountName || `New (${newDurationDiscountPercent}% off)`
      });
      setShowLowerDurationTierModal(true);
      // Update ref to current value
      previousDurationDiscountRef.current = {
        percent: newDurationDiscountPercent,
        name: newDurationDiscountName
      };
    } else if (durationChanged) {
      // Update ref even if modal doesn't show
      previousDurationDiscountRef.current = {
        percent: newDurationDiscountPercent,
        name: newDurationDiscountName
      };
    }
  }, [newTotals, originalBooking, newBooking, showLowerTransportationTierModal, showLowerDurationTierModal]);


  // Sync delivery/homePickup with newBooking and handle inStorePickup logic
  useEffect(() => {
    if (!newBooking) return;
    
    // Prevent updates during calculateTotals to avoid loops
    if (isCalculatingTotalsRef.current) {
      return;
    }
    
    const currentDelivery = newBooking.delivery || false;
    const currentHomePickup = newBooking.homePickup || false;
    
    // Only update if values actually changed
    if (prevDeliveryRef.current === currentDelivery && prevHomePickupRef.current === currentHomePickup) {
      return;
    }
    
    prevDeliveryRef.current = currentDelivery;
    prevHomePickupRef.current = currentHomePickup;
    
    // If either delivery or homePickup is checked, set inStorePickup to false
    if (currentDelivery || currentHomePickup) {
      if (newBooking.inStorePickup) {
        setNewBooking(prev => ({
          ...prev,
          inStorePickup: false
        }));
        setInStorePickup(false);
      }
    } 
    // If neither is checked, set inStorePickup to true
    else if (!currentDelivery && !currentHomePickup) {
      if (!newBooking.inStorePickup) {
        setNewBooking(prev => ({
          ...prev,
          inStorePickup: true
        }));
        setInStorePickup(true);
      }
    }
  }, [newBooking?.delivery, newBooking?.homePickup]);
  
  // Update product catalogs when Zustand store changes
  useEffect(() => {
    if (products1FromStore.length > 0) {
      setProducts1Catalog(products1FromStore);
    }
  }, [products1FromStore]);

  // Load Google Maps API script if not already loaded
  useEffect(() => {
    const GOOGLE_MAPS_API_KEY = 'AIzaSyDnMrIVkx-6V0Brm4pxezeOVcTOELsTmdQ';

    const initAutocomplete = () => {
      if (window.google && window.google.maps && window.google.maps.places) {
        if (!autocompleteServiceRef.current) {
          autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
        }
        if (!placesServiceRef.current) {
          const mapDiv = document.createElement('div');
          placesServiceRef.current = new window.google.maps.places.PlacesService(mapDiv);
        }
        setMapsLoaded(true);
      }
    };

    // Load Google Maps API script if not already loaded
    if (!window.google || !window.google.maps || !window.google.maps.places) {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places,geometry,directions`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        initAutocomplete();
      };
      document.head.appendChild(script);
    } else {
      initAutocomplete();
    }
  }, []);

  // Validate button enabled only when user has typed 5+ chars in New Address (no default pre-fill)
  const newAddressTrimmed = (newAddress || "").trim();
  const isValidateEnabled = (newBooking?.delivery || newBooking?.homePickup) &&
    newAddressTrimmed.length >= 5 &&
    !addressConfirmed;

  // Animate validate button when enabled
  useEffect(() => {
    if (isValidateEnabled) {
      const blinkAnimation = Animated.loop(
        Animated.sequence([
          Animated.timing(validateButtonOpacity, {
            toValue: 0.3,
            duration: 500,
            useNativeDriver: true,
          }),
          Animated.timing(validateButtonOpacity, {
            toValue: 1,
            duration: 500,
            useNativeDriver: true,
          }),
        ])
      );
      blinkAnimation.start();
      return () => blinkAnimation.stop();
    } else {
      validateButtonOpacity.setValue(1);
    }
  }, [isValidateEnabled, validateButtonOpacity]);

  // Initialize map with route when address validation modal opens
  useEffect(() => {
    if (!showAddressValidationModal || !mapsLoaded) {
      if (!showAddressValidationModal && mapInstance1.current) {
        mapInstance1.current = null;
        directionsRenderer1.current = null;
      }
      return;
    }
    
    const currentAddress = (newAddress || newBooking?.deliveryAddress || "").trim();
    if (!currentAddress) return;

    const initializeMap = () => {
      if (!mapRef1.current) return;
      const mapElement = mapRef1.current;
      if (!mapElement.offsetWidth || !mapElement.offsetHeight) return;

      const shopAddress = startingAddress || "9201 Bonita Beach Rd SE, Bonita Springs, FL 34135";

      const map = new window.google.maps.Map(mapElement, {
        zoom: 12,
        mapTypeControl: true,
        streetViewControl: true,
        fullscreenControl: true,
        zoomControl: true,
      });
      mapInstance1.current = map;

      const directionsService = new window.google.maps.DirectionsService();
      const directionsRenderer = new window.google.maps.DirectionsRenderer({
        map: map,
        suppressMarkers: false,
      });
      directionsService1.current = directionsService;
      directionsRenderer1.current = directionsRenderer;

      directionsService.route(
        {
          origin: shopAddress,
          destination: currentAddress,
          travelMode: window.google.maps.TravelMode.DRIVING,
        },
        (result, status) => {
          if (status === "OK") {
            directionsRenderer.setDirections(result);
            const bounds = new window.google.maps.LatLngBounds();
            result.routes[0].legs.forEach((leg) => {
              bounds.extend(leg.start_location);
              bounds.extend(leg.end_location);
            });
            map.fitBounds(bounds);
            setTimeout(() => {
              window.google.maps.event.trigger(map, "resize");
            }, 100);
          } else {
            setMapError("Could not calculate route");
          }
        }
      );
    };

    const checkAndInitialize = () => {
      if (!mapRef1.current) {
        setTimeout(checkAndInitialize, 100);
        return;
      }
      const mapElement = mapRef1.current;
      if (mapElement.offsetWidth > 0 && mapElement.offsetHeight > 0) {
        initializeMap();
      } else {
        setTimeout(checkAndInitialize, 100);
      }
    };

    const timer = setTimeout(checkAndInitialize, 300);
    return () => {
      clearTimeout(timer);
      if (directionsRenderer1.current) {
        directionsRenderer1.current.setMap(null);
      }
    };
  }, [showAddressValidationModal, mapsLoaded, startingAddress]);

  const handleValidateAddress = async () => {
    const currentAddress = (newAddress || newBooking?.deliveryAddress || "").trim();
    if (!currentAddress || currentAddress.length < 5) return;

    setMapLoading(true);
    setMapError(null);
    setShowAddressValidationModal(true);

    try {
      const getDistance = httpsCallable(functions, "getDistance");
      const result = await getDistance({ fullAddress: currentAddress });

      if (result.data?.miles !== null && result.data?.miles !== undefined) {
        setAddressDistanceMiles(parseFloat(result.data.miles.toFixed(1)));
      } else {
        setMapError("Could not calculate distance");
      }
    } catch (error) {
      console.error("Error calculating distance:", error);
      setMapError(error.message || "Failed to calculate distance");
    } finally {
      setMapLoading(false);
    }
  };

  const handleConfirmAddress = () => {
    setAddressConfirmed(true);
    setShowAddressValidationModal(false);

    if (addressDistanceMiles !== null && addressDistanceMiles !== undefined) {
      const updatedBooking = { ...newBooking, numMiles: addressDistanceMiles };
      updateNewBooking(updatedBooking).catch((err) => {
        console.error("handleConfirmAddress: updateNewBooking failed", err);
      });
    }

    setAddressDistanceMiles(null);
    setMapError(null);
  };

  const handleWrongAddress = () => {
    setAddressConfirmed(false);
    setShowAddressValidationModal(false);
    setAddressDistanceMiles(null);
    setMapError(null);
    setTimeout(() => {
      setShowAddressWrongModal(true);
    }, 300);
  };

  useEffect(() => {
    if (products2FromStore.length > 0) {
      setProducts2Catalog(products2FromStore);
    }
  }, [products2FromStore]);
  
  // Watch for discount unlocks (only after initialization)
  useEffect(() => {
    if (!newBooking || !originalBooking || !newTotals) return;

    // Check for transportation discount unlock
    const newTransportPercent = newTotals?.transportation?.transportationDiscountToSaveInBooking?.discountPercent || 0;
    const oldTransportPercent = originalBooking?.transportationDiscount?.discountPercent || 0;
    const newTransportName = newTotals?.transportation?.transportationDiscountToSaveInBooking?.discountName || "";
    const oldTransportName = originalBooking?.transportationDiscount?.discountName || "";

    // Initialize ref if not already set (for higher tier detection)
    if (previousTransportationDiscountForHigherTierRef.current === null) {
      previousTransportationDiscountForHigherTierRef.current = {
        percent: newTransportPercent,
        name: newTransportName
      };
      // Don't show modal on first initialization
    } else {
      // Check if transportation discount has changed from previous
      const previousPercent = previousTransportationDiscountForHigherTierRef.current?.percent || 0;
      const transportationChanged =
        previousPercent !== newTransportPercent ||
        previousTransportationDiscountForHigherTierRef.current?.name !== newTransportName;

      // Only show if tier actually increased (greater than original AND greater than previous ref value)
      const isHigherThanOriginal = newTransportPercent > oldTransportPercent;
      const isHigherThanPrevious = newTransportPercent > previousPercent;

      if (transportationChanged &&
        isHigherThanOriginal &&
        isHigherThanPrevious &&
        oldTransportPercent >= 0 &&
        !shownTransportationUnlock &&
        !discountUnlockModalVisible) {
      setUnlockedDiscountType("transportation");
        setUnlockedDiscountName(newTransportName || "Transportation Discount");
      setUnlockedDiscountPercent(newTransportPercent);
      setDiscountUnlockModalVisible(true);
      setShownTransportationUnlock(true);
        // Update ref
        previousTransportationDiscountForHigherTierRef.current = {
          percent: newTransportPercent,
          name: newTransportName
        };
      return; // Exit early to avoid showing both modals at once
      } else if (transportationChanged) {
        // Update ref even if modal doesn't show
        previousTransportationDiscountForHigherTierRef.current = {
          percent: newTransportPercent,
          name: newTransportName
        };
      }
    }

    // Check for duration discount unlock (only if transportation modal is not showing)
    if (!discountUnlockModalVisible) {
      const newDurationPercent = newTotals?.durationDiscount?.durationDiscountToSaveInBooking?.discountPercent || 0;
      const oldDurationPercent = originalBooking?.durationDiscount?.discountPercent || 0;
      const newDurationName = newTotals?.durationDiscount?.durationDiscountToSaveInBooking?.discountName || "";
      const oldDurationName = originalBooking?.durationDiscount?.discountName || "";

      // Initialize ref if not already set (for higher tier detection)
      if (previousDurationDiscountForHigherTierRef.current === null) {
        previousDurationDiscountForHigherTierRef.current = {
          percent: newDurationPercent,
          name: newDurationName
        };
        // Don't show modal on first initialization
      } else {
        // Check if duration discount has changed from previous
        const durationChanged =
          previousDurationDiscountForHigherTierRef.current?.percent !== newDurationPercent ||
          previousDurationDiscountForHigherTierRef.current?.name !== newDurationName;

        // Only show if tier actually increased (greater than original AND greater than previous)
        if (durationChanged &&
          newDurationPercent > oldDurationPercent &&
          newDurationPercent > (previousDurationDiscountForHigherTierRef.current?.percent || 0) &&
          oldDurationPercent >= 0 &&
          !shownDurationUnlock) {
        setUnlockedDiscountType("duration");
          setUnlockedDiscountName(newDurationName || "Duration Discount");
        setUnlockedDiscountPercent(newDurationPercent);
        setDiscountUnlockModalVisible(true);
        setShownDurationUnlock(true);
          // Update ref
          previousDurationDiscountForHigherTierRef.current = {
            percent: newDurationPercent,
            name: newDurationName
          };
        } else if (durationChanged) {
          // Update ref even if modal doesn't show
          previousDurationDiscountForHigherTierRef.current = {
            percent: newDurationPercent,
            name: newDurationName
          };
        }
      }
    }
  }, [newBooking, newTotals, originalBooking, shownTransportationUnlock, shownDurationUnlock, discountUnlockModalVisible]);

  // Removed initializeUnauthListeners

  const loadBookingData = async (overrideBookingId = null) => {
    try {
      setLoading(true);
      let currentBooking = null;
      
      const bookingIdToLoad =
        overrideBookingId ||
        bookingId ||
        location.state?.editBookingId ||
        authenticatedBookingId;
      if (!bookingIdToLoad) {
        alert("Error: No booking ID provided");
        navigate("/");
        return;
      }
      
      // Try to load from Zustand store (now populated for all users)
      const cachedBooking = bookingsFromStore.find(
        (b) => b.bookingId === bookingIdToLoad || b.id === bookingIdToLoad
      );

      if (!cachedBooking) {
        // If store is loaded but booking not found, show error
        if (bookingsFromStore.length > 0) {
          alert("Error: Booking not found");
          navigate("/");
        }
        // If store is empty, we wait for it to load (via useEffect dependency)
        return;
      } else {
        currentBooking = cachedBooking;
        setBooking(currentBooking);
        // Create a deep copy to ensure originalBooking is never mutated
        setOriginalBooking(JSON.parse(JSON.stringify(currentBooking)));
        // Reset discount unlock flags when booking changes
        setShownTransportationUnlock(false);
        setShownDurationUnlock(false);
        previousTransportationDiscountForHigherTierRef.current = null;
        previousDurationDiscountForHigherTierRef.current = null;
        // Load admin notes
        if (currentBooking?.adminNotes) {
          setAdminNotes(currentBooking.adminNotes);
          // Show admin notes if they exist
          setShowAdminNotes(!!currentBooking.adminNotes && currentBooking.adminNotes.trim().length > 0);
        }
      }
      
      if (!currentBooking) return;
      
      // Initialize newBooking from original booking data
      // Include id and bookingId so calculateProductDurationAvailability can filter out the original booking
      // Get product catalogs for price lookup
      const products1CatalogToUse = products1Catalog.length > 0 ? products1Catalog : products1FromStore;
      const products2CatalogToUse = products2Catalog.length > 0 ? products2Catalog : products2FromStore;

      // Get original duration discount percent from the booking
      const originalDurationDiscountPercent = currentBooking.durationDiscount?.discountPercent || 0;

      // Map product1 items with additional fields
      const product1WithFields = (currentBooking.product1 || []).map((item) => {
        const catalogProduct = products1CatalogToUse.find((p) => p.id === item.id);
        const itemPrice = catalogProduct?.price || 0; // Price in cents
        const durationDiscountCents = Math.round(itemPrice * (originalDurationDiscountPercent / 100));
        return {
          ...item,
          isFromOriginalBooking: true,
          removedFromOriginalBooking: false,
          itemPrice: itemPrice,
          durationDiscountCents: durationDiscountCents,
        };
      });

      // Map product2 items with additional fields
      const product2WithFields = (currentBooking.product2 || []).map((item) => {
        const catalogProduct = products2CatalogToUse.find((p) => p.id === item.id);
        const itemPrice = catalogProduct?.price || 0; // Price in cents
        const durationDiscountCents = Math.round(itemPrice * (originalDurationDiscountPercent / 100));
        return {
          ...item,
          isFromOriginalBooking: true,
          removedFromOriginalBooking: false,
          itemPrice: itemPrice,
          durationDiscountCents: durationDiscountCents,
        };
      });

      const initialNewBooking = JSON.parse(JSON.stringify({
        id: currentBooking.id,
        bookingId: currentBooking.bookingId,
        product1: product1WithFields,
        product2: product2WithFields,
        homePickup: currentBooking.homePickup || false,
        delivery: currentBooking.delivery || false,
        inStorePickup: currentBooking.inStorePickup || false,
        deliveryAddress: currentBooking.deliveryAddress || "",
        startDate: currentBooking.startDate,
        endDate: currentBooking.endDate,
        gatedCommunity: currentBooking.gatedCommunity || false,
        communityName: currentBooking.communityName || "",
        addressNotes: currentBooking.addressNotes || "",
        numMiles: currentBooking.numMiles !== undefined && currentBooking.numMiles !== null 
          ? parseFloat(Number(currentBooking.numMiles).toFixed(1)) 
          : null,
        // Discount objects
        durationDiscount: currentBooking.durationDiscount || { discountCents: 0, discountPercent: 0, discountName: "" },
        transportationDiscount: currentBooking.transportationDiscount || { discountCents: 0, discountPercent: 0, discountName: "" },
        // Cost/pricing fields
        deliveryCostCents: currentBooking.deliveryCostCents || 0,
        pickupCostCents: currentBooking.pickupCostCents || 0,
        productPriceAmount: currentBooking.productPriceAmount || 0,
      }));
      
      setNewBooking(initialNewBooking);
      setGatedCommunity(currentBooking.gatedCommunity || false);
      setCommunityName(currentBooking.communityName || "");
      setAddressNotes(currentBooking.addressNotes || "");
      
      // Initialize delivery/pickup state variables from booking
      setDeliveryNeeded(currentBooking.delivery || false);
      setHomePickupNeeded(currentBooking.homePickup || false);
      setInStorePickup(currentBooking.inStorePickup || false);
      
      // Check if rental has started
      let startDate;
      try {
        startDate = parseFirestoreDate(currentBooking.startDate);
        
        const now = new Date();
        const hasStarted = now >= startDate;
        setRentalHasStarted(hasStarted);
      } catch (error) {
        console.error("Error checking rental start:", error);
      }

      // ============================================================
      // ⚠️ END OF newBooking INITIALIZATION ⚠️
      // ============================================================




      
      // Use products from Zustand store (already subscribed)
      setProducts1Catalog(products1FromStore);
      setProducts2Catalog(products2FromStore);
      
      // Load settings - pass booking data so it can use the correct tenant/store context
      // Get settings object directly from return value (don't wait for state update)
      const loadedSettings = await loadSettings(currentBooking);
      setSettingsObject(loadedSettings)
      // log('loaded', loadedSettings)
      if (!loadedSettings || Object.keys(loadedSettings).length === 0) {
        console.warn("⚠️ Settings not loaded, proceeding anyway...");
      } else {
        console.log("✅ Settings loaded successfully");
      }

      // Calculate calendar availability immediately after products and settings are loaded
      // Pass initialNewBooking directly to avoid React state async update issue
      // This ensures availability is shown in the Rental Items section on page load
      calculateCalendarAvailability(initialNewBooking);

      // Calculate newTotals immediately after newBooking is populated, settings are loaded, and products are available
      // Use the original booking as both newBooking and originalBooking (they're the same on page load)
      // Settings and products are now loaded, so calculateTotals can proceed
      console.log("🔄 Running calculateNewTotals on page load...");
      calculateTotals(initialNewBooking, currentBooking, loadedSettings, products1FromStore, products2FromStore).catch((error) => {
        console.error("❌ Error in calculateNewTotals on page load:", error);
        // Don't block loading completion if calculateNewTotals fails
      });
      
      // Calculate available inventory after everything is loaded
      // We'll do this in a separate useEffect to ensure all dependencies are ready
    } catch (error) {
      console.error("❌ Error loading booking:", error);
      console.error("❌ Error message:", error.message);
      console.error("❌ Error stack:", error.stack);
      alert("Error: Failed to load booking - " + error.message);
    } finally {
      setLoading(false);
    }
  };
  


  /////////////////////////////////////////////////////////////////////////////////////////
  ////////////////////////////////////////////////////////////////////////////////////
  /////////////////////////////////////////////////////////////////////////////////////
  
  // Calculate date availability for extension (30 days after current end date)
  const calculateDateAvailability = async () => {
    setCalculatingAvailability(true);
    
    try {
      const latestSub = booking;
      const currentEndDate = parseFirestoreDate(latestSub.endDate);
      
      // Get all products currently in the booking (including pending changes)
      const allProducts1 = [];
      const allProducts2 = {};
      
      const products1Items = booking.product1;
      products1Items.forEach((item) => {
        // Apply pending changes
        let qty = item.quantity;
        if (quantityChanges.products1?.[item.id] !== undefined) {
          qty = quantityChanges.products1[item.id];
        }
        
        const existing = allProducts1.find((p) => p.id === item.id);
        if (existing) {
          existing.quantity += qty;
        } else {
          allProducts1.push({ ...item, quantity: qty });
        }
      });

          const products2Items = booking.product2;
      products2Items.forEach((item) => {
        let qty = item.quantity;
        
        // Apply pending changes
        if (quantityChanges.products2?.[item.id] !== undefined) {
          qty = quantityChanges.products2[item.id];
        }
        
        if (!allProducts2[item.id]) {
          allProducts2[item.id] = { quantity: qty };
        } else {
          const existing = allProducts2[item.id].quantity;
          allProducts2[item.id] = { quantity: existing + qty };
        }
      });
      
      // Use all bookings from Zustand store (already subscribed)
      const allBookings = bookingsFromStore;
      const availability = {};
      
      // Get all days in the current viewing month
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth();
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      
      // Check each day in the current month
      for (let day = 1; day <= daysInMonth; day++) {
        const checkDate = new Date(year, month, day);
        checkDate.setHours(0, 0, 0, 0); // Normalize to midnight
        const dateStr = checkDate.toISOString().split("T")[0];
        
        // Only check dates after current end date
        if (checkDate <= currentEndDate) {
          availability[dateStr] = false; // Past dates not available for extension
          continue;
        }
        
        // Check if all product1 items are available for this date
        let allAvailable = true;
        
        // Check products1 availability
        for (const item of allProducts1) {
          let bookedQty = 0;
          
          // Debug logging for first date
          const debugThisDate = day === 1;
          
          allBookings.forEach((b) => {
            // Skip current booking
            const bId = b.bookingId || b.id;
            const currentBookingId = booking.bookingId || booking.id;
            if (bId === currentBookingId) {
              return;
            }
            
              // Check if this booking overlaps with our check date
            const bStart = parseFirestoreDate(b.startDate);
            const bEnd = parseFirestoreDate(b.endDate);
              
              // Normalize dates to midnight for comparison
            bStart.setHours(0, 0, 0, 0);
            bEnd.setHours(0, 0, 0, 0);

            if (checkDate >= bStart && checkDate <= bEnd) {
              const bProducts1 = b.product1;
              const matchingItem = bProducts1.find((p) => p.id === item.id);
                if (matchingItem) {
                  bookedQty += matchingItem.quantity;
                }
              }
          });
          
          // Check if there's enough inventory
          const product = products1Catalog.find((p) => p.id === item.id);
          const totalInventory = product.quantity;
          
          // If product has no inventory tracking (quantity = 0), assume unlimited
          if (totalInventory === 0) {
            continue; // Skip to next item (don't mark as unavailable)
          }
          
          const availableQty = totalInventory - bookedQty;
          
          if (availableQty < item.quantity) {
            allAvailable = false;
            break;
          }
        }
        
        // Check products2 availability (for user-selection items with inventory)
        if (allAvailable) {
          for (const [productId, value] of Object.entries(allProducts2)) {
            const product = products2Catalog.find((p) => p.id === productId);
            
            // Skip if not user-selection (per-bike/per-booking don't track inventory)
            if (product.option !== "user-selection") continue;
            
            // Skip if no inventory tracking (quantity = 0 means unlimited)
            if (product.quantity === 0) {
              continue;
            }
            
            const neededQty = value.quantity;
            let bookedQty = 0;
            
            allBookings.forEach((b) => {
              // Skip current booking
              const bId = b.bookingId || b.id;
              const currentBookingId = booking.bookingId || booking.id;
              if (bId === currentBookingId) {
                return;
              }
              
              const bStart = parseFirestoreDate(b.startDate);
              const bEnd = parseFirestoreDate(b.endDate);
                
                // Normalize dates to midnight for comparison
              bStart.setHours(0, 0, 0, 0);
              bEnd.setHours(0, 0, 0, 0);

              if (checkDate >= bStart && checkDate <= bEnd) {
                const bProducts2 = b.product2;
                const bItem = bProducts2.find((p) => p.id === productId);
                if (bItem) {
                  bookedQty += bItem.quantity;
                }
              }
            });
            
            const availableQty = product.quantity - bookedQty;
            
            if (availableQty < neededQty) {
              allAvailable = false;
              break;
            }
          }
        }
        
        availability[dateStr] = allAvailable;
        
        // Debug first 3 future dates
        if (day <= 3 && checkDate > currentEndDate) {
        }
      }
      
      // Summary
      const availableDays = Object.values(availability).filter(
        (v) => v === true
      ).length;
      const unavailableDays = Object.values(availability).filter(
        (v) => v === false
      ).length;
      const totalChecked = Object.keys(availability).length;
      
      setDateAvailability(availability);
    } catch (error) {
      console.error("❌ Error calculating date availability:", error);
    } finally {
      setCalculatingAvailability(false);
    }
  };
  
  // Calculate available inventory for a specific product
  // optional originalQtyOverride is used to seed pending quantity for "Your Items" rows
  const calculateProductAvailability = (
    productId,
    productType,
    originalQtyOverride,
    pendingQtyOverride
  ) => {
    if (!booking) return null;
    
    const catalog =
      productType === "products1" ? products1Catalog : products2Catalog;
    const product = catalog.find((p) => p.id === productId);
    
    const latestSub = booking;
    const startDate = newStartDate ? new Date(newStartDate) : parseFirestoreDate(latestSub.startDate);
    const endDate = newEndDate ? new Date(newEndDate) : parseFirestoreDate(latestSub.endDate);
    
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);
    
    // Products1 uses 'quantity', Products2 uses 'inventory'
    const inventoryField =
      productType === "products1" ? product.quantity : product.inventory;
    
    // Check inventory - return null if no tracking
    if (inventoryField <= 0) {
      return null;
    }
    
    const totalInventory = inventoryField;
    const allBookings = bookingsFromStore;
    let bookedCount = 0;
    
    allBookings.forEach((b) => {
      const bId = b.bookingId || b.id;
      if (bId === bookingId) return;

      const bStart = parseFirestoreDate(b.startDate);
      const bEnd = parseFirestoreDate(b.endDate);
        bStart.setHours(0, 0, 0, 0);
        bEnd.setHours(0, 0, 0, 0);
        
        const overlaps = !(endDate < bStart || startDate > bEnd);
        if (overlaps) {
        if (productType === "products1") {
          const items = b.product1;
          const item = items.find((p) => p.id === productId);
            if (item) {
              const qty = item.quantity;
              bookedCount += qty;
            }
          } else {
          const items = b.product2;
            const item = items.find((p) => p.id === productId);
            if (item) {
              bookedCount += item.quantity;
            }
          }
        }
    });
    
    let currentQuantity = 0;
    // Process current booking products
    if (booking) {
      if (productType === "products1") {
        const items = booking.product1;
        const item = items.find((p) => p.id === productId);
        if (item) {
          currentQuantity = item.quantity;
        }
      } else {
        const items = booking.product2;
        const item = items.find((p) => p.id === productId);
        if (item) {
          currentQuantity = item.quantity;
        }
      }
    }
    
    // Get pending quantity from changes or, if none, from originalQtyOverride (for "Your Items")
    // pendingQtyOverride takes precedence (for availability checks during quantity changes)
    let pendingQuantity = currentQuantity;
    if (typeof pendingQtyOverride === "number") {
      pendingQuantity = pendingQtyOverride;
    } else if (quantityChanges?.[productType]?.[productId] !== undefined) {
      pendingQuantity = quantityChanges[productType][productId];
    } else if (typeof originalQtyOverride === "number") {
      pendingQuantity = originalQtyOverride;
    }
    
    // Available = total inventory minus other bookings minus the pending quantity for this booking
    const available = Math.max(
      0,
      totalInventory - bookedCount - pendingQuantity
    );
    
    return {
      total: totalInventory,
      current: currentQuantity,
      pending: pendingQuantity,
      available,
      changed: pendingQuantity !== currentQuantity,
    };
  };
  
  // Generate calendar days for current month
  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    return days;
  };

  // Navigate to previous month
  const goToPreviousMonth = () => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(currentMonth.getMonth() - 1);
    setCurrentMonth(newMonth);
    // Recalculate calendar availability for the new month
    calculateCalendarAvailability();
  };

  // Navigate to next month
  const goToNextMonth = () => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(currentMonth.getMonth() + 1);
    setCurrentMonth(newMonth);
    // Recalculate calendar availability for the new month
    calculateCalendarAvailability();
  };
  
  // Calculate cost for date extension
  const calculateExtensionCost = (newEnd) => {
    if (!newEnd) return null;
    
    const latestSub = booking;
    
    // Parse original end date
    const originalEnd = parseFirestoreDate(latestSub.endDate);
    
    // Parse original start date for total calculation
    const originalStart = parseFirestoreDate(latestSub.startDate);
    
    // Normalize to midnight
    originalEnd.setHours(0, 0, 0, 0);
    originalStart.setHours(0, 0, 0, 0);
    const newEndNormalized = new Date(newEnd);
    newEndNormalized.setHours(0, 0, 0, 0);
    
    // Calculate additional days
    const additionalDays = Math.round(
      (newEndNormalized - originalEnd) / (1000 * 60 * 60 * 24)
    );
    
    if (additionalDays <= 0) return null;
    
    // Calculate original daily rate
    const originalDays = Math.round(
      (originalEnd - originalStart) / (1000 * 60 * 60 * 24)
    );
    const firstPayment = booking.payments[0];
    const originalTotal = firstPayment.costBreakdown.totalCharged;
    let dailyRate = originalTotal / originalDays;
    
    // Add costs for any product additions from quantity changes
    if (quantityChanges && booking) {
      // Products1 additions
      if (quantityChanges.products1) {
        Object.entries(quantityChanges.products1).forEach(
          ([productId, newQty]) => {
            const originalItem = booking.product1.find(
              (p) => p.id === productId
            );
          const originalQty = originalItem.quantity;
          const qtyAdded = Math.max(0, newQty - originalQty);
          if (qtyAdded > 0) {
            dailyRate += qtyAdded * originalItem.price;
          }
          }
        );
      }

      // Products2 additions
      if (quantityChanges.products2) {
        Object.entries(quantityChanges.products2).forEach(
          ([productId, newQty]) => {
            const originalItems = booking.product2;
            const originalItem = originalItems.find((p) => p.id === productId);
            const originalQty = originalItem ? originalItem.quantity : 0;
          const qtyAdded = Math.max(0, newQty - originalQty);
          if (qtyAdded > 0) {
              const product = products2Catalog.find((p) => p.id === productId);
              const basePrice = product.price;
              if (product.option === "per-bike") {
                  const products1Items = booking.product1;
                  const totalProducts1Qty = products1Items.reduce(
                    (sum, item) => sum + item.quantity,
                    0
                  );
                  dailyRate += qtyAdded * basePrice * totalProducts1Qty;
              } else if (product.option === "per-booking") {
                dailyRate += qtyAdded * basePrice;
              } else if (product.option === "user-selection") {
                dailyRate += qtyAdded * basePrice;
              }
          }
          }
        );
      }
    }
    
    // Calculate extension cost (before tax)
    let extensionSubtotal = dailyRate * additionalDays;
    
    // Add delivery/pickup costs if they're being added (one-time charges, not daily)
    const originalDeliveryNeeded = latestSub.delivery;
    const originalHomePickupNeeded = latestSub.homePickup;
    
    if (deliveryNeeded && !originalDeliveryNeeded && deliveryAddress) {
      extensionSubtotal += deliveryCost;
    }
    if (homePickupNeeded && !originalHomePickupNeeded && deliveryAddress) {
      extensionSubtotal += pickupCost;
    }
    
    const extensionTax = extensionSubtotal * (salesTaxPercent / 100);
    const extensionTotal = extensionSubtotal + extensionTax;
    
    return {
      additionalDays,
      dailyRate,
      subtotal: extensionSubtotal,
      tax: extensionTax,
      total: extensionTotal,
    };
  };
  
  // Helper function to calculate penalty based on cancellation policy
  const calculateCancellationPenalty = (
    hoursUntilRental,
    totalAmount,
    bookingDays
  ) => {
    // Find the applicable window
    let applicableWindow = null;
    
    // Windows are sorted descending by hours (48, 24, 0)
    // Find the first window where hoursUntilRental >= window.hours
    const sortedWindows = [...cancellationWindows].sort(
      (a, b) => b.hours - a.hours
    );
    
    for (const window of sortedWindows) {
      if (hoursUntilRental >= window.hours) {
        applicableWindow = window;
        break;
      }
    }
    
    // If no window matched (shouldn't happen with 0 hour window), use last window
    if (!applicableWindow) {
      applicableWindow = sortedWindows[sortedWindows.length - 1];
    }
    
    if (penaltyType === "days") {
      // Days-based penalty: (total / bookingDays) * daysPenalty
      // Cap penalty at total purchase amount to prevent exceeding what was paid
      const dailyRate = totalAmount / bookingDays;
      const rawPenaltyAmount = dailyRate * applicableWindow.daysPenalty;
      const penaltyAmount = Math.min(rawPenaltyAmount, totalAmount); // Cap at total purchase
      const refundPercentage = Math.max(
        ((totalAmount - penaltyAmount) / totalAmount) * 100,
        0 // Minimum 0% refund (can't charge more than total)
      );
      
      return {
        penaltyAmount,
        refundPercentage,
        penaltyType: "days",
        daysPenalty: applicableWindow.daysPenalty,
        percentPenalty: applicableWindow.percentPenalty,
      };
    } else {
      // Percentage-based penalty
      const penaltyAmount =
        totalAmount * (applicableWindow.percentPenalty / 100);
      const refundPercentage = 100 - applicableWindow.percentPenalty;
      
      return {
        penaltyAmount,
        refundPercentage,
        penaltyType: "percentage",
        daysPenalty: applicableWindow.daysPenalty,
        percentPenalty: applicableWindow.percentPenalty,
      };
    }
  };
  
  // Calculate distance using Google Maps API
  const calculateDistance = async (address) => {
    if (!address || !startingAddress) return null;
    
    try {
      // Don't set calculatingDistance - this function should not be called when address is selected from dropdown
      // setCalculatingDistance(true);
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/distancematrix/json?` +
        `origins=${encodeURIComponent(startingAddress)}` +
        `&destinations=${encodeURIComponent(address)}` +
        `&key=AIzaSyDnMrIVkx-6V0Brm4pxezeOVcTOELsTmdQ` +
        `&units=imperial`
      );
      
      const data = await response.json();
      
      if (data.rows && data.rows[0]?.elements?.[0]?.distance) {
        const distanceMeters = data.rows[0].elements[0].distance.value;
        const distanceMiles = (distanceMeters / 1609.34).toFixed(2);
        return parseFloat(distanceMiles);
      }
      
      return null;
    } catch (error) {
      console.error("Error calculating distance:", error);
      return null;
    } finally {
      // Don't set calculatingDistance - this function should not be called when address is selected from dropdown
      // setCalculatingDistance(false);
    }
  };
  
  // Calculate delivery cost difference
  const calculateDeliveryCostDifference = () => {
    if (!booking) {
      return { costDifference: 0, isRefund: false, isCharge: false };
    }

    const latestSub = booking;
    const originalDeliveryNeeded = latestSub.delivery;
    const originalHomePickupNeeded = latestSub.homePickup;
      const firstPayment = booking.payments[0];
      const originalDeliveryCost = firstPayment.costBreakdown.deliveryCost;
      const originalPickupCost = firstPayment.costBreakdown.pickupCost;

    const originalTotalCost =
      (originalDeliveryNeeded ? originalDeliveryCost : 0) +
      (originalHomePickupNeeded ? originalPickupCost : 0);

    const newDeliverySelected = deliveryNeeded;
    const newPickupSelected = homePickupNeeded;

    // If nothing selected now or before, no change
    if (
      !originalDeliveryNeeded &&
      !originalHomePickupNeeded &&
      !newDeliverySelected &&
      !newPickupSelected
    ) {
      return { costDifference: 0, isRefund: false, isCharge: false };
    }
    
    let newDeliveryCostValue = 0;
    if (newDeliverySelected) {
      if (deliveryCost > 0) {
        newDeliveryCostValue += deliveryCost;
      } else if (originalDeliveryNeeded) {
        newDeliveryCostValue += originalDeliveryCost;
      }
    }

    let newPickupCostValue = 0;
    if (newPickupSelected) {
      if (pickupCost > 0) {
        newPickupCostValue += pickupCost;
      } else if (originalHomePickupNeeded) {
        newPickupCostValue += originalPickupCost;
      }
    }

    const newTotalCost = newDeliveryCostValue + newPickupCostValue;
    const costDifference = newTotalCost - originalTotalCost;

    let changeType = "none";
    if (costDifference > 0) {
      if (!originalDeliveryNeeded && !originalHomePickupNeeded) {
        changeType = "added";
      } else {
        changeType = "adjusted";
      }
    } else if (costDifference < 0) {
      if (!newDeliverySelected && !newPickupSelected) {
        changeType = "removed";
      } else {
        changeType = "adjusted";
      }
    }
      
      return {
        costDifference: Math.abs(costDifference),
        isRefund: costDifference < 0,
        isCharge: costDifference > 0,
      originalCost: originalTotalCost,
      newCost: newTotalCost,
      changeType,
      };
  };
  
  // Calculate cancellation fee for date changes
  const calculateDateChangeCancellationFee = (
    originalStartDate,
    originalEndDate,
    newStartDate,
    newEndDate
  ) => {
    try {
      // Parse dates
      let origStart = originalStartDate?.toDate
        ? originalStartDate.toDate()
        : new Date(originalStartDate);
      let origEnd = originalEndDate?.toDate
        ? originalEndDate.toDate()
        : new Date(originalEndDate);
      let newStart = new Date(newStartDate);
      let newEnd = new Date(newEndDate);

      // Normalize to midnight so the diffs are whole days (plus the DST hour,
      // which round absorbs) — same semantics as getRentalDays.
      origStart.setHours(0, 0, 0, 0);
      origEnd.setHours(0, 0, 0, 0);
      newStart.setHours(0, 0, 0, 0);
      newEnd.setHours(0, 0, 0, 0);

      // Calculate original and new durations
      const originalDays = Math.round(
        (origEnd - origStart) / (1000 * 60 * 60 * 24)
      );
      const newDays = Math.round((newEnd - newStart) / (1000 * 60 * 60 * 24));
      
      // Only charge cancellation fee if duration is SHRINKING
      if (newDays >= originalDays) {
        return { fee: 0, percentage: 0, reason: "duration_extended" };
      }
      
      // Duration is shrinking - check if original start date falls within cancellation windows
      const now = new Date();
      const origStartAt11AM = new Date(origStart);
      origStartAt11AM.setHours(11, 0, 0, 0);
      const hoursUntilOriginalStart =
        (origStartAt11AM - now) / (1000 * 60 * 60);
      
      // Calculate the dollar amount lost
      const daysLost = originalDays - newDays;
      const latestSub = booking;
      const firstPayment = booking.payments && booking.payments.length > 0 ? booking.payments[0] : null;
      const originalTotal = firstPayment ? firstPayment.costBreakdown.totalCharged : 0;
      const dailyRate = originalTotal / originalDays;
      const grossLost = dailyRate * daysLost;
      
      // Calculate penalty using configurable policy
      const penaltyCalc = calculateCancellationPenalty(
        hoursUntilOriginalStart,
        grossLost,
        daysLost
      );
      const fee = penaltyCalc.penaltyAmount;
      const refundPercentage = penaltyCalc.refundPercentage;
      
      return {
        fee,
        percentage: feePercentage,
        reason:
          feePercentage > 0
            ? "duration_shortened_within_window"
            : "duration_shortened_outside_window",
        daysLost,
        hoursUntilStart: hoursUntilOriginalStart,
      };
    } catch (error) {
      console.error("❌ Error calculating cancellation fee:", error);
      return { fee: 0, percentage: 0, reason: "error" };
    }
  };
  
  // State to hold entire settings object
  const [settingsObject, setSettingsObject] = useState({});
  // Ref to hold settings object for immediate access (before state updates)
  const settingsObjectRef = useRef({});

  const loadSettings = async (bookingData = null) => {
    try {
      // Use booking data's tenant/store context if available, otherwise use default
      const bookingToUse = bookingData || booking;
      const tenantId = bookingToUse?.tenantId || "1234";
      const storeId = bookingToUse?.storeId || "999";
      const settingsPath = `tenants/${tenantId}/stores/${storeId}/settings`;
      
      const settingsSnapshot = await getDocs(collection(db, settingsPath));
      
      const settings = {};
      settingsSnapshot.forEach((doc) => {
        settings[doc.id] = doc.data();
      });
      
      const cat1Name = settings.product1Title?.title || "Product1";
      const cat2Name = settings.product2Title?.title || "Product2";
      
      setProduct1CategoryName(cat1Name);
      setProduct2CategoryName(cat2Name);
      setSalesTaxPercent(settings.salesTax?.percent || 0);
      setRefundProcessingFee(settings.refundProcessingFee?.percent || 3);
      
      const defaultPolicy =
        "Our cancellation policy required 48 hours advance notice for a full refund, and a 24 hour advance notice for a 75% refund. All other cancellations only receive 50% refund.";
      setCancellationPolicy(
        settings.cancellationPolicy?.policy || defaultPolicy
      );
      
      setShopInfo({
        name: settings.storeName?.name || "",
        email: settings.storeEmail?.email || "",
        phone: settings.storePhone?.phone || "",
      });
      
      setStoreLogo(settings.storeLogo?.url || "");
      
      // Load delivery settings
      setMileageRate(settings.mileageRate?.rate || 0);
      setLoadingFee(settings.loadingFee?.fee || 0);
      setStartingAddress(settings.startingAddress?.address || "");
      setDeliveryInfo(settings.deliveryInfo?.info || "");
      setPickupInfo(settings.pickupInfo?.info || "");
      
      // Load delivery discounts array
      if (settings.deliveryDiscounts?.discounts) {
        setDeliveryDiscounts(settings.deliveryDiscounts.discounts);
      }
      
      // Load duration discount settings
      setUseDurationDiscounts(settings.useDurationDiscounts?.enabled || false);
      if (settings.durationDiscounts?.discounts) {
        setDurationDiscounts(settings.durationDiscounts.discounts);
      }

      // Store entire settings object in both state and ref
      // log('settings from storeeeeeeee', settings)
      setSettingsObject(settings);
      settingsObjectRef.current = settings; // Store in ref for immediate access
      
      // Load cancellation policy windows
      if (settings.cancellationPolicyWindows) {
        setCancellationWindows(
          settings.cancellationPolicyWindows.windows || [
          { hours: 48, percentPenalty: 0, daysPenalty: 0 },
          { hours: 24, percentPenalty: 25, daysPenalty: 1 },
          { hours: 0, percentPenalty: 50, daysPenalty: 2 },
          ]
        );
        setPenaltyType(
          settings.cancellationPolicyWindows.penaltyType || "percentage"
        );
      }

      // Return settings object so caller can use it immediately
      return settings;
    } catch (error) {
      console.error("❌ ERROR in loadSettings:", error);
      console.error("❌ ERROR stack:", error.stack);
      return {};
    }
  };
  
  
  const selectAddress = async (input) => {
    if (!input) return;

    selectingAddressRef.current = true;
    setShowSuggestions(false);
    setAddressSuggestions([]);

    const applySelection = async (fullAddress) => {
        setDeliveryAddress(fullAddress);
        setAddressInput(fullAddress);
      setNewAddress(fullAddress);
      setAddressConfirmed(false);
      setAddressLoadedFromBooking(true);
        hasInteractedWithAddressRef.current = true;
      setAddressManuallyChanged(false);

      const updatedBooking = { ...newBooking, deliveryAddress: fullAddress };
      // numMiles will be resolved inside calculateTotals (via the cloud function)
      // and written back to newBooking state there; cost fields will be recomputed
      // once calculateTotals calls setNewBooking with the resolved numMiles.
      await updateNewBooking(updatedBooking);

      setTimeout(() => {
        selectingAddressRef.current = false;
      }, 0);
    };

    if (!placesServiceRef.current) return;

    placesServiceRef.current.getDetails({ placeId: input }, (place, status) => {
      if (
        status === window.google.maps.places.PlacesServiceStatus.OK &&
        place
      ) {
        applySelection(place.formatted_address);
      }
    });
  };
  
  
  // Handle process changes
  const handleProcessChanges = async () => {
    const changes = calculateChanges();
    const latestSub = booking || {};
    const firstName = latestSub.firstName ? latestSub.firstName.charAt(0).toUpperCase() + latestSub.firstName.slice(1).toLowerCase() : '';
    const lastName = latestSub.lastName ? latestSub.lastName.charAt(0).toUpperCase() + latestSub.lastName.slice(1).toLowerCase() : '';
    const customerName = `${firstName} ${lastName}`;
    
    // Build confirmation message
    let confirmMessage = `⚠️ CONFIRM BOOKING CHANGES\n\n`;
    confirmMessage += `Customer: ${customerName}\n\n`;
    
    if (changes.addedItems.length > 0) {
      confirmMessage += `➕ Items Added:\n`;
      changes.addedItems.forEach((item) => {
        confirmMessage += `  • ${item.quantity}x ${item.name}\n`;
      });
      confirmMessage += `\n`;
    }
    
    if (changes.removedItems.length > 0) {
      confirmMessage += `➖ Items Removed:\n`;
      changes.removedItems.forEach((item) => {
        confirmMessage += `  • ${item.quantity}x ${item.name}\n`;
      });
      confirmMessage += `\n`;
    }
    
    if (changes.isNetCharge) {
      confirmMessage += `💳 Charge Customer: $${changes.netAmount.toFixed(
        2
      )}\n`;
    } else if (changes.isNetRefund) {
      confirmMessage += `💰 Refund Customer: $${changes.netAmount.toFixed(
        2
      )}\n`;
    }
    
    confirmMessage += `\nThis will:\n`;
    confirmMessage += `• Update the booking in the database\n`;
    confirmMessage += `• Send confirmation email and SMS\n`;
    if (changes.isNetCharge) {
      confirmMessage += `• Charge the customer's payment method\n`;
    } else if (changes.isNetRefund) {
      confirmMessage += `• Issue a refund to the customer\n`;
    }
    
    confirmMessage += `\nProceed with these changes?`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }
    
    try {
      setProcessing(true);
      
      // Check if this is a dev bypass booking (starts with "DEV_")
      const isDevBypass = booking.bookingId?.startsWith("DEV_");
      
      // If dev bypass, skip payment processing entirely
      if (isDevBypass) {
        // Just process the booking edits without charging
        if (!functions) {
          throw new Error("Firebase Functions not initialized");
        }
        
        const sendEditedBookingNotification = httpsCallable(
          functions,
          "sendEditedBookingNotification"
        );
        
        const latestSub = booking || {};
        const originalDeliveryNeeded = latestSub.delivery || false;
        const originalHomePickupNeeded = latestSub.homePickup || false;
        
        const tenantId = booking.tenantId;
        const storeId = booking.storeId;
        
        await sendEditedBookingNotification({
          bookingId: booking.bookingId,
          tenantId: tenantId,
          storeId: storeId,
          quantityChanges: quantityChanges,
          dateChanges:
            newStartDate || newEndDate
              ? {
            newStartDate: newStartDate,
            newEndDate: newEndDate,
                }
              : null,
          addressChanges:
            newAddress && newDistance !== null
              ? {
            newAddress: newAddress,
            newDistance: newDistance,
                }
              : null,
          deliveryChanges:
            deliveryNeeded !== originalDeliveryNeeded ||
            homePickupNeeded !== originalHomePickupNeeded ||
            inStorePickup !== (latestSub.inStorePickup || false)
              ? {
                delivery: deliveryNeeded,
                homePickup: homePickupNeeded,
            inStorePickup: inStorePickup,
            deliveryAddress: deliveryAddress,
            gatedCommunity: gatedCommunity,
            communityName: communityName,
            addressNotes: addressNotes,
                }
              : null,
          changes: changes,
          paymentMethod: "dev-bypass",
          isDevBypass: true,
        });
        
        alert(
          `✅ Dev Booking Updated Successfully!\n\n` +
          `DEV BYPASS - No payment processing\n` +
            (changes.isNetCharge
              ? `Would charge: $${changes.netAmount.toFixed(2)}\n`
              : "") +
            (changes.isNetRefund
              ? `Would refund: $${changes.netAmount.toFixed(2)}\n`
              : "") +
          `\nCustomer notification sent.`
        );
        
        // Navigate to view booking page
        navigate(`/booking/${encodeURIComponent(booking.bookingId)}`);
        return;
      }
      
      // If there's a net charge and user selected "new" payment method, create Stripe checkout session
      if (changes.isNetCharge && paymentMethod === "new") {
        if (!functions) {
          throw new Error("Firebase Functions not initialized");
        }
        
        const latestSub = booking || {};
        const amountCents = Math.round(changes.netAmount * 100);
        
        // Call cloud function to create Stripe checkout session
        const createPaymentLink = httpsCallable(functions, "createPaymentLink");
        const actualBookingId = booking.bookingId;
        const originalDeliveryNeeded = latestSub.delivery || false;
        const originalHomePickupNeeded = latestSub.homePickup || false;
        
        const paymentResult = await createPaymentLink({
          amountCents: amountCents,
          description: `Booking Changes - ${actualBookingId}`,
          customerEmail: latestSub.customerEmail,
          testMode: useTestStripe,
          customerPhone: latestSub.customerPhone,
          customerName: `${latestSub.firstName ? latestSub.firstName.charAt(0).toUpperCase() + latestSub.firstName.slice(1).toLowerCase() : ''} ${latestSub.lastName ? latestSub.lastName.charAt(0).toUpperCase() + latestSub.lastName.slice(1).toLowerCase() : ''}`,
          saveCard: true,
          bookingId: actualBookingId,
          isBookingEdit: true, // Flag to indicate this is an edit
          editChanges: {
            additionsTotal: changes.additionsTotal,
            removalsTotal: changes.removalsTotal,
            netAmount: changes.netAmount,
            quantityChanges: quantityChanges,
            deliveryChanges:
              deliveryNeeded !== originalDeliveryNeeded ||
              homePickupNeeded !== originalHomePickupNeeded ||
              inStorePickup !== (latestSub.inStorePickup || false)
                ? {
                  delivery: deliveryNeeded,
                  homePickup: homePickupNeeded,
              inStorePickup: inStorePickup,
              deliveryAddress: deliveryAddress,
              gatedCommunity: gatedCommunity,
              communityName: communityName,
              addressNotes: addressNotes,
                  }
                : null,
            addressChanges:
              newAddress && newDistance !== null
                ? {
              newAddress: newAddress,
              newDistance: newDistance,
                  }
                : null,
            dateChanges:
              newStartDate || newEndDate
                ? {
                    newStartDate: newStartDate
                      ? newStartDate instanceof Date
                        ? (isNaN(newStartDate.getTime()) ? null : newStartDate.toISOString())
                        : newStartDate
                      : null,
                    newEndDate: newEndDate
                      ? newEndDate instanceof Date
                        ? (isNaN(newEndDate.getTime()) ? null : newEndDate.toISOString())
                        : newEndDate
                      : null,
                  }
                : null,
          },
        });
        
        // Store edit data in localStorage for when we return from Stripe
        localStorage.setItem(
          "bookingEditData",
          JSON.stringify({
          bookingId: actualBookingId,
          quantityChanges: quantityChanges,
          changes: changes,
          paymentMethod: "new",
          })
        );
        
        // Redirect to Stripe checkout
        window.location.href = paymentResult.data.url;
        return;
      }
      
      // If using existing card or net refund, process immediately
      if (changes.isNetCharge && paymentMethod === "existing") {
        if (!functions) {
          throw new Error("Firebase Functions not initialized");
        }
        
        // Get last4 from the most recent payment with cardInfo
        let last4 = null;
        if (booking && booking.payments && booking.payments.length > 0) {
          for (let i = booking.payments.length - 1; i >= 0; i--) {
            if (booking.payments[i].cardInfo?.last4) {
              last4 = booking.payments[i].cardInfo.last4;
              break;
            }
          }
        }
        
        if (!last4) {
          throw new Error(
            "No saved payment method found. Please use 'Enter New Credit Card' option."
          );
        }
        
        const chargeSavedCard = httpsCallable(functions, "chargeSavedCard");
        const amountCents = Math.round(changes.netAmount * 100);
        
        const actualBookingId = booking.bookingId;
        const tenantId = booking.tenantId;
        const storeId = booking.storeId;
        const chargePayload = {
          bookingId: actualBookingId,
          amountCents: amountCents,
          description: `Booking Changes - ${actualBookingId}`,
          tenantId: tenantId,
          storeId: storeId,
          last4: last4,
          testMode: useTestStripe,
        };
        
        const result = await chargeSavedCard(chargePayload);
        
        if (result.data.status !== "succeeded") {
          throw new Error(`Payment failed: ${result.data.status}`);
        }
      }
      
      // Process the booking edits
      if (!functions) {
        throw new Error("Firebase Functions not initialized");
      }
      
      const sendEditedBookingNotification = httpsCallable(
        functions,
        "sendEditedBookingNotification"
      );
      
      const latestSub = booking || {};
      const originalDeliveryNeeded = latestSub.delivery || false;
      const originalHomePickupNeeded = latestSub.homePickup || false;
      
      const tenantId = booking.tenantId;
      const storeId = booking.storeId;
      
      // Build cost fields from newTotals to persist updated pricing after a change-only save
      const costFields = {
        productPriceAmount: newTotals?.productCost?.newCost || 0,
        cardProcessingFeeAmount:
          (booking.cardProcessingFeeAmount || booking.bookingPlatformFee || 0) +
          (newTotals?.totals?.cardProcessingFee || 0),
        salesTaxAmount: Math.max(
          0,
          Math.round((booking.salesTaxAmount || 0) + (newTotals?.salesTax || 0))
        ),
        totalAmount: Math.max(
          0,
          Math.round((booking.totalAmount || 0) + (newTotals?.totals?.finalTotal || 0))
        ),
        ...(newTotals?.transportation?.newDeliveryChargeCents !== undefined && {
          deliveryCostCents: newTotals.transportation.newDeliveryChargeCents,
        }),
        ...(newTotals?.transportation?.newPickupChargeCents !== undefined && {
          pickupCostCents: newTotals.transportation.newPickupChargeCents,
        }),
        ...(newBooking?.numMiles != null && {
          numMiles: Number(newBooking.numMiles),
        }),
        ...(newTotals?.transportation?.transportationDiscountToSaveInBooking && {
          transportationDiscount: {
            discountCents: newTotals.transportation.transportationDiscountToSaveInBooking.discountCents || 0,
            discountPercent: newTotals.transportation.transportationDiscountToSaveInBooking.discountPercent || 0,
            discountName: newTotals.transportation.transportationDiscountToSaveInBooking.discountName || "",
          },
        }),
        ...(newTotals?.durationDiscount?.durationDiscountToSaveInBooking && {
          durationDiscount: {
            discountCents: newTotals.durationDiscount.durationDiscountToSaveInBooking.discountCents || 0,
            discountPercent: newTotals.durationDiscount.durationDiscountToSaveInBooking.discountPercent || 0,
            discountName: newTotals.durationDiscount.durationDiscountToSaveInBooking.discountName || "",
          },
        }),
      };

      const requestPayload = {
        bookingId: booking.bookingId,
        tenantId: tenantId,
        storeId: storeId,
        quantityChanges: quantityChanges,
        dateChanges:
          newStartDate || newEndDate
            ? {
          newStartDate: newStartDate,
          newEndDate: newEndDate,
              }
            : null,
        addressChanges:
          newAddress && newDistance !== null
            ? {
          newAddress: newAddress,
          newDistance: newDistance,
              }
            : null,
        deliveryChanges:
          deliveryNeeded !== originalDeliveryNeeded ||
          homePickupNeeded !== originalHomePickupNeeded ||
          inStorePickup !== (latestSub.inStorePickup || false)
            ? {
              delivery: deliveryNeeded,
              homePickup: homePickupNeeded,
          inStorePickup: inStorePickup,
          deliveryAddress: deliveryAddress,
          gatedCommunity: gatedCommunity,
          communityName: communityName,
          addressNotes: addressNotes,
              }
            : null,
        changes: changes,
        paymentMethod: paymentMethod,
        costFields: costFields,
      };
      
      await sendEditedBookingNotification(requestPayload);
      
      alert(
        `✅ Booking Updated Successfully!\n\n` +
          (changes.isNetCharge
            ? `Charge: $${changes.netAmount.toFixed(2)}\n`
            : "") +
          (changes.isNetRefund
            ? `Refund: $${changes.netAmount.toFixed(2)}\n`
            : "") +
        `\nCustomer has been notified via email and SMS.`
      );
      
      // Navigate to view booking page
      navigate(`/booking/${encodeURIComponent(booking.bookingId)}`);
    } catch (error) {
      console.error("❌ Error processing changes:", error);
      alert(`Error: Failed to process changes - ${error.message}`);
    } finally {
      setProcessing(false);
    }
  };
  
  // Handle process refund from checkout button
  const handleProcessRefund = async () => {
    try {
      setProcessingType("refund");
      setProcessing(true);
      setShowRefundConfirmModal(false);

      const adminOverrideActive =
        (useCustomAmount && parseFloat(customRefundAmount) > 0) ||
        (useRefundOverride && refundOverridePercent > 0);

      const calculatedRefund = Math.abs(newTotals?.totals?.finalTotal || 0);
      const hasBookingChanges = hasChanges || (newTotals?.totals?.finalTotal !== 0);

      let refundAmountCents;
      if (adminOverrideActive) {
        if (useCustomAmount) {
          refundAmountCents = Math.round(parseFloat(customRefundAmount) * 100);
        } else if (hasBookingChanges) {
          // Percent override applied to the computed booking delta
          refundAmountCents = Math.round(calculatedRefund * (refundOverridePercent / 100));
        } else {
          // NOTE: When there are no booking changes (percent-override mode only), the base switches
          // from the computed delta to the total amount available to refund. This allows the admin
          // to issue a partial standalone refund using the percent field. The refund confirmation
          // modal displays the exact dollar amount before the admin confirms.
          refundAmountCents = Math.round(getAvailableToRefundDollars() * (refundOverridePercent / 100) * 100);
        }
      } else {
        refundAmountCents = calculatedRefund;
      }

      if (refundAmountCents <= 0) {
        alert("No refund amount to process");
        return;
      }

      // Check if this is a dev bypass booking
      const isDevBypass = booking.bookingId?.startsWith("DEV_");

      if (isDevBypass) {
        // For dev bypass, just update booking and send notifications
        await updateBookingAndSendNotifications(refundAmountCents, null, "refund");
        alert("✅ Dev Booking Updated Successfully!\n\nWould refund: " + formatCentsAsUSD(refundAmountCents) + "\n\nCustomer notification sent.");
        navigate(`/booking/${encodeURIComponent(booking.bookingId)}`);
        return;
      }

      // Process refund via cloud function
      if (!functions) {
        throw new Error("Firebase Functions not initialized");
      }

      const issueRefund = httpsCallable(functions, "processRefund");
      const firstPayment = booking.payments[0];
      const paymentIntentId = firstPayment.paymentIntentId || firstPayment.stripePaymentIntentId;

      if (!paymentIntentId) {
        throw new Error("No payment intent found for refund");
      }

      const refundResult = await issueRefund({
        bookingId: booking.bookingId,
        paymentIntentId: paymentIntentId,
        amountCents: refundAmountCents,
        reason: "booking_edit",
        tenantId: booking.tenantId,
        storeId: booking.storeId,
        testMode: useTestStripe,
        sendReceipt: true,
      });

      if (refundResult.data.success) {
        // Update booking and send notifications
        await updateBookingAndSendNotifications(refundAmountCents, refundResult.data.refundId, "refund");
        alert("✅ Refund Processed Successfully!\n\nRefund Amount: " + formatCentsAsUSD(refundAmountCents) + "\n\nCustomer has been notified via email and SMS.");
        navigate(`/booking/${encodeURIComponent(booking.bookingId)}`);
      } else {
        throw new Error(refundResult.data.error || "Refund failed");
      }
    } catch (error) {
      console.error("❌ Error processing refund:", error);
      alert(`Error: Failed to process refund - ${error.message}`);
    } finally {
      setProcessing(false);
    }
  };

  // Handle process payment from checkout button
  const handleProcessPayment = async () => {
    try {
      setProcessing(true);

      const chargeAmountCents = newTotals?.totals?.finalTotal || 0;
      if (chargeAmountCents <= 0) {
        alert("No charge amount to process");
        return;
      }

      // Check if this is a dev bypass booking
      const isDevBypass = booking.bookingId?.startsWith("DEV_");

      if (isDevBypass) {
        // For dev bypass, just update booking and send notifications
        await updateBookingAndSendNotifications(chargeAmountCents, null, "charge");
        alert("✅ Dev Booking Updated Successfully!\n\nWould charge: " + formatCentsAsUSD(chargeAmountCents) + "\n\nCustomer notification sent.");
        navigate(`/booking/${encodeURIComponent(booking.bookingId)}`);
        return;
      }

      let paymentResult = null;

      // If using new card, create Stripe checkout session
      if (paymentMethod === "new") {
        if (!functions) {
          throw new Error("Firebase Functions not initialized");
        }

        const createPaymentLink = httpsCallable(functions, "createPaymentLink");
        const latestSub = booking || {};

        const paymentLinkResult = await createPaymentLink({
          amountCents: chargeAmountCents,
          description: `Booking Changes - ${booking.bookingId}`,
          customerEmail: latestSub.customerEmail,
          testMode: useTestStripe,
          customerPhone: latestSub.customerPhone,
          customerName: `${latestSub.firstName ? latestSub.firstName.charAt(0).toUpperCase() + latestSub.firstName.slice(1).toLowerCase() : ''} ${latestSub.lastName ? latestSub.lastName.charAt(0).toUpperCase() + latestSub.lastName.slice(1).toLowerCase() : ''}`,
          saveCard: true,
          bookingId: booking.bookingId,
          isBookingEdit: true,
          editChanges: getEditChangesData(),
        });

        // Store edit data in localStorage for when we return from Stripe
        localStorage.setItem(
          "bookingEditData",
          JSON.stringify({
            bookingId: booking.bookingId,
            quantityChanges: quantityChanges,
            changes: calculateChanges(),
            paymentMethod: "new",
            editChanges: getEditChangesData(),
          })
        );

        // Redirect to Stripe checkout
        window.location.href = paymentLinkResult.data.url;
        return;
      }

      // If using existing card, charge it
      // Use selectedPaymentMethodId or default to first card
      // Use the first payment with card info
      const selectedPayment = booking.payments?.find(p => p.cardInfo);

      if (!selectedPayment || !selectedPayment.cardInfo) {
        throw new Error("No saved payment method found");
      }

      if (!functions) {
        throw new Error("Firebase Functions not initialized");
      }

        const chargeSavedCard = httpsCallable(functions, "chargeSavedCard");
        const chargePayload = {
          bookingId: booking.bookingId,
          amountCents: chargeAmountCents,
          description: `Booking Changes - ${booking.bookingId}`,
          tenantId: booking.tenantId,
          storeId: booking.storeId,
          last4: selectedPayment.cardInfo.last4,
          testMode: useTestStripe,
        };

        paymentResult = await chargeSavedCard(chargePayload);

        if (paymentResult.data.status !== "succeeded") {
          throw new Error(`Payment failed: ${paymentResult.data.status}`);
        }

      // Update booking and send notifications
      await updateBookingAndSendNotifications(chargeAmountCents, paymentResult?.data?.paymentIntentId, "charge");
      alert("✅ Payment Processed Successfully!\n\nCharge Amount: " + formatCentsAsUSD(chargeAmountCents) + "\n\nCustomer has been notified via email and SMS.");
      navigate(`/booking/${encodeURIComponent(booking.bookingId)}`);
    } catch (error) {
      console.error("❌ Error processing payment:", error);
      alert(`Error: Failed to process payment - ${error.message}`);
    } finally {
      setProcessing(false);
    }
  };

  // Helper function to get edit changes data
  const getEditChangesData = () => {
    const changes = calculateChanges();
    const latestSub = booking || {};
    const originalDeliveryNeeded = latestSub.delivery || false;
    const originalHomePickupNeeded = latestSub.homePickup || false;

    return {
      additionsTotal: changes.additionsTotal,
      removalsTotal: changes.removalsTotal,
      netAmount: changes.netAmount,
      quantityChanges: quantityChanges,
      deliveryChanges:
        deliveryNeeded !== originalDeliveryNeeded ||
          homePickupNeeded !== originalHomePickupNeeded ||
          inStorePickup !== (latestSub.inStorePickup || false)
          ? {
            delivery: deliveryNeeded,
            homePickup: homePickupNeeded,
            inStorePickup: inStorePickup,
            deliveryAddress: deliveryAddress,
            gatedCommunity: gatedCommunity,
            communityName: communityName,
            addressNotes: addressNotes,
          }
          : null,
      addressChanges:
        newAddress && newDistance !== null
          ? {
            newAddress: newAddress,
            newDistance: newDistance,
          }
          : null,
      dateChanges:
        newStartDate || newEndDate
          ? {
            newStartDate: newStartDate
              ? newStartDate instanceof Date
                ? (isNaN(newStartDate.getTime()) ? null : newStartDate.toISOString())
                : newStartDate
              : null,
            newEndDate: newEndDate
              ? newEndDate instanceof Date
                ? (isNaN(newEndDate.getTime()) ? null : newEndDate.toISOString())
                : newEndDate
              : null,
          }
          : null,
    };
  };

  // Build the payload object sent to DB for Process Payment (for logging or actual update)
  const buildPaymentUpdatePayload = (amountCents, paymentRefundId, type) => {
    const updatedBookingData = {};
    
    // Update products from newBooking (final quantities)
    // Calculate total product1 quantity for per-bike calculations
    const totalProduct1Quantity = (newBooking.product1 || []).reduce(
      (sum, p) => sum + (p.quantity || 0),
      0
    );

    if (newBooking.product1 && Array.isArray(newBooking.product1)) {
      const products1CatalogToUse = products1Catalog.length > 0 ? products1Catalog : products1FromStore;
      updatedBookingData.product1 = newBooking.product1.map(item => {
        let pricePaid = item.pricePaid;
        if (pricePaid === undefined || pricePaid === null) {
          const catProd = products1CatalogToUse.find(p => p.id === item.id);
          pricePaid = catProd?.price || item.itemPrice || 0;
        }
        return { ...item, pricePaid };
      });
    }
    
    if (newBooking.product2 && Array.isArray(newBooking.product2)) {
      const products2CatalogToUse = products2Catalog.length > 0 ? products2Catalog : products2FromStore;
      updatedBookingData.product2 = newBooking.product2.map(item => {
        let pricePaid = item.pricePaid;
        const catProd = products2CatalogToUse.find(p => p.id === item.id);
        const basePrice = catProd?.price || item.itemPrice || 0;
        const option = catProd?.option || item.option;
        
        if (option === 'per-bike') {
           pricePaid = basePrice * totalProduct1Quantity;
        } else if (pricePaid === undefined || pricePaid === null) {
           pricePaid = basePrice;
        }
        return { ...item, pricePaid };
      });
    }
    
    // Update dates from newBooking
    const toTimestamp = (val) => {
      if (!val) return null;
      try {
        if (val instanceof Date) {
          return isNaN(val.getTime()) ? null : Timestamp.fromDate(val);
        }
        if (typeof val.toDate === "function") {
          const d = val.toDate();
          return (d instanceof Date && !isNaN(d.getTime())) ? Timestamp.fromDate(d) : null;
        }
        if (typeof val.seconds === "number" && !isNaN(val.seconds)) {
          const nanos = typeof val.nanoseconds === "number" && !isNaN(val.nanoseconds) ? val.nanoseconds : 0;
          const ts = new Timestamp(val.seconds, nanos);
          const check = ts.toDate();
          return (check instanceof Date && !isNaN(check.getTime())) ? ts : null;
        }
        const d = new Date(val);
        return isNaN(d.getTime()) ? null : Timestamp.fromDate(d);
      } catch (e) {
        console.warn("toTimestamp: failed to convert value", val, e);
        return null;
      }
    };
    if (newBooking.startDate) {
      const ts = toTimestamp(newBooking.startDate);
      if (ts) updatedBookingData.startDate = ts;
    }
    if (newBooking.endDate) {
      const ts = toTimestamp(newBooking.endDate);
      if (ts) updatedBookingData.endDate = ts;
    }
    
    // Update delivery/pickup fields from newBooking
    if (newBooking.delivery !== undefined) {
      updatedBookingData.delivery = newBooking.delivery;
    }
    if (newBooking.homePickup !== undefined) {
      updatedBookingData.homePickup = newBooking.homePickup;
    }
    if (newBooking.inStorePickup !== undefined) {
      updatedBookingData.inStorePickup = newBooking.inStorePickup;
    }
    if (newBooking.deliveryAddress !== undefined) {
      updatedBookingData.deliveryAddress = newBooking.deliveryAddress;
    }
    if (newBooking.gatedCommunity !== undefined) {
      updatedBookingData.gatedCommunity = newBooking.gatedCommunity;
    }
    if (newBooking.communityName !== undefined) {
      updatedBookingData.communityName = newBooking.communityName;
    }
    if (newBooking.addressNotes !== undefined) {
      updatedBookingData.addressNotes = newBooking.addressNotes;
    }
    
    // Update transportation costs from newTotals
    if (newTotals?.transportation) {
      if (newTotals.transportation.newDeliveryChargeCents !== undefined) {
        updatedBookingData.deliveryCostCents = newTotals.transportation.newDeliveryChargeCents;
      }
      if (newTotals.transportation.newPickupChargeCents !== undefined) {
        updatedBookingData.pickupCostCents = newTotals.transportation.newPickupChargeCents;
      }
    }

    // Persist numMiles from newBooking
    if (newBooking?.numMiles != null) {
      updatedBookingData.numMiles = Number(newBooking.numMiles);
    }

    // Update transportation discount from newTotals (use transportationDiscountToSaveInBooking)
    if (newTotals?.transportation?.transportationDiscountToSaveInBooking) {
      updatedBookingData.transportationDiscount = {
        discountCents: newTotals.transportation.transportationDiscountToSaveInBooking.discountCents || 0,
        discountPercent: newTotals.transportation.transportationDiscountToSaveInBooking.discountPercent || 0,
        discountName: newTotals.transportation.transportationDiscountToSaveInBooking.discountName || "",
      };
    }
    
    // Update duration discount from newTotals (use durationDiscountToSaveInBooking)
    if (newTotals?.durationDiscount?.durationDiscountToSaveInBooking) {
      updatedBookingData.durationDiscount = {
        discountCents: newTotals.durationDiscount.durationDiscountToSaveInBooking.discountCents || 0,
        discountPercent: newTotals.durationDiscount.durationDiscountToSaveInBooking.discountPercent || 0,
        discountName: newTotals.durationDiscount.durationDiscountToSaveInBooking.discountName || "",
      };
    }

    // Update total amounts
    const originalFee = booking.cardProcessingFeeAmount || booking.bookingPlatformFee || 0;
    const newAdditionalFee = newTotals?.totals?.cardProcessingFee || 0;
    
    const originalTax = booking.salesTaxAmount || 0;
    const newAdditionalTax = newTotals?.salesTax || 0;
    
    const originalTotal = booking.totalAmount || 0;
    const differenceTotal = newTotals?.totals?.finalTotal || 0;

    updatedBookingData.productPriceAmount = newTotals?.productCost?.newCost || 0;
    updatedBookingData.cardProcessingFeeAmount = originalFee + newAdditionalFee;
    updatedBookingData.salesTaxAmount = Math.max(0, Math.round(originalTax + newAdditionalTax));
    updatedBookingData.totalAmount = Math.max(0, Math.round(originalTotal + differenceTotal));

    // Add payment or refund record
    if (type === "charge" && paymentRefundId) {
      // Use the first payment card info (single card support)
      const cardInfoToUse = booking.payments.find(p => p.cardInfo)?.cardInfo || null;

      const newPayment = {
        amount: amountCents,
        paymentIntentId: paymentRefundId,
        cardInfo: cardInfoToUse,
        timestamp: Timestamp.now(),
        description: `Booking Changes - ${booking.bookingId}`,
      };
      updatedBookingData.payments = arrayUnion(newPayment);
    } else if (type === "refund" && paymentRefundId) {
      const newRefund = {
        amount: amountCents,
        refundId: paymentRefundId,
        timestamp: Timestamp.now(),
        reason: "booking_edit",
      };
      updatedBookingData.refunds = arrayUnion(newRefund);
    }

    // Get selected user for metadata
    const selectedUser = useAuthStore.getState().selectedUser;
    const userMetadata = selectedUser ? {
      userId: selectedUser.id,
      firstName: selectedUser.firstName || "",
      lastName: selectedUser.lastName || "",
    } : null;

    // Update timestamp
    updatedBookingData.updatedAt = Timestamp.now();

    // Add user metadata to editedBy array with enhanced tracking
    if (userMetadata) {
      const changeEntry = createChangeTrackingEntry(userMetadata, {
        changeType: type === "refund" ? "refund" : type === "charge" ? "charge" : "edit",
        fieldChanged: "booking_details",
        description: type === "refund"
          ? `Refund processed: ${centsToDollars(amountCents)}`
          : type === "charge"
            ? `Additional charge: ${centsToDollars(amountCents)}`
            : "Booking details updated",
        timestamp: Timestamp.now(),
      });

      if (changeEntry) {
        updatedBookingData.editedBy = arrayUnion(changeEntry);
      }
    }

    return updatedBookingData;
  };

  // Recursively strip Date/Timestamp objects from data for safe httpsCallable serialization
  const sanitizeForCallable = (obj) => {
    if (obj === null || obj === undefined) return obj;
    if (typeof obj === "number" || typeof obj === "string" || typeof obj === "boolean") return obj;
    if (obj instanceof Date) {
      return isNaN(obj.getTime()) ? null : obj.toISOString();
    }
    if (typeof obj.toDate === "function") {
      const d = obj.toDate();
      return (d instanceof Date && !isNaN(d.getTime())) ? d.toISOString() : null;
    }
    if (Array.isArray(obj)) return obj.map(sanitizeForCallable);
    if (typeof obj === "object") {
      const out = {};
      for (const k of Object.keys(obj)) out[k] = sanitizeForCallable(obj[k]);
      return out;
    }
    return obj;
  };

  // Helper function to update booking fields and send notifications
  const updateBookingAndSendNotifications = async (amountCents, paymentRefundId, type) => {
    if (!functions) {
      throw new Error("Firebase Functions not initialized");
    }

    const bookingsPath = getCollectionPath("bookings");
    const bookingRef = doc(db, bookingsPath, booking.bookingId);
    const bookingDoc = await getDoc(bookingRef);

    if (!bookingDoc.exists()) {
      throw new Error("Booking not found");
    }

    console.log("📦 Step 1: Building payment update payload...");
    const updatedBookingData = buildPaymentUpdatePayload(amountCents, paymentRefundId, type);

    console.log("📦 Step 2: Updating booking in database...");
    await updateDoc(bookingRef, updatedBookingData);
    console.log("✅ Step 2 complete: Booking updated in database");

    // Send notifications (non-blocking — payment already succeeded)
    try {
      console.log("📦 Step 3: Sending edited booking notification...");
      const sendEditedBookingNotification = httpsCallable(functions, "sendEditedBookingNotification");
      const changes = calculateChanges();
      const latestSub = booking || {};
      const originalDeliveryNeeded = latestSub.delivery || false;
      const originalHomePickupNeeded = latestSub.homePickup || false;

      const notificationData = sanitizeForCallable({
        bookingId: booking.bookingId,
        tenantId: booking.tenantId,
        storeId: booking.storeId,
        quantityChanges: quantityChanges,
        dateChanges: newStartDate || newEndDate ? {
          newStartDate: newStartDate,
          newEndDate: newEndDate,
        } : null,
        addressChanges: newAddress && newDistance !== null ? {
          newAddress: newAddress,
          newDistance: newDistance,
        } : null,
        deliveryChanges: deliveryNeeded !== originalDeliveryNeeded ||
          homePickupNeeded !== originalHomePickupNeeded ||
          inStorePickup !== (latestSub.inStorePickup || false) ? {
            delivery: deliveryNeeded,
            homePickup: homePickupNeeded,
          inStorePickup: inStorePickup,
          deliveryAddress: deliveryAddress,
          gatedCommunity: gatedCommunity,
          communityName: communityName,
          addressNotes: addressNotes,
        } : null,
        changes: changes,
        paymentMethod: paymentMethod || "existing",
        amountPaid: type === "charge" ? amountCents : 0,
        amountRefunded: type === "refund" ? amountCents : 0,
      });

      await sendEditedBookingNotification(notificationData);
      console.log("✅ Step 3 complete: Notification sent");
    } catch (notifError) {
      console.error("⚠️ Notification failed (payment still succeeded):", notifError);
    }
  };

  // Serialize payload for readable console logging (Timestamps, FieldValues, Dates)
  const toReadablePayload = (obj) => {
    if (obj === null || obj === undefined) return obj;
    if (typeof obj === "number" || typeof obj === "string" || typeof obj === "boolean") return obj;
    if (typeof obj.toDate === "function") {
      const d = obj.toDate();
      return isNaN(d.getTime()) ? "[Invalid Date]" : d.toISOString();
    }
    if (obj instanceof Date) return isNaN(obj.getTime()) ? "[Invalid Date]" : obj.toISOString();
    if (obj && typeof obj === "object" && typeof obj._method !== "undefined")
      return `[FieldValue.${obj._method}]`;
    if (Array.isArray(obj)) return obj.map(toReadablePayload);
    if (typeof obj === "object") {
      const out = {};
      for (const k of Object.keys(obj)) out[k] = toReadablePayload(obj[k]);
      return out;
    }
    return obj;
  };

  // Build the refund update payload that would be sent to DB (for logging)
  const buildRefundUpdatePayload = () => {
    const availableToRefund = getAvailableToRefundDollars();
    const currentRefunds = booking?.refunds || [];
    const refundAmount = useCustomAmount
      ? parseFloat(customRefundAmount || "0")
      : parseFloat((availableToRefund * ((useRefundOverride ? refundOverridePercent : 100) / 100)).toFixed(2));
    const refundEntry = {
      id: `refund_${Date.now()}`,
      createdAt: new Date(),
      refundAmount: refundAmount,
      refundPercent: useCustomAmount
        ? ((refundAmount / (availableToRefund || 1)) * 100).toFixed(2)
        : useRefundOverride
          ? refundOverridePercent
          : 100,
      refundReason: useCustomAmount
        ? `Admin custom amount refund ($${refundAmount.toFixed(2)})`
        : useRefundOverride
          ? `Admin override refund (${refundOverridePercent}%)`
          : "Full refund",
      isRefundOnly: true,
      adminInitiated: true,
    };
    const updateData = {
      refunds: [...currentRefunds, refundEntry],
      updatedAt: Timestamp.now(),
    };
    const selectedUser = useAuthStore.getState().selectedUser;
    const userMetadata = selectedUser
      ? { userId: selectedUser.id, firstName: selectedUser.firstName || "", lastName: selectedUser.lastName || "", timestamp: Timestamp.now() }
      : null;
    if (userMetadata) {
      const changeEntry = createChangeTrackingEntry(userMetadata, {
        changeType: "refund",
        fieldChanged: "refunds",
        description: `Refund issued: $${refundAmount.toFixed(2)} (${refundEntry.refundPercent}%) - ${refundEntry.refundReason}`,
        timestamp: Timestamp.now(),
      });
      if (changeEntry) updateData.editedBy = arrayUnion(changeEntry);
    }
    return updateData;
  };
  
  const handleIssueRefundOnly = () => {
    if (!isAuthenticated) return;

    const available = getAvailableToRefundDollars();

    if (available <= 0) {
      alert("No funds available to refund. The customer has already been fully refunded.");
      return;
    }

    let refundAmount;
    let refundDescription;

    if (useCustomAmount) {
      refundAmount = parseFloat(customRefundAmount);
      if (refundAmount > available) {
        alert(`Custom amount ($${refundAmount.toFixed(2)}) exceeds available refund ($${available.toFixed(2)})`);
        return;
      }
      refundDescription = `Custom Amount`;
    } else {
      const refundPercent = useRefundOverride ? refundOverridePercent : 100;
      refundAmount = parseFloat((available * (refundPercent / 100)).toFixed(2));
      refundDescription = `${refundPercent}% of available`;
    }

    // Card the refund will be issued to (first payment with cardInfo)
    const paymentWithCard = booking.payments?.find((p) => p.cardInfo);
    const cardInfo = paymentWithCard?.cardInfo;
    const cardBrand = cardInfo?.brand ? cardInfo.brand.charAt(0).toUpperCase() + cardInfo.brand.slice(1) : null;
    const cardLast4 = cardInfo?.last4 || null;
    const cardDisplay = cardBrand && cardLast4 ? `${cardBrand} •••• ${cardLast4}` : cardLast4 ? `•••• ${cardLast4}` : null;

    setRefundOnlyData({
      refundAmount,
      refundDescription,
      availableToRefund: available,
      isDevBypass: booking.bookingId?.startsWith("DEV_"),
      cardDisplay,
    });
    setShowRefundOnlyModal(true);
  };

  const handleConfirmRefundOnly = async () => {
    if (!refundOnlyData) return;
    setShowRefundOnlyModal(false);

    const { refundAmount, isDevBypass } = refundOnlyData;
    const availableToRefundDollars = refundOnlyData.availableToRefund;
    
    try {
      setProcessing(true);
      
      if (!functions) {
        throw new Error("Firebase Functions not initialized");
      }
      
      // Create a new refund entry
      const { getCollectionPath } = await import("../utils/dbPaths");
      const bookingPath = getCollectionPath("bookings");
      const bookingRef = doc(db, bookingPath, booking.bookingId);
      
      const refundEntry = {
        id: `refund_${Date.now()}`,
        createdAt: new Date(),
        refundAmount: refundAmount,
        refundPercent: useCustomAmount
          ? ((refundAmount / (availableToRefundDollars || 1)) * 100).toFixed(2)
          : useRefundOverride
          ? refundOverridePercent
          : 100,
        refundReason: useCustomAmount 
          ? `Admin custom amount refund ($${refundAmount.toFixed(2)})`
          : useRefundOverride
          ? `Admin override refund (${refundOverridePercent}%)`
          : "Full refund",
        isRefundOnly: true,
        adminInitiated: true,
      };
      
      // Get selected user for metadata
      const selectedUser = useAuthStore.getState().selectedUser;
      const userMetadata = selectedUser ? {
        userId: selectedUser.id,
        firstName: selectedUser.firstName || "",
        lastName: selectedUser.lastName || "",
        timestamp: Timestamp.now(),
      } : null;

      // Process the refund via Stripe first (unless dev bypass).
      // Only write to Firestore after Stripe succeeds — the CF writes the authoritative refund entry.
      if (!isDevBypass) {
        const processRefund = httpsCallable(functions, "processRefundOnly");
        await processRefund({
          bookingId: booking.bookingId,
          tenantId: booking.tenantId,
          storeId: booking.storeId,
          refundAmount: refundAmount,
          refundPercent: refundEntry.refundPercent,
          refundReason: refundEntry.refundReason,
          testMode: useTestStripe,
        });
      } else {
        // Dev bypass: CF is skipped, so write the refund entry manually
        await updateDoc(bookingRef, {
          refunds: arrayUnion(refundEntry),
          updatedAt: Timestamp.now(),
        });
      }

      // Write admin tracking metadata after Stripe/CF succeeds
      const trackingData = { updatedAt: Timestamp.now() };
      if (userMetadata) {
        const refundPercent = useCustomAmount
          ? ((refundAmount / (availableToRefundDollars || 1)) * 100).toFixed(2)
          : useRefundOverride
            ? refundOverridePercent
            : 100;
        const refundReason = useCustomAmount
          ? `Admin custom amount refund ($${refundAmount.toFixed(2)})`
          : useRefundOverride
            ? `Admin override refund (${refundOverridePercent}%)`
            : "Full refund";

        const changeEntry = createChangeTrackingEntry(userMetadata, {
          changeType: "refund",
          fieldChanged: "refunds",
          description: `Refund issued: $${refundAmount.toFixed(2)} (${refundPercent}%) - ${refundReason}`,
          timestamp: Timestamp.now(),
        });

        if (changeEntry) {
          trackingData.editedBy = arrayUnion(changeEntry);
        }
      }
      await updateDoc(bookingRef, trackingData);
      
      // Send notification email and SMS
      const sendRefundNotification = httpsCallable(
        functions,
        "sendRefundOnlyNotification"
      );
      await sendRefundNotification({
        bookingId: booking.bookingId,
        tenantId: booking.tenantId,
        storeId: booking.storeId,
        refundAmount: refundAmount,
        refundPercent: refundEntry.refundPercent,
        refundReason: refundEntry.refundReason,
        isDevBypass: isDevBypass,
      });
      
      const alertMessage = isDevBypass 
        ? `✅ Dev Booking - Refund Logged!\n\n` +
          `DEV BYPASS - No Stripe refund processed\n` +
          `Would refund: $${refundAmount.toFixed(2)}\n` +
          `The refund entry has been logged but the booking remains active.\n\n` +
          `Customer has been notified via email and SMS.`
        : `✅ Refund Issued Successfully!\n\n` +
          `Amount: $${refundAmount.toFixed(2)}\n` +
          `The customer has been refunded but the booking remains active.\n\n` +
          `Customer has been notified via email and SMS.`;
      
      alert(alertMessage);
      
      // Reload booking data
      await loadBookingData(booking.bookingId);
    } catch (error) {
      console.error("❌ Error issuing refund:", error);
      alert(`❌ Failed to issue refund: ${error.message}`);
    } finally {
      setProcessing(false);
      setRefundOnlyData(null);
    }
  };

  // Calculate cancellation penalty based on policy (for cancel booking modal)
  const getCancellationPenaltyInfo = (bookingData) => {
    if (!bookingData || !bookingData.startDate || !settingsObject) {
      return { penaltyCents: 0, penaltyPercent: 0, refundPercent: 100 };
    }

    const now = new Date();
    let startDate;
    if (bookingData.startDate?.toDate) {
      startDate = bookingData.startDate.toDate();
    } else if (bookingData.startDate?.seconds) {
      startDate = new Date(bookingData.startDate.seconds * 1000);
    } else {
      startDate = new Date(bookingData.startDate);
    }

    // Set start date to 11 AM for comparison
    const startDateAt11AM = new Date(startDate);
    startDateAt11AM.setHours(11, 0, 0, 0);

    const hoursUntilRental = (startDateAt11AM - now) / (1000 * 60 * 60);

    // Load cancellation policy from settings
    const policyDoc = settingsObject.cancellationPolicyWindows || null;
    let windows = cancellationWindows;
    let penaltyTypeValue = penaltyType;

    if (policyDoc) {
      windows = policyDoc.windows || windows;
      penaltyTypeValue = policyDoc.penaltyType || penaltyTypeValue;
    }

    // Find applicable window
    const sortedWindows = [...windows].sort((a, b) => b.hours - a.hours);
    let applicableWindow = sortedWindows[sortedWindows.length - 1]; // Default to last

    for (const window of sortedWindows) {
      if (hoursUntilRental >= window.hours) {
        applicableWindow = window;
        break;
      }
    }

    // Calculate penalty percentage
    const penaltyPercent = applicableWindow.percentPenalty || 0;
    const refundPercent = 100 - penaltyPercent;

    return { penaltyCents: 0, penaltyPercent, refundPercent, applicableWindow };
  };

  // Handle cancel entire booking
  const handleCancelEntireBooking = async () => {
    const latestSub = booking || {};
    const firstName = latestSub.firstName ? latestSub.firstName.charAt(0).toUpperCase() + latestSub.firstName.slice(1).toLowerCase() : '';
    const lastName = latestSub.lastName ? latestSub.lastName.charAt(0).toUpperCase() + latestSub.lastName.slice(1).toLowerCase() : '';
    const customerName = `${firstName} ${lastName}`;
    
    // All amounts stored in cents
    let totalPaidCents = 0;
    if (booking.payments && Array.isArray(booking.payments) && booking.payments.length > 0) {
      booking.payments.forEach((payment) => {
        const paymentCents = Number(payment.costBreakdown?.totalCharged ?? payment.amount ?? 0);
        totalPaidCents += paymentCents;
      });
    }
    const totalRefundedCents = (booking.refunds || []).reduce(
      (sum, refund) => sum + Number(refund.amount ?? refund.refundAmount ?? 0),
      0
    );
    const availableToRefundCents = totalPaidCents - totalRefundedCents;
    
    // Calculate cancellation penalty (handle bookings with no payments)
    const penaltyInfo = getCancellationPenaltyInfo(booking);
    const policyPenaltyPercent = penaltyInfo.penaltyPercent || 0;
    const policyRefundPercent = penaltyInfo.refundPercent || 100;

    // Calculate penalty amount in cents (only if there are payments)
    const policyPenaltyCents = totalPaidCents > 0 
      ? Math.round((totalPaidCents * policyPenaltyPercent) / 100)
      : 0;
    const policyRefundCents = totalPaidCents > 0
      ? totalPaidCents - policyPenaltyCents
      : 0;
    
    // Check if this is a dev bypass booking
    const isDevBypass = booking.bookingId?.startsWith("DEV_");
    
    // ADMIN FLOW: Direct cancellation with confirmation modal
    if (isAdmin) {
      // Calculate refund amount based on mode (handle bookings with no payments)
      let refundAmountCents;
      let refundPercent;
      
      if (totalPaidCents <= 0) {
        // No payments - set refund to 0
        refundAmountCents = 0;
        refundPercent = 0;
      } else if (useCustomAmount) {
        refundAmountCents = Math.round(parseFloat(customRefundAmount) * 100); // Convert dollars to cents
        if (refundAmountCents > availableToRefundCents) {
          alert(
            `⚠️ Custom amount (${formatDollarsAsUSDWithCents(centsToDollars(refundAmountCents))}) exceeds available refund (${formatDollarsAsUSDWithCents(centsToDollars(availableToRefundCents))})`
          );
          return;
        }
        refundPercent = availableToRefundCents > 0 
          ? ((refundAmountCents / availableToRefundCents) * 100).toFixed(2)
          : 0;
      } else {
        refundPercent = useRefundOverride ? refundOverridePercent : policyRefundPercent;
        refundAmountCents = availableToRefundCents > 0
          ? Math.round((availableToRefundCents * parseFloat(refundPercent)) / 100)
          : 0;
      }
      
      // Calculate cancellation penalty (difference between total paid and refund amount)
      const cancellationPenaltyCents = totalPaidCents - refundAmountCents;

      // Set modal data
      setCancelBookingData({
        customerName,
        totalPaidCents,
        totalRefundedCents,
        availableToRefundCents,
        refundAmountCents,
        refundPercent: parseFloat(refundPercent),
        cancellationPenaltyCents,
        policyPenaltyCents,
        policyPenaltyPercent,
        useCustomAmount,
        useRefundOverride,
        isDevBypass,
      });
      setCancelBookingModalVisible(true);
      return;
    }

    // AUTHENTICATED USER FLOW: Show styled modal for confirmation
    if (isAuthenticated) {
      const actualRefundCents = Math.min(
        Math.round(availableToRefundCents * (policyRefundPercent / 100)),
        availableToRefundCents
      );

      setAuthCancelData({
        customerName,
        bookingId: booking.bookingId,
        totalPaidCents,
        totalRefundedCents,
        availableToRefundCents,
        refundAmountCents: actualRefundCents,
        policyRefundPercent,
        policyPenaltyPercent,
        policyPenaltyCents: totalPaidCents - actualRefundCents,
        policyRefundCents,
        isDevBypass,
      });
      setAuthCancelModalVisible(true);
      return;
    }

    // UNAUTHENTICATED CUSTOMER FLOW: Show modal instead of window.confirm
    const totalPaid = totalPaidCents / 100; // Convert cents to dollars for display
    setCancellationRequestData({
      customerName,
      totalPaid,
    });
    setShowCancellationRequestModal(true);
    return;
  };

  // Handle cancellation request confirmation (for unauthenticated users)
  const handleConfirmCancellationRequest = async () => {
    setShowCancellationRequestModal(false);

    const latestSub = booking || {};
    const firstName = latestSub.firstName ? latestSub.firstName.charAt(0).toUpperCase() + latestSub.firstName.slice(1).toLowerCase() : '';
    const lastName = latestSub.lastName ? latestSub.lastName.charAt(0).toUpperCase() + latestSub.lastName.slice(1).toLowerCase() : '';
    const customerName = `${firstName} ${lastName}`;

    try {
      setProcessingType("cancellation");
      setProcessing(true);

      if (!functions) {
        throw new Error("Firebase Functions not initialized");
      }

      const sendCancellationRequestEmail = httpsCallable(
        functions,
        "sendCancellationRequestEmail"
      );

      const result = await sendCancellationRequestEmail({
        bookingId: booking.bookingId,
        bookingId: booking.bookingId,
      });

      alert(
        `✅ Cancellation Confirmation Email Sent!\n\n` +
        `An email with a cancellation confirmation link has been sent to:\n` +
        `${latestSub.customerEmail}\n\n` +
        `You must click the link in the email to complete the cancellation.`
      );
    } catch (error) {
      console.error("❌ Error sending cancellation request:", error);
      alert(`Error: Failed to send cancellation request - ${error.message}`);
    } finally {
      setProcessing(false);
    }
  };

  // Handle actual cancellation after modal confirmation
  const handleConfirmCancelBooking = async () => {
    if (!cancelBookingData) return;

    const {
      refundAmountCents,
      refundPercent,
      useCustomAmount,
      useRefundOverride,
      isDevBypass,
    } = cancelBookingData;

    setCancelBookingModalVisible(false);

    try {
      setProcessing(true);

      // If dev bypass, handle locally without cloud function
      if (isDevBypass) {
        // Move booking to completedBookings with canceled flag
        const { getCollectionPath } = await import("../utils/dbPaths");
        const bookingsPath = getCollectionPath("bookings");
        const completedBookingsPath = getCollectionPath("completedBookings");
        
        // Get the booking document
        const bookingRef = doc(db, bookingsPath, booking.bookingId);
        const bookingDoc = await getDoc(bookingRef);
        
        if (bookingDoc.exists()) {
          const bookingData = bookingDoc.data();
          
          // Add cancelled flag to booking
          const canceledBookingData = {
            ...bookingData,
            cancelled: true,
            cancelledAt: Timestamp.now(),
          };
          
          // Move to completedBookings collection
          const completedBookingRef = doc(db, completedBookingsPath, booking.bookingId);
          await setDoc(completedBookingRef, canceledBookingData);
          
          // Delete from active bookings collection
        await deleteDoc(bookingRef);
        }

        alert(
          `✅ Dev Booking Cancelled Successfully!\n\n` +
          `DEV BYPASS - No refund processed\n` +
          `Would refund: ${formatDollarsAsUSDWithCents(centsToDollars(refundAmountCents))}\n` +
          `Refund Percentage: ${refundPercent}%\n\n` +
          `Booking moved to completed bookings.\n` +
          `Customer notification sent.`
        );

        // Navigate back to admin page
        navigate("/admin");
        return;
      }

      if (!functions) {
        throw new Error("Firebase Functions not initialized");
      }

      const cancelBooking = httpsCallable(
        functions,
        "onCustomerCancellation"
      );

      const result = await cancelBooking({
        bookingId: booking.bookingId,
        adminOverride:
          useRefundOverride || useCustomAmount
            ? {
              refundPercent: parseFloat(refundPercent),
              customAmount: useCustomAmount
                ? centsToDollars(refundAmountCents)
                : null,
            }
            : null,
      });

      // After refunds finish processing, move booking to completedBookings and add canceled flag
      const { getCollectionPath } = await import("../utils/dbPaths");
      const bookingsPath = getCollectionPath("bookings");
      const completedBookingsPath = getCollectionPath("completedBookings");
      
      // Get the booking document
      const bookingRef = doc(db, bookingsPath, booking.bookingId);
      const bookingDoc = await getDoc(bookingRef);
      
      if (bookingDoc.exists()) {
        const bookingData = bookingDoc.data();
        
        // Add canceled flag to booking
        const canceledBookingData = {
          ...bookingData,
          canceled: true,
          canceledAt: Timestamp.now(),
        };
        
        // Move to completedBookings collection
        const completedBookingRef = doc(db, completedBookingsPath, booking.bookingId);
        await setDoc(completedBookingRef, canceledBookingData);
        
        // Delete from active bookings collection
        await deleteDoc(bookingRef);
      }

      alert(
        `✅ Booking Cancelled Successfully!\n\n` +
        `Refund Amount: ${formatDollarsAsUSDWithCents(centsToDollars(result.data.totalRefunded * 100 || cancelBookingData.totalPaidCents))}\n` +
        `Refund Percentage: ${result.data.refundPercentage || 100}%\n\n` +
        `Customer has been notified via email and SMS.\n` +
        `Booking has been moved to completed bookings.`
      );

      // Navigate back to admin page
      navigate("/admin");
    } catch (error) {
      console.error("❌ Error cancelling booking:", error);
      alert(`Error: Failed to cancel booking - ${error.message}`);
    } finally {
      setProcessing(false);
      setCancelBookingData(null);
    }
  };
  
  const handleConfirmAuthCancel = async () => {
    if (!authCancelData) return;
    setAuthCancelModalVisible(false);

    try {
      setProcessingType("cancellation");
      setProcessing(true);

      if (!functions) {
        throw new Error("Firebase Functions not initialized");
      }

      const cancelBooking = httpsCallable(functions, "onCustomerCancellation");
      const result = await cancelBooking({
        bookingId: authCancelData.bookingId,
        tenantId: booking.tenantId,
        storeId: booking.storeId,
      });

      const { getCollectionPath } = await import("../utils/dbPaths");
      const bookingsPath = getCollectionPath("bookings");
      const completedBookingsPath = getCollectionPath("completedBookings");

      const bookingRef = doc(db, bookingsPath, authCancelData.bookingId);
      const bookingDoc = await getDoc(bookingRef);

      if (bookingDoc.exists()) {
        const bookingData = bookingDoc.data();
        const canceledBookingData = {
          ...bookingData,
          cancelled: true,
          cancelledAt: Timestamp.now(),
        };
        const completedBookingRef = doc(db, completedBookingsPath, authCancelData.bookingId);
        await setDoc(completedBookingRef, canceledBookingData);
        await deleteDoc(bookingRef);
      }

      const resultData = result?.data || {};
      const totalRefunded = resultData.totalRefunded || authCancelData.policyRefundCents;
      const refundPercentage = resultData.refundPercentage || authCancelData.policyRefundPercent;

      alert(
        `Booking Cancelled Successfully!\n\n` +
        `Refund Amount: $${(totalRefunded / 100).toFixed(2)}\n` +
        `Refund Percentage: ${refundPercentage}%\n\n` +
        `You have been notified via email and SMS.`
      );

      navigate("/");
    } catch (error) {
      console.error("Error cancelling booking:", error);
      alert(`Error: Failed to cancel booking - ${error.message}`);
    } finally {
      setProcessing(false);
      setAuthCancelData(null);
    }
  };

  // Calculate totals for additions and removals
  const calculateChanges = () => {
    let additionsSubtotal = 0;
    let removalsSubtotal = 0;
    const addedItems = [];
    const removedItems = [];
    let dateChangeFee = 0;
    let dateChangeFeePercentage = 0;
    let daysLost = 0;
    let days = 1; // Will be calculated from booking dates
    
    // Check for date changes
    const latestSub = booking;
    if (newStartDate || newEndDate) {
      const origStart = latestSub.startDate;
      const origEnd = latestSub.endDate;
      const changedStart = newStartDate || origStart;
      const changedEnd = newEndDate || origEnd;
      
      const feeCalc = calculateDateChangeCancellationFee(
        origStart,
        origEnd,
        changedStart,
        changedEnd
      );
      dateChangeFee = feeCalc.fee;
      dateChangeFeePercentage = feeCalc.percentage;
      daysLost = feeCalc.daysLost || 0;
      
      // If extending duration, add the extension cost
      if (extensionCost && extensionCost.total > 0) {
        additionsSubtotal += extensionCost.subtotal;
        addedItems.push({ 
          name: `Duration Extension (${extensionCost.additionalDays} day${extensionCost.additionalDays !== 1 ? "s" : ""
          })`,
          quantity: 1, 
          price: extensionCost.subtotal,
        });
      }
    }
    
    // Check for delivery address changes
    // CRITICAL: Only calculate if state has been initialized from booking
    const originalDeliveryNeededForCost = latestSub.delivery;
    const originalHomePickupNeededForCost = latestSub.homePickup;
    const deliveryStateInitializedForCost = deliveryNeeded === originalDeliveryNeededForCost || deliveryNeeded === true;
    const pickupStateInitializedForCost = homePickupNeeded === originalHomePickupNeededForCost || homePickupNeeded === true;

    const deliveryCostChange = (deliveryStateInitializedForCost && pickupStateInitializedForCost)
      ? calculateDeliveryCostDifference()
      : { costDifference: 0, isRefund: false, isCharge: false };
    const deliveryCostDiff = deliveryCostChange.costDifference || 0;
    const deliveryIsRefund = deliveryCostChange.isRefund;
    const deliveryIsCharge = deliveryCostChange.isCharge;
    const transportLabelAdd = () => {
      const labels = [];
      if (deliveryNeeded && !originalDeliveryNeededForCost) labels.push("Delivery");
      if (homePickupNeeded && !originalHomePickupNeededForCost) labels.push("Pickup");
      if (labels.length === 0) return "Transportation Adjustment";
      return `${labels.join(" & ")} Added`;
    };
    const transportLabelRemove = () => {
      const labels = [];
      if (!deliveryNeeded && originalDeliveryNeededForCost) labels.push("Delivery");
      if (!homePickupNeeded && originalHomePickupNeededForCost) labels.push("Pickup");
      if (labels.length === 0) return "Transportation Adjustment";
      return `${labels.join(" & ")} Removed`;
    };
    let transportAdditionLabel = null;
    let transportRefundLabel = null;
    if (deliveryCostDiff > 0) {
      if (deliveryIsCharge) {
        transportAdditionLabel = transportLabelAdd();
        additionsSubtotal += deliveryCostDiff;
        addedItems.push({
          name: transportAdditionLabel,
          quantity: 1,
          price: deliveryCostDiff,
        });
      } else if (deliveryIsRefund) {
        transportRefundLabel = transportLabelRemove();
        removalsSubtotal += deliveryCostDiff;
        removedItems.push({
          name: transportRefundLabel,
          quantity: 1,
          price: deliveryCostDiff,
        });
      }
    }
    
    // Calculate REMAINING days for this booking (from today to end, or start to end if not started)
    // This needs to be calculated regardless of quantityChanges, as it's used for display
    if (booking) {
      try {
        let startDate, endDate;
        if (booking.startDate?.toDate) {
          startDate = booking.startDate.toDate();
        } else if (booking.startDate?.seconds) {
          startDate = new Date(booking.startDate.seconds * 1000);
        } else {
          startDate = new Date(booking.startDate);
        }
        
        // Use new end date if extending, otherwise use original end date
        if (newEndDate) {
          endDate = new Date(newEndDate);
        } else if (booking.endDate?.toDate) {
          endDate = booking.endDate.toDate();
        } else if (booking.endDate?.seconds) {
          endDate = new Date(booking.endDate.seconds * 1000);
        } else {
          endDate = new Date(booking.endDate);
        }
        
        // Calculate from TODAY (or start date if rental hasn't started) to end date
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(0, 0, 0, 0);
        
        // Use the later of today or start date (whichever is in the future)
        const effectiveStartDate = today > startDate ? today : startDate;
        
        // Calculate remaining days
        days = Math.max(
          1,
          Math.round((endDate - effectiveStartDate) / (1000 * 60 * 60 * 24))
        );
      } catch (error) {
        console.error("Error calculating days:", error);
        days = 1;
      }
    }
    
    // Process quantity changes for current booking
    // CRITICAL: Only process if quantityChanges has been initialized from booking
    // Check if we have products in booking - if yes, quantityChanges should match
    const hasProducts = (booking.product1 && booking.product1.length > 0) || (booking.product2 && booking.product2.length > 0);
    const hasQuantityChanges = Object.keys(quantityChanges.products1 || {}).length > 0 || Object.keys(quantityChanges.products2 || {}).length > 0;
    
    // Only process if both booking has products AND quantityChanges has been initialized
    // Also check that quantityChanges actually has different values than the booking (not just initialized)
    if (booking && quantityChanges && hasProducts && hasQuantityChanges) {
      const changes = quantityChanges;
      
      // Products1 changes
      Object.entries(changes.products1 || {}).forEach(([productId, newQty]) => {
        const bookingProducts1 = booking.product1;
        const originalItem = bookingProducts1.find((p) => p.id === productId);
        
        // Only process if item exists in booking (to avoid false additions/removals)
        if (!originalItem) return;
        
        const originalQty = originalItem.quantity;
        const qtyDiff = newQty - originalQty;
        
        // Only process if there's an actual change
        if (qtyDiff === 0) return;
        
        // Get price from catalog, not from booking item (booking items don't have price)
        const products1Item = products1Catalog.find((p) => p.id === productId);
        const pricePerDay = products1Item.price;
        const totalPrice = pricePerDay * days;
        
        if (qtyDiff > 0) {
          // Addition
          additionsSubtotal += qtyDiff * totalPrice;
          addedItems.push({
            id: productId,
            type: 'product1',
            name: originalItem.name,
            quantity: qtyDiff,
            price: totalPrice,
            pricePaid: pricePerDay,
          });
        } else if (qtyDiff < 0) {
          // Removal
          removalsSubtotal += Math.abs(qtyDiff) * totalPrice;
          removedItems.push({
            name: originalItem.name,
            quantity: Math.abs(qtyDiff),
            price: totalPrice,
          });
        }
      });

      // Products2 changes
      Object.entries(changes.products2 || {}).forEach(([productId, newQty]) => {
        const originalItems = booking.product2;
        const originalItem = originalItems.find((p) => p.id === productId);
        
        // Only process if item exists in booking (to avoid false additions/removals)
        if (!originalItem) return;
        
        const originalQty = originalItem.quantity;
        const qtyDiff = newQty - originalQty;
        
        // Only process if there's an actual change
        if (qtyDiff === 0) return;
        
        const products2Item = products2Catalog.find((p) => p.id === productId);
        const basePrice = products2Item.price;
        const option = products2Item.option;
        
        // Calculate price based on option type (using 'days' from outer scope)
        let price = basePrice;
        if (option === "per-bike") {
          // Per-bike: multiply by number of products1 items and days
          const products1Items = booking.product1;
          const totalProducts1Qty = products1Items.reduce(
            (sum, item) => sum + item.quantity,
            0
          );
          price = basePrice * totalProducts1Qty * days;
        } else if (option === "per-booking") {
          // Per-booking: multiply by quantity (not days)
          price = basePrice * newQty;
        } else if (option === "user-selection") {
          // User-selection: one-time cost, no multiplication by days
          price = basePrice;
        }
        
        if (qtyDiff > 0) {
          // Addition
          const totalPrice = Math.abs(qtyDiff) * price;
          additionsSubtotal += totalPrice;
          addedItems.push({
            id: productId,
            type: 'product2',
            name: products2Item.name,
            quantity: Math.abs(qtyDiff),
            price: totalPrice / Math.abs(qtyDiff),
            pricePaid: option === 'per-bike' ? basePrice * totalProducts1Qty : basePrice,
          });
        } else if (qtyDiff < 0) {
          // Removal
          const totalPrice = Math.abs(qtyDiff) * price;
          removalsSubtotal += totalPrice;
          removedItems.push({
            name: products2Item.name,
            quantity: Math.abs(qtyDiff),
            price: totalPrice / Math.abs(qtyDiff),
          });
        }
      });
    }
    
    // Check for delivery/pickup additions and removals
    const originalDeliveryNeeded = latestSub.delivery;
    const originalHomePickupNeeded = latestSub.homePickup;
    
    // CRITICAL: Only process delivery/pickup changes if state has been initialized from booking
    // State starts as false, then gets set from booking. If state matches booking, it's initialized.
    // If state doesn't match booking AND state is false, it means state hasn't been initialized yet.
    const deliveryStateInitialized = deliveryNeeded === originalDeliveryNeeded || deliveryNeeded === true;
    const pickupStateInitialized = homePickupNeeded === originalHomePickupNeeded || homePickupNeeded === true;
    
    // Only process delivery/pickup changes if state differs from booking AND state is initialized
    const deliveryChanged = deliveryNeeded !== originalDeliveryNeeded && deliveryStateInitialized;
    const pickupChanged = homePickupNeeded !== originalHomePickupNeeded && pickupStateInitialized;
    
    // Delivery addition (but not if already counted in extension cost)
    if (deliveryChanged && deliveryNeeded && !originalDeliveryNeeded) {
      // Only add if NOT extending (extension cost already includes it)
      if (!extensionCost || extensionCost.total === 0) {
        // Only count if we have a valid delivery cost AND address
        if (deliveryCost > 0 && deliveryAddress) {
          additionsSubtotal += deliveryCost;
          addedItems.push({
            name: "Delivery",
            quantity: 1,
            price: deliveryCost,
          });
        }
      }
    }
    
    // Delivery removal (refund) - only if user explicitly removed it
    // deliveryChanged already includes the stateInitialized check above
    if (deliveryChanged && !deliveryNeeded && originalDeliveryNeeded) {
      const firstPayment = booking.payments[0];
      const originalDeliveryCost = firstPayment.costBreakdown.deliveryCost;
      if (originalDeliveryCost > 0) {
        removalsSubtotal += originalDeliveryCost;
        removedItems.push({
          name: "Delivery",
          quantity: 1,
          price: originalDeliveryCost,
        });
      }
    }
    
    // Home Pickup addition (but not if already counted in extension cost)
    if (pickupChanged && homePickupNeeded && !originalHomePickupNeeded) {
      // Only add if NOT extending (extension cost already includes it)
      if (!extensionCost || extensionCost.total === 0) {
        // Only count if we have a valid pickup cost AND address
        if (pickupCost > 0 && deliveryAddress) {
          additionsSubtotal += pickupCost;
          addedItems.push({
            name: "Home Pickup",
            quantity: 1,
            price: pickupCost,
          });
        }
      }
    }
    
    // Home Pickup removal (refund) - only if user explicitly removed it
    // pickupChanged already includes the stateInitialized check above
    if (pickupChanged && !homePickupNeeded && originalHomePickupNeeded) {
      const firstPayment = booking.payments[0];
      const originalPickupCost = firstPayment.costBreakdown.pickupCost;
      if (originalPickupCost > 0) {
        removalsSubtotal += originalPickupCost;
        removedItems.push({
          name: "Home Pickup",
          quantity: 1,
          price: originalPickupCost,
        });
      }
    }
    
    // Calculate tax (after all additions/removals)
    const additionsTax = additionsSubtotal * (salesTaxPercent / 100);
    const removalsTax = removalsSubtotal * (salesTaxPercent / 100);
    
    // Calculate totals with tax
    const additionsTotal = additionsSubtotal + additionsTax;
    const removalsBeforeFee = removalsSubtotal + removalsTax;
    
    // Apply processing fee to refund amount
    const processingFee = removalsBeforeFee * (refundProcessingFee / 100);
    const removalsTotal = removalsBeforeFee - processingFee;
    
    // Calculate net difference (additions minus removals, PLUS cancellation fee, PLUS/MINUS delivery cost change)
    let netDifference = additionsTotal - removalsTotal + dateChangeFee;
    
    // Add delivery cost changes
    if (deliveryIsCharge) {
      netDifference += deliveryCostDiff; // Add to charge
    } else if (deliveryIsRefund) {
      netDifference -= deliveryCostDiff; // Subtract from charge (or add to refund)
    }
    
    const isNetCharge = netDifference > 0;
    const isNetRefund = netDifference < 0;
    const netAmount = Math.abs(netDifference);
    
    return { 
      additionsSubtotal,
      additionsTax,
      additionsTotal, 
      removalsSubtotal,
      removalsTax,
      removalsBeforeFee,
      processingFee,
      removalsTotal, 
      addedItems, 
      removedItems,
      dateChangeFee,
      dateChangeFeePercentage,
      daysLost,
      hasDateChange: (() => {
        if (!newStartDate && !newEndDate) return false;
        if (!latestSub || !latestSub.startDate || !latestSub.endDate) return false;

        // Parse original dates
        const origStart = latestSub.startDate?.toDate ? latestSub.startDate.toDate() :
          latestSub.startDate?.seconds ? new Date(latestSub.startDate.seconds * 1000) :
            new Date(latestSub.startDate);
        const origEnd = latestSub.endDate?.toDate ? latestSub.endDate.toDate() :
          latestSub.endDate?.seconds ? new Date(latestSub.endDate.seconds * 1000) :
            new Date(latestSub.endDate);

        // Parse new dates
        const newStart = newStartDate ? new Date(newStartDate) : origStart;
        const newEnd = newEndDate ? new Date(newEndDate) : origEnd;

        // Normalize to compare dates only (ignore time)
        origStart.setHours(0, 0, 0, 0);
        origEnd.setHours(0, 0, 0, 0);
        newStart.setHours(0, 0, 0, 0);
        newEnd.setHours(0, 0, 0, 0);

        // Only return true if dates actually changed
        return origStart.getTime() !== newStart.getTime() || origEnd.getTime() !== newEnd.getTime();
      })(),
      deliveryCostDiff,
      deliveryIsRefund,
      deliveryIsCharge,
      deliveryNewCost: deliveryCostChange.newCost,
      deliveryOriginalCost: deliveryCostChange.originalCost,
      hasAddressChange: !!(newAddress && newDistance !== null),
      netDifference,
      isNetCharge,
      isNetRefund,
      netAmount,
      transportAdditionLabel,
      transportRefundLabel,
      days, // Rental duration in days
    };
  };
  
  // Show error if booking not found for unauthenticated user
  if (bookingNotFound) {
    const storePhoneDoc = settingsFromStore?.storePhone;
    const storeEmailDoc = settingsFromStore?.storeEmail;
    const storePhone = storePhoneDoc?.phone || "";
    const storeEmail = storeEmailDoc?.email || "";
    
    return (
      <View style={styles.loadingContainer}>
        <View style={styles.loadingContent}>
          <Text style={styles.loadingTitle}>Booking Not Found</Text>
          <Text style={styles.loadingSubtitle}>
            We couldn't find the booking you're looking for.
          </Text>
          {storePhone && (
            <Text style={styles.errorContactInfo}>Phone: {storePhone}</Text>
          )}
          {storeEmail && (
            <Text style={styles.errorContactInfo}>Email: {storeEmail}</Text>
          )}
          <TouchableOpacity dataSet={{ hover: "lift" }}
            style={styles.errorButton}
            onPress={() => navigate("/")}
          >
            <Text style={styles.errorButtonText}>Go Back</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }
  
  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <View style={styles.loadingContent}>
          {/* Store Logo (if available) */}
          {storeLogo && (
            <Image 
              source={{ uri: storeLogo }} 
              style={styles.loadingLogo}
              resizeMode="center"
            />
          )}

          {/* Bike Icon */}
          <View style={styles.loadingIconContainer}>
            <Text style={styles.loadingIcon}>🚴</Text>
          </View>

          {/* Loading Text */}
          <Text style={styles.loadingTitle}>Bonita Bikes</Text>
          <Text style={styles.loadingSubtitle}>Loading booking...</Text>

          {/* Loading Bar */}
          <View style={styles.loadingBarContainer}>
            <View style={styles.loadingBar}>
              <View style={styles.loadingBarFill} />
            </View>
          </View>

          <Text style={styles.loadingHint}>Please wait</Text>
        </View>
      </View>
    );
  }
  
  if (!booking) {
    return (
      <View style={styles.container}>
        <Text style={styles.errorText}>Booking not found</Text>
        <TouchableOpacity dataSet={{ hover: "lift" }} style={styles.button} onPress={() => navigate("/")}>
          <Text style={styles.buttonText}>Go Home</Text>
        </TouchableOpacity>
      </View>
    );
  }
  
  // Calculate changes from newTotals
  const changes = newTotals ? {
    productCost: newTotals.productCost?.differenceCents || 0,
    transportationCost: (newTotals.transportation?.deliveryCostCents || 0) + (newTotals.transportation?.pickupCostCents || 0) - (newTotals.transportation?.originalDeliveryCostCents || 0) - (newTotals.transportation?.originalPickupCostCents || 0),
    transportationDiscount: (newTotals.transportationDiscount?.discountCents || 0) - (newTotals.transportationDiscount?.originalDiscountCents || 0),
    durationDiscount: (newTotals.durationDiscount?.discountCents || 0) - (newTotals.durationDiscount?.originalDiscountCents || 0),
    salesTax: newTotals.salesTax || 0,
    netAmount: (newTotals.productCost?.differenceCents || 0) +
      ((newTotals.transportation?.deliveryCostCents || 0) + (newTotals.transportation?.pickupCostCents || 0) - (newTotals.transportation?.originalDeliveryCostCents || 0) - (newTotals.transportation?.originalPickupCostCents || 0)) -
      ((newTotals.transportationDiscount?.discountCents || 0) - (newTotals.transportationDiscount?.originalDiscountCents || 0)) -
      ((newTotals.durationDiscount?.discountCents || 0) - (newTotals.durationDiscount?.originalDiscountCents || 0)) +
      (newTotals.salesTax || 0)
  } : null;
  
  // Use originalBooking for latest subscription data
  const latestSub = originalBooking || booking;
  
  // Check if booking has ended (today >= end date) - calculated once per render
  // This is a cheap calculation, so no need for useMemo which can cause render loops with Firestore Timestamps
  let isBookingEnded = false;
  if (latestSub && latestSub.endDate) {
    try {
      let endDate;
      if (latestSub.endDate?.toDate) {
        endDate = latestSub.endDate.toDate();
      } else if (latestSub.endDate?.seconds) {
        endDate = new Date(latestSub.endDate.seconds * 1000);
      } else {
        endDate = new Date(latestSub.endDate);
      }

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);

      isBookingEnded = today.getTime() >= endDate.getTime();
    } catch (error) {
      console.error("Error checking booking end date:", error);
    }
  }
  
  // Don't show changes if booking hasn't loaded yet
  let hasChanges = false;
  if (latestSub && latestSub.product1) {
    const originalDeliveryNeeded = latestSub.delivery;
    const originalHomePickupNeeded = latestSub.homePickup;

    // CRITICAL: Only process delivery/pickup changes if state has been initialized from booking.
    // State starts as false, then gets set from booking.
    // Initialized = state matches booking (unchanged) OR the user actively set it to true.
    // If the user unchecked a box (original=true → state=false), that IS a real change;
    // detect initialization by checking if the original value was true OR current is true.
    const deliveryStateInitialized = originalDeliveryNeeded === true || deliveryNeeded === true;
    const pickupStateInitialized = originalHomePickupNeeded === true || homePickupNeeded === true;

    // Only count as changes if state differs from booking AND state is initialized
    const hasDeliveryPickupChanges =
      (deliveryNeeded !== originalDeliveryNeeded && deliveryStateInitialized) ||
      (homePickupNeeded !== originalHomePickupNeeded && pickupStateInitialized);

    // quantityChanges is a useMemo derived from newBooking vs originalBooking.
    // It only contains *differences*, so comparing its count to the total product count
    // is wrong. Initialization is simply: both bookings are loaded.
    const quantityChangesInitialized = !!(newBooking && originalBooking);
  
  // If booking ended and user is not admin, no changes allowed
    // Only show changes if there are actual differences (not just initialized state)
    // AND quantityChanges has been initialized (to avoid false positives on initial load)
    hasChanges =
      (!isBookingEnded || isAdmin) && quantityChangesInitialized
        ? (changes.additionsTotal > 0 ||
        changes.removalsTotal > 0 ||
        changes.hasDateChange ||
        changes.hasAddressChange ||
        hasDeliveryPickupChanges ||
          (extensionCost && extensionCost.total > 0))
      : false;

    // Debug logging (remove after testing)
    if (hasChanges) {
      console.log("🔍 hasChanges is true:", {
        additionsTotal: changes.additionsTotal,
        removalsTotal: changes.removalsTotal,
        hasDateChange: changes.hasDateChange,
        hasAddressChange: changes.hasAddressChange,
        hasDeliveryPickupChanges,
        extensionCost: extensionCost?.total,
        quantityChangesInitialized,
        isBookingEnded,
        isAdmin,
      });
    }
  }
  
  // Check if editing is locked for unauthorized users when booking has ended
  const isEditingLocked = isBookingEnded && !isAdmin;
  
  // Check if rental items are locked (rental started OR booking ended, for unauthorized users)
  const isRentalItemsLocked = (rentalHasStarted || isBookingEnded) && !isAdmin;
  
  // Check if delivery/pickup is locked (rental started OR booking ended, for unauthorized users)
  const isDeliveryPickupLocked =
    (rentalHasStarted || isBookingEnded) && !isAdmin;
  
  // Calendar is only locked if booking ended (not locked if rental has started)
  const isCalendarLocked = isBookingEnded && !isAdmin;
  
  // Function to handle locked section toggle
  const handleLockedToggle = (sectionName, isLocked, lockReason) => {
    if (isLocked) {
      let message = "";
      if (lockReason === "rentalStarted") {
        message = `⏰ Your rental has started. ${sectionName} cannot be modified. Please contact us if you need assistance.`;
      } else if (lockReason === "bookingEnded") {
        message = `🔒 This rental has ended. ${sectionName} cannot be modified. Please contact us if you need assistance.`;
      } else {
        message = `🔒 ${sectionName} cannot be modified. Please contact us if you need assistance.`;
      }
      setLockDialogMessage(message);
      setShowLockDialog(true);
    }
  };
  
  // Helper function to render customer info
  const renderCustomerInfo = () => {
    if (!originalBooking && !booking) return null;
    const latestSub = originalBooking || booking;
    const firstName = latestSub?.firstName ? latestSub.firstName.charAt(0).toUpperCase() + latestSub.firstName.slice(1).toLowerCase() : '';
    const lastName = latestSub?.lastName ? latestSub.lastName.charAt(0).toUpperCase() + latestSub.lastName.slice(1).toLowerCase() : '';
    const customerName = latestSub ? `${firstName} ${lastName}`.trim() : '';
    const customerEmail = latestSub?.customerEmail || '';
    const customerPhone = latestSub?.customerPhone || '';
    const storeName = shopInfo?.name || '';

    // Format booking dates
    let startDate = "N/A";
    let endDate = "N/A";
    let days = 1;

    if (latestSub?.startDate && latestSub?.endDate) {
      try {
        const start = parseFirestoreDate(latestSub.startDate);
        const end = parseFirestoreDate(latestSub.endDate);

        if (start && end) {
          startDate = start.toLocaleDateString("en-US", {
            weekday: "long",
            month: "short",
            day: "numeric",
            year: "numeric",
          });
          endDate = end.toLocaleDateString("en-US", {
            weekday: "long",
            month: "short",
            day: "numeric",
            year: "numeric",
          });
          days = Math.round((end - start) / (1000 * 60 * 60 * 24)) || 1;
        }
      } catch (error) {
        console.error("Error formatting dates:", error);
      }
    }

            return (
              <>
                <View style={styles.infoRow}>
                  <Text style={styles.infoLabel}>Name:</Text>
                  <Text style={styles.infoValue}>
                    {latestSub?.firstName ? latestSub.firstName.charAt(0).toUpperCase() + latestSub.firstName.slice(1).toLowerCase() : ''}{" "}
                    {latestSub?.lastName ? latestSub.lastName.charAt(0).toUpperCase() + latestSub.lastName.slice(1).toLowerCase() : ''}
                  </Text>
                </View>
                <View style={styles.infoRow}>
                  <Text style={styles.infoLabel}>Email:</Text>
                  <TouchableOpacity
                    style={{ flex: 1 }}
                    onPress={() => {
              navigate("/gmail-inbox", {
                        state: {
                  customerEmail: customerEmail,
                  composeTo: customerEmail,
                  composeSubject: `${storeName || "Bonita Bikes"} rental communication`,
                  customerName: customerName,
                  storeName: storeName,
                },
                      });
                    }}
                  >
            <Text
              style={[
                styles.infoValue,
                { color: "#0E7C6B", textDecorationLine: "underline" },
              ]}
            >
                      {customerEmail}
                    </Text>
                  </TouchableOpacity>
                </View>
                <View style={styles.infoRow}>
                  <Text style={styles.infoLabel}>Phone:</Text>
                  {isAuthenticated ? (
                    <TouchableOpacity
                      style={{ flex: 1 }}
                      onPress={() => {
                        setSelectedCustomer({
                              id: customerPhone,
                              phone: customerPhone,
                              fullName: customerName,
                              firstName: latestSub?.firstName || '',
                              lastName: latestSub?.lastName || '',
                              email: customerEmail,
                              bookingId: latestSub?.bookingId || latestSub?.id,
                        });
                        setContactModalVisible(true);
                      }}
                    >
                      <Text style={[styles.infoValue, { color: "#0E7C6B" }]}>
                        {customerPhone ? formatPhoneNumber(customerPhone) : ''}
                      </Text>
                    </TouchableOpacity>
                  ) : (
                      <Text style={styles.infoValue}>
                        {customerPhone ? formatPhoneNumber(customerPhone) : ''}
                      </Text>
                  )}
                </View>
                <View style={styles.infoRow}>
                  <Text style={styles.infoLabel}>Start Date:</Text>
                  <Text style={[styles.infoValue, { color: "#1F8A5B", fontWeight: "600" }]}>
                    {startDate}
                  </Text>
                </View>
                <View style={styles.infoRow}>
                  <Text style={styles.infoLabel}>End Date:</Text>
                  <Text style={[styles.infoValue, { color: "#C4453D", fontWeight: "600" }]}>
                    {endDate}
                  </Text>
                </View>
                <View style={styles.infoRow}>
                  <Text style={styles.infoLabel}>Duration:</Text>
                  <Text style={styles.infoValue}>
                    {days} day{days !== 1 ? "s" : ""}
                  </Text>
                </View>
              </>
            );
  };

  // Helper function to get current dates display
  const getCurrentDatesDisplay = () => {
    if (!originalBooking || !originalBooking.startDate || !originalBooking.endDate) return null;
    let start, end;
    try {
      start = parseFirestoreDate(originalBooking.startDate);
      end = parseFirestoreDate(originalBooking.endDate);
      const days = Math.round((end - start) / (1000 * 60 * 60 * 24));
      const startFormatted = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      const endFormatted = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      return (
        <Text style={styles.currentDatesText}>
          {startFormatted} → {endFormatted} ({days} day
          {days !== 1 ? "s" : ""})
        </Text>
      );
    } catch (error) {
      return (
        <Text style={styles.currentDatesText}>Date range unavailable</Text>
      );
    }
  };

  // Helper function to get new dates display
  const getNewDatesDisplay = () => {
    if (!newBooking || !newBooking.startDate || !newBooking.endDate) return null;
    try {
      const start = parseFirestoreDate(newBooking.startDate);
      const end = parseFirestoreDate(newBooking.endDate);
      const newDays = Math.round((end - start) / (1000 * 60 * 60 * 24));
      const startFormatted = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      const endFormatted = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

      let daysAdded = 0;
      if (originalBooking?.endDate) {
        const origEnd = parseFirestoreDate(originalBooking.endDate);
        const origStart = parseFirestoreDate(originalBooking.startDate || newBooking.startDate);
        const originalDays = Math.round((origEnd - origStart) / (1000 * 60 * 60 * 24));
        daysAdded = newDays - originalDays;
      }

      return (
        <>
          <Text style={styles.newDatesText}>
            {startFormatted} →{" "}
            {endFormatted} ({newDays} day
            {newDays !== 1 ? "s" : ""})
          </Text>
          {daysAdded > 0 && (
            <Text style={styles.daysAddedText}>
              +{daysAdded} Day{daysAdded !== 1 ? "s" : ""} Added
            </Text>
          )}
          {daysAdded < 0 && (
            <Text style={styles.daysRemovedText}>
              {daysAdded} Day{Math.abs(daysAdded) !== 1 ? "s" : ""} Removed
            </Text>
          )}
        </>
      );
    } catch (error) {
      return null;
    }
  };

  // Helper function to render delivery needed checkbox
  const renderDeliveryNeededCheckbox = () => {
    if (!newBooking || !originalBooking) return null;
    let isDeliveryDayOrAfter = false;

    // Check if today is on or after the start date (delivery day or later)
    try {
      if (newBooking.startDate) {
      const startDate = parseFirestoreDate(newBooking.startDate);

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      startDate.setHours(0, 0, 0, 0);

        isDeliveryDayOrAfter = today >= startDate;
      }
    } catch (error) {
      console.error("Error checking delivery day:", error);
    }

    // Disable if trying to de-select on or after delivery day (for non-admins)
    const isDisabled = !isAdmin && newBooking.delivery && isDeliveryDayOrAfter;

    return (
      <TouchableOpacity
        style={styles.checkboxContainer}
        onPress={() => {
          if (isDisabled) {
            alert(
              "⚠️ Cannot cancel delivery on or after your scheduled delivery date."
            );
            return;
          }

          const newValue = !newBooking.delivery;
          onPickupOrDeliveryChange("delivery", newValue);

          // Update in-store pickup based on new state
          if (newValue && newBooking.homePickup) {
          // Both delivery and home pickup will be checked - uncheck in-store pickup
            setInStorePickup(false);
          } else {
            // Any other case - in-store pickup must be checked
            setInStorePickup(true);
          }
          // Don't clear address - keep it for future use
        }}
      >
        <View
          style={[
            styles.checkbox,
            newBooking.delivery && styles.checkboxChecked,
          ]}
        >
          {newBooking.delivery && <Text style={styles.checkboxCheckmark}>✓</Text>}
        </View>
        <Text
          style={[
            styles.checkboxLabel,
            isDisabled && styles.checkboxLabelDisabled,
          ]}
        >
          Delivery Needed {isDisabled && "(Cannot cancel)"}
        </Text>
      </TouchableOpacity>
    );
  };

  // Helper function to render home pickup needed checkbox
  const renderHomePickupNeededCheckbox = () => {
    if (!newBooking || !originalBooking) return null;
    let isPickupDayOrLater = false;

    // Check if today is on or after the end date (pickup day or later)
          try {
      if (newBooking.endDate) {
            const endDate = parseFirestoreDate(newBooking.endDate);
            
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            endDate.setHours(0, 0, 0, 0);
            
        isPickupDayOrLater = today >= endDate;
      }
          } catch (error) {
      console.error("Error checking pickup day:", error);
          }
          
    // Disable if trying to de-select on or after pickup day (for non-admins)
    const isDisabled = !isAdmin && newBooking.homePickup && isPickupDayOrLater;

            return (
      <TouchableOpacity
                style={styles.checkboxContainer}
        onPress={() => {
          if (isDisabled) {
            alert(
              "⚠️ Cannot cancel home pickup on or after your scheduled pickup date."
            );
            return;
          }

          const newValue = !newBooking.homePickup;
          onPickupOrDeliveryChange("homePickup", newValue);

          // Update in-store pickup based on new state
          if (newValue && newBooking.delivery) {
          // Both home pickup and delivery will be checked - uncheck in-store pickup
            setInStorePickup(false);
          } else {
            // Any other case - in-store pickup must be checked
            setInStorePickup(true);
          }
          // Don't clear address - keep it for future use
        }}
      >
        <View
          style={[
            styles.checkbox,
                    newBooking.homePickup && styles.checkboxChecked,
          ]}
        >
                  {newBooking.homePickup && <Text style={styles.checkboxCheckmark}>✓</Text>}
        </View>
        <Text
          style={[
            styles.checkboxLabel,
            isDisabled && styles.checkboxLabelDisabled,
          ]}
        >
          Home Pickup Needed {isDisabled && "(Cannot cancel)"}
        </Text>
      </TouchableOpacity>
    );
  };

  // Helper function to render delivery/pickup cost display
  const renderDeliveryPickupCostDisplay = () => {
    const latestSub = originalBooking || booking;
    const originalDeliveryNeeded = latestSub?.delivery || false;
    const originalHomePickupNeeded = latestSub?.homePickup || false;
    const firstPayment = latestSub?.payments?.[0];
    const originalDeliveryCostCents = latestSub?.deliveryCostCents || firstPayment?.costBreakdown?.deliveryCost || 0;
    const originalPickupCostCents = latestSub?.pickupCostCents || firstPayment?.costBreakdown?.pickupCost || 0;
    const originalDeliveryCost = centsToDollars(originalDeliveryCostCents);
    const originalPickupCost = centsToDollars(originalPickupCostCents);

    // Show costs for additions or removals
    const addressReady = newBooking?.deliveryAddress && newBooking.deliveryAddress.trim();
    const newDeliveryCostCents = newTotals?.transportation?.deliveryCostCents || 0;
    const newPickupCostCents = newTotals?.transportation?.pickupCostCents || 0;
    const showDeliveryCost = newBooking?.delivery && addressReady && newDeliveryCostCents > 0;
    const showPickupCost = newBooking?.homePickup && addressReady && newPickupCostCents > 0;
    const showDeliveryRefund =
      !newBooking?.delivery && originalDeliveryNeeded && originalDeliveryCostCents > 0;
    const showPickupRefund =
      !newBooking?.homePickup && originalHomePickupNeeded && originalPickupCostCents > 0;

    if (
      !showDeliveryCost &&
      !showPickupCost &&
      !showDeliveryRefund &&
      !showPickupRefund
    ) {
          return null;
    }

    return (
      <View
        style={{
          marginTop: 12,
          padding: 12,
          backgroundColor:
            showDeliveryRefund || showPickupRefund ? "#E4F4EC" : "#E3F2EF",
          borderRadius: 6,
        }}
      >
        {/* Delivery Cost (when adding) */}
        {showDeliveryCost && (
          <Text
            style={[styles.infoText, { fontWeight: "600", color: "#0E7C6B" }]}
          >
            ➕ Delivery Cost: {formatCentsAsUSD(newDeliveryCostCents)}
            </Text>
        )}

        {/* Pickup Cost (when adding) */}
        {showPickupCost && (
          <Text
            style={[
              styles.infoText,
              {
                fontWeight: "600",
                marginTop: showDeliveryCost ? 4 : 0,
                color: "#0E7C6B",
              },
            ]}
          >
            ➕ Pickup Cost: {formatCentsAsUSD(newPickupCostCents)}
          </Text>
        )}

        {/* Delivery Refund (when removing) */}
        {showDeliveryRefund && (
          <Text
            style={[styles.infoText, { fontWeight: "600", color: "#1F8A5B" }]}
          >
            ↩️ Delivery Refund: {formatCentsAsUSD(originalDeliveryCostCents)}
          </Text>
        )}

        {/* Pickup Refund (when removing) */}
        {showPickupRefund && (
          <Text
            style={[
              styles.infoText,
              {
                fontWeight: "600",
                marginTop: showDeliveryRefund ? 4 : 0,
                color: "#1F8A5B",
              },
            ]}
          >
            ↩️ Pickup Refund: {formatCentsAsUSD(originalPickupCostCents)}
          </Text>
        )}

        {/* Discount */}
        {newTotals?.transportationDiscount?.discountCents > 0 && (showDeliveryCost || showPickupCost) && (
          <Text style={[styles.infoText, { color: "#1F8A5B", marginTop: 4 }]}>
            💰 Discount Applied: {formatCentsAsUSD(newTotals.transportationDiscount.discountCents)}
          </Text>
        )}
          </View>
    );
  };

  const getAvailableToRefundDollars = () => {
    let totalPaidCents = 0;
    if (booking?.payments && Array.isArray(booking.payments)) {
      booking.payments.forEach((payment) => {
        const amt = Number(payment.costBreakdown?.totalCharged ?? payment.amount ?? 0);
        totalPaidCents += amt;
      });
    }
    const totalRefundedCents = (booking?.refunds || []).reduce(
      (sum, r) => sum + Number(r.amount ?? r.refundAmount ?? 0),
      0
    );
    return (totalPaidCents - totalRefundedCents) / 100;
  };

  const getRefundOverridePercentHint = () => {
    const hasBookingChanges = hasChanges || (newTotals?.totals?.finalTotal !== 0);
    if (hasBookingChanges && newTotals?.totals?.finalTotal < 0) {
      const owedRefundDollars = Math.abs(newTotals.totals.finalTotal) / 100;
      const refundAmount = (owedRefundDollars * (refundOverridePercent / 100)).toFixed(2);
      return `Refund Amount: $${refundAmount} of $${owedRefundDollars.toFixed(2)} owed from booking changes`;
    }
    const availableToRefund = getAvailableToRefundDollars();
    const refundAmount = (availableToRefund * (refundOverridePercent / 100)).toFixed(2);
    return `Refund Amount: $${refundAmount} of $${availableToRefund.toFixed(2)} available`;
  };

  const getCustomRefundAmountHint = () => {
    const hasBookingChanges = hasChanges || (newTotals?.totals?.finalTotal !== 0);
    const amount = parseFloat(customRefundAmount) || 0;
    if (hasBookingChanges && newTotals?.totals?.finalTotal < 0) {
      const owedRefundDollars = Math.abs(newTotals.totals.finalTotal) / 100;
      return amount <= owedRefundDollars
        ? `Will refund: $${amount.toFixed(2)} of $${owedRefundDollars.toFixed(2)} owed from booking changes`
        : `⚠️ Amount exceeds owed refund ($${owedRefundDollars.toFixed(2)})`;
    }
    const availableToRefund = getAvailableToRefundDollars();
    return amount <= availableToRefund
      ? `Will refund: $${amount.toFixed(2)} of $${availableToRefund.toFixed(2)} available`
      : `⚠️ Amount exceeds available refund ($${availableToRefund.toFixed(2)})`;
  };

  // Helper function to render refund split summary

  // Helper function to render calendar grid
  const renderCalendarGrid = () => {
    if (!newBooking || !newBooking.startDate || !newBooking.endDate) return null;
    let currentStart, originalEnd, selectedEnd;
    try {
      currentStart = parseFirestoreDate(newBooking.startDate);
      selectedEnd = parseFirestoreDate(newBooking.endDate);
      originalEnd = originalBooking?.endDate
        ? parseFirestoreDate(originalBooking.endDate)
        : parseFirestoreDate(booking.endDate);

      currentStart.setHours(0, 0, 0, 0);
      selectedEnd.setHours(0, 0, 0, 0);
      originalEnd.setHours(0, 0, 0, 0);
    } catch (error) {
      return null;
    }

    const calendarDays = generateCalendarDays();

    return calendarDays.map((date, index) => {
      if (!date) {
        return <View key={`empty-${index}`} style={styles.calendarDayEmpty} />;
      }

      const dateStr = date.toISOString().split("T")[0];

      const normalizedDate = new Date(date);
      normalizedDate.setHours(0, 0, 0, 0);
      const isAfterOriginalEnd = normalizedDate > originalEnd;

      const calendarDayData = calendarAvailability[dateStr];
      let isAvailable = true;

      if (isAfterOriginalEnd) {
        isAvailable = calendarDayData?.allAvailable !== false;
      }

      const isUnavailable = !isAvailable;
      const isSelected =
        selectedEnd && normalizedDate.getTime() === selectedEnd.getTime();

      const activeEnd = selectedEnd < originalEnd ? selectedEnd : originalEnd;
      const isCurrentBooking =
        normalizedDate >= currentStart && normalizedDate <= activeEnd;

      const isPotentialExtension =
        normalizedDate > originalEnd && normalizedDate <= selectedEnd;

      const isRemovedFromBooking =
        selectedEnd < originalEnd &&
        normalizedDate > selectedEnd && normalizedDate <= originalEnd;

      // Minimum selectable end date: day after start, or today if rental has started
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const minEndDate = rentalHasStarted
        ? (today > currentStart ? today : currentStart)
        : currentStart;

      return (
        <TouchableOpacity
          key={index}
          style={[
            styles.calendarDayCell,
            isCurrentBooking && styles.calendarDayCurrentBackground,
            isPotentialExtension && styles.calendarDayPotentialBackground,
            isRemovedFromBooking && styles.calendarDayRemovedBackground,
            isSelected && styles.calendarDaySelected,
          ]}
          onPress={() => {
            if (normalizedDate <= minEndDate) {
              return;
            }

            // For dates after the original end, check availability
            if (normalizedDate > originalEnd) {
              const dayData = calendarAvailability[dateStr];
              if (dayData && dayData.allAvailable === false) {
                alert(
                  `❌ Items Not Available\n\n` +
                  `Your booking includes items that are not available on this date.\n\n` +
                  `Please choose a different date where all items are available.`
                );
                return;
              }

              const unavailableDates = [];
              const startCheck = new Date(originalEnd);
              startCheck.setDate(startCheck.getDate() + 1);

              let checkDay = new Date(startCheck);
              while (checkDay <= normalizedDate) {
                const checkDateStr = checkDay.toISOString().split("T")[0];
                const checkDayData = calendarAvailability[checkDateStr];
                if (checkDayData && checkDayData.allAvailable === false) {
                  unavailableDates.push(checkDay.toLocaleDateString());
                }
                checkDay.setDate(checkDay.getDate() + 1);
              }

              if (unavailableDates.length > 0) {
                alert(
                  `❌ Cannot extend to this date\n\n` +
                  `Your booking includes items that are already reserved by other customers on the following date(s):\n\n` +
                  unavailableDates.slice(0, 5).join("\n") +
                  (unavailableDates.length > 5
                    ? `\n...and ${unavailableDates.length - 5} more dates`
                    : "") +
                  `\n\nPlease choose an earlier end date where all items are available.`
                );
                return;
              }
            }

            onDateChange("endDate", date);
          }}
                              >
                                <View style={styles.calendarDayContent}>
            <Text
              style={[
                                    styles.calendarDayNumber,
                (isSelected || isCurrentBooking || isPotentialExtension) && {
                  color: "#FFFFFF",
                },
              ]}
            >
                                    {date.getDate()}
                                  </Text>
                                  {/* Only show availability dots for dates after original end date */}
                                  {isAfterOriginalEnd && (
              <View
                style={[
                                      styles.availabilityDot,
                                        // Use allAvailable from calendarAvailability
                                        // Default to green (available) if not set or if allAvailable !== false
                                        (calendarDayData?.allAvailable !== false) 
                                          ? styles.availableDot 
                                          : styles.unavailableDot,
                ]}
              />
                                  )}
                                  {isCurrentBooking && (
              <View
                style={[styles.availabilityDot, styles.currentBookingDot]}
              />
                                  )}
                                </View>
                              </TouchableOpacity>
                            );
                          });
  };

  // Helper function to render delivery address editor
  const renderDeliveryAddressEditor = () => {
    if (!newBooking) return null;
    const hasDelivery = newBooking.delivery || newBooking.homePickup;
            
            if (!hasDelivery) return null;

            const originalAddress = originalBooking?.deliveryAddress || "";
    const showComponentRed = !newAddress.trim() || !addressConfirmed;
            
            return (
              <View style={[
                styles.dateEditorContainer,
                showComponentRed && { backgroundColor: "#FBEAE9", borderColor: "#C4453D" },
              ]}>
                <View style={[
                  styles.dateEditorHeader,
                  showComponentRed && { backgroundColor: "#C4453D", borderBottomColor: "#C4453D" },
                ]}>
                  <Text style={[styles.dateEditorTitle, showComponentRed && { color: "#FFFFFF" }]}>📍 Edit Delivery Address</Text>
                </View>
                
                <View style={[
                  styles.dateEditorContent,
                  showComponentRed && { backgroundColor: "#FBEAE9" },
                ]}>
                    {/* Original Address Infographic */}
                    {originalAddress ? (
                      <View style={styles.originalAddressInfoBox}>
                        <Text style={styles.originalAddressLabel}>📋 Current Address on File:</Text>
                        <Text style={styles.originalAddressText}>{originalAddress}</Text>
                      </View>
                    ) : (
                      <View style={[styles.originalAddressInfoBox, { backgroundColor: "#FDF3E3", borderLeftColor: "#B4761B" }]}>
                        <Text style={[styles.originalAddressLabel, { color: "#7A4E12" }]}>⚠️ No Address on File</Text>
                        <Text style={[styles.originalAddressText, { color: "#7A4E12" }]}>
                          This booking does not have a delivery address. Please enter one below.
                        </Text>
                      </View>
                    )}

                    {/* New Address Input */}
                    <View style={styles.addressInputSection}>
                      <Text style={styles.dateEditorLabel}>New Address:</Text>

                      <View style={styles.addressInputWithSuggestionsWrapper}>
                      <View style={styles.addressInputRow}>
                        <View style={styles.addressInputWrapper}>
                      <TextInput
                        style={[
                          styles.addressInput,
                          addressConfirmed && styles.addressInputValidated,
                        ]}
                        placeholder="Enter new delivery address..."
                        placeholderTextColor="#8A9794"
                            value={newAddress}
                      onChangeText={(text) => {
                          setNewAddress(text);
                        if (addressConfirmed) setAddressConfirmed(false);
                        setAddressManuallyChanged(true);
                        setNewBooking((prev) => ({
                          ...prev,
                          deliveryAddress: text
                        }));

                        if (addressDebounceTimer.current) {
                          clearTimeout(addressDebounceTimer.current);
                          }

                        if (!text.trim()) {
                          setAddressSuggestions([]);
                          setShowSuggestions(false);
                          return;
                        }

                        addressDebounceTimer.current = setTimeout(() => {
                          if (window.google && window.google.maps && window.google.maps.places && text.trim().length > 2) {
                            if (!autocompleteServiceRef.current) {
                              autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
                            }

                            autocompleteServiceRef.current.getPlacePredictions(
                              {
                                input: text.trim(),
                                componentRestrictions: { country: 'us' }
                              },
                              (predictions, status) => {
                                if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
                                  setAddressSuggestions(predictions);
                                  setShowSuggestions(true);
                                } else {
                                  setAddressSuggestions([]);
                                  setShowSuggestions(false);
                                }
                              }
                            );
                          } else {
                            setAddressSuggestions([]);
                            setShowSuggestions(false);
                          }
                        }, 300);
                      }}
                      onFocus={() => {
                        if (window.google && window.google.maps && window.google.maps.places) {
                          if (!autocompleteServiceRef.current) {
                            autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
                          }
                          if (!placesServiceRef.current) {
                            const mapDiv = document.createElement('div');
                            placesServiceRef.current = new window.google.maps.places.PlacesService(mapDiv);
                          }

                          const currentText = (newAddress || "").trim();
                          if (currentText.length > 2 && autocompleteServiceRef.current) {
                            autocompleteServiceRef.current.getPlacePredictions(
                              {
                                input: currentText,
                                componentRestrictions: { country: 'us' }
                              },
                              (predictions, status) => {
                                if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
                                  setAddressSuggestions(predictions);
                                  setShowSuggestions(true);
                                } else {
                                  setAddressSuggestions([]);
                                  setShowSuggestions(false);
                                }
                              }
                            );
                          }
                        }
                      }}
                      onBlur={() => {
                        if (blurTimeoutRef.current) {
                          clearTimeout(blurTimeoutRef.current);
                        }
                        blurTimeoutRef.current = setTimeout(() => {
                          setShowSuggestions(false);
                          blurTimeoutRef.current = null;
                        }, 200);
                        }}
                      />
                        </View>
                        <Animated.View
                          style={[
                            isValidateEnabled && { opacity: validateButtonOpacity },
                          ]}
                        >
                          <TouchableOpacity dataSet={{ hover: "lift" }}
                            style={[
                              styles.validateButton,
                              !isValidateEnabled && !addressConfirmed && styles.validateButtonDisabled,
                              addressConfirmed && styles.validateButtonConfirmed,
                            ]}
                            disabled={!isValidateEnabled && !addressConfirmed}
                            onPress={() => handleValidateAddress()}
                          >
                            <Text style={[
                              styles.validateButtonText,
                              addressConfirmed && styles.validateButtonTextConfirmed,
                            ]}>
                              {addressConfirmed ? "✓ Validated" : "Validate"}
                            </Text>
                          </TouchableOpacity>
                        </Animated.View>
                      </View>
                        {showSuggestions && addressSuggestions.length > 0 && (
                          <View style={styles.suggestionsContainer}>
                            <ScrollView
                              style={styles.suggestionsList}
                              keyboardShouldPersistTaps="handled"
                            >
                              {addressSuggestions.map((prediction, index) => (
                                <TouchableOpacity dataSet={{ hover: "lift" }}
                                  key={prediction.place_id}
                                  style={[
                                    styles.suggestionItem,
                                    index === addressSuggestions.length - 1 && styles.suggestionItemLast,
                                  ]}
                                  onPress={() => selectAddress(prediction.place_id)}
                                >
                                  <Text style={styles.suggestionMainText} numberOfLines={2}>
                                    {prediction.description}
                                  </Text>
                                </TouchableOpacity>
                              ))}
                            </ScrollView>
                          </View>
                        )}
                      </View>
                      {/* Show distance (miles charged) when address is validated */}
                      {addressConfirmed && (newBooking?.numMiles != null || newDistance != null) && (
                        <Text style={styles.distanceText}>
                          📏 Distance: {(newBooking?.numMiles ?? newDistance).toFixed(1)} miles from shop (used for delivery/pickup charge)
                        </Text>
                      )}
                      {!addressConfirmed && newDistance !== null && (
                        <Text style={styles.distanceText}>
                          📏 Distance: {newDistance.toFixed(1)} miles from shop
                        </Text>
                      )}

                    {!addressConfirmed && newAddress.trim().length >= 7 && (
                      <Text style={styles.addressConfirmNotice}>
                        ⚠️ Please press "Validate" to confirm this address before submitting.
                      </Text>
                    )}

                    <View style={[styles.dateWarning, { marginTop: 5 }]}>
                      <Text style={styles.dateWarningText}>
                        💡 Changing your delivery address may result in a refund or
                        additional charge based on the distance from our shop.
                      </Text>
                    </View>
                    </View>
                    
                    {/* Delivery Discount Tiers */}
                    {useDeliveryDiscounts && deliveryDiscounts.length > 0 && (
                      <View style={styles.discountTiersSection}>
                        <Text style={styles.dateEditorLabel}>
                          Available Transportation Discounts:
                        </Text>
                {deliveryDiscounts
                  .sort((a, b) => a.from - b.from)
                  .map((discount, index) => {
                          // Calculate total equipment subtotal using newTotals
                    // Calculate new rental cost gross from newBooking
                    let newRentalCostGross = 0;
                    if (newBooking && products1Catalog.length > 0 && products2Catalog.length > 0) {
                      const startDate = parseFirestoreDate(newBooking.startDate);
                      const endDate = parseFirestoreDate(newBooking.endDate);
                      if (startDate && endDate) {
                        startDate.setHours(0, 0, 0, 0);
                        endDate.setHours(0, 0, 0, 0);
                        const newDays = Math.round((endDate - startDate) / (1000 * 60 * 60 * 24));
                      
                        newBooking.product1.forEach((item) => {
                          const catalogProduct = products1Catalog.find((p) => p.id === item.id);
                          if (catalogProduct) {
                            const priceCents = dollarsToCents(catalogProduct.price);
                            newRentalCostGross += priceCents * (item.quantity || 0) * newDays;
                          }
                        });
                      
                        newBooking.product2.forEach((item) => {
                          const catalogProduct = products2Catalog.find((p) => p.id === item.id);
                          if (catalogProduct) {
                            const priceCents = dollarsToCents(catalogProduct.price);
                            if (catalogProduct.option === "per-booking" && item.quantity > 0) {
                              newRentalCostGross += priceCents;
                            } else if (catalogProduct.option === "per-bike" && item.quantity > 0) {
                              newRentalCostGross += priceCents * item.quantity;
                            } else if (catalogProduct.option === "user-selection") {
                              newRentalCostGross += priceCents * (item.quantity || 0) * newDays;
                            }
                          }
                        });
                      }
                    }
                    
                    const equipmentSubtotal = centsToDollars(newRentalCostGross);
                          
                    const isInRange =
                      equipmentSubtotal >= discount.from &&
                      equipmentSubtotal <= discount.to;
                    const applicableDiscounts = deliveryDiscounts.filter(
                      (d) => {
                        return (
                          equipmentSubtotal >= d.from &&
                          equipmentSubtotal <= d.to
                        );
                      }
                    );
                    const highestDiscount =
                      applicableDiscounts.length > 0
                        ? applicableDiscounts.reduce(
                            (max, d) => (d.value > max.value ? d : max),
                            applicableDiscounts[0]
                          )
                            : null;
                    const isActive =
                      highestDiscount && discount.id === highestDiscount.id;
                          
                    const amountNeeded =
                      equipmentSubtotal < discount.from
                            ? discount.from - equipmentSubtotal
                            : 0;
                          
                          return (
                            <View key={index} style={styles.discountTierRow}>
                        <Text
                          style={[
                                styles.discountTierText,
                            isActive && styles.discountTierActive,
                          ]}
                        >
                          ${discount.from}-${discount.to} order:{" "}
                          {discount.value}% off delivery
                                {isActive && " ✓"}
                              </Text>
                              {amountNeeded > 0 && (
                                <Text style={styles.discountTierNeeded}>
                                  ${amountNeeded.toFixed(2)} to unlock
                                </Text>
                              )}
                              {isActive && newTotals.activeDeliveryDiscountTier && (
                                <Text style={[styles.discountTierText, { fontSize: 11, color: "#1F8A5B", marginTop: 4 }]}>
                                  Active Tier: ${newTotals.activeDeliveryDiscountTier.from}-${newTotals.activeDeliveryDiscountTier.to} ({newTotals.activeDeliveryDiscountTier.value}% off)
                                </Text>
                              )}
                            </View>
                          );
                        })}
                      </View>
                    )}
                    
                    {/* Clear Address Change Button */}
                    {newAddress && newDistance !== null && (
                      <TouchableOpacity dataSet={{ hover: "lift" }}
                        style={styles.clearDatesButton}
                        onPress={() => {
                          setNewAddress("");
                          setNewDistance(null);
                          setAddressConfirmed(!!originalBooking?.deliveryAddress);
                        }}
                      >
                <Text style={styles.clearDatesText}>
                  ✕ Clear Address Change
                </Text>
                      </TouchableOpacity>
                    )}
                    
                  {/* Gated Community checkbox - same as NewBooking */}
                  <TouchableOpacity
                    style={[
                      styles.checkboxContainer,
                      styles.gatedCommunityContainer,
                    ]}
                    onPress={() => {
                      const newValue = !gatedCommunity;
                      setGatedCommunity(newValue);
                      if (!newValue) setCommunityName("");
                      setNewBooking((prev) => ({
                        ...prev,
                        gatedCommunity: newValue,
                        communityName: newValue ? prev.communityName : "",
                      }));
                    }}
                  >
                    <View
                      style={[
                        styles.checkbox,
                        styles.gatedCommunityCheckbox,
                        gatedCommunity && styles.gatedCommunityChecked,
                      ]}
                    >
                      {gatedCommunity && (
                        <Text style={styles.gatedCommunityCheckmark}>✓</Text>
                      )}
                    </View>
                    <Text style={styles.gatedCommunityLabel}>Gated Community</Text>
                  </TouchableOpacity>

                  {/* Community Name Input - Show when Gated Community is checked */}
                  {gatedCommunity && (
                    <View style={styles.communityNameContainer}>
                      <TextInput
                        style={styles.communityNameInput}
                        placeholder="Community name (required)..."
                        placeholderTextColor="#AEB8B6"
                        value={communityName}
                        onChangeText={(text) => {
                          const capped = capitalizeWords(text);
                          setCommunityName(capped);
                          setNewBooking((prev) => ({ ...prev, communityName: capped }));
                        }}
                      />
                    </View>
                  )}

                  {/* Delivery Notes - Moved inside delivery address editor */}
                  <View style={[styles.addressNotesContainer, { marginTop: 16 }]}>
                    <Text style={styles.addressNotesLabel}>
                      Delivery Notes (optional):
                      </Text>
                    <TextInput
                      style={styles.addressNotesInput}
                      placeholder="E.g., Gate code, building number, special instructions..."
                      placeholderTextColor="#AEB8B6"
                      value={addressNotes}
                      onChangeText={(text) => {
                        setAddressNotes(text);
                        setNewBooking((prev) => ({
                          ...prev,
                          addressNotes: text,
                        }));
                      }}
                      multiline
                      numberOfLines={2}
                    />
                    </View>
                  </View>
              </View>
            );
  };

  // Helper function to render existing payment method option
  const renderExistingPaymentMethod = () => {
    // Get the most recent card info from payments array
    let latestCard = null;
    if (booking && booking.payments && booking.payments.length > 0) {
      // Find most recent payment with card info
      for (let i = booking.payments.length - 1; i >= 0; i--) {
        if (booking.payments[i].cardInfo) {
          latestCard = booking.payments[i].cardInfo;
          break;
        }
      }
    }

    return (
      <TouchableOpacity
        style={[
          styles.paymentMethodOption,
          paymentMethod === "existing" && styles.paymentMethodOptionSelected,
        ]}
        onPress={() => setPaymentMethod("existing")}
      >
        <View style={styles.radioButton}>
          {paymentMethod === "existing" && (
            <View style={styles.radioButtonInner} />
          )}
        </View>
        <View style={styles.paymentMethodDetails}>
          <Text style={styles.paymentMethodOptionTitle}>
            Use Previous Payment Method
          </Text>
          <View style={styles.cardInfoDisplay}>
            <Text style={styles.cardInfoText}>
              {latestCard?.brand?.toUpperCase() || "xxxx"} ****
              {latestCard?.last4 || "xxxx"}
            </Text>
            {latestCard?.cardholderName && (
              <Text style={styles.cardInfoText}>
                {" "}
                • {latestCard.cardholderName}
              </Text>
            )}
            <Text style={styles.cardInfoText}>
              {" "}
              • Exp:{" "}
              {latestCard
                ? `${String(latestCard.expMonth).padStart(2, "0")}/${latestCard.expYear
                  }`
                : "xx/xxxx"}
            </Text>
          </View>
        </View>
      </TouchableOpacity>
    );
  };

  // Show PIN auth layer for unauthenticated users
  const id = bookingId || location.state?.editBookingId;
  if (!isAuthenticated && !isVerified && id) {
    return (
      <View style={{ flex: 1, backgroundColor: "#F6F8F7" }}>
        <AuthHeader
          title="Edit Booking"
          subtitle=""
          isDesktop={window.innerWidth >= 768}
          isAuthenticated={isAuthenticated}
          shopInfo={{}}
          storeLogo=""
        />
        <PinAuthLayer
          onAuthenticated={() => {
            console.log("✅ PIN verified for EditBooking:", id);
            setIsVerified(true);
          }}
          bookingId={id}
        />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <AuthHeader
        title="Edit Booking"
        subtitle={shopInfo.name ? `by ${shopInfo.name}` : ""}
        isDesktop={window.innerWidth >= 768}
        isAuthenticated={isAuthenticated}
        shopInfo={shopInfo}
        storeLogo={storeLogo}
      />

      {/* Admin Notes Field - Only for authenticated users */}
      {isAuthenticated && (
        <View style={window.innerWidth >= 768 ? styles.desktopWrapper : null}>
          <View style={styles.adminNotesContainer}>
            <TouchableOpacity
              style={styles.adminNotesHeader}
              onPress={() => setShowAdminNotes(!showAdminNotes)}
            >
              <Text style={styles.adminNotesLabel}>
                📋 Administrator Notes
              </Text>
              <Text style={styles.adminNotesToggle}>
                {showAdminNotes ? "▼ Hide" : "▶ Show"}
              </Text>
            </TouchableOpacity>

            {showAdminNotes && (
              <>
                <TextInput
                  style={styles.adminNotesInput}
                  placeholder="Enter notes about this booking..."
                  placeholderTextColor="#8A9794"
                  value={adminNotes}
                  onChangeText={setAdminNotes}
                  multiline
                  numberOfLines={3}
                />
                <Text style={styles.adminNotesHelper}>
                  {adminNotesSaving ? "Saving..." : "Notes auto-saved"}
                </Text>
              </>
            )}
          </View>
        </View>
      )}

      <View style={window.innerWidth >= 768 ? styles.desktopWrapper : null}>
      <ScrollView style={styles.content}>
        {/* Customer Information */}
        <View style={styles.customerInfoSection}>
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Booking ID:</Text>
            <Text style={styles.infoValue}>
              {originalBooking?.customerBookingID || booking?.customerBookingID || originalBooking?.bookingId || originalBooking?.id || booking?.bookingId || booking?.id || 'N/A'}
            </Text>
          </View>
          <Text style={styles.customerInfoTitle}>Customer Information</Text>
          {renderCustomerInfo()}
        </View>

        {/* REMOVED: Duplicate Payment Methods section - only shown at bottom under totals */}

        {/* Cancellation Policy */}
        {cancellationPolicy && (
          <View style={styles.cancellationPolicyContainer}>
            <Text style={styles.cancellationPolicyTitle}>
              📋 Cancellation Policy
            </Text>
            <Text style={styles.cancellationPolicyText}>
              {cancellationPolicy}
            </Text>
          </View>
        )}

        {/* Booking Ended Banner - Non-admin users */}
        {isBookingEnded && !isAdmin && (
          <View style={styles.bookingEndedBanner}>
            <Text style={styles.bookingEndedText}>
              🔒 This rental has ended. No changes can be made to this booking.
              Please contact us if you need assistance.
            </Text>
          </View>
        )}

        {/* Aggregated Rental Items */}
        {rentalHasStarted && !isBookingEnded && !isAdmin && (
          <View style={styles.rentalStartedBanner}>
            <Text style={styles.rentalStartedText}>
              ⏰ Your rental has started! Item additions/removals are disabled.
              You can only extend the rental end date (see "Edit Booking Dates"
              below).
            </Text>
          </View>
        )}

        {/* Aggregated Products Editor - Collapsible */}
          {!(isBookingEnded && !isAdmin) && (
            <View style={styles.dateEditorContainer}>
              <TouchableOpacity
                style={styles.dateEditorHeader}
                onPress={() => {
                  if (isRentalItemsLocked) {
                    const lockReason =
                      rentalHasStarted && !isAdmin
                        ? "rentalStarted"
                        : "bookingEnded";
                    handleLockedToggle("Rental items", true, lockReason);
                  } else {
                    setShowProductsEditor(!showProductsEditor);
                  }
                }}
              >
                <Text style={styles.dateEditorTitle}>🛒 Rental Items</Text>
                <Text
                  style={[
                    styles.dateEditorToggle,
                    showProductsEditor ? styles.toggleHide : styles.toggleShow,
                  ]}
                >
                  {showProductsEditor ? "▼ Hide" : "▶ Show"}
                </Text>
              </TouchableOpacity>

              {showProductsEditor && (
            <View style={styles.dateEditorContent}>
              {/* Your Items Section */}
              <View style={{ backgroundColor: '#E3F2EF', borderRadius: 6, borderLeftWidth: 4, borderLeftColor: '#0E7C6B', overflow: 'hidden', marginBottom: 4 }}>
                <View style={{ paddingHorizontal: 12, paddingTop: 10, paddingBottom: 4 }}>
                  <Text style={{ fontWeight: '700', fontSize: 20, color: '#C4453D' }}>Your Items</Text>
                </View>

              {/* Product1 Items */}
              {newBooking.product1 && newBooking.product1.filter((item) => !item.removedFromOriginalBooking).length > 0 && (
                <View style={[styles.yourItemsProduct1Section, { marginTop: 0, borderRadius: 0, borderLeftWidth: 0, borderBottomWidth: 0 }]}>
                  <Text style={[styles.sectionTitle, { fontWeight: '500', fontSize: 14, color: '#5C6B68' }]}>{product1CategoryName}</Text>
                  {newBooking.product1.filter((item) => !item.removedFromOriginalBooking).map((item, idx) => {
                    const catalogProduct = products1Catalog.find((p) => p.id === item.id);
                    if (!catalogProduct) return null;
                    
                    const currentQty = item.quantity || 0;
                    const isDisabled = isRentalItemsLocked;
                    const isLastItem = idx === newBooking.product1.length - 1;
                    
                    // Get price: check newBooking first, then catalog (prices are in cents)
                    const priceCents = item.price || catalogProduct.price || 0;
                    const priceFormatted = formatPriceWithoutCents(priceCents);
                    const priceDisplay = priceFormatted ? `$${priceFormatted}/day` : '';
                    
                    // Check if item exists in original booking
                    // Use originalBooking state which is set once on page load and never mutated
                    const originalItem = originalBooking?.product1?.find((p) => p && p.id === item.id);
                    const isNewItem = !originalItem;
                    const originalQty = originalItem ? (originalItem.quantity || 0) : 0;
                    const quantityDifference = currentQty - originalQty;
                    
                    // Check if product has inventory (null means unlimited)
                    const hasInventory = productLeftForBooking[item.id] !== null && productLeftForBooking[item.id] !== undefined;
                    
                    // Get duration discount from newTotals (already calculated)
                    const durationDiscountPercent = newTotals?.durationDiscount?.discountPercent || 0;
                    const durationDiscountName = newTotals?.durationDiscount?.discountName || '';
                    const hasDiscount = durationDiscountPercent > 0 && durationDiscountName;

                    return (
                      <View key={idx} style={[styles.bikeCard, isLastItem && styles.bikeCardLast]}>
                        {/* Name/Title with info icon and Just Added badge */}
                        <View style={styles.bikeNameRow}>
                          <Text style={styles.bikeName}>{item.name || catalogProduct.name}</Text>
                          {catalogProduct.description && (
                            <TouchableOpacity
                              style={styles.infoIcon}
                              onPress={() => {
                                setVisibleTooltip(
                                  visibleTooltip === item.id ? null : item.id
                                );
                              }}
                            >
                              <Text style={styles.infoIconText}>ℹ️</Text>
                            </TouchableOpacity>
                          )}
                          {isNewItem && (
                            <View style={{ marginLeft: 8, backgroundColor: '#0E7C6B', paddingHorizontal: 8, paddingVertical: 3, borderRadius: 5, justifyContent: 'center', alignItems: 'center' }}>
                              <Text style={{ color: '#FFFFFF', fontSize: 11, fontWeight: 'bold', lineHeight: 11 }}>Just Added!</Text>
                            </View>
                          )}
                        </View>
                        {/* Tooltip */}
                        {visibleTooltip === item.id && catalogProduct.description && (
                          <View style={styles.tooltip}>
                            <Text style={styles.tooltipText}>
                              {catalogProduct.description ||
                                "Additional information about this bicycle will appear here."}
                            </Text>
                            <View style={styles.tooltipArrow} />
                          </View>
                        )}

                        {/* Included Items */}
                        {catalogProduct.includedItems && (
                          <Text style={styles.includedItemsText}>
                            Includes: {catalogProduct.includedItems}
                          </Text>
                        )}

                        {/* Image Container - 100% width */}
                        <View style={styles.bikeImageContainer}>
                          {catalogProduct.imageUrl ? (
                            <TouchableOpacity
                              onPress={() => {
                                setSelectedImageUrl(catalogProduct.imageUrl);
                                setImageModalVisible(true);
                              }}
                            >
                              <img
                                src={catalogProduct.imageUrl}
                                alt={item.name || catalogProduct.name}
                                style={{
                                  ...styles.productImage,
                                  ...(window.innerWidth >= 768 ? styles.productImageDesktop : {})
                                }}
                              />
                            </TouchableOpacity>
                          ) : (
                            <View style={styles.bikeImagePlaceholder}>
                              <Text style={styles.bikeImagePlaceholderText}>📷</Text>
                            </View>
                          )}
                        </View>
                        
                        {/* Price, Availability and Controls Container - 30%/40%/30% layout */}
                        <View style={styles.bikeInfoControlsContainer}>
                          {/* Left Container (30%): Price and Duration Discount */}
                          <View style={{ width: '30%', justifyContent: 'center' }}>
                            <Text style={styles.bikePrice}>
                              {priceDisplay}
                            </Text>
                            {/* Duration Discount Display */}
                            {!hasDiscount ? (
                              <Text style={{ fontSize: 11, fontStyle: 'italic', color: '#8A9794', marginTop: 2 }}>
                                (No duration discount unlocked)
                              </Text>
                            ) : (
                              <Text style={{ fontSize: 12, fontWeight: '600', color: '#1F8A5B', marginTop: 2 }}>
                                🎉 {durationDiscountName} ({durationDiscountPercent}% off)
                              </Text>
                            )}
                          </View>

                          {/* Middle Container (40%): Quantity Controls */}
                          <View style={{ width: '40%', justifyContent: 'center', alignItems: 'center' }}>
                            <View style={styles.quantitySelector}>
                              <TouchableOpacity dataSet={{ hover: "lift" }}
                                style={styles.qtyButton}
                                onPress={() => {
                                  if (!isDisabled) {
                                    handleQuantityButtonChange('down', currentQty, item.id);
                                  }
                                }}
                                disabled={isDisabled || currentQty === 0}
                              >
                                <Text style={styles.qtyButtonText}>-</Text>
                              </TouchableOpacity>
                              <Text style={styles.qtyValue}>{currentQty}</Text>
                              <TouchableOpacity dataSet={{ hover: "lift" }}
                                style={styles.qtyButton}
                                onPress={() => {
                                  if (!isDisabled) {
                                    handleQuantityButtonChange('up', currentQty, item.id);
                                  }
                                }}
                                disabled={isDisabled}
                              >
                                <Text style={styles.qtyButtonText}>+</Text>
                              </TouchableOpacity>
                            </View>
                          </View>

                          {/* Right Container (30%): Availability and Quantity Change Indicator */}
                          <View style={{ width: '30%', justifyContent: 'center', alignItems: 'flex-end' }}>
                            {hasInventory && (
                              <Text style={styles.bikeAvailability}>
                                {productLeftForBooking[item.id]} Available!
                              </Text>
                            )}
                            {/* Quantity change indicator - below availability */}
                            {quantityDifference !== 0 && (
                              <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 4 }}>
                                <Text style={{
                                  color: quantityDifference > 0 ? '#1F8A5B' : '#C4453D',
                                  fontSize: 17,
                                  fontWeight: '600'
                                }}>
                                  {quantityDifference > 0 ? '+' : ''}{quantityDifference}
                                </Text>
                                <Text style={{
                                  color: quantityDifference > 0 ? '#1F8A5B' : '#C4453D',
                                  fontSize: 11,
                                  fontWeight: '600',
                                  marginLeft: 4,
                                  textTransform: 'uppercase'
                                }}>
                                  {quantityDifference > 0 ? 'added' : 'removed'}
                                </Text>
                              </View>
                            )}
                          </View>
                        </View>
                      </View>
                    );
                  })}
                </View>
              )}

              {/* Product2 Items */}
              {newBooking.product2 && newBooking.product2.filter((item) => !item.removedFromOriginalBooking).length > 0 && (
                <View style={styles.yourItemsProduct2Section}>
                  <Text style={[styles.sectionTitle, { fontWeight: '500', fontSize: 14, color: '#5C6B68' }]}>{product2CategoryName}</Text>
                  {newBooking.product2.filter((item) => !item.removedFromOriginalBooking).map((item, idx) => {
                    const catalogProduct = products2Catalog.find((p) => p.id === item.id);
                    if (!catalogProduct) return null;
                    
                    const currentQty = item.quantity || 0;
                    const isDisabled = isRentalItemsLocked;
                    const isLastItem = idx === newBooking.product2.length - 1;
                    
                    // Get price: check newBooking first, then catalog (prices are in cents)
                    const priceCents = item.price || catalogProduct.price || 0;
                    const priceFormatted = formatPriceWithoutCents(priceCents);
                    
                    // Add per-bike or per-booking suffix if applicable
                    let priceSuffix = '';
                    if (catalogProduct.option === 'per-bike') {
                      priceSuffix = ' / bike';
                    } else if (catalogProduct.option === 'per-booking') {
                      priceSuffix = ' / booking';
                    } else {
                      priceSuffix = '/day';
                    }
                    const priceDisplay = priceFormatted ? `$${priceFormatted}${priceSuffix}` : '';
                    
                    // Check if item exists in original booking
                    // Use originalBooking state which is set once on page load and never mutated
                    const originalItem = originalBooking?.product2?.find((p) => p && p.id === item.id);
                    const isNewItem = !originalItem;
                    const originalQty = originalItem ? (originalItem.quantity || 0) : 0;
                    const quantityDifference = currentQty - originalQty;
                    
                    // Check if product has inventory (null means unlimited)
                    const hasInventory = productLeftForBooking[item.id] !== null && productLeftForBooking[item.id] !== undefined;
                    
                    // Get duration discount from newTotals (already calculated)
                    const durationDiscountPercent = newTotals?.durationDiscount?.discountPercent || 0;
                    const durationDiscountName = newTotals?.durationDiscount?.discountName || '';
                    const hasDiscount = durationDiscountPercent > 0 && durationDiscountName;

                    return (
                      <View 
                        key={idx} 
                        style={[styles.bikeCard, isLastItem && styles.bikeCardLast]}>
                        {/* Name/Title with info icon and Just Added badge */}
                        <View style={styles.bikeNameRow}>
                          <Text style={styles.bikeName}>{item.name || catalogProduct.name}</Text>
                          {catalogProduct.description && (
                            <TouchableOpacity
                              style={styles.infoIcon}
                              onPress={() => {
                                setVisibleTooltip(
                                  visibleTooltip === item.id ? null : item.id
                                );
                              }}
                            >
                              <Text style={styles.infoIconText}>ℹ️</Text>
                            </TouchableOpacity>
                          )}
                          {isNewItem && (
                            <View style={{ marginLeft: 8, backgroundColor: '#0E7C6B', paddingHorizontal: 8, paddingVertical: 3, borderRadius: 5, justifyContent: 'center', alignItems: 'center' }}>
                              <Text style={{ color: '#FFFFFF', fontSize: 11, fontWeight: 'bold', lineHeight: 11 }}>Just Added!</Text>
                            </View>
                          )}
                        </View>
                        {/* Tooltip */}
                        {visibleTooltip === item.id && catalogProduct.description && (
                          <View style={styles.tooltip}>
                            <Text style={styles.tooltipText}>
                              {catalogProduct.description ||
                                "Additional information about this add-on will appear here."}
                            </Text>
                            <View style={styles.tooltipArrow} />
                          </View>
                        )}

                        {/* Included Items */}
                        {catalogProduct.includedItems && (
                          <Text style={styles.includedItemsText}>
                            Includes: {catalogProduct.includedItems}
                          </Text>
                        )}

                        {/* Image Container - 100% width */}
                        <View style={styles.bikeImageContainer}>
                          {catalogProduct.imageUrl ? (
                            <TouchableOpacity
                              onPress={() => {
                                setSelectedImageUrl(catalogProduct.imageUrl);
                                setImageModalVisible(true);
                              }}
                            >
                              <img
                                src={catalogProduct.imageUrl}
                                alt={item.name || catalogProduct.name}
                                style={{
                                  ...styles.productImage,
                                  ...(window.innerWidth >= 768 ? styles.productImageDesktop : {})
                                }}
                              />
                            </TouchableOpacity>
                          ) : (
                            <View style={styles.bikeImagePlaceholder}>
                              <Text style={styles.bikeImagePlaceholderText}>📷</Text>
                            </View>
                          )}
                        </View>
                        
                        {/* Price, Availability and Controls Container - 30%/40%/30% layout */}
                        <View style={styles.bikeInfoControlsContainer}>
                          {/* Left Container (30%): Price and Duration Discount */}
                          <View style={{ width: '30%', justifyContent: 'center' }}>
                            <Text style={styles.bikePrice}>
                              {priceDisplay}
                            </Text>
                            {/* Duration Discount Display */}
                            {!hasDiscount ? (
                              <Text style={{ fontSize: 11, fontStyle: 'italic', color: '#8A9794', marginTop: 2 }}>
                                (No duration discount unlocked)
                              </Text>
                            ) : (
                              <Text style={{ fontSize: 12, fontWeight: '600', color: '#1F8A5B', marginTop: 2 }}>
                                🎉 {durationDiscountName} ({durationDiscountPercent}% off)
                              </Text>
                            )}
                          </View>

                          {/* Middle Container (40%): Quantity Controls */}
                          <View style={{ width: '40%', justifyContent: 'center', alignItems: 'center' }}>
                            {catalogProduct.option === 'per-bike' ? (
                              // For per-bike option: show checkbox (unchecking removes from newBooking)
                              <TouchableOpacity
                                style={{
                                  flexDirection: 'row',
                                  alignItems: 'center',
                                  paddingVertical: 8,
                                  paddingHorizontal: 12,
                                  backgroundColor: isDisabled ? '#F6F8F7' : '#FFFFFF',
                                  borderRadius: 5,
                                  borderWidth: 2,
                                  borderColor: currentQty > 0 ? '#1F8A5B' : '#DDE3E2',
                                  justifyContent: 'center'
                                }}
                                onPress={async () => {
                                  if (!isDisabled) {
                                    // If checked (currentQty > 0), unchecking removes from newBooking
                                    if (currentQty > 0) {
                                      const updatedProduct2 = (newBooking.product2 || []).filter(p => p.id !== item.id);
                                      const updatedBooking = { ...newBooking, product2: updatedProduct2 };
                                      await updateNewBooking(updatedBooking);
                                    } else {
                                      const updatedProduct2 = [...(newBooking.product2 || []), {
                                        id: item.id,
                                        name: item.name || catalogProduct.name,
                                        quantity: 1,
                                        price: catalogProduct.price
                                      }];
                                      const updatedBooking = { ...newBooking, product2: updatedProduct2 };
                                      await updateNewBooking(updatedBooking);
                                    }
                                  }
                                }}
                                disabled={isDisabled}
                              >
                                <View style={{
                                  width: 20,
                                  height: 20,
                                  borderRadius: 4,
                                  borderWidth: 2,
                                  borderColor: currentQty > 0 ? '#1F8A5B' : '#8A9794',
                                  backgroundColor: currentQty > 0 ? '#1F8A5B' : '#FFFFFF',
                                  marginRight: 8,
                                  justifyContent: 'center',
                                  alignItems: 'center'
                                }}>
                                  {currentQty > 0 && (
                                    <Text style={{ color: '#FFFFFF', fontSize: 14, fontWeight: 'bold' }}>✓</Text>
                                  )}
                                </View>
                                <Text style={{
                                  fontSize: 14,
                                  fontWeight: '600',
                                  color: currentQty > 0 ? '#1F8A5B' : '#5C6B68'
                                }}>
                                  {currentQty > 0 ? 'Included' : 'Include in booking'}
                                </Text>
                              </TouchableOpacity>
                            ) : (
                              // For user-selection and per-booking: show +/- quantity selector (same as product1)
                              <View style={styles.quantitySelector}>
                                <TouchableOpacity dataSet={{ hover: "lift" }}
                                  style={styles.qtyButton}
                                  onPress={() => {
                                    if (!isDisabled) {
                                      handleQuantityButtonChange('down', currentQty, item.id);
                                    }
                                  }}
                                  disabled={isDisabled || currentQty === 0}
                                >
                                  <Text style={styles.qtyButtonText}>-</Text>
                                </TouchableOpacity>
                                <Text style={styles.qtyValue}>{currentQty}</Text>
                                <TouchableOpacity dataSet={{ hover: "lift" }}
                                  style={styles.qtyButton}
                                  onPress={() => {
                                    if (!isDisabled) {
                                      handleQuantityButtonChange('up', currentQty, item.id);
                                    }
                                  }}
                                  disabled={isDisabled}
                                >
                                  <Text style={styles.qtyButtonText}>+</Text>
                                </TouchableOpacity>
                              </View>
                            )}
                          </View>

                          {/* Right Container (30%): Availability and Quantity Change Indicator */}
                          <View style={{ width: '30%', justifyContent: 'center', alignItems: 'flex-end' }}>
                            {hasInventory && (
                              <Text style={styles.bikeAvailability}>
                                {productLeftForBooking[item.id]} Available!
                              </Text>
                            )}
                            {/* Quantity change indicator - below availability */}
                            {quantityDifference !== 0 && (
                              <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 4 }}>
                                <Text style={{
                                  color: quantityDifference > 0 ? '#1F8A5B' : '#C4453D',
                                  fontSize: 17,
                                  fontWeight: '600'
                                }}>
                                  {quantityDifference > 0 ? '+' : ''}{quantityDifference}
                                </Text>
                                <Text style={{
                                  color: quantityDifference > 0 ? '#1F8A5B' : '#C4453D',
                                  fontSize: 11,
                                  fontWeight: '600',
                                  marginLeft: 4,
                                  textTransform: 'uppercase'
                                }}>
                                  {quantityDifference > 0 ? 'added' : 'removed'}
                                </Text>
                              </View>
                            )}
                          </View>
                        </View>
                      </View>
                    );
                  })}
                </View>
              )}

              {/* Empty State for Your Items */}
              {(!newBooking.product1 || newBooking.product1.filter((item) => !item.removedFromOriginalBooking).length === 0) &&
                (!newBooking.product2 || newBooking.product2.filter((item) => !item.removedFromOriginalBooking).length === 0) && (
                <Text style={[styles.emptyStateText, { paddingHorizontal: 12, paddingBottom: 10 }]}>No items in this booking.</Text>
              )}
              </View>{/* end Your Items blue wrapper */}

              {/* More Items to Rent Section */}
              <View style={{ marginTop: 16, backgroundColor: '#E4F4EC', borderRadius: 6, borderLeftWidth: 4, borderLeftColor: '#1F8A5B', overflow: 'hidden' }}>
                <View style={{ paddingHorizontal: 12, paddingTop: 10, paddingBottom: 4 }}>
                  <Text style={{ fontWeight: '700', fontSize: 19, color: '#C4453D' }}>More Items to Rent</Text>
                </View>

              {/* Product1 Items NOT in newBooking */}
              {products1Catalog && products1Catalog.length > 0 && (
                <View style={[styles.yourItemsProduct1Section, { backgroundColor: '#E4F4EC', marginTop: 0, borderRadius: 0, borderLeftWidth: 0 }]}>
                  <Text style={[styles.sectionTitle, { fontWeight: '500', fontSize: 14, color: '#5C6B68' }]}>{product1CategoryName}</Text>
                  {(() => {
                    const filteredProducts = products1Catalog.filter((catalogProduct) => {
                      // Show items NOT in newBooking OR items with removedFromOriginalBooking: true
                      const itemInBooking = newBooking.product1?.find((item) => item.id === catalogProduct.id);
                      return !itemInBooking || itemInBooking.removedFromOriginalBooking === true;
                    });
                    return filteredProducts.map((catalogProduct, idx) => {
                      const isDisabled = isRentalItemsLocked;
                      const isLastItem = idx === filteredProducts.length - 1;
                      
                      // Get price from catalog (prices are in cents)
                      const priceCents = catalogProduct.price || 0;
                      const priceFormatted = formatPriceWithoutCents(priceCents);
                      const priceDisplay = priceFormatted ? `$${priceFormatted}/day` : '';
                      
                      // Check if product has inventory (null means unlimited)
                      const hasInventory = productLeftForBooking[catalogProduct.id] !== null && productLeftForBooking[catalogProduct.id] !== undefined;
                      
                      // Get duration discount from newTotals (already calculated)
                      const durationDiscountPercent = newTotals?.durationDiscount?.discountPercent || 0;
                      const durationDiscountName = newTotals?.durationDiscount?.discountName || '';
                      const hasDiscount = durationDiscountPercent > 0 && durationDiscountName;

                      return (
                        <View key={idx} style={[styles.bikeCard, isLastItem && styles.bikeCardLast]}>
                          {/* Name/Title with info icon */}
                          <View style={styles.bikeNameRow}>
                            <Text style={styles.bikeName}>{catalogProduct.name}</Text>
                            {catalogProduct.description && (
                              <TouchableOpacity
                                style={styles.infoIcon}
                                onPress={() => {
                                  setVisibleTooltip(
                                    visibleTooltip === catalogProduct.id ? null : catalogProduct.id
                                  );
                                }}
                              >
                                <Text style={styles.infoIconText}>ℹ️</Text>
                              </TouchableOpacity>
                            )}
                          </View>
                          {/* Tooltip */}
                          {visibleTooltip === catalogProduct.id && catalogProduct.description && (
                            <View style={styles.tooltip}>
                              <Text style={styles.tooltipText}>
                                {catalogProduct.description ||
                                  "Additional information about this product will appear here."}
                              </Text>
                              <View style={styles.tooltipArrow} />
                            </View>
                          )}
                          
                          {/* Image Container - 100% width */}
                          <View style={styles.bikeImageContainer}>
                            {catalogProduct.imageUrl ? (
                              <TouchableOpacity
                                onPress={() => {
                                  setSelectedImageUrl(catalogProduct.imageUrl);
                                  setImageModalVisible(true);
                                }}
                              >
                                <img
                                  src={catalogProduct.imageUrl}
                                  alt={catalogProduct.name}
                                  style={{
                                    ...styles.productImage,
                                    ...(window.innerWidth >= 768 ? styles.productImageDesktop : {})
                                  }}
                                />
                              </TouchableOpacity>
                            ) : (
                              <View style={styles.bikeImagePlaceholder}>
                                <Text style={styles.bikeImagePlaceholderText}>📷</Text>
                              </View>
                            )}
                          </View>

                          {/* Price, Availability and Controls Container - 30%/40%/30% layout */}
                          <View style={styles.bikeInfoControlsContainer}>
                            {/* Left Container (30%): Price and Duration Discount */}
                            <View style={{ width: '30%', justifyContent: 'center' }}>
                              <Text style={styles.bikePrice}>
                                {priceDisplay}
                              </Text>
                              {/* Duration Discount Display */}
                              {!hasDiscount ? (
                                <Text style={{ fontSize: 11, fontStyle: 'italic', color: '#8A9794', marginTop: 2 }}>
                                  (No duration discount unlocked)
                                </Text>
                              ) : (
                                <Text style={{ fontSize: 12, fontWeight: '600', color: '#1F8A5B', marginTop: 2 }}>
                                  🎉 {durationDiscountName} ({durationDiscountPercent}% off)
                                </Text>
                              )}
                            </View>

                            {/* Middle Container (40%): Add Button */}
                            <View style={{ width: '40%', justifyContent: 'center', alignItems: 'center' }}>
                              <TouchableOpacity dataSet={{ hover: "lift" }}
                                style={{
                                  backgroundColor: '#1F8A5B',
                                  width: 40,
                                  height: 40,
                                  borderRadius: 20,
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                  opacity: isDisabled ? 0.5 : 1
                                }}
                                onPress={async () => {
                                  if (!isDisabled) {
                                    // Add product to booking
                                    await handleQuantityButtonChange('up', 0, catalogProduct.id);
                                  }
                                }}
                                disabled={isDisabled}
                              >
                                <Text style={{ color: '#FFFFFF', fontSize: 20, fontWeight: 'bold' }}>+</Text>
                              </TouchableOpacity>
                            </View>

                            {/* Right Container (30%): Availability */}
                            <View style={{ width: '30%', justifyContent: 'center', alignItems: 'flex-end' }}>
                              {hasInventory && (
                                <Text style={styles.bikeAvailability}>
                                  {productLeftForBooking[catalogProduct.id]} Available!
                                </Text>
                              )}
                            </View>
                          </View>
                        </View>
                      );
                    });
                  })()}
                </View>
              )}

              {/* Product2 Items NOT in newBooking */}
              {products2Catalog && products2Catalog.length > 0 && (
                <View style={[styles.yourItemsProduct2Section, { backgroundColor: '#E4F4EC', marginTop: 0, borderRadius: 0, borderLeftWidth: 0 }]}>
                  <Text style={[styles.sectionTitle, { fontWeight: '500', fontSize: 14, color: '#5C6B68' }]}>{product2CategoryName}</Text>
                  {(() => {
                    const filteredProducts = products2Catalog.filter((catalogProduct) => {
                      // Show items NOT in newBooking OR items with removedFromOriginalBooking: true
                      const itemInBooking = newBooking.product2?.find((item) => item.id === catalogProduct.id);
                      return !itemInBooking || itemInBooking.removedFromOriginalBooking === true;
                    });
                    return filteredProducts.map((catalogProduct, idx) => {
                      const isDisabled = isRentalItemsLocked;
                      const isLastItem = idx === filteredProducts.length - 1;
                      
                      // Get price from catalog (prices are in cents)
                      const priceCents = catalogProduct.price || 0;
                      const priceFormatted = formatPriceWithoutCents(priceCents);
                      
                      // Add per-bike or per-booking suffix if applicable
                      let priceSuffix = '';
                      if (catalogProduct.option === 'per-bike') {
                        priceSuffix = ' / bike';
                      } else if (catalogProduct.option === 'per-booking') {
                        priceSuffix = ' / booking';
                      } else {
                        priceSuffix = '/day';
                      }
                      const priceDisplay = priceFormatted ? `$${priceFormatted}${priceSuffix}` : '';
                      
                      // Check if product has inventory (null means unlimited)
                      const hasInventory = productLeftForBooking[catalogProduct.id] !== null && productLeftForBooking[catalogProduct.id] !== undefined;
                      
                      // Get duration discount from newTotals (already calculated)
                      const durationDiscountPercent = newTotals?.durationDiscount?.discountPercent || 0;
                      const durationDiscountName = newTotals?.durationDiscount?.discountName || '';
                      const hasDiscount = durationDiscountPercent > 0 && durationDiscountName;

                      return (
                        <View key={idx} style={[styles.bikeCard, isLastItem && styles.bikeCardLast]}>
                          {/* Name/Title with info icon */}
                          <View style={styles.bikeNameRow}>
                            <Text style={styles.bikeName}>{catalogProduct.name}</Text>
                            {catalogProduct.description && (
                              <TouchableOpacity
                                style={styles.infoIcon}
                                onPress={() => {
                                  setVisibleTooltip(
                                    visibleTooltip === catalogProduct.id ? null : catalogProduct.id
                                  );
                                }}
                              >
                                <Text style={styles.infoIconText}>ℹ️</Text>
                              </TouchableOpacity>
                            )}
                          </View>
                          {/* Tooltip */}
                          {visibleTooltip === catalogProduct.id && catalogProduct.description && (
                            <View style={styles.tooltip}>
                              <Text style={styles.tooltipText}>
                                {catalogProduct.description ||
                                  "Additional information about this product will appear here."}
                              </Text>
                              <View style={styles.tooltipArrow} />
                            </View>
                          )}
                          
                          {/* Image Container - 100% width */}
                          <View style={styles.bikeImageContainer}>
                            {catalogProduct.imageUrl ? (
                              <TouchableOpacity
                                onPress={() => {
                                  setSelectedImageUrl(catalogProduct.imageUrl);
                                  setImageModalVisible(true);
                                }}
                              >
                                <img
                                  src={catalogProduct.imageUrl}
                                  alt={catalogProduct.name}
                                  style={{
                                    ...styles.productImage,
                                    ...(window.innerWidth >= 768 ? styles.productImageDesktop : {})
                                  }}
                                />
                              </TouchableOpacity>
                            ) : (
                              <View style={styles.bikeImagePlaceholder}>
                                <Text style={styles.bikeImagePlaceholderText}>📷</Text>
                              </View>
                            )}
                          </View>

                          {/* Price, Availability and Controls Container - 30%/40%/30% layout */}
                          <View style={styles.bikeInfoControlsContainer}>
                            {/* Left Container (30%): Price and Duration Discount */}
                            <View style={{ width: '30%', justifyContent: 'center' }}>
                              <Text style={styles.bikePrice}>
                                {priceDisplay}
                              </Text>
                              {/* Duration Discount Display */}
                              {!hasDiscount ? (
                                <Text style={{ fontSize: 11, fontStyle: 'italic', color: '#8A9794', marginTop: 2 }}>
                                  (No duration discount unlocked)
                                </Text>
                              ) : (
                                <Text style={{ fontSize: 12, fontWeight: '600', color: '#1F8A5B', marginTop: 2 }}>
                                  🎉 {durationDiscountName} ({durationDiscountPercent}% off)
                                </Text>
                              )}
                            </View>

                            {/* Middle Container (40%): Add Button */}
                            <View style={{ width: '40%', justifyContent: 'center', alignItems: 'center' }}>
                              <TouchableOpacity dataSet={{ hover: "lift" }}
                                style={{
                                  backgroundColor: '#1F8A5B',
                                  width: 40,
                                  height: 40,
                                  borderRadius: 20,
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                  opacity: isDisabled ? 0.5 : 1
                                }}
                                onPress={async () => {
                                  if (!isDisabled) {
                                    // Add product to booking
                                    await handleQuantityButtonChange('up', 0, catalogProduct.id);
                                  }
                                }}
                                disabled={isDisabled}
                              >
                                <Text style={{ color: '#FFFFFF', fontSize: 20, fontWeight: 'bold' }}>+</Text>
                              </TouchableOpacity>
                            </View>

                            {/* Right Container (30%): Availability */}
                            <View style={{ width: '30%', justifyContent: 'center', alignItems: 'flex-end' }}>
                              {hasInventory && (
                                <Text style={styles.bikeAvailability}>
                                  {productLeftForBooking[catalogProduct.id]} Available!
                                </Text>
                              )}
                            </View>
                          </View>
                        </View>
                      );
                    });
                  })()}
                </View>
              )}
              </View>{/* end More Items to Rent green wrapper */}
            </View>
          )}
            </View>
          )}

        {/* Edit Booking Dates */}
          {!(isBookingEnded && !isAdmin) && (
            <View style={styles.dateEditorContainer}>
              <TouchableOpacity
                style={styles.dateEditorHeader}
                onPress={() => {
                  if (isCalendarLocked) {
                    handleLockedToggle("Booking dates", true, "bookingEnded");
                  } else {
                    setShowDateEditor(!showDateEditor);
                  }
                }}
              >
                <Text style={styles.dateEditorTitle}>📅 Edit Booking Dates</Text>
                <Text
                  style={[
                    styles.dateEditorToggle,
                    showDateEditor ? styles.toggleHide : styles.toggleShow,
                  ]}
                >
                  {showDateEditor ? "▼ Hide" : "▶ Show"}
                </Text>
              </TouchableOpacity>

              {showDateEditor && (
            <View style={styles.dateEditorContent}>
              {rentalHasStarted && !isBookingEnded && !isAdmin && (
                <View style={styles.rentalStartedBanner}>
                  <Text style={styles.rentalStartedText}>
                    ⏰ Your rental has started. You can extend the end date if
                    all your purchased items are available, but cannot add or
                    remove items.
                  </Text>
                </View>
              )}

              {calculatingAvailability ? (
                <View style={styles.calculatingContainer}>
                  <ActivityIndicator size="small" color="#0E7C6B" />
                  <Text style={styles.calculatingText}>
                    Calculating availability...
                  </Text>
                </View>
              ) : (
                <>
                  {/* Current Dates Display */}
                  <View style={styles.currentDatesSection}>
                    <Text style={styles.dateEditorLabel}>
                      Current Rental Period:
                    </Text>
                    {getCurrentDatesDisplay()}
                  </View>

                  {/* Extension Calendar */}
                  <View style={styles.calendarSection}>
                    {/* Month Navigation */}
                    <View style={styles.monthNavigation}>
                      <TouchableOpacity dataSet={{ hover: "lift" }}
                        onPress={goToPreviousMonth}
                        style={styles.monthNavButton}
                      >
                        <Text style={styles.monthNavText}>◀</Text>
                      </TouchableOpacity>
                      <Text style={styles.monthTitle}>
                        {currentMonth.toLocaleDateString("en-US", {
                          month: "long",
                          year: "numeric",
                        })}
                      </Text>
                      <TouchableOpacity dataSet={{ hover: "lift" }}
                        onPress={goToNextMonth}
                        style={styles.monthNavButton}
                      >
                        <Text style={styles.monthNavText}>▶</Text>
                      </TouchableOpacity>
                    </View>

                    <Text style={styles.calendarHint}>
                      🟢 = Available 🔴 = Unavailable (blocks all future dates)
                      ⚫ = Current booking
                    </Text>

                    {/* Day Headers */}
                    <View style={styles.calendarHeader}>
                      {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(
                        (day) => (
                          <View key={day} style={styles.calendarHeaderDay}>
                            <Text style={styles.calendarHeaderText}>{day}</Text>
                          </View>
                        )
                      )}
                    </View>

                    <View style={styles.calendarGrid}>
                      {renderCalendarGrid()}
                    </View>
                  </View>

                  {/* Selected New Dates */}
                    {newBooking?.startDate && newBooking?.endDate && (
                    <View style={styles.newDatesSection}>
                      <Text style={styles.newDatesLabel}>Current Dates:</Text>
                      {getNewDatesDisplay()}

                      <TouchableOpacity dataSet={{ hover: "lift" }}
                        style={styles.clearDatesButton}
                        onPress={() => {
                          if (originalBooking?.startDate && originalBooking?.endDate) {
                            setNewBooking((prev) => ({
                              ...prev,
                              startDate: originalBooking.startDate,
                              endDate: originalBooking.endDate,
                            }));
                          }
                          setExtensionCost(null);
                        }}
                      >
                        <Text style={styles.clearDatesText}>
                          ✕ Reset to Original Dates
                        </Text>
                      </TouchableOpacity>
                    </View>
                  )}

                  {/* Warning about date changes */}
                  <View style={styles.dateWarning}>
                    <Text style={styles.dateWarningText}>
                      {rentalHasStarted
                        ? "⏰ Your rental is currently active. You can only extend the end date to keep your items longer."
                        : "⚠️ Note: If you shorten the rental duration and your original start date falls within our cancellation policy windows, a cancellation fee will apply."}
                    </Text>
                  </View>
                </>
              )}
            </View>
          )}
            </View>
          )}

        {/* Add/Remove Delivery & Pickup */}
          {!(isBookingEnded && !isAdmin) && (
          <View style={styles.dateEditorContainer}>
            <TouchableOpacity 
              style={styles.dateEditorHeader}
              onPress={() => {
                if (isDeliveryPickupLocked) {
                const lockReason =
                  rentalHasStarted && !isAdmin
                    ? "rentalStarted"
                    : "bookingEnded";
                handleLockedToggle(
                  "Delivery and pickup options",
                  true,
                  lockReason
                );
                } else {
                  setShowDeliveryEditor(!showDeliveryEditor);
                }
              }}
            >
            <Text style={styles.dateEditorTitle}>
              🚚 Transportation Options
            </Text>
            <Text
              style={[
                styles.dateEditorToggle,
                showDeliveryEditor ? styles.toggleHide : styles.toggleShow,
              ]}
            >
                {showDeliveryEditor ? "▼ Hide" : "▶ Show"}
              </Text>
            </TouchableOpacity>
            
            {showDeliveryEditor && (
              <View style={styles.dateEditorContent}>
                {/* In-Store Pickup Checkbox */}
                <TouchableOpacity
                  style={styles.checkboxContainer}
                  onPress={async () => {
                    // Cannot uncheck in-store pickup if neither delivery nor home pickup are checked
                    if (inStorePickup && !newBooking.delivery && !newBooking.homePickup) {
                      return; // Disabled - cannot uncheck
                    }

                    // If both delivery and home pickup are selected, clicking in-store pickup will clear them
                    // BUT only if the dates haven't passed (for non-admins)
                    if (!inStorePickup && newBooking.delivery && newBooking.homePickup) {
                      // Check if delivery can be unchecked (not on or after delivery day)
                      let isDeliveryDayOrAfter = false;
                      try {
                        if (newBooking.startDate) {
                          const startDate = parseFirestoreDate(newBooking.startDate);
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          startDate.setHours(0, 0, 0, 0);
                          isDeliveryDayOrAfter = today >= startDate;
                        }
                      } catch (error) {
                        console.error("Error checking delivery day:", error);
                      }

                      // Check if home pickup can be unchecked (not on or after pickup day)
                      let isPickupDayOrLater = false;
                      try {
                        if (newBooking.endDate) {
                          const endDate = parseFirestoreDate(newBooking.endDate);
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          endDate.setHours(0, 0, 0, 0);
                          isPickupDayOrLater = today >= endDate;
                        }
                      } catch (error) {
                        console.error("Error checking pickup day:", error);
                      }

                      // If admin, can always clear both
                      if (isAdmin) {
                        const updatedBooking = {
                          ...newBooking,
                          delivery: false,
                          homePickup: false,
                          inStorePickup: true,
                        };
                        setInStorePickup(true);
                        await updateNewBooking(updatedBooking);
                        return;
                      }

                      // For non-admins, check if dates have passed
                      if (isDeliveryDayOrAfter && isPickupDayOrLater) {
                        alert("⚠️ Cannot change delivery options - both delivery and pickup dates have passed.");
                        return;
                      } else if (isDeliveryDayOrAfter) {
                        alert("⚠️ Cannot cancel delivery on or after your scheduled delivery date.");
                        return;
                      } else if (isPickupDayOrLater) {
                        alert("⚠️ Cannot cancel home pickup on or after your scheduled pickup date.");
                        return;
                      }

                      // Dates haven't passed, can clear both
                      const updatedBooking = {
                        ...newBooking,
                        delivery: false,
                        homePickup: false,
                        inStorePickup: true,
                      };
                      setInStorePickup(true);
                      await updateNewBooking(updatedBooking);
                      return;
                    }

                    const newValue = !inStorePickup;
                    setInStorePickup(newValue);
                  }}
                >
                  <View
                    style={[
                      styles.checkbox,
                      inStorePickup && styles.checkboxChecked,
                    ]}
                  >
                    {inStorePickup && <Text style={styles.checkboxCheckmark}>✓</Text>}
                  </View>
                  <Text style={styles.checkboxLabel}>In-Store Pickup</Text>
                </TouchableOpacity>
                
                {/* Pickup Info Message - Show when in-store pickup is selected */}
                {inStorePickup && pickupInfo && (
                <View
                  style={[
                    styles.infoMessageContainer,
                    {
                      marginLeft: 0,
                      marginBottom: 12,
                      backgroundColor: "#E4F4EC",
                      borderLeftColor: "#1F8A5B",
                    },
                  ]}
                >
                    <Text style={styles.infoMessageText}>{pickupInfo}</Text>
                  </View>
                )}
                
                {/* Delivery Needed Checkbox */}
              {renderDeliveryNeededCheckbox()}
                
                {/* Home Pickup Needed Checkbox */}
              {renderHomePickupNeededCheckbox()}
                
                {/* Delivery/Pickup Details */}
              {(newBooking?.delivery || newBooking?.homePickup) && (
                  <>
                  {/* Delivery Options - Moved to top */}
                  <View style={{ marginTop: 16, marginBottom: 8 }}>
                    <Text style={styles.sectionTitle}>
                      Delivery Options ({mileageRate ? formatCentsAsUSD(mileageRate) : '$0'}/mile)
                    </Text>
                    <Text
                      style={[styles.infoText, { color: "#5C6B68", marginTop: 4 }]}
                    >
                      Loading/Unloading Fee: {loadingFee ? formatCentsAsUSD(loadingFee) : '$0'}
                    </Text>
                    {null !== null && (
                      <Text
                        style={[
                          styles.infoText,
                          { color: "#2E3A38", marginTop: 4, fontWeight: "600" },
                        ]}
                      >
                        Distance: {(0).toFixed(1)} miles
                      </Text>
                    )}
                    {renderDeliveryPickupCostDisplay()}
                  </View>
                    
                  {/* Delivery Info Message with Show/Hide - Above delivery address editor */}
                    {deliveryInfo && (
                      <View style={styles.infoMessageContainer}>
                      <Text style={styles.infoMessageText}>
                        {showDeliveryInfoFull
                          ? deliveryInfo
                          : deliveryInfo.split('\n').slice(0, 3).join('\n') + (deliveryInfo.split('\n').length > 3 ? '...' : '')}
                      </Text>
                      {deliveryInfo.split('\n').length > 3 && (
                        <TouchableOpacity dataSet={{ hover: "lift" }}
                          onPress={() => setShowDeliveryInfoFull(!showDeliveryInfoFull)}
                          style={styles.infoToggleButton}
                        >
                          <Text style={styles.infoToggleText}>
                            {showDeliveryInfoFull ? '▼ Show Less' : '▶ Show More'}
                          </Text>
                        </TouchableOpacity>
                      )}
                      </View>
                    )}
                    
                  {/* Delivery Address Editor - show/hide toggle */}
                  {(newBooking?.delivery || newBooking?.homePickup) && (
                    <View style={{ marginTop: 12, marginBottom: 4 }}>
                      <TouchableOpacity
                        style={{
                          flexDirection: 'row',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          backgroundColor: (!newAddress.trim() || !addressConfirmed) ? '#C4453D' : '#C4453D',
                          paddingVertical: 10,
                          paddingHorizontal: 14,
                          borderRadius: showDeliveryAddressEditor ? 6 : 6,
                        }}
                        onPress={() => setShowDeliveryAddressEditor(!showDeliveryAddressEditor)}
                      >
                        <Text style={{ color: '#FFFFFF', fontWeight: '700', fontSize: 15 }}>📍 Edit Delivery Address</Text>
                        <Text style={{ color: '#FFFFFF', fontSize: 13, fontWeight: '600' }}>
                          {showDeliveryAddressEditor ? '▼ Hide' : '▶ Show'}
                        </Text>
                      </TouchableOpacity>
                      {showDeliveryAddressEditor && renderDeliveryAddressEditor()}
                    </View>
                  )}

                    {/* Available Transportation Discounts */}
                    {useDeliveryDiscounts && deliveryDiscounts.length > 0 && (
                      <View style={{ marginTop: 12, marginBottom: 8 }}>
                      <Text
                        style={{
                          fontSize: 14,
                          fontWeight: "600",
                          color: "#2E3A38",
                          marginBottom: 10,
                        }}
                      >
                          Available Transportation Discounts:
                        </Text>
                      {[...deliveryDiscounts]
                        .sort((a, b) => (a.from || 0) - (b.from || 0))
                        .map((discount, index) => {
                          // Calculate total equipment subtotal using newTotals
                          // Calculate new rental cost gross from newBooking
                          let newRentalCostGrossCents = 0;
                          if (newBooking?.startDate && newBooking?.endDate && newBooking?.product1 && newBooking?.product2) {
                            const newStartDate = parseFirestoreDate(newBooking.startDate);
                            const newEndDate = parseFirestoreDate(newBooking.endDate);
                            const newDays = Math.round((newEndDate - newStartDate) / (1000 * 60 * 60 * 24));
                            
                            // Calculate product1 costs
                            (newBooking.product1 || []).forEach((item) => {
                              const catalogProduct = products1FromStore.find((p) => p.id === item.id);
                              if (catalogProduct && item.quantity > 0) {
                                const priceCents = catalogProduct.price || 0;
                                newRentalCostGrossCents += priceCents * item.quantity * newDays;
                              }
                            });
                            
                            // Calculate product2 costs
                            const totalProduct1Quantity = (newBooking.product1 || []).reduce((sum, item) => sum + (item.quantity || 0), 0);
                            (newBooking.product2 || []).forEach((item) => {
                              const catalogProduct = products2FromStore.find((p) => p.id === item.id);
                              if (catalogProduct && item.quantity > 0) {
                                const priceCents = catalogProduct.price || 0;
                                if (catalogProduct.option === "per-booking") {
                                  newRentalCostGrossCents += priceCents * item.quantity;
                                } else if (catalogProduct.option === "per-bike") {
                                  newRentalCostGrossCents += priceCents * totalProduct1Quantity * newDays;
                                } else if (catalogProduct.option === "user-selection") {
                                  newRentalCostGrossCents += priceCents * item.quantity * newDays;
                                }
                              }
                            });
                          }
                          
                          // Convert to dollars for comparison with discount tiers (which are in dollars)
                          const equipmentSubtotal = centsToDollars(newRentalCostGrossCents);
                          
                          const isInRange =
                            equipmentSubtotal >= (discount.from || 0) &&
                            equipmentSubtotal <= (discount.to || Infinity);
                          const applicableDiscounts = deliveryDiscounts.filter(
                            (d) =>
                              equipmentSubtotal >= (d.from || 0) &&
                              equipmentSubtotal <= (d.to || Infinity)
                          );
                          const highestDiscount =
                            applicableDiscounts.length > 0
                              ? applicableDiscounts.reduce(
                                (max, d) => ((d.value || 0) > (max.value || 0) ? d : max),
                                  applicableDiscounts[0]
                                )
                            : null;
                          const isActive =
                            highestDiscount &&
                            (highestDiscount.value || 0) === (discount.value || 0);
                          const amountNeeded =
                            equipmentSubtotal < discount.from
                              ? discount.from - equipmentSubtotal
                              : 0;
                          
                          return (
                            <View key={index} style={styles.discountTierRow}>
                              <Text
                                style={[
                                styles.discountTierText,
                                  isActive && styles.discountTierActive,
                                ]}
                              >
                                ${discount.from}-${discount.to} order:{" "}
                                {discount.value}% off delivery
                                {isActive && " ✓"}
                              </Text>
                              {amountNeeded > 0 && (
                                <Text style={styles.discountTierNeeded}>
                                  ${amountNeeded.toFixed(2)} to unlock
                                </Text>
                              )}
                              {isActive && highestDiscount && (
                                <Text style={[styles.discountTierText, { fontSize: 11, color: "#1F8A5B", marginTop: 4 }]}>
                                  Active Tier: ${highestDiscount.from || 0}-${highestDiscount.to || 0} ({(highestDiscount.value || 0)}% off)
                                </Text>
                              )}
                            </View>
                          );
                        })}
                      </View>
                    )}

                    
                    {/* Real-time Cost Preview */}
                  {addressConfirmed &&
                    deliveryAddress &&
                    (deliveryCost > 0 || pickupCost > 0) && (
                      <View
                        style={{
                        marginTop: 16,
                        padding: 12,
                        backgroundColor: "#E3F2EF",
                        borderRadius: 6,
                        borderLeftWidth: 4,
                        borderLeftColor: "#0E7C6B",
                        }}
                      >
                        <Text
                          style={{
                            fontSize: 14,
                            fontWeight: "600",
                            color: "#0E7C6B",
                            marginBottom: 8,
                          }}
                        >
                          💵 Transportation Costs
                        </Text>
                        {newDistance !== null && (
                          <Text
                            style={{
                              fontSize: 13,
                              color: "#5C6B68",
                              marginBottom: 6,
                            }}
                          >
                            📏 Distance: {newDistance.toFixed(1)} miles
                          </Text>
                        )}
                      {newBooking?.delivery && newTotals?.transportation?.deliveryCostCents > 0 && (
                          <Text style={{ fontSize: 13, color: "#2E3A38" }}>
                          🚚 Delivery: {formatCentsAsUSD(newTotals.transportation.deliveryCostCents)}
                          </Text>
                        )}
                      {newBooking?.homePickup && newTotals?.transportation?.pickupCostCents > 0 && (
                          <Text
                            style={{
                              fontSize: 13,
                              color: "#2E3A38",
                            marginTop: newBooking?.delivery ? 4 : 0,
                            }}
                          >
                          🏠 Pickup: {formatCentsAsUSD(newTotals.transportation.pickupCostCents)}
                          </Text>
                        )}
                      {newTotals?.transportationDiscount?.discountCents > 0 && (
                          <Text
                            style={{
                              fontSize: 13,
                              color: "#1F8A5B",
                              marginTop: 6,
                              fontWeight: "600",
                            }}
                          >
                          💰 Discount: -{formatCentsAsUSD(newTotals.transportationDiscount.discountCents)}
                          </Text>
                        )}
                      </View>
                    )}
                  </>
                )}
              </View>
            )}
          </View>
          )}


        {/* Reset Changes Button */}
          {!(isBookingEnded && !isAdmin) && (
            <TouchableOpacity dataSet={{ hover: "lift" }}
              style={[styles.resetButton, { marginTop: 16 }]}
              disabled={isBookingEnded && !isAdmin}
              onPress={() => {
                if (originalBooking) {
                  const products1CatalogToUse = products1Catalog.length > 0 ? products1Catalog : products1FromStore;
                  const products2CatalogToUse = products2Catalog.length > 0 ? products2Catalog : products2FromStore;
                  const originalDurationDiscountPercent = originalBooking.durationDiscount?.discountPercent || 0;

                  const product1WithFields = (originalBooking.product1 || []).map((item) => {
                    const catalogProduct = products1CatalogToUse.find((p) => p.id === item.id);
                    const itemPrice = catalogProduct?.price || 0;
                    const durationDiscountCents = Math.round(itemPrice * (originalDurationDiscountPercent / 100));
                    return {
                      ...item,
                      isFromOriginalBooking: true,
                      removedFromOriginalBooking: false,
                      itemPrice,
                      durationDiscountCents,
                    };
                  });

                  const product2WithFields = (originalBooking.product2 || []).map((item) => {
                    const catalogProduct = products2CatalogToUse.find((p) => p.id === item.id);
                    const itemPrice = catalogProduct?.price || 0;
                    const durationDiscountCents = Math.round(itemPrice * (originalDurationDiscountPercent / 100));
                    return {
                      ...item,
                      isFromOriginalBooking: true,
                      removedFromOriginalBooking: false,
                      itemPrice,
                      durationDiscountCents,
                    };
                  });

                  const resetBooking = {
                    id: originalBooking.id,
                    bookingId: originalBooking.bookingId,
                    product1: product1WithFields,
                    product2: product2WithFields,
                    homePickup: originalBooking.homePickup || false,
                    delivery: originalBooking.delivery || false,
                    inStorePickup: originalBooking.inStorePickup || false,
                    deliveryAddress: originalBooking.deliveryAddress || "",
                    startDate: originalBooking.startDate || null,
                    endDate: originalBooking.endDate || null,
                    gatedCommunity: originalBooking.gatedCommunity || false,
                    communityName: originalBooking.communityName || "",
                    addressNotes: originalBooking.addressNotes || "",
                    numMiles: originalBooking.numMiles !== undefined && originalBooking.numMiles !== null
                      ? parseFloat(Number(originalBooking.numMiles).toFixed(1))
                      : null,
                  };

                  setNewBooking(resetBooking);

                  setGatedCommunity(originalBooking.gatedCommunity || false);
                  setCommunityName(originalBooking.communityName || "");
                  setAddressNotes(originalBooking.addressNotes || "");
                  setDeliveryNeeded(resetBooking.delivery);
                  setHomePickupNeeded(resetBooking.homePickup);
                  setInStorePickup(resetBooking.inStorePickup);

                  setDeliveryAddress(originalBooking.deliveryAddress || "");
                  setAddressInput(originalBooking.deliveryAddress || "");
                  setAddressConfirmed(!!originalBooking.deliveryAddress);
                  setAddressManuallyChanged(false);
                  setNewDistance(null);
                  setAddressSuggestions([]);
                  setShowSuggestions(false);

                  setUseRefundOverride(false);
                  setRefundOverridePercent(0);
                  setUseCustomAmount(false);
                  setCustomRefundAmount("0.00");
                  setShowAdminOverride(false);

                  setShownTransportationUnlock(false);
                  setShownDurationUnlock(false);
                  previousTransportationDiscountForHigherTierRef.current = null;
                  previousDurationDiscountForHigherTierRef.current = null;

                  calculateTotals(resetBooking, originalBooking).catch((err) => {
                    console.error("Error recalculating totals after reset:", err);
                  });
                }
              }}
            >
              <Text style={styles.resetButtonText}>🔄 Reset Changes</Text>
            </TouchableOpacity>
          )}
        
        {/* 1) Previous Order | 2) What Changed | 3) Current Order */}
          {!(isBookingEnded && !isAdmin) && (
            <View style={{ marginHorizontal: 16, marginTop: 12 }}>
              {(!newTotals || !newBooking || !originalBooking) ? (
                <View style={[styles.section, { backgroundColor: "#EDF1F0", borderWidth: 2, borderColor: "#8A9794", borderRadius: 6, padding: 16, marginBottom: 12 }]}>
                  <Text style={styles.sectionTitle}>Order Summary</Text>
                  <Text style={styles.emptyStateText}>
                    Add or remove products, change the booking dates, or modify delivery options to see price additions/changes.
                  </Text>
                </View>
              ) : (
                <>
                    <View style={[styles.dateEditorContainer, { marginBottom: 12, backgroundColor: "#FBEAE9", borderColor: "#C4453D" }]}>
                    <TouchableOpacity
                        style={[styles.dateEditorHeader, { backgroundColor: "#FBEAE9", borderBottomColor: "#FBEAE9" }]}
                      onPress={() => setShowPreviousOrder((v) => !v)}
                      activeOpacity={0.7}
                    >
                        <Text style={[styles.dateEditorTitle, { color: "#C4453D" }]}>📋 Previous Order</Text>
                        <Text
                          style={[
                            styles.dateEditorToggle,
                            showPreviousOrder ? styles.toggleHide : styles.toggleShow,
                          ]}
                        >
                          {showPreviousOrder ? "▼ Hide" : "▶ Show"}
                        </Text>
                    </TouchableOpacity>
                    {showPreviousOrder && (
                        <View style={styles.dateEditorContent}>
                          <PreviousOrderSummary
                            booking={originalBooking}
                            styles={styles}
                            product1Catalog={products1Catalog.length > 0 ? products1Catalog : products1FromStore}
                            product2Catalog={products2Catalog.length > 0 ? products2Catalog : products2FromStore}
                            product1Title={product1CategoryName}
                            product2Title={product2CategoryName}
                            settingsObject={settingsObject}
                            productCostCents={previousOrderProductCostCents}
                            formatDollarsAsUSDWithCents={formatDollarsAsUSDWithCents}
                            centsToDollars={centsToDollars}
                          />
                        </View>
                    )}
                  </View>
                  <View style={{ marginBottom: 12 }}>
                    <VerbalOrderChanges
                      originalBooking={originalBooking}
                      newBooking={newBooking}
                      styles={styles}
                      product1Catalog={products1Catalog.length > 0 ? products1Catalog : products1FromStore}
                      product2Catalog={products2Catalog.length > 0 ? products2Catalog : products2FromStore}
                      product1Title={product1CategoryName}
                      product2Title={product2CategoryName}
                      addressConfirmed={addressConfirmed}
                        originalProductCostCents={previousOrderProductCostCents}
                    />
                  </View>
                  <View style={{ marginBottom: 12 }}>
                    <CurrentOrderSummary
                      booking={newBooking}
                      styles={styles}
                      product1Catalog={products1Catalog.length > 0 ? products1Catalog : products1FromStore}
                      product2Catalog={products2Catalog.length > 0 ? products2Catalog : products2FromStore}
                      product1Title={product1CategoryName}
                      product2Title={product2CategoryName}
                      settingsObject={settingsObject}
                      productCostCents={newBooking?.productPriceAmount ?? 0}
                      deliveryCentsOverride={newBooking?.deliveryCostCents}
                      pickupCentsOverride={newBooking?.pickupCostCents}
                      formatDollarsAsUSDWithCents={formatDollarsAsUSDWithCents}
                      centsToDollars={centsToDollars}
                      netChangeCents={newTotals?.totals?.finalTotal ?? null}
                    />
                  </View>
                </>
              )}
            </View>
          )}

          {/* Payments & Refunds — graphic clone of view-booking, show/hide, default show */}
          {booking && (
            <View style={{
              margin: 16,
              marginTop: 16,
              borderWidth: 2,
              borderColor: "#DDE3E2",
              borderRadius: 8,
              backgroundColor: "#F4F6F6",
              padding: 12,
              gap: 12,
            }}>
              <TouchableOpacity
                style={{ flexDirection: "row", alignItems: "center", justifyContent: "space-between", paddingVertical: 4 }}
                onPress={() => setShowPaymentsRefunds((v) => !v)}
                activeOpacity={0.7}
              >
                <Text style={styles.sectionTitle}>💳 Payments & Refunds</Text>
                <Text style={{ fontSize: 18, color: "#5C6B68" }}>{showPaymentsRefunds ? "▼" : "▶"}</Text>
              </TouchableOpacity>
              {showPaymentsRefunds && (() => {
                const paymentList = booking?.payments || [];
                const hasPayments = paymentList.length > 0;
                const totalPaid = paymentList.reduce((sum, p) => sum + (Number(p.amount ?? 0) / 100), 0);
                const refunds = booking?.refunds && Array.isArray(booking.refunds) ? booking.refunds : [];
                const refundToDollars = (r) => Number(r.amount ?? r.refundAmount ?? 0) / 100;
                const totalRefunded = refunds.reduce((sum, r) => sum + refundToDollars(r), 0);
                // Same date handling as ViewBooking; normalize serialized Firestore { seconds } so we get a valid Date
                const formatShortDate = (ts) => {
                  if (!ts) return "—";
                  if (typeof ts === "object" && typeof ts.seconds === "number" && !ts.toDate) {
                    ts = new Date(ts.seconds * 1000);
                  }
                  const d = ts?.toDate ? ts.toDate() : ts instanceof Date ? ts : new Date(ts);
                  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
                };
                return (
                  <View style={{ marginTop: 8 }}>
                    {hasPayments ? (
                      <>
                        {paymentList.map((payment, index) => {
                          const paymentType = payment.type === "initial" ? "Initial Booking" : payment.type;
                          const cardInfo = payment.cardInfo || {};
                          const brand = cardInfo.brand || "";
                          const last4 = cardInfo.last4 || "";
                          const expMonth = cardInfo.expMonth || "";
                          const expYear = cardInfo.expYear || "";
                          const expiration = expMonth && expYear ? `${expMonth}/${expYear}` : "";
                          const paymentDate = formatShortDate(payment.timestamp);
                          return (
                            <View key={`payment-${index}`} style={styles.infoRow}>
                              <View style={{ flex: 1 }}>
                                <Text style={[styles.infoLabel, { color: "#1F8A5B", width: "100%" }]}>
                                  Payment {index + 1}{paymentType ? ` (${paymentType})` : ""}
                                </Text>
                                <Text style={[styles.infoValue, { fontSize: 12, color: "#1F8A5B" }]}>{paymentDate}</Text>
                                {cardInfo && (brand || last4 || expiration) && (
                                  <Text style={[styles.infoValue, { fontSize: 12, color: "#5C6B68" }]}>
                                    {brand ? brand.charAt(0).toUpperCase() + brand.slice(1) : ""}
                                    {last4 ? ` •••• ${last4}` : ""}
                                    {expiration ? ` • ${expiration}` : ""}
                                  </Text>
                                )}
                              </View>
                              <Text style={[styles.infoValue, { color: "#1F8A5B", fontWeight: "500" }]}>
                                {formatDollarsAsUSDWithCents(centsToDollars(payment.amount || 0))}
                              </Text>
                            </View>
                          );
                        })}
                        {refunds.map((refund, index) => {
                          const refundDollars = refundToDollars(refund);
                          const pct = refund.refundPercent ?? refund.refundPercentage ?? 0;
                          const refundDate = formatShortDate(refund.timestamp ?? refund.createdAt);
                          return (
                            <View key={`refund-${index}`} style={styles.infoRow}>
                              <View style={{ flex: 1 }}>
                                <Text style={[styles.infoLabel, { color: "#C4453D", width: "100%" }]}>
                                  Refund {index + 1}{pct ? ` (${pct}%)` : ""}
                                </Text>
                                <Text style={[styles.infoValue, { fontSize: 12, color: "#C4453D" }]}>{refundDate}</Text>
                                {(refund.refundReason || refund.stripeRefundId) && (
                                  <Text style={[styles.infoValue, { fontSize: 12, color: "#8A9794" }]}>
                                    {refund.refundReason || (refund.stripeRefundId ? `Stripe ID: ${refund.stripeRefundId}` : "")}
                                  </Text>
                                )}
                              </View>
                              <Text style={[styles.infoValue, { color: "#C4453D", fontWeight: "500" }]}>
                                -{formatDollarsAsUSDWithCents(centsToDollars(Number(refund.amount ?? refund.refundAmount ?? 0)))}
                              </Text>
                            </View>
                          );
                        })}
                        <View style={[styles.infoRow, { marginTop: 12, paddingTop: 12, borderTopWidth: 1, borderTopColor: "#EDF1F0" }]}>
                          <Text style={[styles.infoLabel, { fontWeight: "bold", width: "100%" }]}>Total Paid:</Text>
                          <Text style={[styles.infoValue, { fontWeight: "bold", color: "#1F8A5B" }]}>${totalPaid.toFixed(2)}</Text>
                        </View>
                        {totalRefunded > 0 && (
                          <View style={styles.infoRow}>
                            <Text style={[styles.infoLabel, { fontWeight: "bold", color: "#5C6B68", width: "100%" }]}>Total Refunded:</Text>
                            <Text style={[styles.infoValue, { fontWeight: "bold", color: "#C4453D" }]}>-${totalRefunded.toFixed(2)}</Text>
                          </View>
                        )}
                      </>
                    ) : (
                      <>
                        <View style={styles.infoRow}>
                          <Text style={styles.infoLabel}>Manual Booking, no payment collected</Text>
                        </View>
                        {refunds.length > 0 && (
                          <>
                            {refunds.map((refund, index) => {
                              const refundDollars = refundToDollars(refund);
                              const pct = refund.refundPercent ?? refund.refundPercentage ?? 0;
                              const refundDate = formatShortDate(refund.timestamp ?? refund.createdAt);
                              return (
                                <View key={`refund-${index}`} style={styles.infoRow}>
                                  <View style={{ flex: 1 }}>
                                    <Text style={[styles.infoLabel, { color: "#C4453D", width: "100%" }]}>
                                      Refund {index + 1}{pct ? ` (${pct}%)` : ""}
                                    </Text>
                                    <Text style={[styles.infoValue, { fontSize: 12, color: "#C4453D" }]}>{refundDate}</Text>
                                  </View>
                                  <Text style={[styles.infoValue, { color: "#C4453D", fontWeight: "500" }]}>
                                    -{formatDollarsAsUSDWithCents(centsToDollars(Number(refund.amount ?? refund.refundAmount ?? 0)))}
                                  </Text>
                                </View>
                              );
                            })}
                            <View style={[styles.infoRow, { marginTop: 8 }]}>
                              <Text style={[styles.infoLabel, { fontWeight: "bold", color: "#5C6B68", width: "100%" }]}>Total Refunded:</Text>
                              <Text style={[styles.infoValue, { fontWeight: "bold", color: "#C4453D" }]}>-${totalRefunded.toFixed(2)}</Text>
                            </View>
                          </>
                        )}
                      </>
                    )}
                  </View>
                );
              })()}
            </View>
          )}

        {/* Refund Section Bounding Box */}
        {isAuthenticated && (
          <View style={{
            margin: 16,
            borderWidth: 2,
            borderColor: "#DDE3E2",
            borderRadius: 8,
            backgroundColor: "#F4F6F6",
            padding: 12,
            gap: 12,
          }}>

        {/* Custom Refund Entry — hidden when booking changes total > 0 (customer owes) */}
        {(newTotals?.totals?.finalTotal ?? 0) <= 0 && (() => {
          const overrideHasValue =
            (useRefundOverride && refundOverridePercent > 0) ||
            (useCustomAmount && parseFloat(customRefundAmount) > 0);
          return (
          <View style={[
            styles.dateEditorContainer,
            { marginTop: 0 },
            overrideHasValue && { backgroundColor: "#FBEAE9", borderColor: "#C4453D" },
          ]}>
            <View 
              style={[
                styles.dateEditorHeader,
                overrideHasValue
                  ? { backgroundColor: "#C4453D" }
                  : { backgroundColor: "#FDF3E3" },
              ]}
            >
              <Text style={[styles.dateEditorTitle, overrideHasValue && { color: "#FFFFFF" }]}>
                🔐 Custom Refund Entry
              </Text>
            </View>
            
              <View style={[styles.dateEditorContent, overrideHasValue && { backgroundColor: "#FBEAE9" }]}>
                {/* Refund Percentage Option */}
                <View style={styles.overrideCheckboxRow}>
                  <TouchableOpacity
                    style={styles.checkboxContainer}
                    onPress={() => {
                      setUseRefundOverride(!useRefundOverride);
                      if (!useRefundOverride) setUseCustomAmount(false);
                    }}
                  >
                    <View
                      style={[
                        styles.checkbox,
                        useRefundOverride && styles.checkboxChecked,
                      ]}
                    >
                      {useRefundOverride && (
                        <Text style={styles.checkboxCheckmark}>✓</Text>
                      )}
                    </View>
                    <Text style={styles.checkboxLabel}>
                      Use Custom Refund Percentage
                    </Text>
                  </TouchableOpacity>
                </View>
                
                {useRefundOverride && (
                  <View style={styles.refundPercentInputContainer}>
                    <Text style={styles.refundPercentLabel}>
                      Refund Percentage (0-100%):
                    </Text>
                    <TextInput
                      style={styles.refundPercentInput}
                      keyboardType="numeric"
                        value={refundOverridePercent > 0 ? refundOverridePercent.toString() : ""}
                      onChangeText={(text) => {
                        if (text === "" || text === "0") {
                          setRefundOverridePercent(0);
                          return;
                        }
                        const num = parseInt(text) || 0;
                        if (num >= 0 && num <= 100) {
                          setRefundOverridePercent(num);
                        }
                      }}
                        onFocus={() => {
                          setRefundOverridePercent(0);
                        }}
                        selectTextOnFocus
                        placeholder="0"
                        placeholderTextColor="#AEB8B6"
                    />
                    <Text style={styles.refundPercentHint}>
                      {getRefundOverridePercentHint()}
                    </Text>
                  </View>
                )}
                
                {/* Custom Amount Option */}
                <View style={[styles.overrideCheckboxRow, { marginTop: 16 }]}>
                  <TouchableOpacity
                    style={styles.checkboxContainer}
                    onPress={() => {
                      setUseCustomAmount(!useCustomAmount);
                      if (!useCustomAmount) setUseRefundOverride(false);
                    }}
                  >
                    <View
                      style={[
                        styles.checkbox,
                        useCustomAmount && styles.checkboxChecked,
                      ]}
                    >
                      {useCustomAmount && (
                        <Text style={styles.checkboxCheckmark}>✓</Text>
                      )}
                    </View>
                    <Text style={styles.checkboxLabel}>
                      Use Custom Refund Amount
                    </Text>
                  </TouchableOpacity>
                </View>
                
                {useCustomAmount && (
                  <View style={styles.refundPercentInputContainer}>
                    <Text style={styles.refundPercentLabel}>
                      Custom Refund Amount ($):
                    </Text>
                    <TextInput
                      style={styles.refundPercentInput}
                      keyboardType="decimal-pad"
                      value={customRefundAmount === "0.00" ? "" : customRefundAmount}
                      onChangeText={(text) => {
                        let cleaned = text.replace(/[^\d.]/g, "");

                        const dotIndex = cleaned.indexOf(".");
                        if (dotIndex !== -1) {
                          cleaned = cleaned.slice(0, dotIndex + 1) + cleaned.slice(dotIndex + 1).replace(/\./g, "");
                          if (cleaned.length > dotIndex + 3) {
                            cleaned = cleaned.slice(0, dotIndex + 3);
                          }
                        }

                        const numValue = parseFloat(cleaned) || 0;
                        const availableToRefund = getAvailableToRefundDollars();

                        if (numValue > availableToRefund) {
                          return;
                        }

                        setCustomRefundAmount(cleaned);
                      }}
                      onBlur={() => {
                        const num = parseFloat(customRefundAmount) || 0;
                        if (num > 0) {
                          setCustomRefundAmount(num.toFixed(2));
                        } else {
                          setCustomRefundAmount("0.00");
                        }
                      }}
                      placeholder="0.00"
                      placeholderTextColor="#AEB8B6"
                    />
                    <Text style={styles.refundPercentHint}>
                      {getCustomRefundAmountHint()}
                    </Text>
                  </View>
                )}
                
                {/* Issue Refund Only Button - hidden when there are booking changes */}
                  {!(hasChanges || (newTotals?.totals?.finalTotal !== 0)) && (useRefundOverride || useCustomAmount) && (() => {
                    const availableToRefund = getAvailableToRefundDollars();

                    const percentValid = useRefundOverride && refundOverridePercent > 0 && refundOverridePercent <= 100;
                    const amountValid = useCustomAmount && parseFloat(customRefundAmount) > 0 && parseFloat(customRefundAmount) <= availableToRefund;
                    const isValid = percentValid || amountValid;

                    return (
                      <TouchableOpacity dataSet={{ hover: "lift" }}
                        style={[styles.refundOnlyButton, !isValid && styles.buttonDisabled]}
                        onPress={handleIssueRefundOnly}
                        disabled={processing || !isValid}
                      >
                        {processing ? (
                          <ActivityIndicator color="#FFFFFF" />
                        ) : (
                          <Text style={styles.refundOnlyButtonText}>
                            💰 Issue Refund (No Booking Changes)
                          </Text>
                        )}
                      </TouchableOpacity>
                    );
                  })()}
              </View>
          </View>
          );
        })()}

        {/* Refund Component - Show if total is negative */}
        {newTotals?.totals && newTotals.totals.finalTotal < 0 && (() => {
          const adminOverrideActive =
            (useCustomAmount && parseFloat(customRefundAmount) > 0) ||
            (useRefundOverride && refundOverridePercent > 0);
          const hasBookingChanges = hasChanges || (newTotals?.totals?.finalTotal !== 0);
          const isDisabled = !hasBookingChanges && adminOverrideActive;

          const calculatedRefundCents = Math.abs(newTotals.totals.finalTotal);
          let displayRefundCents = calculatedRefundCents;
          let overrideLabel = null;

          if (hasBookingChanges && adminOverrideActive) {
            if (useCustomAmount) {
              displayRefundCents = Math.round(parseFloat(customRefundAmount) * 100);
              overrideLabel = "Admin Custom Amount";
            } else if (useRefundOverride) {
              displayRefundCents = Math.round(calculatedRefundCents * (refundOverridePercent / 100));
              overrideLabel = `Admin Override (${refundOverridePercent}% of owed refund)`;
            }
          }

          return (
          <View style={[styles.refundMethodContainer, { margin: 0 }, isDisabled && { opacity: 0.4 }]}>
            <Text style={styles.refundMethodTitle}>💰 Refund Information</Text>
            {isDisabled && (
              <Text style={{ color: "#C4453D", fontSize: 13, marginBottom: 8, fontStyle: "italic" }}>
                Disabled — Use the Custom Refund Entry button above to issue a refund without booking changes
              </Text>
            )}
            {hasBookingChanges && adminOverrideActive && overrideLabel && (
              <View style={{
                backgroundColor: "#FDF3E3",
                borderRadius: 5,
                padding: 10,
                marginBottom: 10,
                borderLeftWidth: 3,
                borderLeftColor: "#B4761B",
              }}>
                <Text style={{ fontSize: 13, color: "#7A4E12", fontWeight: "600" }}>
                  {overrideLabel} — overriding calculated refund of {formatCentsAsUSD(calculatedRefundCents)}
                </Text>
              </View>
            )}
            <Text style={styles.refundMethodSubtitle}>
              A refund of {formatCentsAsUSD(displayRefundCents)} will be processed to the customer's original payment method.
            </Text>
            <View style={styles.refundInfoBox}>
              <Text style={styles.refundInfoText}>
                • Refund will be processed within 5-10 business days
              </Text>
              <Text style={styles.refundInfoText}>
                • Customer will receive email and SMS confirmation
              </Text>
              <Text style={styles.refundInfoText}>
                • Refund amount: {formatCentsAsUSD(displayRefundCents)}
              </Text>
            </View>
            <TouchableOpacity dataSet={{ hover: "lift" }}
              style={[styles.button, styles.submitButton, { marginTop: 16 }, isDisabled && styles.buttonDisabled]}
              onPress={() => setShowRefundConfirmModal(true)}
              disabled={processing || isDisabled}
            >
              {processing ? (
                <ActivityIndicator color="#FFFFFF" />
              ) : (
                <Text style={styles.buttonText}>Change Booking and Process Refund</Text>
              )}
            </TouchableOpacity>
          </View>
          );
        })()}

          </View>
        )}

        {/* Payment Method Selection - Show if total is positive */}
        {newTotals?.totals && newTotals.totals.finalTotal > 0 && (
          <View style={styles.paymentMethodContainer}>
            <Text style={styles.paymentMethodTitle}>💳 Payment Method</Text>
            <Text style={styles.paymentMethodSubtitle}>
              Select how you'd like to pay for the {formatCentsAsUSD(newTotals.totals.finalTotal)} charge
            </Text>
            
            {/* Existing Card Option */}
            {renderExistingPaymentMethod()}
            
            {/* New Card Option */}
            <TouchableOpacity
              style={[
                styles.paymentMethodOption,
                paymentMethod === "new" && styles.paymentMethodOptionSelected,
              ]}
              onPress={() => setPaymentMethod("new")}
            >
              <View style={styles.radioButton}>
                {paymentMethod === "new" && (
                  <View style={styles.radioButtonInner} />
                )}
              </View>
              <View style={styles.paymentMethodDetails}>
                <Text style={styles.paymentMethodOptionTitle}>
                  Enter New Credit Card
                </Text>
                <Text style={styles.paymentMethodDescription}>
                  You'll be redirected to our secure payment portal
                </Text>
              </View>
            </TouchableOpacity>

            <TouchableOpacity dataSet={{ hover: "lift" }}
              style={[styles.button, styles.submitButton, { marginTop: 16 }]}
              onPress={() => setShowPaymentConfirmModal(true)}
              disabled={processing}
            >
              {processing ? (
                <ActivityIndicator color="#FFFFFF" />
              ) : (
                <Text style={styles.buttonText}>Change Booking & Process Payment</Text>
              )}
            </TouchableOpacity>
          </View>
        )}
        
        {/* Action Buttons */}
        {hasChanges && (
          <View style={styles.actionsContainer}>
            <TouchableOpacity dataSet={{ hover: "lift" }}
              style={[styles.button, styles.cancelButton]}
              onPress={() => navigate(-1)}
            >
              <Text style={styles.buttonText}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity dataSet={{ hover: "lift" }}
              style={[
                styles.button, 
                styles.submitButton,
                (deliveryNeeded || homePickupNeeded) &&
                  !deliveryAddress.trim() &&
                  styles.buttonDisabled,
              ]}
              onPress={handleProcessChanges}
              disabled={
                processing ||
                ((deliveryNeeded || homePickupNeeded) &&
                  !deliveryAddress.trim())
              }
            >
              {processing ? (
                <ActivityIndicator color="#FFFFFF" />
              ) : (
                <Text style={styles.buttonText}>
                    {hasChanges && paymentMethod === "new"
                    ? "Continue to Payment"
                    : "Process Changes"}
                </Text>
              )}
            </TouchableOpacity>
          </View>
        )}

        
          {/* Payment Methods Section - Show if no payments */}
          {originalBooking && (!originalBooking.payments || originalBooking.payments.length === 0) && (
            <View style={styles.paymentMethodsSection}>
              <Text style={styles.paymentMethodsSectionTitle}>💳 Payment Methods Used</Text>
              <View style={styles.paymentMethodsContent}>
                <View style={styles.infoRow}>
                  <Text style={styles.label}>Manual Booking, no payment collected</Text>
                </View>
                {isAuthenticated ? (
                  <View style={styles.paymentButtonsContainer}>
                    <TouchableOpacity dataSet={{ hover: "lift" }}
                      style={styles.collectButton}
                      onPress={async () => {
                        const totalAmountCents = originalBooking.totalAmount || 0;
                        if (totalAmountCents <= 0) {
                          alert("Error: Invalid payment amount");
                          return;
                        }
                        setProcessing(true);
                        try {
                          const { getFunctions, httpsCallable } = await import("firebase/functions");
                          const { app } = await import("../firebase.config");
                          const functions = getFunctions(app);
                          const createPaymentLink = httpsCallable(functions, "createPaymentLink");
                          const customerName = capitalizeWords(`${originalBooking.firstName || ""} ${originalBooking.lastName || ""}`.trim()) || "Customer";
                          const paymentResult = await createPaymentLink({
                            amountCents: totalAmountCents,
                            description: `Payment for Booking ${bookingId}`,
                            customerEmail: originalBooking.customerEmail || "",
                            customerPhone: originalBooking.customerPhone || "",
                            customerName: customerName,
                            saveCard: true,
                            bookingId: bookingId,
                            isBookingEdit: true,
                            skipBookingConfirmation: true,
                            testMode: useTestStripe,
                          });
                          if (!paymentResult.data?.url) {
                            throw new Error("Failed to create payment link");
                          }
                          navigate("/stripe-checkout", {
                            state: {
                              paymentUrl: paymentResult.data.url,
                              bookingId: bookingId,
                              referrer: `/edit-booking/${bookingId}`,
                            },
                          });
                          // Don't reset - modal stays visible during navigation
                        } catch (error) {
                          console.error("❌ Error creating payment link:", error);
                          setProcessing(false);
                          alert("Error: " + (error.message || "Failed to create payment link"));
                        }
                      }}
                    >
                      <Text style={styles.collectButtonText}>💳 Collect Payment</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={styles.sendLinkButton}
                      onPress={async () => {
                        const totalAmountCents = originalBooking.totalAmount || 0;
                        if (totalAmountCents <= 0) {
                          alert("Error: Invalid payment amount");
                          return;
                        }
                        if (!originalBooking.customerPhone) {
                          alert("Error: Customer phone number not available");
                          return;
                        }
                        setProcessing(true);
                        try {
                          const { getFunctions, httpsCallable } = await import("firebase/functions");
                          const { app } = await import("../firebase.config");
                          const functions = getFunctions(app);
                          const createPaymentLink = httpsCallable(functions, "createPaymentLink");
                          const customerName = capitalizeWords(`${originalBooking.firstName || ""} ${originalBooking.lastName || ""}`.trim()) || "Customer";
                          const paymentResult = await createPaymentLink({
                            amountCents: totalAmountCents,
                            description: `Payment for Booking ${bookingId}`,
                            customerEmail: originalBooking.customerEmail || "",
                            customerPhone: originalBooking.customerPhone || "",
                            customerName: customerName,
                            saveCard: true,
                            bookingId: bookingId,
                            isBookingEdit: true,
                            skipBookingConfirmation: true,
                            testMode: useTestStripe,
                          });
                          if (!paymentResult.data?.url) {
                            throw new Error("Failed to create payment link");
                          }
                          const sendPaymentLinkSms = httpsCallable(functions, "sendPaymentLinkSms");
                          await sendPaymentLinkSms({
                            bookingId: bookingId,
                            paymentUrl: paymentResult.data.url,
                            customerPhone: originalBooking.customerPhone,
                            customerName: customerName,
                          });
                          setProcessing(false);
                          alert("Payment link sent successfully!");
                        } catch (error) {
                          console.error("❌ Error sending payment link:", error);
                          setProcessing(false);
                          alert("Error: " + (error.message || "Failed to send payment link"));
                        }
                      }}
                    >
                      <Text style={styles.sendLinkButtonText}>📱 Send Text Invoice</Text>
                    </TouchableOpacity>
                  </View>
                ) : (
                  <View style={styles.paymentButtonsContainer}>
                    <TouchableOpacity
                      style={styles.payInvoiceButton}
                      onPress={async () => {
                        const totalAmountCents = originalBooking.totalAmount || 0;
                        if (totalAmountCents <= 0) {
                          alert("Error: Invalid payment amount");
                          return;
                        }
                        setProcessing(true);
                        try {
                          const { getFunctions, httpsCallable } = await import("firebase/functions");
                          const { app } = await import("../firebase.config");
                          const functions = getFunctions(app);
                          const createPaymentLink = httpsCallable(functions, "createPaymentLink");
                          const customerName = capitalizeWords(`${originalBooking.firstName || ""} ${originalBooking.lastName || ""}`.trim()) || "Customer";
                          const paymentResult = await createPaymentLink({
                            amountCents: totalAmountCents,
                            description: `Payment for Booking ${bookingId}`,
                            customerEmail: originalBooking.customerEmail || "",
                            customerPhone: originalBooking.customerPhone || "",
                            customerName: customerName,
                            saveCard: true,
                            bookingId: bookingId,
                            isBookingEdit: true,
                            skipBookingConfirmation: true,
                            testMode: useTestStripe,
                          });
                          if (!paymentResult.data?.url) {
                            throw new Error("Failed to create payment link");
                          }
                          navigate("/stripe-checkout", {
                            state: {
                              paymentUrl: paymentResult.data.url,
                              bookingId: bookingId,
                              referrer: `/edit-booking/${bookingId}`,
                            },
                          });
                          // Don't reset - modal stays visible during navigation
                        } catch (error) {
                          console.error("❌ Error creating payment link:", error);
                          setProcessing(false);
                          alert("Error: " + (error.message || "Failed to create payment link"));
                        }
                      }}
                    >
                      <Text style={styles.payInvoiceButtonText}>💳 Pay Invoice</Text>
                    </TouchableOpacity>
                  </View>
                )}
              </View>
            </View>
          )}

        {/* Cancel Entire Booking Button */}
        <View style={styles.cancelBookingContainer}>
          <TouchableOpacity dataSet={{ hover: "lift" }}
              style={[styles.cancelEntireBookingButton, (() => {
                // Check if button should be visually disabled for unauthorized users
                if (!isAuthenticated && (rentalHasStarted || isBookingEnded)) {
                  return styles.buttonDisabled;
                }
                if (!isAuthenticated && newTotals?.totals && newTotals.totals.finalTotal !== 0) {
                  return styles.buttonDisabled;
                }

                if (isAdmin && (useRefundOverride || useCustomAmount)) {
                  const availableToRefund = getAvailableToRefundDollars();
                  const percentValid = useRefundOverride && refundOverridePercent > 0 && refundOverridePercent <= 100;
                  const amountValid = useCustomAmount && parseFloat(customRefundAmount) > 0 && parseFloat(customRefundAmount) <= availableToRefund;
                  if (!(percentValid || amountValid)) return styles.buttonDisabled;
                }
                return null;
              })()]}
            onPress={handleCancelEntireBooking}
            disabled={
              processing ||
              (!isAuthenticated && (rentalHasStarted || isBookingEnded)) ||
              (!isAuthenticated && newTotals?.totals && newTotals.totals.finalTotal !== 0) ||
              (isAdmin && (useRefundOverride || useCustomAmount) && (() => {
                const availableToRefund = getAvailableToRefundDollars();
                const percentValid = useRefundOverride && refundOverridePercent > 0 && refundOverridePercent <= 100;
                const amountValid = useCustomAmount && parseFloat(customRefundAmount) > 0 && parseFloat(customRefundAmount) <= availableToRefund;
                return !(percentValid || amountValid);
              })())
            }
          >
            {processing ? (
              <ActivityIndicator color="#FFFFFF" />
            ) : (
              <Text style={styles.cancelEntireBookingText}>
                {isAdmin
                  ? "🚫 Cancel Entire Booking & Issue Refund"
                      : isAuthenticated
                        ? "🚫 Cancel Booking"
                  : "🚫 Request Booking Cancellation"}
              </Text>
            )}
          </TouchableOpacity>
          <Text style={styles.cancelWarningText}>
              {isAuthenticated
                ? "This will move the booking to Cancelled, issue a refund based on the cancellation policy unless you have entered a custom refund in the Custom Refund Entry section"
                : "We will send you a cancel booking link in your email to confirm your request. Refunds will be issued based on the cancellation policy listed above"}
          </Text>
            <TouchableOpacity
              style={{
                marginTop: 16,
                paddingVertical: 12,
                paddingHorizontal: 20,
                backgroundColor: "#B4761B",
                borderRadius: 6,
                alignSelf: "center",
              }}
              onPress={() => navigate(`/booking/${encodeURIComponent(booking?.bookingId || bookingId || "")}`)}
            >
              <Text style={{ color: "#FFFFFF", fontSize: 16, fontWeight: "600" }}>
                Return to View Booking
              </Text>
            </TouchableOpacity>
        </View>

        {/* Direct Edit link - authenticated users only */}
        {isAuthenticated && (bookingId || location.state?.editBookingId) && (
          <View style={styles.directEditLinkContainer}>
            <TouchableOpacity
              onPress={() => navigate(`/direct-edit-booking/${bookingId || location.state?.editBookingId}`)}
              style={styles.directEditLinkTouchable}
            >
              <Text style={styles.directEditLinkText}>Direct Edit Booking →</Text>
            </TouchableOpacity>
          </View>
        )}
      </ScrollView>
      </View>
      
      {/* Lock Dialog */}
      <Modal
        visible={showLockDialog}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setShowLockDialog(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>🔒 Editing Locked</Text>
            <Text style={styles.modalMessage}>{lockDialogMessage}</Text>
            <TouchableOpacity dataSet={{ hover: "lift" }}
              style={styles.modalButton}
              onPress={() => setShowLockDialog(false)}
            >
              <Text style={styles.modalButtonText}>OK</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      {/* Loading Modal for Payment Actions (Collect Payment / Send Invoice) */}
      <Modal
        visible={processing}
        transparent={true}
        animationType="fade"
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <ActivityIndicator size="large" color="#0E7C6B" />
            <Text style={styles.modalMessage}>
              {processingType === "refund" 
                ? "Processing refund..." 
                : processingType === "cancellation"
                  ? "Processing Refund & Cancellation"
                : "Processing payment..."}
            </Text>
          </View>
        </View>
      </Modal>

      {/* Invisible overlay to close tooltip when clicking outside */}
      {visibleTooltip && (
        <TouchableOpacity
          style={styles.tooltipOverlay}
          activeOpacity={1}
          onPress={() => setVisibleTooltip(null)}
        />
      )}

      {/* Full Screen Image Modal */}
      <Modal
        visible={imageModalVisible}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setImageModalVisible(false)}
      >
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setImageModalVisible(false)}
        >
          <View style={styles.modalImageContent}>
            <TouchableOpacity dataSet={{ hover: "lift" }}
              style={styles.closeButton}
              onPress={() => setImageModalVisible(false)}
            >
              <Text style={styles.closeButtonText}>✕</Text>
            </TouchableOpacity>
            {selectedImageUrl && (
              <img
                src={selectedImageUrl}
                alt="Full screen"
                style={styles.fullScreenImage}
              />
            )}
          </View>
        </TouchableOpacity>
      </Modal>

      {/* Discount Unlock Modal */}
      <Modal
        visible={discountUnlockModalVisible}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setDiscountUnlockModalVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.discountUnlockModalContent}>
            <Text style={styles.discountUnlockEmoji}>
              {unlockedDiscountType === "transportation" ? "🎉" : "⏰"}🎊✨
            </Text>
            <Text style={styles.discountUnlockTitle}>
              🎁 Extra Savings Unlocked! 🎁
            </Text>
            <Text style={styles.discountUnlockMessage}>
              Congratulations! You've unlocked a new {unlockedDiscountType === "transportation" ? "Transportation" : "Duration"} Discount!
            </Text>
            <View style={styles.discountUnlockDetails}>
              <Text style={styles.discountUnlockDiscountName}>
                {unlockedDiscountName}
              </Text>
              <Text style={styles.discountUnlockPercent}>
                {unlockedDiscountPercent}% OFF
              </Text>
            </View>
            <Text style={styles.discountUnlockSubtext}>
              Your order total has been automatically updated with these extra savings! 💰
            </Text>
            <TouchableOpacity dataSet={{ hover: "lift" }}
              style={styles.discountUnlockButton}
              onPress={() => setDiscountUnlockModalVisible(false)}
            >
              <Text style={styles.discountUnlockButtonText}>Awesome! 🚀</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      {/* Cancel Booking Confirmation Modal */}
      <Modal
        visible={cancelBookingModalVisible}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setCancelBookingModalVisible(false)}
      >
        <View style={styles.modalOverlay}>
          {cancelBookingData && (
            <View style={[
              styles.section,
              {
                backgroundColor: "#FFFFFF",
                borderWidth: 2,
                borderColor: "#C4453D",
                borderRadius: 8,
                padding: 16,
                marginHorizontal: 16,
                maxWidth: 600,
                width: "90%",
              }
            ]}>
              <Text style={[styles.sectionTitle, { color: "#C4453D", marginBottom: 16 }]}>
                ⚠️ Cancel Entire Booking?
              </Text>

              {/* Customer Info */}
              <View style={styles.summaryRow}>
                <Text style={styles.summaryLabel}>Customer:</Text>
                <Text style={styles.summaryValue}>{cancelBookingData.customerName}</Text>
              </View>

              {/* Financial Summary */}
              <View style={styles.summaryItemRow}>
                <Text style={styles.summaryItemText}>Total Paid:</Text>
                <Text style={styles.summaryItemPrice}>
                  {formatDollarsAsUSDWithCents(centsToDollars(cancelBookingData.totalPaidCents))}
                </Text>
              </View>

              <View style={styles.summaryItemRow}>
                <Text style={styles.summaryItemText}>Already Refunded:</Text>
                <Text style={styles.summaryItemPrice}>
                  {formatDollarsAsUSDWithCents(centsToDollars(cancelBookingData.totalRefundedCents))}
                </Text>
              </View>

              <View style={styles.summaryItemRow}>
                <Text style={styles.summaryItemText}>Available to Refund:</Text>
                <Text style={styles.summaryItemPrice}>
                  {formatDollarsAsUSDWithCents(centsToDollars(cancelBookingData.availableToRefundCents))}
                </Text>
              </View>

              <View style={styles.summaryDivider} />

              {/* Cancellation Penalty - Always show */}
              <View style={styles.summaryItemRow}>
                <Text style={styles.summaryItemText}>
                  Cancellation Penalty{cancelBookingData.policyPenaltyPercent > 0 ? ` (${cancelBookingData.policyPenaltyPercent}%)` : ''}:
                </Text>
                <Text style={[
                  styles.summaryItemPrice,
                  { color: cancelBookingData.cancellationPenaltyCents > 0 ? "#C4453D" : "#5C6B68" }
                ]}>
                  {formatDollarsAsUSDWithCents(centsToDollars(cancelBookingData.cancellationPenaltyCents))}
                </Text>
              </View>

              {/* Cancellation Policy Text - Show only if penalty > 0 */}
              {cancelBookingData.cancellationPenaltyCents > 0 && cancellationPolicy && (
                <View style={[styles.cancellationPolicyContainer, { margin: 0, marginTop: 12, marginBottom: 12 }]}>
                  <Text style={styles.cancellationPolicyTitle}>
                    📋 Cancellation Policy
                  </Text>
                  <Text style={styles.cancellationPolicyText}>
                    {cancellationPolicy}
                  </Text>
                </View>
              )}

              <View style={styles.summaryDivider} />

              {/* Refund Amount */}
              <View style={styles.summaryItemRow}>
                <Text style={[styles.summaryItemText, { fontWeight: "600", fontSize: 15 }]}>
                  Refund Amount{cancelBookingData.useCustomAmount
                    ? " (Custom)"
                    : cancelBookingData.useRefundOverride
                      ? ` (${cancelBookingData.refundPercent}%)`
                      : " (Policy-based)"}:
                </Text>
                <Text style={[styles.summaryItemPrice, { fontWeight: "600", fontSize: 15, color: "#1F8A5B" }]}>
                  {formatDollarsAsUSDWithCents(centsToDollars(cancelBookingData.refundAmountCents))}
                </Text>
              </View>

              <View style={styles.summaryDivider} />

              {/* Action List */}
              <View style={{ marginTop: 12, marginBottom: 16 }}>
                <Text style={[styles.summarySectionTitle, { marginBottom: 8 }]}>This will:</Text>
                <Text style={[styles.summaryItemText, { marginBottom: 4 }]}>• Cancel booking</Text>
                {cancelBookingData.refundAmountCents > 0 ? (
                  <Text style={[styles.summaryItemText, { marginBottom: 4 }]}>
                    • Issue {cancelBookingData.useCustomAmount
                      ? "custom amount"
                      : cancelBookingData.useRefundOverride
                        ? `${cancelBookingData.refundPercent}%`
                        : "policy-based"} refund
                  </Text>
                ) : (
                  <Text style={[styles.summaryItemText, { marginBottom: 4, color: "#C4453D" }]}>
                    • NO refund will be issued
                  </Text>
                )}
                <Text style={[styles.summaryItemText, { marginBottom: 4 }]}>• Remove booking from system</Text>
                <Text style={[styles.summaryItemText, { marginBottom: 4 }]}>• Send confirmation email & SMS</Text>
              </View>

              <View style={styles.summaryDivider} />

              <Text style={[styles.summaryItemText, { color: "#C4453D", fontWeight: "600", textAlign: "center", marginBottom: 16 }]}>
                ⚠️ This action CANNOT be undone!
              </Text>

              {/* Cancel Button */}
              <TouchableOpacity dataSet={{ hover: "lift" }}
                style={[
                  styles.modalButton,
                  {
                    backgroundColor: "#5C6B68",
                    paddingVertical: 12,
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: 16,
                  }
                ]}
                onPress={() => {
                  setCancelBookingModalVisible(false);
                  setCancelBookingData(null);
                  setSliderPosition(0);
                }}
              >
                <Text style={[styles.modalButtonText, { textAlign: "center" }]}>Cancel</Text>
              </TouchableOpacity>

              {/* Slider to Confirm Cancellation */}
              <View style={styles.sliderContainer}>
                <View
                  ref={sliderTrackRef}
                  style={styles.sliderTrack}
                  onTouchStart={(e) => {
                    if (sliderTrackRef.current) {
                      const rect = sliderTrackRef.current.getBoundingClientRect();
                      const touchX = e.nativeEvent.touches[0].pageX - rect.left;
                      const newPosition = Math.max(0, Math.min(220, touchX - 25));
                      setSliderPosition(newPosition);
                    }
                  }}
                  onTouchMove={(e) => {
                    if (sliderTrackRef.current) {
                      const rect = sliderTrackRef.current.getBoundingClientRect();
                      const touchX = e.nativeEvent.touches[0].pageX - rect.left;
                      const newPosition = Math.max(0, Math.min(220, touchX - 25));
                      setSliderPosition(newPosition);
                    }
                  }}
                  onTouchEnd={() => {
                    if (sliderPosition >= 200) {
                      // Fully slid - trigger cancellation
                      handleConfirmCancelBooking();
                    } else {
                      // Not fully slid - reset
                      setSliderPosition(0);
                    }
                  }}
                >
                  <View
                    style={[
                      styles.sliderFill,
                      { width: `${Math.min(100, (sliderPosition / 220) * 100)}%` }
                    ]}
                  />
                  <View
                    style={[
                      styles.sliderThumb,
                      { left: sliderPosition }
                    ]}
                  >
                    <Text style={styles.sliderThumbText}>→</Text>
                  </View>
                </View>
                <Text style={styles.sliderText}>
                  {sliderPosition >= 200
                    ? "Release to confirm cancellation"
                    : "Slide to confirm cancellation"}
                </Text>
              </View>
            </View>
          )}
        </View>
      </Modal>

      {/* Authenticated User Cancel Booking Modal */}
      <Modal
        visible={authCancelModalVisible}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setAuthCancelModalVisible(false)}
      >
        <View style={styles.modalOverlay}>
          {authCancelData && (
            <View style={{
              backgroundColor: "#FFFFFF",
              borderRadius: 12,
              padding: 24,
              marginHorizontal: 16,
              maxWidth: 500,
              width: "90%",
              boxShadow: "0 4px 12px rgba(15, 26, 25, 0.1), 0 28px 64px rgba(15, 26, 25, 0.18)",
              elevation: 8,
            }}>
              {/* Header */}
              <View style={{ alignItems: "center", marginBottom: 20 }}>
                <View style={{
                  width: 56,
                  height: 56,
                  borderRadius: 28,
                  backgroundColor: "#FBEAE9",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: 12,
                }}>
                  <Text style={{ fontSize: 28 }}>⚠️</Text>
                </View>
                <Text style={{ fontSize: 20, fontWeight: "700", color: "#C4453D", textAlign: "center" }}>
                  Cancel Booking?
                </Text>
                <Text style={{ fontSize: 14, color: "#5C6B68", textAlign: "center", marginTop: 6 }}>
                  This action cannot be undone
                </Text>
              </View>

              {/* Customer & Booking Info */}
              <View style={{
                backgroundColor: "#F4F6F6",
                borderRadius: 6,
                padding: 14,
                marginBottom: 16,
              }}>
                <View style={{ flexDirection: "row", justifyContent: "space-between", marginBottom: 6 }}>
                  <Text style={{ fontSize: 13, color: "#5C6B68" }}>Customer</Text>
                  <Text style={{ fontSize: 13, fontWeight: "600", color: "#2E3A38" }}>{authCancelData.customerName}</Text>
                </View>
                <View style={{ flexDirection: "row", justifyContent: "space-between" }}>
                  <Text style={{ fontSize: 13, color: "#5C6B68" }}>Booking ID</Text>
                  <Text style={{ fontSize: 13, fontWeight: "600", color: "#2E3A38" }}>{authCancelData.bookingId}</Text>
                </View>
              </View>

              {/* Financial Breakdown */}
              <View style={{
                borderWidth: 1,
                borderColor: "#F4F6F6",
                borderRadius: 6,
                padding: 14,
                marginBottom: 16,
              }}>
                <Text style={{ fontSize: 14, fontWeight: "600", color: "#2E3A38", marginBottom: 10 }}>
                  Financial Summary
                </Text>

                <View style={{ flexDirection: "row", justifyContent: "space-between", marginBottom: 6 }}>
                  <Text style={{ fontSize: 13, color: "#5C6B68" }}>Total Paid</Text>
                  <Text style={{ fontSize: 13, fontWeight: "500", color: "#2E3A38" }}>
                    {formatDollarsAsUSDWithCents(centsToDollars(authCancelData.totalPaidCents))}
                  </Text>
                </View>

                {authCancelData.totalRefundedCents > 0 && (
                  <View style={{ flexDirection: "row", justifyContent: "space-between", marginBottom: 6 }}>
                    <Text style={{ fontSize: 13, color: "#5C6B68" }}>Already Refunded</Text>
                    <Text style={{ fontSize: 13, fontWeight: "500", color: "#B4761B" }}>
                      -{formatDollarsAsUSDWithCents(centsToDollars(authCancelData.totalRefundedCents))}
                    </Text>
                  </View>
                )}

                {authCancelData.policyPenaltyPercent > 0 && (
                  <View style={{ flexDirection: "row", justifyContent: "space-between", marginBottom: 6 }}>
                    <Text style={{ fontSize: 13, color: "#5C6B68" }}>
                      Cancellation Penalty ({authCancelData.policyPenaltyPercent}%)
                    </Text>
                    <Text style={{ fontSize: 13, fontWeight: "500", color: "#C4453D" }}>
                      -{formatDollarsAsUSDWithCents(centsToDollars(authCancelData.policyPenaltyCents))}
                    </Text>
                  </View>
                )}

                <View style={{
                  borderTopWidth: 1,
                  borderTopColor: "#F4F6F6",
                  marginTop: 8,
                  paddingTop: 10,
                  flexDirection: "row",
                  justifyContent: "space-between",
                }}>
                  <Text style={{ fontSize: 15, fontWeight: "700", color: "#2E3A38" }}>Refund Amount</Text>
                  <Text style={{ fontSize: 15, fontWeight: "700", color: "#1F8A5B" }}>
                    {formatDollarsAsUSDWithCents(centsToDollars(authCancelData.refundAmountCents))}
                  </Text>
                </View>
              </View>

              {/* What Will Happen */}
              <View style={{
                backgroundColor: "#FDF3E3",
                borderRadius: 6,
                padding: 14,
                marginBottom: 20,
                borderLeftWidth: 3,
                borderLeftColor: "#B4761B",
              }}>
                <Text style={{ fontSize: 13, fontWeight: "600", color: "#5C6B68", marginBottom: 8 }}>
                  What will happen:
                </Text>
                <Text style={{ fontSize: 13, color: "#5C6B68", marginBottom: 4 }}>
                  • Your booking will be cancelled
                </Text>
                {authCancelData.refundAmountCents > 0 ? (
                  <Text style={{ fontSize: 13, color: "#5C6B68", marginBottom: 4 }}>
                    • A {authCancelData.policyRefundPercent}% refund will be issued
                  </Text>
                ) : (
                  <Text style={{ fontSize: 13, color: "#C4453D", marginBottom: 4 }}>
                    • No refund will be issued
                  </Text>
                )}
                <Text style={{ fontSize: 13, color: "#5C6B68", marginBottom: 4 }}>
                  • Booking will be removed from the system
                </Text>
                <Text style={{ fontSize: 13, color: "#5C6B68" }}>
                  • Confirmation sent via email & SMS
                </Text>
              </View>

              {/* Buttons */}
              <TouchableOpacity
                style={{
                  backgroundColor: "#C4453D",
                  borderRadius: 6,
                  paddingVertical: 14,
                  alignItems: "center",
                  marginBottom: 10,
                }}
                onPress={handleConfirmAuthCancel}
                disabled={processing}
              >
                {processing ? (
                  <ActivityIndicator color="#FFFFFF" />
                ) : (
                  <Text style={{ color: "#FFFFFF", fontSize: 16, fontWeight: "700" }}>
                    Confirm Cancellation
                  </Text>
                )}
              </TouchableOpacity>

              <TouchableOpacity
                style={{
                  backgroundColor: "#F4F6F6",
                  borderRadius: 6,
                  borderWidth: 1,
                  borderColor: "#DDE3E2",
                  paddingVertical: 14,
                  alignItems: "center",
                }}
                onPress={() => {
                  setAuthCancelModalVisible(false);
                  setAuthCancelData(null);
                }}
              >
                <Text style={{ color: "#5C6B68", fontSize: 16, fontWeight: "600" }}>
                  Go Back
                </Text>
              </TouchableOpacity>
            </View>
          )}
        </View>
      </Modal>

      {/* Refund Confirmation Modal */}
      <Modal
        visible={showRefundConfirmModal}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setShowRefundConfirmModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={[styles.section, {
            backgroundColor: "#FFFFFF",
            borderWidth: 2,
            borderColor: "#B4761B",
            borderRadius: 8,
            padding: 20,
            marginHorizontal: 16,
            maxWidth: 600,
            width: "90%",
          }]}>
            <Text style={[styles.sectionTitle, { color: "#7A4E12", marginBottom: 16 }]}>
              💰 Confirm Refund
            </Text>
            {(() => {
              const overrideActive =
                (useCustomAmount && parseFloat(customRefundAmount) > 0) ||
                (useRefundOverride && refundOverridePercent > 0);
              const modalCalculatedRefund = Math.abs(newTotals?.totals?.finalTotal || 0);
              const modalHasBookingChanges = hasChanges || (newTotals?.totals?.finalTotal !== 0);
              let modalRefundCents;
              if (overrideActive) {
                if (useCustomAmount) {
                  modalRefundCents = Math.round(parseFloat(customRefundAmount) * 100);
                } else if (modalHasBookingChanges) {
                  modalRefundCents = Math.round(modalCalculatedRefund * (refundOverridePercent / 100));
                } else {
                  modalRefundCents = Math.round(getAvailableToRefundDollars() * (refundOverridePercent / 100) * 100);
                }
              } else {
                modalRefundCents = modalCalculatedRefund;
              }
              return (
                <>
                  <Text style={[styles.summaryLabel, { fontSize: 18, fontWeight: "700" }]}>
                    Refund Amount: <Text style={{ color: "#C4453D" }}>{formatCentsAsUSD(modalRefundCents)}</Text>
                  </Text>
                  {overrideActive && (
                    <Text style={[styles.summaryLabel, { marginTop: 6, fontSize: 13, color: "#B4761B", fontStyle: "italic" }]}>
                      (Admin Override — calculated refund was {formatCentsAsUSD(Math.abs(newTotals?.totals?.finalTotal || 0))})
                    </Text>
                  )}
                  {(() => {
                    const paymentWithCard = booking?.payments?.find((p) => p.cardInfo);
                    const cardInfo = paymentWithCard?.cardInfo;
                    const cardBrand = cardInfo?.brand ? cardInfo.brand.charAt(0).toUpperCase() + cardInfo.brand.slice(1) : null;
                    const cardLast4 = cardInfo?.last4 || null;
                    const cardDisplay = cardBrand && cardLast4 ? `${cardBrand} •••• ${cardLast4}` : cardLast4 ? `•••• ${cardLast4}` : null;
                    return cardDisplay ? (
                      <Text style={[styles.summaryLabel, { marginTop: 8, fontSize: 14, color: "#2E3A38" }]}>
                        Refund to: {cardDisplay}
                      </Text>
                    ) : null;
                  })()}
                </>
              );
            })()}
            <Text style={[styles.summaryLabel, { marginTop: 12, fontSize: 14, color: "#5C6B68" }]}>
              This will process a refund and update the booking. The customer will receive email and SMS confirmation.
            </Text>
            <View style={styles.actionsContainer}>
              <TouchableOpacity dataSet={{ hover: "lift" }}
                style={[styles.button, styles.cancelButton]}
                onPress={() => setShowRefundConfirmModal(false)}
              >
                <Text style={styles.buttonText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity dataSet={{ hover: "lift" }}
                style={[styles.button, styles.submitButton]}
                onPress={handleProcessRefund}
                disabled={processing}
              >
                {processing ? (
                  <ActivityIndicator color="#FFFFFF" />
                ) : (
                  <Text style={styles.buttonText}>Confirm Refund</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Refund Only Confirmation Modal */}
      <Modal
        visible={showRefundOnlyModal}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setShowRefundOnlyModal(false)}
      >
        <View style={styles.modalOverlay}>
          {refundOnlyData && (
            <View style={{
              backgroundColor: "#FFFFFF",
              borderRadius: 12,
              padding: 24,
              marginHorizontal: 16,
              maxWidth: 500,
              width: "90%",
              boxShadow: "0 4px 12px rgba(15, 26, 25, 0.1), 0 28px 64px rgba(15, 26, 25, 0.18)",
              elevation: 8,
            }}>
              {/* Header */}
              <View style={{ alignItems: "center", marginBottom: 20 }}>
                <View style={{
                  width: 56,
                  height: 56,
                  borderRadius: 28,
                  backgroundColor: "#E4F4EC",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: 12,
                }}>
                  <Text style={{ fontSize: 28 }}>💰</Text>
                </View>
                <Text style={{ fontSize: 20, fontWeight: "700", color: "#2E3A38", textAlign: "center" }}>
                  Issue Refund
                </Text>
                <Text style={{ fontSize: 14, color: "#5C6B68", textAlign: "center", marginTop: 6 }}>
                  No booking changes will be made
                </Text>
              </View>

              {/* Financial Details */}
              <View style={{
                borderWidth: 1,
                borderColor: "#F4F6F6",
                borderRadius: 6,
                padding: 14,
                marginBottom: 16,
              }}>
                <View style={{ flexDirection: "row", justifyContent: "space-between", marginBottom: 8 }}>
                  <Text style={{ fontSize: 13, color: "#5C6B68" }}>Available to Refund</Text>
                  <Text style={{ fontSize: 13, fontWeight: "500", color: "#2E3A38" }}>
                    ${refundOnlyData.availableToRefund.toFixed(2)}
                  </Text>
                </View>

                <View style={{ flexDirection: "row", justifyContent: "space-between", marginBottom: 8 }}>
                  <Text style={{ fontSize: 13, color: "#5C6B68" }}>Method</Text>
                  <Text style={{ fontSize: 13, fontWeight: "500", color: "#5C6B68" }}>
                    {refundOnlyData.refundDescription}
                  </Text>
                </View>

                {refundOnlyData.cardDisplay != null && (
                  <View style={{ flexDirection: "row", justifyContent: "space-between", marginBottom: 8 }}>
                    <Text style={{ fontSize: 13, color: "#5C6B68" }}>Refund to</Text>
                    <Text style={{ fontSize: 13, fontWeight: "500", color: "#2E3A38" }}>
                      {refundOnlyData.cardDisplay}
                    </Text>
                  </View>
                )}

                <View style={{
                  borderTopWidth: 1,
                  borderTopColor: "#F4F6F6",
                  marginTop: 4,
                  paddingTop: 10,
                  flexDirection: "row",
                  justifyContent: "space-between",
                }}>
                  <Text style={{ fontSize: 16, fontWeight: "700", color: "#2E3A38" }}>Refund Amount</Text>
                  <Text style={{ fontSize: 16, fontWeight: "700", color: "#1F8A5B" }}>
                    ${refundOnlyData.refundAmount.toFixed(2)}
                  </Text>
                </View>
              </View>

              {/* Info Note */}
              <View style={{
                backgroundColor: "#FDF3E3",
                borderRadius: 6,
                padding: 12,
                marginBottom: 20,
                borderLeftWidth: 3,
                borderLeftColor: "#B4761B",
              }}>
                <Text style={{ fontSize: 13, color: "#5C6B68" }}>
                  The customer will be refunded but the booking will remain active. They will still have access to their rental.
                </Text>
              </View>

              {/* Buttons */}
              <TouchableOpacity
                style={{
                  backgroundColor: "#1F8A5B",
                  borderRadius: 6,
                  paddingVertical: 14,
                  alignItems: "center",
                  marginBottom: 10,
                }}
                onPress={handleConfirmRefundOnly}
                disabled={processing}
              >
                {processing ? (
                  <ActivityIndicator color="#FFFFFF" />
                ) : (
                  <Text style={{ color: "#FFFFFF", fontSize: 16, fontWeight: "700" }}>
                    Confirm Refund
                  </Text>
                )}
              </TouchableOpacity>

              <TouchableOpacity
                style={{
                  backgroundColor: "#F4F6F6",
                  borderRadius: 6,
                  borderWidth: 1,
                  borderColor: "#DDE3E2",
                  paddingVertical: 14,
                  alignItems: "center",
                }}
                onPress={() => {
                  setShowRefundOnlyModal(false);
                  setRefundOnlyData(null);
                }}
              >
                <Text style={{ color: "#5C6B68", fontSize: 16, fontWeight: "600" }}>
                  Cancel
                </Text>
              </TouchableOpacity>
            </View>
          )}
        </View>
      </Modal>

      {/* Payment Confirmation Modal */}
      <Modal
        visible={showPaymentConfirmModal}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setShowPaymentConfirmModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={[styles.section, {
            backgroundColor: "#FFFFFF",
            borderWidth: 2,
            borderColor: "#0E7C6B",
            borderRadius: 8,
            padding: 20,
            marginHorizontal: 16,
            maxWidth: 600,
            width: "90%",
          }]}>
            <Text style={[styles.sectionTitle, { color: "#0E7C6B", marginBottom: 16 }]}>
              💳 Confirm Payment
            </Text>
            <Text style={styles.summaryLabel}>
              Charge Amount: {formatCentsAsUSD(newTotals?.totals?.finalTotal || 0)}
            </Text>
            <Text style={[styles.summaryLabel, { marginTop: 12, fontSize: 14, color: "#5C6B68" }]}>
              Select a payment method to proceed:
            </Text>
            <TouchableOpacity dataSet={{ hover: "lift" }}
              style={[styles.button, styles.submitButton, { marginTop: 16 }]}
              onPress={() => {
                setShowPaymentConfirmModal(false);
                setPaymentMethod("existing");
                handleProcessPayment();
              }}
            >
              <Text style={styles.buttonText}>Use Saved Card</Text>
            </TouchableOpacity>
            <TouchableOpacity dataSet={{ hover: "lift" }}
              style={[styles.button, styles.submitButton, { marginTop: 8 }]}
              onPress={() => {
                setShowPaymentConfirmModal(false);
                setPaymentMethod("new");
                handleProcessPayment();
              }}
            >
              <Text style={styles.buttonText}>Use New Card</Text>
            </TouchableOpacity>
            <TouchableOpacity dataSet={{ hover: "lift" }}
              style={[styles.button, styles.cancelButton, { marginTop: 8 }]}
              onPress={() => setShowPaymentConfirmModal(false)}
            >
              <Text style={styles.buttonText}>Cancel</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      {/* Cancellation Request Modal (for unauthenticated users) */}
      <Modal
        visible={showCancellationRequestModal}
        transparent={true}
        animationType="fade"
        onRequestClose={() => {
          if (!processing) {
            setShowCancellationRequestModal(false);
            setCancellationRequestData(null);
          }
        }}
      >
        <View style={styles.modalOverlay}>
          {cancellationRequestData && (
            <View style={[styles.section, {
              backgroundColor: "#FFFFFF",
              borderWidth: 2,
              borderColor: "#C4453D",
              borderRadius: 8,
              padding: 24,
              marginHorizontal: 16,
              maxWidth: 500,
              width: "90%",
            }]}>
              <Text style={[styles.sectionTitle, { color: "#C4453D", marginBottom: 16, fontSize: 20 }]}>
                ⚠️ REQUEST BOOKING CANCELLATION?
              </Text>

              <View style={{ marginBottom: 16 }}>
                <Text style={[styles.summaryLabel, { marginBottom: 8 }]}>
                  Customer: {cancellationRequestData.customerName}
                </Text>
                <Text style={[styles.summaryLabel, { marginBottom: 16 }]}>
                  Total Paid: ${cancellationRequestData.totalPaid.toFixed(2)}
                </Text>

                <Text style={[styles.summaryLabel, { marginTop: 12, fontSize: 14, color: "#2E3A38", lineHeight: 20 }]}>
                  This will send a cancellation confirmation email.
                </Text>
                <Text style={[styles.summaryLabel, { marginTop: 8, fontSize: 14, color: "#2E3A38", lineHeight: 20 }]}>
                  You must click the link in the email to complete the cancellation.
                </Text>
              </View>

              <View style={styles.actionsContainer}>
                <TouchableOpacity dataSet={{ hover: "lift" }}
                  style={[styles.button, styles.cancelButton]}
                  onPress={() => {
                    setShowCancellationRequestModal(false);
                    setCancellationRequestData(null);
                  }}
                  disabled={processing}
                >
                  <Text style={styles.buttonText}>Cancel</Text>
                </TouchableOpacity>
                <TouchableOpacity dataSet={{ hover: "lift" }}
                  style={[styles.button, styles.submitButton, { backgroundColor: "#C4453D" }]}
                  onPress={handleConfirmCancellationRequest}
                  disabled={processing}
                >
                  {processing ? (
                    <ActivityIndicator color="#FFFFFF" />
                  ) : (
                    <Text style={styles.buttonText}>Continue</Text>
                  )}
                </TouchableOpacity>
              </View>
            </View>
          )}
        </View>
      </Modal>


      {/* Lower Transportation Discount Tier Modal */}
      <Modal
        visible={showLowerTransportationTierModal}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setShowLowerTransportationTierModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={[styles.section, {
            backgroundColor: "#FDF3E3",
            borderWidth: 2,
            borderColor: "#B4761B",
            borderRadius: 12,
            padding: 24,
            marginHorizontal: 16,
            maxWidth: 600,
            width: "90%",
          }]}>
            <Text style={[styles.sectionTitle, { color: "#7A4E12", marginBottom: 16, fontSize: 20 }]}>
              ⚠️ Transportation Discount Tier Changed
            </Text>

            <View style={{ marginBottom: 16 }}>
              <Text style={[styles.summaryLabel, { fontWeight: "600", marginBottom: 8 }]}>
                Previous Tier:
              </Text>
              <Text style={[styles.summaryLabel, { color: "#7A4E12", marginBottom: 16 }]}>
                {lowerTransportationTierData.oldName || "No discount"}
              </Text>

              <Text style={[styles.summaryLabel, { fontWeight: "600", marginBottom: 8 }]}>
                New Tier:
              </Text>
              <Text style={[styles.summaryLabel, { color: "#7A4E12", marginBottom: 16 }]}>
                {lowerTransportationTierData.newName || "No discount"}
              </Text>
            </View>

            <View style={{
              backgroundColor: "#FFFFFF",
              padding: 16,
              borderRadius: 6,
              marginBottom: 16,
            }}>
              <Text style={[styles.summaryLabel, { color: "#7A4E12", lineHeight: 22 }]}>
                You have lowered the total price of the booking and now achieved a lower transportation discount tier. Since the discount tier is now lower, you are not entitled to the same discount for transportation. The system has taken that into account and adjusted the totals accordingly, to reflect your new transportation discount tier across the ENTIRE booking.
              </Text>
            </View>

            <TouchableOpacity dataSet={{ hover: "lift" }}
              style={[styles.modalButton, {
                backgroundColor: "#B4761B",
                paddingVertical: 14,
                alignItems: "center",
                justifyContent: "center",
              }]}
              onPress={() => setShowLowerTransportationTierModal(false)}
            >
              <Text style={[styles.modalButtonText, { color: "#7A4E12", fontWeight: "600" }]}>
                Got it
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      {/* Lower Duration Discount Tier Modal */}
      <Modal
        visible={showLowerDurationTierModal}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setShowLowerDurationTierModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={[styles.section, {
            backgroundColor: "#FDF3E3",
            borderWidth: 2,
            borderColor: "#B4761B",
            borderRadius: 12,
            padding: 24,
            marginHorizontal: 16,
            maxWidth: 600,
            width: "90%",
          }]}>
            <Text style={[styles.sectionTitle, { color: "#7A4E12", marginBottom: 16, fontSize: 20 }]}>
              ⚠️ Duration Discount Tier Changed
            </Text>

            <View style={{ marginBottom: 16 }}>
              <Text style={[styles.summaryLabel, { fontWeight: "600", marginBottom: 8 }]}>
                Previous Tier:
              </Text>
              <Text style={[styles.summaryLabel, { color: "#7A4E12", marginBottom: 16 }]}>
                {lowerDurationTierData.oldName || "No discount"}
              </Text>

              <Text style={[styles.summaryLabel, { fontWeight: "600", marginBottom: 8 }]}>
                New Tier:
              </Text>
              <Text style={[styles.summaryLabel, { color: "#7A4E12", marginBottom: 16 }]}>
                {lowerDurationTierData.newName || "No discount"}
              </Text>
            </View>

            <View style={{
              backgroundColor: "#FFFFFF",
              padding: 16,
              borderRadius: 6,
              marginBottom: 16,
            }}>
              <Text style={[styles.summaryLabel, { color: "#7A4E12", lineHeight: 22 }]}>
                You have shortened your rental duration, resulting in a lower rental booking duration discount tier. The system will update the original booking to use the new LOWER discount tier (you will see "added" charges in the order summary, however these are not actually added charges, just a reflection of the monetary difference in the 2 discounts).
              </Text>
            </View>

            <TouchableOpacity dataSet={{ hover: "lift" }}
              style={[styles.modalButton, {
                backgroundColor: "#B4761B",
                paddingVertical: 14,
                alignItems: "center",
                justifyContent: "center",
              }]}
              onPress={() => setShowLowerDurationTierModal(false)}
            >
              <Text style={[styles.modalButtonText, { color: "#7A4E12", fontWeight: "600" }]}>
                Got it
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      {/* Address Validation Modal with Google Maps */}
      <Modal
        visible={showAddressValidationModal}
        transparent={true}
        animationType="slide"
        onRequestClose={handleWrongAddress}
      >
        <View style={styles.addressValidationModalOverlay}>
          <View style={styles.addressValidationModalContent}>
            <View style={styles.addressValidationMapContainer}>
              {mapLoading ? (
                <View style={styles.addressValidationMapLoading}>
                  <ActivityIndicator size="large" color="#0E7C6B" />
                  <Text style={styles.addressValidationMapLoadingText}>
                    Loading map...
                  </Text>
                </View>
              ) : mapError ? (
                <View style={styles.addressValidationMapError}>
                  <Text style={styles.addressValidationMapErrorText}>
                    {mapError}
                  </Text>
                </View>
              ) : (
                <div
                  ref={mapRef1}
                  style={{
                    width: "100%",
                    height: "100%",
                    minHeight: "400px",
                    backgroundColor: "#DDE3E2",
                    position: "relative",
                  }}
                />
              )}
            </View>

            <View style={styles.addressValidationBottomSection}>
              {addressDistanceMiles !== null && (
                <Text style={styles.addressValidationDistanceText}>
                  You are {addressDistanceMiles} miles away from the shop
                </Text>
              )}
              <View style={styles.addressValidationButtonRow}>
                <TouchableOpacity dataSet={{ hover: "lift" }}
                  style={[styles.addressValidationButton, styles.addressValidationButtonConfirm]}
                  onPress={handleConfirmAddress}
                >
                  <Text style={styles.addressValidationButtonText}>Confirm</Text>
                </TouchableOpacity>
                <TouchableOpacity dataSet={{ hover: "lift" }}
                  style={[styles.addressValidationButton, styles.addressValidationButtonWrong]}
                  onPress={handleWrongAddress}
                >
                  <Text style={styles.addressValidationButtonText}>Wrong</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </View>
      </Modal>

      {/* Address Wrong Informational Modal */}
      <Modal
        visible={showAddressWrongModal}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setShowAddressWrongModal(false)}
      >
        <View style={styles.addressWrongModalOverlay}>
          <View style={styles.addressWrongModalContent}>
            <Text style={styles.addressWrongModalTitle}>
              Address Not Found
            </Text>
            <Text style={styles.addressWrongModalMessage}>
              Please try entering the address again. You may want to try a neighboring address or use a more specific location.
            </Text>
            <Text style={styles.addressWrongModalMessage}>
              If you must proceed with a custom address, please enter additional address information in the "Delivery Notes" field to help our delivery team locate you.
            </Text>
            <Text style={styles.addressWrongModalWarning}>
              ⚠️ Your booking changes cannot be saved until you press "Confirm" in the address validation modal.
            </Text>
            <TouchableOpacity dataSet={{ hover: "lift" }}
              style={styles.addressWrongModalButton}
              onPress={() => setShowAddressWrongModal(false)}
            >
              <Text style={styles.addressWrongModalButtonText}>OK</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      {/* Contact Options Modal */}
      <ContactOptionsModal
        visible={contactModalVisible}
        onClose={() => setContactModalVisible(false)}
        customer={selectedCustomer}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#F6F8F7",
  },
  desktopWrapper: {
    width: "100%",
    maxWidth: 800,
    flex: 1,
    alignSelf: "center",
  },
  content: {
    flex: 1,
    paddingHorizontal: 7,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#F6F8F7",
    minHeight: "100vh",
  },
  loadingContent: {
    alignItems: "center",
    justifyContent: "center",
    padding: 32,
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    boxShadow: "0 1px 3px rgba(15, 26, 25, 0.07), 0 6px 16px rgba(15, 26, 25, 0.08)",
    elevation: 5,
    minWidth: 300,
  },
  loadingLogo: {
    width: 150,
    height: 100,
    marginBottom: 20,
    alignSelf: "center",
  },
  loadingIconContainer: {
    position: "relative",
    width: 100,
    height: 100,
    marginBottom: 24,
    justifyContent: "center",
    alignItems: "center",
  },
  loadingIcon: {
    fontSize: 64,
    position: "absolute",
  },
  loadingTitle: {
    fontSize: 28,
    fontWeight: "700",
    color: "#0E7C6B",
    marginBottom: 8,
    textAlign: "center",
  },
  loadingSubtitle: {
    fontSize: 16,
    color: "#5C6B68",
    marginBottom: 24,
    textAlign: "center",
  },
  loadingBarContainer: {
    width: "100%",
    marginBottom: 16,
  },
  loadingBar: {
    height: 8,
    backgroundColor: "#DDE3E2",
    borderRadius: 4,
    overflow: "hidden",
  },
  loadingBarFill: {
    height: "100%",
    backgroundColor: "#0E7C6B",
    borderRadius: 4,
    width: "75%",
    animation: "pulse 1.5s ease-in-out infinite",
  },
  errorContactInfo: {
    fontSize: 14,
    color: "#5C6B68",
    marginTop: 8,
    textAlign: "center",
  },
  errorButton: {
    marginTop: 24,
    backgroundColor: "#0E7C6B",
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 6,
    alignSelf: "center",
  },
  errorButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
  },
  loadingHint: {
    fontSize: 14,
    color: "#8A9794",
    textAlign: "center",
  },
  loadingText: {
    marginTop: 20,
    fontSize: 16,
    color: "#5C6B68",
  },
  errorText: {
    fontSize: 18,
    color: "#C4453D",
    textAlign: "center",
    marginBottom: 20,
  },
  header: {
    backgroundColor: "#0E7C6B",
    padding: 20,
    alignItems: "center",
  },
  headerLogo: {
    width: 120,
    height: 80,
    marginBottom: 12,
  },
  headerTitle: {
    fontSize: 24,
    fontWeight: "bold",
    color: "#FFFFFF",
    textAlign: "center",
  },
  headerSubtitle: {
    fontSize: 12,
    color: "#FFFFFF",
    marginTop: 4,
    opacity: 0.9,
    textAlign: "center",
  },
  customerInfoSection: {
    backgroundColor: "#FFFFFF",
    margin: 16,
    padding: 16,
    borderRadius: 6,
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 3,
  },
  customerInfoTitle: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#2E3A38",
    marginBottom: 12,
  },
  dateEditorContainer: {
    backgroundColor: "#F4F6F6",
    marginTop: 16,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "#DDE3E2",
    overflow: "hidden",
  },
  dateEditorHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 16,
    backgroundColor: "#F4F6F6",
    borderBottomWidth: 1,
    borderBottomColor: "#DDE3E2",
  },
  dateEditorTitle: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#0E7C6B",
  },
  dateEditorToggle: {
    fontSize: 12,
    fontWeight: "600",
  },
  toggleShow: {
    color: "#1F8A5B", // Green for "Show"
  },
  toggleHide: {
    color: "#C4453D", // Red for "Hide"
  },
  dateEditorContent: {
    padding: 16,
  },
  dateEditorLabel: {
    fontSize: 14,
    fontWeight: "600",
    color: "#2E3A38",
    marginBottom: 8,
  },
  calculatingContainer: {
    padding: 20,
    alignItems: "center",
  },
  calculatingText: {
    marginTop: 8,
    fontSize: 14,
    color: "#5C6B68",
  },
  currentDatesSection: {
    backgroundColor: "#E3F2EF",
    padding: 12,
    borderRadius: 5,
    marginBottom: 16,
  },
  currentDatesText: {
    fontSize: 15,
    color: "#2E3A38",
    fontWeight: "500",
  },
  calendarSection: {
    marginBottom: 16,
    maxWidth: 420,
    alignSelf: "center",
    width: "100%",
  },
  monthNavigation: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
    paddingBottom: 8,
    borderBottomWidth: 2,
    borderBottomColor: "#0E7C6B",
  },
  monthNavButton: {
    padding: 8,
    backgroundColor: "#0E7C6B",
    borderRadius: 5,
    minWidth: 35,
    alignItems: "center",
  },
  monthNavText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "bold",
  },
  monthTitle: {
    fontSize: 15,
    fontWeight: "bold",
    color: "#2E3A38",
  },
  calendarHeader: {
    flexDirection: "row",
    marginBottom: 6,
    maxWidth: 400,
    alignSelf: "center",
    width: "100%",
  },
  calendarHeaderDay: {
    flex: 1,
    alignItems: "center",
    paddingVertical: 6,
  },
  calendarHeaderText: {
    fontSize: 11,
    fontWeight: "600",
    color: "#5C6B68",
  },
  calendarHint: {
    fontSize: 12,
    color: "#5C6B68",
    marginBottom: 12,
    textAlign: "center",
  },
  calendarGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    maxWidth: 400,
    alignSelf: "center",
  },
  calendarDayCell: {
    width: "14.28%", // 7 days per week
    aspectRatio: 1,
    padding: 2,
  },
  calendarDayEmpty: {
    width: "14.28%",
    aspectRatio: 1,
  },
  calendarDay: {
    flex: 1,
    backgroundColor: "#F4F6F6",
    borderRadius: 5,
    borderWidth: 1,
    borderColor: "#DDE3E2",
    padding: 4,
    alignItems: "center",
    justifyContent: "center",
    minHeight: 50,
  },
  calendarDayCurrentBackground: {
    backgroundColor: "#DDE3E2",
    borderColor: "#8A9794",
  },
  calendarDayPotentialBackground: {
    backgroundColor: "#E4F4EC",
    borderColor: "#1F8A5B",
  },
  calendarDayRemovedBackground: {
    backgroundColor: "#FBEAE9",
    borderColor: "#C4453D",
  },
  calendarDaySelected: {
    backgroundColor: "#0E7C6B",
    borderColor: "#0E7C6B",
  },
  calendarDayCurrent: {
    backgroundColor: "#5C6B68",
    borderColor: "#5C6B68",
  },
  calendarDayUnavailable: {
    opacity: 0.5,
  },
  calendarDayContent: {
    alignItems: "center",
    width: "100%",
  },
  calendarDayNumber: {
    fontSize: 13,
    fontWeight: "600",
    color: "#2E3A38",
    marginBottom: 2,
  },
  availabilityDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    marginTop: 2,
  },
  availableDot: {
    backgroundColor: "#1F8A5B",
  },
  unavailableDot: {
    backgroundColor: "#C4453D",
  },
  currentBookingDot: {
    backgroundColor: "#2E3A38",
  },
  newDatesSection: {
    backgroundColor: "#E4F4EC",
    padding: 12,
    borderRadius: 5,
    marginBottom: 16,
  },
  newDatesLabel: {
    fontSize: 14,
    fontWeight: "600",
    color: "#2E3A38",
    marginBottom: 6,
  },
  newDatesText: {
    fontSize: 15,
    color: "#2E3A38",
    fontWeight: "500",
    marginBottom: 4,
  },
  daysAddedText: {
    fontSize: 14,
    color: "#C4453D",
    fontWeight: "600",
    marginBottom: 12,
  },
  daysRemovedText: {
    fontSize: 14,
    color: "#C4453D",
    fontWeight: "600",
    marginBottom: 12,
  },
  extensionCostSection: {
    backgroundColor: "#E3F2EF",
    padding: 14,
    borderRadius: 6,
    borderWidth: 2,
    borderColor: "#0E7C6B",
    marginTop: 12,
    marginBottom: 16,
  },
  extensionCostTitle: {
    fontSize: 15,
    fontWeight: "bold",
    color: "#0E7C6B",
    marginBottom: 10,
  },
  costRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 8,
  },
  costLabel: {
    fontSize: 14,
    color: "#2E3A38",
  },
  costValue: {
    fontSize: 14,
    color: "#2E3A38",
    fontWeight: "600",
  },
  clearDatesButton: {
    backgroundColor: "#FFFFFF",
    padding: 10,
    borderRadius: 5,
    borderWidth: 1,
    borderColor: "#C4453D",
    alignItems: "center",
  },
  clearDatesText: {
    color: "#C4453D",
    fontSize: 14,
    fontWeight: "600",
  },
  dateWarning: {
    backgroundColor: "#FDF3E3",
    padding: 12,
    borderRadius: 5,
    borderLeftWidth: 4,
    borderLeftColor: "#B4761B",
  },
  dateWarningText: {
    fontSize: 13,
    color: "#7A4E12",
    lineHeight: 20,
  },
  rentalStartedBanner: {
    backgroundColor: "#E3F2EF",
    padding: 12,
    borderRadius: 5,
    borderLeftWidth: 4,
    borderLeftColor: "#0E7C6B",
    marginBottom: 16,
  },
  rentalStartedText: {
    fontSize: 14,
    color: "#0E7C6B",
    fontWeight: "500",
    lineHeight: 20,
  },
  bookingEndedBanner: {
    backgroundColor: "#FBEAE9",
    padding: 16,
    borderRadius: 6,
    borderLeftWidth: 4,
    borderLeftColor: "#C4453D",
    marginBottom: 16,
  },
  bookingEndedText: {
    fontSize: 15,
    color: "#C4453D",
    fontWeight: "600",
    lineHeight: 22,
    textAlign: "center",
  },
  addressInputSection: {
    position: "relative",
    marginBottom: 16,
    position: "relative",
    zIndex: 9999,
    overflow: "visible",
  },
  addressInput: {
    borderWidth: 1,
    borderColor: "#DDE3E2",
    borderRadius: 6,
    padding: 12,
    fontSize: 14,
    color: "#2E3A38",
    backgroundColor: "#FFFFFF",
    marginBottom: 8,
  },
  distanceText: {
    fontSize: 13,
    color: "#0E7C6B",
    fontWeight: "500",
    marginTop: 4,
  },
  discountTiersSection: {
    backgroundColor: "#F4F6F6",
    padding: 12,
    borderRadius: 5,
    marginBottom: 16,
  },
  discountTierRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: "#DDE3E2",
  },
  discountTierText: {
    fontSize: 13,
    color: "#5C6B68",
    flex: 1,
  },
  discountTierActive: {
    color: "#1F8A5B",
    fontWeight: "600",
  },
  discountTierNeeded: {
    fontSize: 12,
    color: "#B4761B",
    fontWeight: "600",
    marginLeft: 8,
  },
  infoRow: {
    flexDirection: "row",
    marginBottom: 8,
  },
  infoLabel: {
    fontSize: 14,
    fontWeight: "600",
    color: "#5C6B68",
    width: 80,
  },
  infoValue: {
    fontSize: 14,
    color: "#2E3A38",
    flex: 1,
  },
  paymentMethodsSection: {
    backgroundColor: "#E4F4EC",
    padding: 16,
    margin: 16,
    borderRadius: 6,
    borderLeftWidth: 4,
    borderLeftColor: "#1F8A5B",
  },
  paymentMethodsTitle: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#1F8A5B",
    marginBottom: 12,
  },
  cardInfoRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 10,
    paddingHorizontal: 12,
    backgroundColor: "#FFFFFF",
    borderRadius: 5,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: "#DDE3E2",
  },
  cardDetails: {
    flex: 1,
  },
  cardBrand: {
    fontSize: 15,
    fontWeight: "600",
    color: "#2E3A38",
    marginBottom: 4,
  },
  cardHolder: {
    fontSize: 13,
    color: "#5C6B68",
    marginBottom: 2,
  },
  cardExpiry: {
    fontSize: 12,
    color: "#8A9794",
  },
  cardAmount: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#1F8A5B",
    marginLeft: 12,
  },
  cancellationPolicyContainer: {
    backgroundColor: "#FDF3E3",
    padding: 16,
    margin: 16,
    borderRadius: 6,
    borderLeftWidth: 4,
    borderLeftColor: "#B4761B",
  },
  cancellationPolicyTitle: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#B4761B",
    marginBottom: 8,
  },
  cancellationPolicyText: {
    fontSize: 14,
    color: "#5C6B68",
    lineHeight: 20,
  },
  infoBanner: {
    backgroundColor: "#E3F2EF",
    padding: 15,
    margin: 16,
    borderRadius: 6,
    borderLeftWidth: 4,
    borderLeftColor: "#0E7C6B",
  },
  infoBannerText: {
    color: "#2E3A38",
    fontSize: 14,
    lineHeight: 20,
    textAlign: "center",
  },
  linkText: {
    color: "#0E7C6B",
    fontWeight: "600",
    textDecorationLine: "underline",
  },
  subBookingsContainer: {
    padding: 16,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: "bold",
    marginBottom: 12,
    color: "#2E3A38",
  },
  summaryRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: "#E3F2EF",
  },
  summaryLabel: {
    fontSize: 14,
    fontWeight: "600",
    color: "#5C6B68",
  },
  summaryValue: {
    fontSize: 14,
    color: "#2E3A38",
    flex: 1,
    textAlign: "right",
  },
  summarySectionTitle: {
    fontSize: 14,
    fontWeight: "600",
    color: "#2E3A38",
    marginTop: 12,
    marginBottom: 8,
  },
  summaryItemRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 6,
    paddingLeft: 12,
  },
  summaryItemText: {
    fontSize: 14,
    color: "#2E3A38",
    flex: 1,
  },
  summaryItemPrice: {
    fontSize: 14,
    fontWeight: "600",
    color: "#2E3A38",
    textAlign: "right",
  },
  summaryDivider: {
    height: 1,
    backgroundColor: "#E3F2EF",
    marginVertical: 12,
  },
  summaryItemDivider: {
    height: 1,
    backgroundColor: "#E3F2EF",
    marginVertical: 12,
  },
  summaryTotalRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingTop: 12,
    borderTopWidth: 2,
    borderTopColor: "#0E7C6B",
  },
  summaryTotalLabel: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#2E3A38",
  },
  summaryTotalValue: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#1F8A5B",
  },
  product1Section: {
    marginTop: 12,
    backgroundColor: "#F4F6F6",
    padding: 12,
    borderRadius: 6,
    borderLeftWidth: 4,
    borderLeftColor: "#0E7C6B",
  },
  product2Section: {
    marginTop: 12,
    backgroundColor: "#FDF3E3",
    padding: 12,
    borderRadius: 6,
    borderLeftWidth: 4,
    borderLeftColor: "#B4761B",
  },
  yourItemsProduct1Section: {
    marginTop: 12,
    backgroundColor: "#E3F2EF",
    padding: 12,
    borderRadius: 6,
    borderLeftWidth: 4,
    borderLeftColor: "#0E7C6B",
  },
  yourItemsProduct2Section: {
    marginTop: 12,
    backgroundColor: "#E3F2EF",
    padding: 12,
    borderRadius: 6,
    borderLeftWidth: 4,
    borderLeftColor: "#0E7C6B",
  },
  toAddProduct1Section: {
    marginTop: 12,
    backgroundColor: "#E4F4EC",
    padding: 12,
    borderRadius: 6,
    borderLeftWidth: 4,
    borderLeftColor: "#1F8A5B",
  },
  toAddProduct2Section: {
    marginTop: 12,
    backgroundColor: "#E4F4EC",
    padding: 12,
    borderRadius: 6,
    borderLeftWidth: 4,
    borderLeftColor: "#1F8A5B",
  },
  productsSectionTitle: {
    fontSize: 14,
    fontWeight: "600",
    color: "#5C6B68",
    marginBottom: 8,
  },
  subsectionTitle: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#2E3A38",
    marginBottom: 12,
    marginTop: 4,
  },
  yourItemsSummary: {
    fontSize: 14,
    color: "#5C6B68",
    marginBottom: 12,
    fontStyle: "italic",
  },
  productRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#EDF1F0",
  },
  productInfo: {
    flex: 1,
  },
  productName: {
    fontSize: 15,
    fontWeight: "500",
    color: "#2E3A38",
  },
  productPrice: {
    fontSize: 13,
    color: "#5C6B68",
    marginTop: 2,
  },
  availabilityText: {
    fontSize: 12,
    color: "#1F8A5B",
    marginTop: 4,
    fontWeight: "600",
  },
  refundAmountText: {
    fontSize: 12,
    color: "#C4453D",
    marginTop: 4,
    fontWeight: "600",
  },
  bikeCard: {
    marginBottom: 16,
    padding: 12,
    backgroundColor: "#FFFFFF",
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "#DDE3E2",
  },
  bikeCardLast: {
    marginBottom: 0,
  },
  bikeName: {
    fontSize: 16,
    fontWeight: "600",
    color: "#2E3A38",
  },
  bikeInfoControlsContainer: {
    backgroundColor: "#F4F6F6",
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#DDE3E2",
    flexDirection: "row",
    width: "100%",
  },
  bikePriceAvailabilityRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    marginBottom: 12,
  },
  bikePrice: {
    fontSize: 14,
    color: "#5C6B68",
  },
  bikeAvailability: {
    fontSize: 14,
    color: "#0E7C6B",
  },
  bikeQuantityRow: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
  },
  quantitySelector: {
    flexDirection: "row",
    alignItems: "center",
  },
  qtyButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: "#0E7C6B",
    justifyContent: "center",
    alignItems: "center",
  },
  qtyButtonDisabled: {
    backgroundColor: "#DDE3E2",
    opacity: 0.5,
  },
  qtyButtonText: {
    color: "#FFFFFF",
    fontSize: 18,
    fontWeight: "bold",
  },
  qtyValue: {
    marginHorizontal: 16,
    fontSize: 16,
    fontWeight: "600",
    minWidth: 30,
    textAlign: "center",
  },
  addedBadge: {
    backgroundColor: "#1F8A5B",
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 8,
    marginLeft: 8,
  },
  removedBadge: {
    backgroundColor: "#C4453D",
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 8,
    marginLeft: 8,
  },
  badgeText: {
    color: "#FFFFFF",
    fontSize: 10,
    fontWeight: "bold",
  },
  checkbox: {
    width: 28,
    height: 28,
    borderRadius: 5,
    borderWidth: 2,
    borderColor: "#0E7C6B",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#FFFFFF",
    marginRight: 12,
  },
  checkboxChecked: {
    backgroundColor: "#0E7C6B",
  },
  checkboxDisabled: {
    borderColor: "#DDE3E2",
    backgroundColor: "#F6F8F7",
    opacity: 0.6,
  },
  checkboxCheckmark: {
    color: "#FFFFFF",
    fontSize: 18,
    fontWeight: "bold",
  },
  addButton: {
    backgroundColor: "#1F8A5B",
    borderRadius: 6,
    width: 40,
    height: 40,
    alignItems: "center",
    justifyContent: "center",
  },
  addButtonText: {
    color: "#FFFFFF",
    fontSize: 24,
    fontWeight: "bold",
  },
  resetButton: {
    backgroundColor: "#B4761B",
    borderRadius: 6,
    padding: 16,
    alignItems: "center",
    justifyContent: "center",
  },
  resetButtonText: {
    color: "#2E3A38",
    fontSize: 16,
    fontWeight: "bold",
  },
  quantityControls: {
    flexDirection: "row",
    alignItems: "center",
  },
  quantityButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: "#0E7C6B",
    alignItems: "center",
    justifyContent: "center",
  },
  quantityButtonDisabled: {
    backgroundColor: "#DDE3E2",
    opacity: 0.5,
  },
  quantityButtonText: {
    fontSize: 20,
    color: "#FFFFFF",
    fontWeight: "bold",
  },
  quantityText: {
    fontSize: 16,
    fontWeight: "600",
    marginHorizontal: 16,
    minWidth: 30,
    textAlign: "center",
  },
  originalCostRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 16,
    paddingTop: 12,
  },
  originalCostLabel: {
    fontSize: 15,
    fontWeight: "600",
    color: "#2E3A38",
  },
  originalCostValue: {
    fontSize: 16,
    fontWeight: "700",
    color: "#1F8A5B",
  },
  changesContainer: {
    padding: 16,
  },
  additionsCard: {
    backgroundColor: "#E4F4EC",
    borderRadius: 8,
    padding: 16,
    marginBottom: 12,
    borderWidth: 2,
    borderColor: "#1F8A5B",
  },
  removalsCard: {
    backgroundColor: "#FBEAE9",
    borderRadius: 10,
    padding: 16,
    marginBottom: 12,
    borderWidth: 2,
    borderColor: "#C4453D",
    maxWidth: 600,
    width: "100%",
    alignSelf: "center",
  },
  changesTitle: {
    fontSize: 18,
    fontWeight: "bold",
    marginBottom: 12,
    color: "#2E3A38",
  },
  changeRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 6,
  },
  changeText: {
    fontSize: 14,
    color: "#2E3A38",
  },
  changeAmount: {
    fontSize: 14,
    fontWeight: "600",
    color: "#2E3A38",
  },
  changesTotalRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: "rgba(0,0,0,0.1)",
  },
  changesTotalLabel: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#2E3A38",
  },
  additionsSubtotal: {
    fontSize: 14,
    color: "#1F8A5B",
  },
  additionsTax: {
    fontSize: 14,
    color: "#1F8A5B",
  },
  additionsTotal: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#1F8A5B",
  },
  removalsSubtotal: {
    fontSize: 14,
    color: "#C4453D",
  },
  removalsTax: {
    fontSize: 14,
    color: "#C4453D",
  },
  removalsTotal: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#C4453D",
  },
  processingFee: {
    fontSize: 14,
    color: "#C4453D",
  },
  netSummaryCard: {
    borderRadius: 8,
    padding: 20,
    marginTop: 16,
    marginBottom: 12,
    borderWidth: 3,
  },
  netChargeCard: {
    backgroundColor: "#E4F4EC",
    borderColor: "#1F8A5B",
  },
  netRefundCard: {
    backgroundColor: "#FBEAE9",
    borderColor: "#C4453D",
  },
  netNeutralCard: {
    backgroundColor: "#F6F8F7",
    borderColor: "#5C6B68",
  },
  netSummaryTitle: {
    fontSize: 20,
    fontWeight: "bold",
    marginBottom: 16,
    color: "#2E3A38",
    textAlign: "center",
  },
  netSummaryRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 8,
  },
  netSummaryLabel: {
    fontSize: 15,
    color: "#2E3A38",
  },
  netSummaryValue: {
    fontSize: 15,
    fontWeight: "600",
    color: "#2E3A38",
  },
  netSummaryTotal: {
    fontSize: 20,
    fontWeight: "bold",
  },
  paymentMethodContainer: {
    backgroundColor: "#FFFFFF",
    margin: 16,
    padding: 20,
    borderRadius: 8,
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 3,
  },
  paymentMethodTitle: {
    fontSize: 20,
    fontWeight: "bold",
    color: "#2E3A38",
    marginBottom: 8,
  },
  paymentMethodSubtitle: {
    fontSize: 14,
    color: "#5C6B68",
    marginBottom: 20,
  },
  paymentMethodOption: {
    flexDirection: "row",
    alignItems: "flex-start",
    padding: 16,
    borderWidth: 2,
    borderColor: "#DDE3E2",
    borderRadius: 6,
    marginBottom: 12,
  },
  paymentMethodOptionSelected: {
    borderColor: "#0E7C6B",
    backgroundColor: "#E3F2EF",
  },
  radioButton: {
    width: 24,
    height: 24,
    borderRadius: 12,
    borderWidth: 2,
    borderColor: "#0E7C6B",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 12,
    marginTop: 2,
  },
  radioButtonInner: {
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: "#0E7C6B",
  },
  paymentMethodDetails: {
    flex: 1,
  },
  paymentMethodOptionTitle: {
    fontSize: 16,
    fontWeight: "600",
    color: "#2E3A38",
    marginBottom: 4,
  },
  paymentMethodDescription: {
    fontSize: 13,
    color: "#5C6B68",
  },
  refundMethodContainer: {
    backgroundColor: "#FDF3E3",
    margin: 16,
    padding: 20,
    borderRadius: 8,
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 3,
    borderWidth: 2,
    borderColor: "#B4761B",
  },
  refundMethodTitle: {
    fontSize: 20,
    fontWeight: "bold",
    color: "#7A4E12",
    marginBottom: 8,
  },
  refundMethodSubtitle: {
    fontSize: 14,
    color: "#7A4E12",
    marginBottom: 16,
    fontWeight: "600",
  },
  refundInfoBox: {
    backgroundColor: "#FFFFFF",
    padding: 16,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "#B4761B",
  },
  refundInfoText: {
    fontSize: 14,
    color: "#7A4E12",
    marginBottom: 8,
    lineHeight: 20,
  },
  cardInfoDisplay: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginTop: 4,
  },
  cardInfoText: {
    fontSize: 13,
    color: "#5C6B68",
  },
  actionsContainer: {
    flexDirection: "row",
    padding: 16,
  },
  button: {
    flex: 1,
    padding: 16,
    borderRadius: 6,
    alignItems: "center",
    marginHorizontal: 8,
  },
  cancelButton: {
    backgroundColor: "#5C6B68",
  },
  submitButton: {
    backgroundColor: "#0E7C6B",
  },
  buttonDisabled: {
    backgroundColor: "#DDE3E2",
    opacity: 0.6,
  },
  buttonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
    textAlign: "center",
  },
  noChangesText: {
    textAlign: "center",
    fontSize: 15,
    color: "#8A9794",
    padding: 32,
    fontStyle: "italic",
  },
  adminOverrideContainer: {
    margin: 16,
    padding: 16,
    backgroundColor: "#FDF3E3",
    borderRadius: 6,
    borderWidth: 2,
    borderColor: "#B4761B",
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 3,
  },
  adminOverrideTitle: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#7A4E12",
    marginBottom: 12,
  },
  overrideCheckboxRow: {
    marginBottom: 12,
  },
  refundPercentInputContainer: {
    marginTop: 12,
    padding: 12,
    backgroundColor: "#FFFFFF",
    borderRadius: 5,
    borderWidth: 1,
    borderColor: "#B4761B",
  },
  refundPercentLabel: {
    fontSize: 14,
    fontWeight: "600",
    color: "#7A4E12",
    marginBottom: 8,
  },
  refundPercentInput: {
    borderWidth: 1,
    borderColor: "#DDE3E2",
    borderRadius: 5,
    padding: 12,
    fontSize: 16,
    fontWeight: "bold",
    color: "#2E3A38",
    backgroundColor: "#FFFFFF",
    marginBottom: 8,
  },
  refundPercentHint: {
    fontSize: 13,
    color: "#7A4E12",
    fontWeight: "500",
  },
  refundOnlyButton: {
    backgroundColor: "#1F8A5B",
    padding: 14,
    borderRadius: 6,
    alignItems: "center",
    marginTop: 12,
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 4,
  },
  refundOnlyButtonText: {
    color: "#FFFFFF",
    fontSize: 15,
    fontWeight: "bold",
  },
  cancelBookingContainer: {
    padding: 16,
    marginTop: 20,
    borderTopWidth: 2,
    borderTopColor: "#DDE3E2",
  },
  paymentMethodsSection: {
    backgroundColor: "#FFFFFF",
    margin: 16,
    padding: 16,
    borderRadius: 6,
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 3,
  },
  paymentMethodsSectionTitle: {
    fontSize: 18,
    fontWeight: "600",
    marginBottom: 12,
  },
  paymentMethodsContent: {
    // Container for content
  },
  infoRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 8,
    flexWrap: "wrap",
  },
  label: {
    fontSize: 14,
    color: "#5C6B68",
    fontWeight: "500",
    marginRight: 8,
  },
  paymentButtonsContainer: {
    flexDirection: "row",
    gap: 12,
    width: "100%",
    marginTop: 16,
  },
  collectButton: {
    flex: 1,
    backgroundColor: "#C4453D",
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 6,
    alignItems: "center",
    justifyContent: "center",
  },
  collectButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
  },
  sendLinkButton: {
    flex: 1,
    backgroundColor: "#0E7C6B",
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 6,
    alignItems: "center",
    justifyContent: "center",
  },
  sendLinkButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
  },
  payInvoiceButton: {
    flex: 1,
    backgroundColor: "#1F8A5B",
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 6,
    alignItems: "center",
    justifyContent: "center",
  },
  payInvoiceButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
  },
  cancelEntireBookingButton: {
    backgroundColor: "#C4453D",
    padding: 16,
    borderRadius: 6,
    alignItems: "center",
    justifyContent: "center",
    minHeight: 50,
  },
  cancelEntireBookingText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "bold",
  },
  cancelWarningText: {
    textAlign: "center",
    fontSize: 12,
    color: "#5C6B68",
    marginTop: 10,
    fontStyle: "italic",
  },
  directEditLinkContainer: {
    marginTop: 24,
    marginBottom: 32,
    alignItems: "center",
  },
  directEditLinkTouchable: {
    paddingVertical: 8,
    paddingHorizontal: 16,
  },
  directEditLinkText: {
    fontSize: 14,
    color: "#0E7C6B",
    fontWeight: "500",
  },
  deliverySection: {
    backgroundColor: "#FFFFFF",
    margin: 16,
    padding: 16,
    borderRadius: 6,
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 3,
  },
  radioButtonContainer: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 12,
  },
  radioButton: {
    width: 24,
    height: 24,
    borderRadius: 12,
    borderWidth: 2,
    borderColor: "#0E7C6B",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 12,
  },
  radioButtonInner: {
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: "#0E7C6B",
  },
  radioButtonLabel: {
    fontSize: 16,
    color: "#2E3A38",
  },
  checkboxContainer: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 12,
    marginBottom: 8,
  },
  checkboxLabel: {
    fontSize: 16,
    color: "#2E3A38",
    marginLeft: 12,
    flex: 1,
  },
  checkboxContainerDisabled: {
    opacity: 0.5,
  },
  checkboxDisabled: {
    borderColor: "#DDE3E2",
    backgroundColor: "#F6F8F7",
  },
  checkboxLabelDisabled: {
    color: "#8A9794",
  },
  infoMessageContainer: {
    backgroundColor: "#E3F2EF",
    padding: 12,
    borderRadius: 6,
    marginTop: 12,
    marginLeft: 20,
    borderLeftWidth: 4,
    borderLeftColor: "#0E7C6B",
  },
  infoMessageText: {
    fontSize: 14,
    color: "#2E3A38",
    lineHeight: 20,
  },
  addressInputContainer: {
    position: "relative",
    zIndex: 9999,
    marginTop: 16,
    marginBottom: 12,
    width: "100%",
    overflow: "visible",
  },
  label: {
    fontSize: 14,
    fontWeight: "600",
    color: "#2E3A38",
    marginBottom: 8,
  },
  requiredAsterisk: {
    color: "#C4453D",
  },
  addressInput: {
    height: 44,
    borderWidth: 1,
    borderColor: "#DDE3E2",
    borderRadius: 6,
    paddingHorizontal: 12,
    fontSize: 16,
    backgroundColor: "#FFFFFF",
  },
  addressInputInvalid: {
    borderColor: "#C4453D",
    color: "#C4453D",
  },
  addressInputValid: {
    borderColor: "#1F8A5B",
    color: "#1F8A5B",
  },
  addressInputWithSuggestionsWrapper: {
    position: "relative",
    width: "100%",
    zIndex: 100001,
    elevation: 100002,
  },
  suggestionsContainer: {
    position: "absolute",
    top: "100%",
    left: 0,
    right: 0,
    zIndex: 100002,
    elevation: 100003,
    marginTop: 4,
    overflow: "visible",
  },
  suggestionsList: {
    maxHeight: 200,
    backgroundColor: "#FDF3E3",
    borderWidth: 2,
    borderColor: "#B4761B",
    borderRadius: 6,
    boxShadow: "0 4px 12px rgba(15, 26, 25, 0.1), 0 28px 64px rgba(15, 26, 25, 0.18)",
    elevation: 30,
    overflow: "hidden",
    borderWidth: 2,
    borderColor: "#B4761B",
    borderRadius: 6,
    boxShadow: "0 4px 12px rgba(15, 26, 25, 0.1), 0 28px 64px rgba(15, 26, 25, 0.18)",
    elevation: 30,
    overflow: "hidden",
  },
  suggestionItem: {
    padding: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#EDF1F0",
  },
  suggestionItemLast: {
    borderBottomWidth: 0,
  },
  suggestionMainText: {
    fontSize: 14,
    color: "#2E3A38",
    fontWeight: "500",
  },
  suggestionSecondaryText: {
    fontSize: 12,
    color: "#5C6B68",
    marginTop: 2,
  },
  gatedCommunityContainer: {
    marginLeft: 20,
  },
  gatedCommunityCheckbox: {
    borderColor: "#C4453D",
  },
  gatedCommunityChecked: {
    backgroundColor: "#C4453D",
    borderColor: "#C4453D",
  },
  gatedCommunityCheckmark: {
    color: "#FFFFFF",
    fontSize: 14,
    fontWeight: "bold",
  },
  gatedCommunityLabel: {
    fontSize: 16,
    color: "#C4453D",
    fontWeight: "500",
  },
  communityNameContainer: {
    marginTop: 12,
    marginBottom: 12,
  },
  communityNameInput: {
    height: 40,
    borderWidth: 1,
    borderColor: "#DDE3E2",
    borderRadius: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    fontSize: 14,
    backgroundColor: "#F4F6F6",
    color: "#2E3A38",
  },
  infoToggleButton: {
    marginTop: 8,
    paddingVertical: 4,
  },
  infoToggleText: {
    fontSize: 13,
    color: "#0E7C6B",
    fontWeight: "600",
  },
  discountTierRow: {
    paddingVertical: 6,
    paddingHorizontal: 4,
    borderLeftWidth: 3,
    borderLeftColor: "#0E7C6B",
    marginVertical: 2,
  },
  discountTierText: {
    fontSize: 13,
    color: "#5C6B68",
  },
  discountTierActive: {
    color: "#1F8A5B",
    fontWeight: "600",
  },
  discountTierNeeded: {
    color: "#B4761B",
    fontSize: 12,
    fontStyle: "italic",
  },
  addressNotesContainer: {
    marginTop: 16,
  },
  addressStatusContainer: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 8,
  },
  addressStatusBadge: {
    width: 17,
    height: 17,
    borderRadius: 6.5,
    marginLeft: 8,
  },
  addressStatusBadgePending: {
    backgroundColor: "#FBEAE9",
  },
  addressStatusBadgeConfirmed: {
    backgroundColor: "#1F8A5B",
  },
  addressStatusText: {
    fontSize: 15,
    color: "#C4453D",
    fontWeight: "600",
  },
  addressStatusTextConfirmed: {
    color: "#1F8A5B",
  },
  addressNotesLabel: {
    fontSize: 14,
    fontWeight: "600",
    color: "#2E3A38",
    marginBottom: 8,
  },
  addressNotesInput: {
    borderWidth: 1,
    borderColor: "#DDE3E2",
    borderRadius: 6,
    padding: 12,
    fontSize: 14,
    backgroundColor: "#FFFFFF",
    minHeight: 60,
    textAlignVertical: "top",
  },
  quantityDisplay: {
    padding: 8,
    minWidth: 40,
    alignItems: "center",
    justifyContent: "center",
  },
  refundSplitContainer: {
    marginTop: 12,
    padding: 12,
    backgroundColor: "#FDF3E3",
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "#B4761B",
  },
  refundSplitLabel: {
    fontSize: 13,
    fontWeight: "600",
    color: "#5C6B68",
    marginBottom: 8,
  },
  refundSplitInput: {
    borderWidth: 1,
    borderColor: "#DDE3E2",
    borderRadius: 6,
    padding: 12,
    fontSize: 16,
    backgroundColor: "#FFFFFF",
    textAlign: "center",
    fontWeight: "bold",
  },
  refundSplitHint: {
    fontSize: 12,
    color: "#5C6B68",
    marginTop: 4,
    textAlign: "right",
  },
  refundSplitSummary: {
    marginTop: 16,
    padding: 16,
    backgroundColor: "#FDF3E3",
    borderRadius: 6,
    borderWidth: 2,
    borderColor: "#B4761B",
  },
  refundSplitBalanced: {
    backgroundColor: "#E4F4EC",
    borderColor: "#1F8A5B",
  },
  refundSplitSummaryText: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#2E3A38",
    textAlign: "center",
  },
  refundSplitWarning: {
    fontSize: 14,
    color: "#C4453D",
    marginTop: 8,
    textAlign: "center",
    fontWeight: "600",
  },
  quantityControls: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  quantityButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: "#0E7C6B",
    justifyContent: "center",
    alignItems: "center",
  },
  quantityButtonDisabled: {
    backgroundColor: "#DDE3E2",
    opacity: 0.5,
  },
  quantityButtonText: {
    color: "#FFFFFF",
    fontSize: 20,
    fontWeight: "bold",
  },
  availabilityText: {
    fontSize: 12,
    marginTop: 4,
    fontWeight: "600",
  },
  availableGreen: {
    color: "#1F8A5B",
  },
  availableRed: {
    color: "#C4453D",
  },
  qtyChangeIndicator: {
    fontSize: 11,
    marginTop: 4,
    fontWeight: "600",
    fontStyle: "italic",
  },
  qtyAdded: {
    color: "#0E7C6B",
  },
  qtyRemoved: {
    color: "#B4761B",
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
    alignItems: "center",
  },
  modalImageContent: {
    position: "relative",
    width: "90%",
    maxWidth: 800,
    aspectRatio: 1,
  },
  closeButton: {
    position: "absolute",
    top: 20,
    right: 20,
    width: 50,
    height: 50,
    borderRadius: 25,
    backgroundColor: "rgba(255, 0, 0, 0.8)",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
    elevation: 1000,
  },
  closeButtonText: {
    color: "#FFFFFF",
    fontSize: 32,
    fontWeight: "bold",
  },
  fullScreenImage: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
  },
  bikeImageContainer: {
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
  },
  bikeImagePlaceholder: {
    width: "100%",
    aspectRatio: 1,
    borderRadius: 8,
    backgroundColor: "#EDF1F0",
    borderWidth: 1,
    borderColor: "#DDE3E2",
    alignItems: "center",
    justifyContent: "center",
  },
  bikeImagePlaceholderText: {
    fontSize: 48,
  },
  productImage: {
    width: "100%",
    aspectRatio: 1,
    borderRadius: 8,
    objectFit: "contain",
    borderWidth: 1,
    borderColor: "#DDE3E2",
    imageRendering: "high-quality",
  },
  productImageDesktop: {
    maxWidth: 300,
    maxHeight: 300,
  },
  includedItemsText: {
    fontSize: 13,
    color: "#5C6B68",
    fontStyle: "italic",
    marginTop: 4,
    marginBottom: 8,
  },
  infoIcon: {
    padding: 4,
    minWidth: 24,
    minHeight: 24,
    alignItems: "center",
    justifyContent: "center",
    marginLeft: 7,
  },
  infoIconText: {
    fontSize: 14,
  },
  tooltip: {
    position: "absolute",
    top: 50,
    left: 0,
    right: 0,
    backgroundColor: "#2E3A38",
    padding: 12,
    borderRadius: 6,
    zIndex: 10002,
    elevation: 10002,
    boxShadow: "0 1px 3px rgba(15, 26, 25, 0.07), 0 6px 16px rgba(15, 26, 25, 0.08)",
  },
  tooltipOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 10001,
    elevation: 10001,
  },
  tooltipText: {
    color: "#FFFFFF",
    fontSize: 14,
    lineHeight: 20,
  },
  tooltipArrow: {
    position: "absolute",
    top: -6,
    left: 20,
    width: 0,
    height: 0,
    borderLeftWidth: 6,
    borderRightWidth: 6,
    borderBottomWidth: 6,
    borderLeftColor: "transparent",
    borderRightColor: "transparent",
    borderBottomColor: "#2E3A38",
  },
  bikeNameRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 4,
  },
  modalContent: {
    backgroundColor: "white",
    borderRadius: 12,
    padding: 24,
    width: "85%",
    maxWidth: 400,
    alignItems: "center",
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: "bold",
    marginBottom: 16,
    color: "#2E3A38",
  },
  modalMessage: {
    fontSize: 16,
    color: "#5C6B68",
    textAlign: "center",
    marginBottom: 24,
    lineHeight: 22,
  },
  modalButton: {
    backgroundColor: "#0E7C6B",
    paddingHorizontal: 32,
    paddingVertical: 12,
    borderRadius: 6,
  },
  modalButtonText: {
    color: "white",
    fontSize: 16,
    fontWeight: "600",
  },
  discountUnlockModalContent: {
    backgroundColor: "#FFFFFF",
    borderRadius: 20,
    padding: 32,
    width: "90%",
    maxWidth: 450,
    alignItems: "center",
    boxShadow: "0 2px 6px rgba(15, 26, 25, 0.08), 0 16px 40px rgba(15, 26, 25, 0.12)",
    elevation: 10,
    borderWidth: 3,
    borderColor: "#B4761B",
  },
  discountUnlockEmoji: {
    fontSize: 48,
    marginBottom: 16,
  },
  discountUnlockTitle: {
    fontSize: 26,
    fontWeight: "bold",
    color: "#C4453D",
    marginBottom: 16,
    textAlign: "center",
  },
  discountUnlockMessage: {
    fontSize: 18,
    color: "#2E3A38",
    textAlign: "center",
    marginBottom: 24,
    lineHeight: 26,
  },
  discountUnlockDetails: {
    backgroundColor: "#E3F2EF",
    borderRadius: 12,
    padding: 20,
    marginBottom: 20,
    width: "100%",
    alignItems: "center",
    borderWidth: 2,
    borderColor: "#0E7C6B",
  },
  discountUnlockDiscountName: {
    fontSize: 20,
    fontWeight: "bold",
    color: "#0E7C6B",
    marginBottom: 8,
  },
  discountUnlockPercent: {
    fontSize: 32,
    fontWeight: "bold",
    color: "#1F8A5B",
  },
  discountUnlockSubtext: {
    fontSize: 16,
    color: "#5C6B68",
    textAlign: "center",
    marginBottom: 24,
    lineHeight: 22,
  },
  discountUnlockButton: {
    backgroundColor: "#1F8A5B",
    paddingHorizontal: 40,
    paddingVertical: 14,
    borderRadius: 16,
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 5,
  },
  discountUnlockButtonText: {
    color: "white",
    fontSize: 18,
    fontWeight: "bold",
  },
  sliderContainer: {
    marginTop: 8,
  },
  sliderTrack: {
    height: 50,
    backgroundColor: "#DDE3E2",
    borderRadius: 25,
    position: "relative",
    overflow: "hidden",
    width: "100%",
    cursor: "pointer",
  },
  sliderFill: {
    height: "100%",
    backgroundColor: "#C4453D",
    borderRadius: 25,
    position: "absolute",
    left: 0,
    top: 0,
  },
  sliderThumb: {
    position: "absolute",
    width: 50,
    height: 50,
    backgroundColor: "#FFFFFF",
    borderRadius: 25,
    borderWidth: 2,
    borderColor: "#C4453D",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 1px 3px rgba(15, 26, 25, 0.07), 0 6px 16px rgba(15, 26, 25, 0.08)",
    elevation: 5,
    zIndex: 10,
  },
  sliderThumbText: {
    fontSize: 20,
    color: "#C4453D",
    fontWeight: "bold",
  },
  sliderText: {
    marginTop: 8,
    textAlign: "center",
    fontSize: 14,
    color: "#5C6B68",
    fontWeight: "500",
  },
  adminNotesContainer: {
    backgroundColor: "#FFFFFF",
    padding: 16,
    marginTop: 12,
    marginBottom: 8,
    borderRadius: 6,
    borderWidth: 2,
    borderColor: "#0E7C6B",
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 3,
  },
  adminNotesHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 4,
  },
  adminNotesLabel: {
    fontSize: 14,
    fontWeight: "700",
    color: "#0E7C6B",
    flex: 1,
  },
  adminNotesToggle: {
    fontSize: 12,
    fontWeight: "600",
    color: "#0E7C6B",
    paddingLeft: 12,
  },
  adminNotesInput: {
    marginTop: 12,
    borderWidth: 1,
    borderColor: "#DDE3E2",
    borderRadius: 6,
    padding: 12,
    fontSize: 14,
    color: "#2E3A38",
    backgroundColor: "#FFFFFF",
    minHeight: 80,
    textAlignVertical: "top",
    marginBottom: 8,
  },
  adminNotesHelper: {
    fontSize: 11,
    color: "#8A9794",
    fontStyle: "italic",
    textAlign: "center",
  },
  originalAddressInfoBox: {
    backgroundColor: "#E3F2EF",
    borderLeftWidth: 4,
    borderLeftColor: "#0E7C6B",
    borderRadius: 6,
    padding: 12,
    marginBottom: 16,
  },
  originalAddressLabel: {
    fontSize: 13,
    fontWeight: "600",
    color: "#0A5F52",
    marginBottom: 4,
  },
  originalAddressText: {
    fontSize: 15,
    color: "#2E3A38",
    fontWeight: "500",
  },
  addressInputRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    width: "100%",
  },
  addressInputWrapper: {
    flex: 1,
  },
  addressInputRed: {
    backgroundColor: "#FBEAE9",
    borderColor: "#C4453D",
  },
  addressInputValidated: {
    borderColor: "#1F8A5B",
    backgroundColor: "#E4F4EC",
  },
  validateButton: {
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 6,
    backgroundColor: "#C4453D",
    justifyContent: "center",
    alignItems: "center",
    minWidth: 100,
    height: 44,
  },
  validateButtonDisabled: {
    backgroundColor: "#DDE3E2",
    opacity: 0.6,
  },
  validateButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
  },
  validateButtonConfirmed: {
    backgroundColor: "#1F8A5B",
  },
  validateButtonTextConfirmed: {
    color: "#FFFFFF",
  },
  addressConfirmNotice: {
    marginTop: 8,
    fontSize: 13,
    color: "#C4453D",
    fontWeight: "600",
  },
  addressValidationModalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.8)",
    justifyContent: "center",
    alignItems: "center",
  },
  addressValidationModalContent: {
    width: "90%",
    maxWidth: 800,
    height: "80%",
    maxHeight: 600,
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    overflow: "hidden",
    boxShadow: "0 2px 6px rgba(15, 26, 25, 0.08), 0 16px 40px rgba(15, 26, 25, 0.12)",
    elevation: 10,
  },
  addressValidationMapContainer: {
    flex: 1,
    width: "100%",
    minHeight: 400,
    backgroundColor: "#DDE3E2",
  },
  addressValidationMapLoading: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  addressValidationMapLoadingText: {
    marginTop: 16,
    fontSize: 16,
    color: "#5C6B68",
  },
  addressValidationMapError: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 20,
  },
  addressValidationMapErrorText: {
    fontSize: 16,
    color: "#C4453D",
    textAlign: "center",
  },
  addressValidationBottomSection: {
    padding: 20,
    borderTopWidth: 1,
    borderTopColor: "#DDE3E2",
    backgroundColor: "#FFFFFF",
  },
  addressValidationDistanceText: {
    fontSize: 18,
    fontWeight: "600",
    color: "#2E3A38",
    textAlign: "center",
    marginBottom: 16,
  },
  addressValidationButtonRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    gap: 12,
  },
  addressValidationButton: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 6,
    alignItems: "center",
    justifyContent: "center",
  },
  addressValidationButtonConfirm: {
    backgroundColor: "#1F8A5B",
  },
  addressValidationButtonWrong: {
    backgroundColor: "#C4453D",
  },
  addressValidationButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
  },
  addressWrongModalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.7)",
    justifyContent: "center",
    alignItems: "center",
  },
  addressWrongModalContent: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    padding: 24,
    width: "90%",
    maxWidth: 500,
    boxShadow: "0 2px 6px rgba(15, 26, 25, 0.08), 0 16px 40px rgba(15, 26, 25, 0.12)",
    elevation: 10,
  },
  addressWrongModalTitle: {
    fontSize: 22,
    fontWeight: "bold",
    color: "#2E3A38",
    marginBottom: 16,
    textAlign: "center",
  },
  addressWrongModalMessage: {
    fontSize: 16,
    color: "#5C6B68",
    marginBottom: 12,
    lineHeight: 24,
    textAlign: "left",
  },
  addressWrongModalWarning: {
    fontSize: 16,
    color: "#C4453D",
    marginTop: 12,
    marginBottom: 20,
    fontWeight: "600",
    lineHeight: 24,
    textAlign: "left",
  },
  addressWrongModalButton: {
    backgroundColor: "#0E7C6B",
    paddingVertical: 14,
    borderRadius: 6,
    alignItems: "center",
    justifyContent: "center",
  },
  addressWrongModalButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
  },
});



export default EditBooking;


